// tabs/action-bar.ts — the docked Action Bars pane (BG3/WoW-style hotbars). Reads caster.action_bars
// (projected from saved base rows) and renders the whole block as ONE CSS grid: a fixed
// 16 button columns shared across every bar (row), with thin divider-track columns interleaved at each
// divider boundary. Because the grid is shared, every row is column-aligned and each divider is a SINGLE
// element spanning all rows (grid-row: 1 / -1) — one unit, one ||-grip, drags as one.
//
// Buttons: rune ability buttons (generateAbilityButton) for abilities; chromeless skill-icon buttons for
// skills (useAbility(id), resolved via window.SKILL_CATALOG). Slots are square (aspect-ratio:1) and scale
// to fill the width; below a min cell the grid x-scrolls (thin slider). Persisted via
// update_player_character_action_bars_inc.php; edits patch held saved rows optimistically.

import { paintedWallProjectileKind } from '../../shared/painted-wall-rules';
import type {} from '../../shared/instant-projectile-bridge';
import { replaceHydratedHtml } from '../../shared/replace-hydrated-html';
import { bindEncounterInfoCircle } from '../../shared/encounter-info-circle';
import { patchMarkup, rememberPatchMarkup } from '../patch-markup';
import { refuseUnported } from '../../janus-engine/unported';
import { actionBarSavedRows } from '../../janus-engine/transport/action-bar-saved-rows';
import { isSpellGrantMode } from '../../janus-engine/ability/spell-grant-mode';
import { fixedSummonCreatureId, usesGroundSummonPlacement } from '../../janus-engine/ability/ground-summon-placement';
import {
    abilityActionPointCost,
    barPicksVariableTargetCharges,
    determineAbilityButtonInputId,
    determineAbilityButtonInputFamily,
    generateAbilityButton,
    renderSimpleTriggerAbilityButton,
    requiredCasterStatusReason,
    runeMountHtml,
    sheetSpellManaDiscount,
    sheetChargeCost,
    sheetChargeBudget,
    sheetManaBudget,
    spellIsLevelUpcastable,
    type AbilityButtonInputFamily,
} from '../ability-button';
import { iconPh, abilityEntriesOfType, abilityEntryId, abilityEntryCharges, abilityEntryMaxCharges, abilityEntryName, abilityEntryRequiresLevel, passiveActive, resolveAbilityNest, sortAbilitySection, uuidOf, sourceIdentity, blobSourceIndex, abilitySourceMap, requiredSkillTierEntries, abilitySkillSource, ACTIONABLE_ABILITY_TYPES, statusChoiceOptions } from './ability-row';
import { renderAbilityIslandIcons, renderAbilityFlyout, islandCatGroupForKey, islandGroupsPresent, type IslandOpen } from './ability-island';
import { classMechanicBars, partyClassMechanicBars } from './weapon-abilities';
import { casterIsCurrentTurn, casterIsInCombat, portraitTurnState, renderStealthRow, renderTurnHalo, turnHaloKey, turnHourglassArt } from './movement';
import { casterHasSkills, renderActionBarSkillsTray, DEFAULT_SKILL_TYPE } from './skills';
import { sanguineDmgPickOptionIds, sanguineDamageTypeName } from '../sanguine-damage-type-pick';
import { isDedicatedCastSanguine } from '../sanguine-dedicated-cast';
import type { ExtraordinaryTargets } from '../../janus-engine/sanguine/extraordinary-plan-21';
import { ftAbilityHumStart, ftAbilityHumStop } from '../ability-hum';
import { equipInventory } from '../../websocket/inventory-equip-truth';
import { bindExplicitWeaponAttack, bindNaturalWeaponAttack, explicitWeaponFamilyRefusal, plainWeaponAbility, reportWeaponAttackRefusal } from '../../janus-engine/actions/explicit-weapon-attack';
import { fullDefinition } from '../../janus-engine/reactions/php-row-rules';
import { weaponAttackSkill } from '../../janus-engine/actions/weapon-attack-skill';
import { findCarriedInventoryEntry } from '../../janus-engine/inventory/find-inventory-entry';
import { inventoryFloorAvailable } from '../../janus-engine/compile/inventory-facet';
import { readInventoryEquippedToken, readInventoryRows } from '../../janus-engine/compile/inventory-raw-readers';
import type { Ft3dAoeTargetPreview, Ft3dAoePlacementEnvelope, Ft3dAoePlacementStageEnvelope, Ft3dAoePoint } from '../../shared/ft3d-aoe-placement-contract';
import type { SceneObjectAction } from '../../encounter-creation-engine/scene-object-action-menu';
import { DANCE_CLIPS, DANCE_PLAYLIST_MAX, normalizeDancePlaylist } from '../../encounter-creation-engine/ft3d-emote-clips';
import { claimFt3dInteractionMode, releaseFt3dInteractionMode } from '../../shared/ft3d-interaction-mode';
import { hasCampaignPermission, plTotal, posCoord, sizeIdOf } from '../../shared/array-types';
import { combatCenterDistanceFt } from '../../../../ft3d/engine/ft3d-combat-distance-core.js';
import { instantProjectileCount, type ProjectilePool, type InstantProjectileCast } from '../../shared/projectile-targeting';
import { nextSpellVolleyPool, notHeldDefinitionId } from '../../janus-engine/sanguine/next-spell';
import { HandBack } from '../../janus-engine/ability/context';
import { bodyPairsLimbs, isPairedLimb } from '../../shared/paired-limb-forms';
import { pageSkillEntries } from '../../shared/page-skill-entries';
import {
    ABILITY_TARGETING_ESTIMATES_SCHEMA,
    AbilityTargetingIntentLifecycle,
    AbilityTargetingPickDeduper,
    abilityAllowsInventoryTargets,
    abilityRangeFeetForId,
    classifyAbilityDispatchVariant,
    deriveAbilityTargetingNumericMaxTargets,
    deriveAbilityTargetingProfile,
    deriveContestedSkillTargetingProfile,
    estimateSkillAttackHitChance,
    type ActiveAbilityTargetingIntent,
    type AbilityTargetingCandidateFact,
    type AbilityTargetingFeedbackReason,
    type AbilityTargetingNumericOptionKind,
    type AbilityTargetingProfile,
} from '../../shared/ability-targeting-contract';
import type {
    ActionBarRow as ABBar,
    ActionBarSlot as ABSlot,
    CachedCaster,
    CasterItemNode,
} from '../types';

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function itemNodeOf(value: unknown): CasterItemNode | null {
    return recordOf(value) as CasterItemNode | null;
}

function callActionBarGlobal(name: string): void {
    const fn = (window as unknown as Record<string, unknown>)[name];
    if (typeof fn === 'function') (fn as () => void)();
}

// Which ability-island category (if any) is currently expanded per caster. Module-level so it survives
// caster_update repaints (the fly-up strip stays open across live updates instead of snapping shut).
const abilityIslandOpen = new Map<string, IslandOpen>();
// Which class-mechanic is shown in the dock readout when a caster has 2+ (the ▲/▼ pivot). Module-level
// so the choice survives caster_update repaints; classMechanicBars wraps the index to the mechanic count.
const classMechIndex = new Map<string, number>();

// Shared projection for compact surfaces (party frame, future encounter cards). This returns the
// exact action-bar class-mechanic schema in read-only mode; callers own placement and interaction.
window.ftRenderActionBarClassMechanics = (caster: CachedCaster, casterId: string): string =>
    classMechanicBars(caster, true, casterId, 0);
window.ftRenderPartyClassMechanics = (caster: CachedCaster, casterId: string): string =>
    partyClassMechanicBars(caster, casterId);
// Action-bar SETTINGS tray (gear) open-state per caster, and the keybind CONFIGURE (bind) mode per caster.
// Module-level so both survive caster_update repaints. `bindListening` is the single slot currently waiting
// for its key (only one at a time, any caster) — null when not capturing.
const settingsOpen = new Map<string, boolean>();
const bindMode = new Map<string, boolean>();
// AP/reaction pill fold-up (FT3D dock only). Like the other dock overlays, this is client UI state so it
// survives websocket repaints without entering the persisted action-bar layout.
const actionPointFoldOpen = new Map<string, boolean>();
// Skills quick-tray popup (the launcher left of the status cluster): open-state + the selected skill TYPE
// (innate by default) per caster. Module-level so both survive caster_update repaints (like settingsOpen).
const skillsPopupOpen = new Map<string, boolean>();
const skillsPopupType = new Map<string, string>();
// "Show all skills" toggle per caster — when on, the tray also lists UNTRAINED (tier-0) skills (flagged red,
// they roll at disadvantage). Default off (trained only).
const skillsPopupShowAll = new Map<string, boolean>();
// Small-screen TOP-ROW tab (owner 2026-06-27): on phone/tablet the meta row (status cluster + ability island +
// controls + contextual actions) is too crowded, so it collapses to a tab strip showing ONE group at a time.
// Tabs: Abilities / Spells (only when the caster has spells/curses/mentas) / Status / Conditional (only when a
// contextual action exists). The island's category icons split by group (islandCatGroupForKey): action-economy
// + movement → Abilities, the castable leveled pools → Spells. The CONDITIONAL tab holds the situational actions
// (Stand Up, Stop Bleeding, Escape Grapple…) — its mere presence (+ a subtle dot) is the "you've got an extra
// button" signal; it vanishes when there's nothing situational. The settings GEAR is no longer a tab — it's a
// standalone button at the right of the strip. All tabs icon-only to save width. Module-level so the choice
// survives caster_update repaints. Desktop ignores this (CSS shows the full combined island + all groups).
type TopTab = 'abilities' | 'spells' | 'curses' | 'mentas' | 'status' | 'conditional';
const topTab = new Map<string, TopTab>();
let bindListening: { casterId: string; barIdx: number; slotIdx: number } | null = null;

type ActionBarOverlayLaneOwner = 'ability-island' | 'action-points' | 'skills' | 'settings'
    | 'weapon' | 'ability-choice' | 'item-target';

/** Claim the dock's single overlay lane for one panel. Every opener uses this same transition so
 *  category, Skills, Settings, AP, weapon, choice, and item-target folds cannot stack in either direction. */
function claimActionBarOverlayLane(casterId: string, owner: ActionBarOverlayLaneOwner): void {
    if (owner !== 'ability-island') abilityIslandOpen.delete(casterId);
    if (owner !== 'action-points') actionPointFoldOpen.delete(casterId);
    if (owner !== 'skills') skillsPopupOpen.delete(casterId);
    if (owner !== 'settings') {
        settingsOpen.delete(casterId);
        bindMode.delete(casterId);
        if (bindListening?.casterId === casterId) bindListening = null;
    }
    if (owner !== 'weapon' && abWeaponMenu?.casterId === casterId) abWeaponMenu = null;
    if (owner === 'ability-choice' || abChoice?.casterId === casterId) {
        abChoice = null;
        abChoiceBranch = null;
    }
    if (owner !== 'item-target' && abItemTargetMenu?.casterId === casterId) setItemTargetMenu(null);
}
// BG3-style ability CHOICE bar (upcast level / shapeshift form / damage type / …). Only ONE open at a time
// (any caster); module-level so it survives caster_update repaints. `inputId` is the ability's canonical
// data-input_id_reference (used only to pick which control to render). The control itself is class-tagged
// (.ft-abchoice-ctl) NOT id'd — the open PC-sheet spell table renders the same canonical input id, so reusing
// it would create a duplicate id (getElementById would read the wrong one). On Cast we read this panel's own
// control and fire useAbilityVariable1(ability, value) — the IDENTICAL POST the input path produces.
// `spellType` is the grant/charged cast marker. Its source owns variable_2; a free grant never
// replaces that provenance with the ordinary spell-level control.
let abChoice: { casterId: string; abilityId: string; inputId: string; ritual: boolean; isAoe: boolean; consumeInvId?: string; quickRitual?: boolean; spellType?: string; itemSourced?: boolean; sourceOccupied?: boolean; sourceId?: string; summonCast?: () => void; aoeButton?: HTMLElement } | null = null;
const aoeStagedTargets = new Map<string, Ft3dAoeTargetPreview>();
type AbilityTargetingRuntime = {
    lifecycle: AbilityTargetingIntentLifecycle;
    pickDeduper: AbilityTargetingPickDeduper;
    sceneLegal: { intentId: number; entityIds: Set<string>; facts: Map<string, AbilityTargetingCandidateFact> } | null;
    deferredDispatch?: { intentId: number; run: (confirmed: ActiveAbilityTargetingIntent) => void } | null;
    singleTargetConfirmTimer?: number | null;
    rangeReadEpoch?: number;
};
const abilityTargetingRuntime = (() => {
    const sharedWindow = window as Window & { __ftAbilityTargetingRuntime?: AbilityTargetingRuntime };
    if (!sharedWindow.__ftAbilityTargetingRuntime) {
        sharedWindow.__ftAbilityTargetingRuntime = {
            lifecycle: new AbilityTargetingIntentLifecycle(),
            pickDeduper: new AbilityTargetingPickDeduper(),
            sceneLegal: null,
            deferredDispatch: null,
            singleTargetConfirmTimer: null,
        };
    }
    return sharedWindow.__ftAbilityTargetingRuntime;
})();
const abilityTargetingLifecycle = abilityTargetingRuntime.lifecycle;
const abilityTargetingPickDeduper = abilityTargetingRuntime.pickDeduper;

function clearSingleTargetAutoConfirm(): void {
    if (abilityTargetingRuntime.singleTargetConfirmTimer != null) {
        window.clearTimeout(abilityTargetingRuntime.singleTargetConfirmTimer);
    }
    abilityTargetingRuntime.singleTargetConfirmTimer = null;
}
// BG3-style WEAPON fold-out (owner 2026-07-02): clicking an equipped-weapon slot in the DOCK opens a
// second bar of every attack usable WITH that weapon (basic attack first, grouped by provenance, sorted
// by required level) instead of firing immediately; re-click fires the plain attack. Single-open like
// abChoice (opening one closes the other); module-level so it survives caster_update repaints.
// `kind` extends the SAME panel to non-weapon nested-effect items (owner 2026-07-19): a potion/coating/
// wondrous item whose nested abilities are the choices ('nested'), vs a weapon's attack list ('weapon').
// The two differ only in which entries are collected, the group labelling, and what a RE-CLICK means —
// a weapon re-click fires its plain swing, a consumable has no "plain" action so re-click just closes.
let abWeaponMenu: { casterId: string; invId: string; kind: 'weapon' | 'natural' | 'nested' } | null = null;
// Weapon stance belongs to this actor's inventory instance, not the transient attack menu/draft.
const thrownWeaponModes = new Set<string>();
// Direct/Lob for a thrown weapon (owner 2026-09-15, one toggle for every throw): weapons default DIRECT
// (straight line of effect); Lob flies the flat 45° arc over cover. Posted as `throw_lob`; Janus re-judges.
const lobWeaponModes = new Set<string>();
const weaponModeKey = (casterId: string, invId: string): string => `${casterId.toUpperCase()}:${invId.toUpperCase()}`;
function weaponInThrowMode(casterId: string, invId: string, weapon: CasterItemNode): boolean {
    return ftLayerHas(weapon, 'item_throwable') && thrownWeaponModes.has(weaponModeKey(casterId, invId));
}
function weaponInLobMode(casterId: string, invId: string): boolean {
    return lobWeaponModes.has(weaponModeKey(casterId, invId));
}
// Own-inventory target picker for item-gated abilities (Frostwind Blade and future armor/shield/ammo
// imbues). First click opens; re-click uses the server-authoritative equipped default. Picking a row
// stamps its inventory UUID into the canonical target selection before firing the normal cast.
let abItemTargetMenu: { casterId: string; abilityId: string; allowedTypes: string[] } | null = null;

/**
 * The ONE place abItemTargetMenu changes. It used to be assigned at 14 sites, which is exactly
 * how a sustained cue leaks — the hum must never hang off individual gestures.
 *
 * ⚠ Closing the picker must NOT silence a creature-targeting hum that is still armed: both lanes
 * share one controller, so we only stop when no AbilityTargetingIntent is live.
 */
function setItemTargetMenu(next: { casterId: string; abilityId: string; allowedTypes: string[] } | null): void {
    const was = abItemTargetMenu;
    abItemTargetMenu = next;
    if (next) {
        if (!was || was.abilityId.toUpperCase() !== next.abilityId.toUpperCase()) {
            const caster = casterFromCache(next.casterId);
            ftAbilityHumStart(next.abilityId, caster ? findAbility(caster, next.abilityId) : null);
        }
    } else if (was && !abilityTargetingLifecycle.current()) {
        ftAbilityHumStop();
    }
}

/** The FT3D iframe's own perception projection (server-authoritative, already senses-filtered). The
 *  parent's `ft3dPerceivedCreatureIds` is a COPY taken from the iframe's last perception post, and it is
 *  nulled on actor clear - arming from a stale/absent copy silently produced zero candidates (no cyan
 *  legal rings, no LOS line) while the targeting panel still looked armed. Prefer the live projection. */
function ft3dLivePerceivedEntityIds(inventory = false): string[] | null {
    try {
        const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
        const projection = (frame?.contentWindow as (Window & {
            __ft3dCampaignPerception?: { visibleCreatureIds?: unknown; visibleInventoryIds?: unknown };
        }) | null | undefined)?.__ft3dCampaignPerception;
        const ids = inventory ? projection?.visibleInventoryIds : projection?.visibleCreatureIds;
        return Array.isArray(ids) ? ids.map(value => String(value)) : null;
    } catch {
        return null;
    }
}

function perceivedAbilityTargetCandidateIds(abilityId: string, casterId: string,
    profile = abilityTargetingLifecycle.current()?.profile): string[] {
    const parentCopy = window.FateTide?.ft3dPerceivedCreatureIds;
    const perceived = ft3dLivePerceivedEntityIds() ?? (parentCopy instanceof Set ? [...parentCopy] : []);
    if (profile?.heldItemTargetTypes?.length) {
        // Held-item imbue: the candidates are the HOLDERS (creature ids only — the scene's rings, snap and
        // reach approach all speak placements). Their equipped rows are resolved per pick, never listed here.
        const caster = casterFromCache(casterId);
        const holders = new Set(caster ? heldItemHolders(caster, profile.heldItemTargetTypes).map(h => h.holderId) : []);
        return perceived.map(value => String(value).trim().toUpperCase()).filter(entityId => holders.has(entityId));
    }
    const types = profile?.inventoryTargetTypes;
    const encounter = recordOf(window.FateTide?.encounterCache?.get(String(window.ftGetActiveEncounterId?.() ?? '')));
    const inventories = types?.length && Array.isArray(encounter?.encounter_inventories_arr) ? encounter.encounter_inventories_arr : [];
    const catalog = recordOf(encounter?.catalog);
    const matchingInventory = new Set(inventories.filter(row => {
        const inventory = recordOf(recordOf(row)?.inventory);
        const item = catalog?.[String(inventory?.inventory_item_id ?? '')];
        if (types?.includes(String(ftItemsCol(item ?? row, 'item_type') ?? '').toLowerCase())) return true;
        // A gate naming war_machine also admits an operated-in-place object (operable_object: piano, floor harp …).
        return Boolean(types?.includes('war_machine') && Number(ftLayerValue(item ?? row, 'operable_object')) === 1);
    }).map(row => String(recordOf(recordOf(row)?.inventory)?.inventory_id ?? '').toUpperCase()));
    return [...(types?.length ? [] : perceived), ...(profile?.allowInventoryTargets ? ft3dLivePerceivedEntityIds(true) ?? [] : [])]
        .map(value => String(value).trim().toUpperCase())
        .filter(Boolean)
        .filter(entityId => !types?.length || matchingInventory.has(entityId))
        .filter(entityId => !window.FateTide?.abilityDropTargetFailure?.(abilityId, casterId, entityId));
}

/** The scene's perception waits on the active encounter's inventory floor (no PHP fallback). Name that refusal rather
 * than reporting "no perceived target" for an empty list the scene never got to compute. The FT3D scene publishes its
 * own gate (it may read a row this page never cached); a page without a scene judges its cached encounter row. */
function abilityTargetingFloorRefusal(): string | null {
    try {
        const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
        const gate = (frame?.contentWindow as (Window & {
            __ft3dCampaignPerceptionGate?: { reason?: unknown } | null;
        }) | null | undefined)?.__ft3dCampaignPerceptionGate;
        if (gate && typeof gate.reason === 'string') return gate.reason;
    } catch { /* A cross-origin scene publishes nothing here. */ }
    const encounter = recordOf(window.FateTide?.encounterCache?.get(String(window.ftGetActiveEncounterId?.() ?? '')));
    if (!encounter || inventoryFloorAvailable(encounter)) return null;
    return typeof encounter._inventory_refusal === 'string' ? encounter._inventory_refusal : 'unported:inventory-base-unavailable';
}

function publishAbilityTargetingIntent(): void {
    window.dispatchEvent(new CustomEvent('ft:ability-targeting-intent', {
        detail: abilityTargetingLifecycle.envelope(),
    }));
}

function setAbilityInventoryTargets(intentId: number, enabled: boolean): void {
    const active = abilityTargetingLifecycle.current();
    if (!active || active.intentId !== intentId || !active.profile.supportsInventoryTargets) return;
    const candidates = perceivedAbilityTargetCandidateIds(active.abilityId, active.casterId,
        { ...active.profile, allowInventoryTargets: enabled });
    const updated = abilityTargetingLifecycle.setInventoryTargets(intentId, enabled, candidates);
    if (!updated) return;
    if (updated.selectedEntityIds.length !== active.selectedEntityIds.length) clearSingleTargetAutoConfirm();
    abilityTargetingRuntime.sceneLegal = null;
    mirrorAbilityTargetSelection(updated, 'ability-targeting-inventory-option');
    publishAbilityTargetingIntent();
    repaintActionBars(updated.casterId);
}

function mirrorAbilityTargetSelection(intent: ActiveAbilityTargetingIntent | null, source: string): void {
    window.ftSetTargetSelection?.(intent?.selectedEntityIds.join(',') ?? '', source);
}

function cancelAbilityTargeting(repaint = true): boolean {
    abilityTargetingRuntime.rangeReadEpoch = (abilityTargetingRuntime.rangeReadEpoch ?? 0) + 1;
    const active = abilityTargetingLifecycle.current();
    if (!active || !abilityTargetingLifecycle.cancel()) {
        releaseFt3dInteractionMode('ability-targeting');
        return false;
    }
    if (abChoice?.casterId === active.casterId
        && abChoice.abilityId.toUpperCase() === active.abilityId.toUpperCase()) {
        abChoice = null;
    }
    mirrorAbilityTargetSelection(null, 'ability-targeting-cancel');
    // The targeting hum is bound to the LIFECYCLE, never to a gesture: there is no right-click
    // and no click-away cancel, and Escape / the ✕ / arming another ability / losing the FT3D
    // actor all funnel through here. A gesture-level hook would leak a LOOPING cue with no UI
    // left to stop it. See ability-hum.ts.
    ftAbilityHumStop();
    clearSingleTargetAutoConfirm();
    abilityTargetingRuntime.deferredDispatch = null;
    abilityTargetingRuntime.sceneLegal = null;
    abilityTargetingPickDeduper.reset();
    publishAbilityTargetingIntent();
    if (repaint) repaintActionBars(active.casterId);
    releaseFt3dInteractionMode('ability-targeting');
    return true;
}

/** One Action Bar interaction owns the scene at a time. Retire FT3D placement before arming
 * a non-AOE targeting draft. */
function cancelActiveAoePlacement(): void {
    const aoe = window.aoeManager;
    if (aoe?.ft3dActive) aoe.cancelFt3d?.();
}

function confirmAbilityTargeting(casterId: string): boolean {
    const active = abilityTargetingLifecycle.current();
    if (!active || active.casterId !== casterId) return false;
    const confirmed = abilityTargetingLifecycle.consumeConfirmation(active.intentId);
    if (!confirmed) return true;
    releaseFt3dInteractionMode('ability-targeting');
    const deferred = abilityTargetingRuntime.deferredDispatch?.intentId === confirmed.intentId
        ? abilityTargetingRuntime.deferredDispatch.run : null;
    // Consume the intent before invoking the legacy helper. A synchronous re-click or duplicated
    // Confirm event therefore has nothing left to dispatch, while target_id still carries the draft.
    mirrorAbilityTargetSelection(confirmed, 'ability-targeting-confirm');
    ftAbilityHumStop();
    clearSingleTargetAutoConfirm();
    abilityTargetingRuntime.deferredDispatch = null;
    abilityTargetingRuntime.sceneLegal = null;
    abilityTargetingPickDeduper.reset();
    // A weapon slot's first press arms the plain swing WHILE keeping its attack fold-out open as an
    // optional override (ftDispatchWeaponClick preserveWeaponMenu). Once the attack actually fires the
    // override is moot — fold it away (owner 2026-09-14).
    if (abWeaponMenu?.casterId === casterId) abWeaponMenu = null;
    publishAbilityTargetingIntent();
    repaintActionBars(casterId);
    if (confirmed.profile.projectilePool) {
        void window.ftUseProjectileAssignments?.(confirmed.casterId, confirmed.abilityId,
            confirmed.selectedEntityIds.map(targetSelection => ({ targetSelection })), confirmed.profile.projectilePool.instant);
    } else if (deferred) {
        deferred(confirmed);
    } else if (confirmed.invocation.kind === 'plain' && confirmed.profile.weaponAttack) {
        const weapon = confirmed.profile.weaponAttack;
        window.useAbilityWithWeapon?.(confirmed.invocation.abilityId, weapon.inventoryId, null,
            weapon.throwing ? 'throw_weapon' : null);
    } else if (confirmed.invocation.kind === 'plain') {
        window.useAbility?.(confirmed.invocation.abilityId);
    } else if (confirmed.invocation.kind === 'variable-1-number') {
        window.openSideLog?.();
        if (confirmed.invocation.secondaryChoice) {
            window.useAbilityVariable2?.(
                confirmed.invocation.abilityId,
                String(confirmed.invocation.value),
                confirmed.invocation.secondaryChoice,
            );
        } else {
            window.useAbilityVariable1?.(confirmed.invocation.abilityId, String(confirmed.invocation.value));
        }
    } else if (confirmed.invocation.kind === 'variable-1-choice') {
        window.openSideLog?.();
        window.useAbilityVariable1?.(confirmed.invocation.abilityId, confirmed.invocation.value);
    }
    return true;
}

function stageAbilityTargetPick(intentId: number, entityId: string, pickId = '', remove = false): boolean {
    const active = abilityTargetingLifecycle.current();
    const normalizedEntityId = String(entityId ?? '').trim().toUpperCase();
    if (active && active.intentId === intentId && active.profile.heldItemTargetTypes?.length) {
        return remove ? false : stageHeldItemTargetPick(active, normalizedEntityId, pickId);
    }
    if (!active || active.intentId !== intentId
        || !active.candidateEntityIds.includes(normalizedEntityId)
        || (abilityTargetingRuntime.sceneLegal?.intentId === intentId
            && !abilityTargetingRuntime.sceneLegal.entityIds.has(normalizedEntityId))) return false;
    if (!abilityTargetingPickDeduper.accept(intentId, normalizedEntityId, pickId)) return true;
    if (!remove && (active.profile.projectilePool || !active.selectedEntityIds.includes(normalizedEntityId))
        && active.selectedEntityIds.length >= active.profile.maxTargets) {
        showAbilityTargetingFeedback(intentId, 'max-targets');
        return false;
    }
    const removing = active.selectedEntityIds.includes(normalizedEntityId);
    const staged = abilityTargetingLifecycle.stagePick(intentId, normalizedEntityId, remove);
    if (!staged) return false;
    clearSingleTargetAutoConfirm();
    mirrorAbilityTargetSelection(staged, 'ability-targeting-stage');
    publishAbilityTargetingIntent();
    repaintActionBars(staged.casterId);
    // The last required world pick resolves the ability. Single-target spells therefore cast on the
    // target click, while multi-target spells still allow an early Cast until their ceiling is reached.
    if (!staged.profile.projectilePool && !removing && staged.selectedEntityIds.length >= staged.profile.maxTargets) {
        confirmAbilityTargeting(staged.casterId);
    }
    return true;
}

function refreshAbilityTargetCandidates(): void {
    const active = abilityTargetingLifecycle.current();
    if (!active) return;
    const candidates = perceivedAbilityTargetCandidateIds(active.abilityId, active.casterId);
    let refreshed = abilityTargetingLifecycle.updateCandidates(active.intentId, candidates);
    if (abilityTargetingRuntime.sceneLegal?.intentId === active.intentId) {
        refreshed = abilityTargetingLifecycle.pruneSelected(active.intentId, [...abilityTargetingRuntime.sceneLegal.entityIds]);
    }
    if (!refreshed) return;
    mirrorAbilityTargetSelection(refreshed, 'ability-targeting-candidates');
    publishAbilityTargetingIntent();
    repaintActionBars(refreshed.casterId);
}

function applyAbilityTargetingSceneLegalCandidates(
    intentId: number,
    entityIds: string[],
    rawCandidates: Array<Record<string, unknown>> = [],
): boolean {
    const active = abilityTargetingLifecycle.current();
    if (!active || active.intentId !== intentId) return false;
    const perceived = new Set(perceivedAbilityTargetCandidateIds(active.abilityId, active.casterId));
    const legal = new Set(entityIds
        .map(entityId => String(entityId).trim().toUpperCase())
        .filter(entityId => perceived.has(entityId)));
    const facts = new Map<string, AbilityTargetingCandidateFact>();
    for (const candidate of rawCandidates) {
        const entityId = String(candidate.entityId ?? '').trim().toUpperCase();
        const rangeState = String(candidate.rangeState ?? 'unknown');
        const lineOfEffect = String(candidate.lineOfEffect ?? 'clear');
        const coverTier = String(candidate.coverTier ?? 'none');
        const delta = Number(candidate.effectiveDefenseDelta ?? 0);
        if (!entityId || !perceived.has(entityId)
            || !['in-range', 'out-of-range', 'unknown'].includes(rangeState)
            || !['clear', 'blocked'].includes(lineOfEffect)
            || !['none', 'partial', 'heavy', 'blocked'].includes(coverTier)
            || !Number.isFinite(delta)) continue;
        facts.set(entityId, {
            entityId,
            targetable: candidate.targetable === true,
            rangeState: rangeState as AbilityTargetingCandidateFact['rangeState'],
            lineOfEffect: lineOfEffect as AbilityTargetingCandidateFact['lineOfEffect'],
            coverTier: coverTier as AbilityTargetingCandidateFact['coverTier'],
            effectiveDefenseDelta: Math.trunc(delta),
            hunkerApplied: candidate.hunkerApplied === true,
            flanked: candidate.flanked === true,
            flankingAllyName: typeof candidate.flankingAllyName === 'string' && candidate.flankingAllyName.trim() !== ''
                ? candidate.flankingAllyName.trim()
                : null,
        });
    }
    abilityTargetingRuntime.sceneLegal = { intentId, entityIds: legal, facts };
    const updated = abilityTargetingLifecycle.pruneSelected(intentId, [...legal]);
    if (!updated) {
        // The legal count feeds the targeting-bar hint ("no target in range...") even when no staged
        // selection changed, so the panel never silently looks armed over an empty legal set.
        repaintActionBars(active.casterId);
        return false;
    }
    mirrorAbilityTargetSelection(updated, 'ability-targeting-scene-legal');
    publishAbilityTargetingHitEstimates(updated, facts);
    publishAbilityTargetingIntent();
    repaintActionBars(updated.casterId);
    return true;
}

function showAbilityTargetingFeedback(intentId: number, reason: AbilityTargetingFeedbackReason, detail?: string): boolean {
    const active = abilityTargetingLifecycle.current();
    if (!active || active.intentId !== intentId) return false;
    // A scene-authored sentence (why a Reach approach was refused) beats the generic reason word.
    // displayWarning renders innerHTML, so escape the detail even though it arrives from our own iframe.
    const sceneDetail = String(detail ?? '').trim()
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const message = sceneDetail
        ? `${active.abilityName}: ${sceneDetail}`
        : reason === 'out-of-range'
        ? `${active.abilityName}: that target is out of range.`
        : reason === 'blocked-line-of-effect'
        ? `${active.abilityName}: the line of effect to that target is blocked.`
        : reason === 'max-targets'
            ? `${active.abilityName} can target at most ${active.profile.maxTargets} ${active.profile.allowInventoryTargets ? 'target' : 'creature'}${active.profile.maxTargets === 1 ? '' : 's'}.`
        : active.candidateEntityIds.length === 0 && abilityTargetingFloorRefusal()
            ? `${active.abilityName}: perception is paused while the encounter floor is unavailable.`
        : `${active.abilityName} cannot target that ${active.profile.allowInventoryTargets ? 'object or creature' : 'creature'}.`;
    window.displayWarning?.(message);
    return true;
}

function setAbilityTargetingNumericOption(intentId: number, rawValue: number): boolean {
    const active = abilityTargetingLifecycle.current();
    if (!active || active.intentId !== intentId) return false;
    const instant = active.profile.projectilePool?.instant;
    if (instant && active.invocation.kind === 'variable-1-number') {
        if (!armInstantProjectileSpell(active.casterId, instant.parentId, String(rawValue), '', instant.variable2)) return false;
        const rearmed = abilityTargetingLifecycle.current();
        if (!rearmed || rearmed.intentId === intentId) return false;
        for (const id of active.selectedEntityIds) abilityTargetingLifecycle.stagePick(rearmed.intentId, id);
        mirrorAbilityTargetSelection(abilityTargetingLifecycle.current(), 'ability-targeting-option');
        publishAbilityTargetingIntent();
        repaintActionBars(active.casterId);
        return true;
    }
    const caster = casterFromCache(active.casterId);
    const ability = caster ? findAbility(caster, active.abilityId) : null;
    const dynamicMaxTargets = ability && active.invocation.kind === 'variable-1-number'
        ? dynamicActionMaxTargets(ability, actionInputFamily(ability, active.invocation.inputId), String(rawValue))
        : undefined;
    // The upcast ceiling keeps Twin / Bouncing's +1 as the level changes (sanguineTargetBonus).
    const ceiling = dynamicMaxTargets !== undefined && caster && ability ? dynamicMaxTargets + sanguineTargetBonus(caster, ability) : dynamicMaxTargets;
    const updated = abilityTargetingLifecycle.updateNumericInvocation(intentId, rawValue, ceiling);
    if (!updated) return false;
    // Lowering an additional-target upcast may trim the draft. Mirror that authoritative client draft
    // immediately so neither the cast request nor the targeting chips retain now-excess target ids.
    mirrorAbilityTargetSelection(updated, 'ability-targeting-option');
    publishAbilityTargetingIntent();
    repaintActionBars(updated.casterId);
    return true;
}

function setAbilityTargetingChoiceOption(intentId: number, rawValue: string): boolean {
    const updated = abilityTargetingLifecycle.updateChoiceInvocation(intentId, rawValue);
    if (!updated) return false;
    publishAbilityTargetingIntent();
    repaintActionBars(updated.casterId);
    return true;
}

function setAbilityTargetingSecondaryChoice(intentId: number, rawValue: string): boolean {
    const updated = abilityTargetingLifecycle.updateSecondaryChoice(intentId, rawValue);
    if (!updated) return false;
    publishAbilityTargetingIntent();
    repaintActionBars(updated.casterId);
    return true;
}

const TEAM_TARGET_LAYERS = [
    'effect_only_affects_opposing_team_type',
    'effect_only_affects_same_team_type',
    'target_opposing_team_only',
    'target_friendly_only',
] as const;
const ABILITY_TARGETING_REACH_RANGE_ID = '36D59FF4-6778-96ED-8664-F5D63638C77B';

function resolveFt3dTargetingProfile(profile: AbilityTargetingProfile, caster: CachedCaster): AbilityTargetingProfile {
    if (profile.rangeId !== ABILITY_TARGETING_REACH_RANGE_ID) return { ...profile };
    // A weapon-pinned melee attack has already folded the clicked weapon's reach extension into
    // rangeFeet. Ordinary Reach abilities leave it null and still resolve from the caster here.
    if (profile.rangeFeet !== null) return { ...profile };
    // Janus defaults an absent creature reach to 5 ft. Resolving that same value into the intent lets
    // FT3D enforce adjacency before a pick can ever reach the parent or the cast precheck.
    const reach = Math.max(0, Number(ftLayerValue(caster, 'reach')) || 5);
    return { ...profile, rangeFeet: reach };
}

/** Every grant `source` the caster's rows name for this ability (ActiveAbilityTargetingIntent.grantSourceIds —
 *  the scene moves the origin onto the war machine the caster is piloting). Reads the raw buckets rather than
 *  abilityEntriesOfType(): its id dedupe keeps only the first row, so a native copy of the ability would hide
 *  the row a piloted machine surfaced. */
function abilityGrantSourceIds(caster: CachedCaster, abilityId: string): string[] {
    const wanted = String(abilityId || '').trim().toUpperCase();
    const sources = new Set<string>();
    if (!wanted) return [];
    for (const type of ACTIONABLE_ABILITY_TYPES) {
        const compiled: unknown = caster.computed?.creature?.abilities?.[type];
        for (const entry of [...ftLayerEntries(caster, type), ...(Array.isArray(compiled) ? compiled : [])]) {
            if (abilityEntryId(entry).trim().toUpperCase() !== wanted) continue;
            const row = recordOf(entry);
            const source = String(row?.source ?? recordOf(row?.items)?.source ?? '').trim().toUpperCase();
            if (source) sources.add(source);
        }
    }
    return [...sources];
}

async function armCreatureTargeting(
    casterId: string,
    abilityId: string,
    caster: CachedCaster,
    ability: CasterItemNode | string,
    profile: AbilityTargetingProfile,
    invocation: ActiveAbilityTargetingIntent['invocation'],
    deferredDispatch: ((confirmed: ActiveAbilityTargetingIntent) => void) | null = null,
): Promise<void> {
    // D-050 (owner 2026-09-30): the cast's range extensions are the front end's — Far Bender's upgraded range, the Distant multiplier, and
    // cast-through-summon's origins (cast-range-gate.ts, via window.ftCastRangeExtension).
    const epoch = abilityTargetingRuntime.rangeReadEpoch = (abilityTargetingRuntime.rangeReadEpoch ?? 0) + 1;
    let extensionRaw = typeof ability === 'string' ? null
        : (window as Window & { ftCastRangeExtension?: (casterId: string, ability: unknown) =>
            { unknown: string } | { rangeId: string; multiplier: number; originIds: string[]; originFeet: Array<number | null> } | null })
            .ftCastRangeExtension?.(casterId, ability) ?? null;
    if (extensionRaw && 'unknown' in extensionRaw) {
        const ensure = (window as Window & { ftEnsureCastRangeExtension?: (casterId: string, ability: unknown) => Promise<typeof extensionRaw> })
            .ftEnsureCastRangeExtension;
        if (ensure) {
            try { extensionRaw = await ensure(casterId, ability); }
            catch { extensionRaw = { unknown: 'range-read-failed' }; }
            if (abilityTargetingRuntime.rangeReadEpoch !== epoch) return;
        }
    }
    if (extensionRaw && 'unknown' in extensionRaw) {
        // an extension the page cannot prove (a feat / summon it does not hold): refused before anything is drawn, never a caster-only guess
        const cause = extensionRaw.unknown.startsWith('error:')
            ? (extensionRaw.unknown.includes('status-definition-not-held') ? 'status-definition-not-held' : 'range-read-failed')
            : extensionRaw.unknown;
        const subject: Record<string, string> = {
            'far-bender': 'a range-raising effect on this caster could not be checked',
            'persistent-range': 'a permanent spell-range modifier could not be checked',
            'cast-through-summon': 'the summon’s casting range or position could not be checked',
            'summon-reach': 'the summon’s reach could not be checked',
            'status-definition-not-held': 'an active effect’s definition is not loaded',
            'range-read-timeout': 'the range definition read timed out',
        };
        const code = `cast-range:${cause}`;
        const name = typeof ability === 'string' ? ability : String(ftItemsCol(ability, 'item_name') ?? 'This spell');
        refuseUnported(code, { actorId: casterId, abilityId, site: extensionRaw.unknown },
            `${name}: ${subject[cause] ?? 'its range could not be checked'}. Report code ${code} with the spell and caster names. Targeting has not started.`);
        return;
    }
    const extension = extensionRaw;
    const active = abilityTargetingLifecycle.current();
    const rearming = active?.casterId.toUpperCase() === casterId.toUpperCase()
        && active.abilityId.toUpperCase() === abilityId.toUpperCase();
    // A refreshed draft is still the same precast. Publishing a cancel here also makes observers
    // stop/restart their cue; replace the draft directly and retain the held audio instead.
    if (active && !rearming) cancelAbilityTargeting(false);
    clearSingleTargetAutoConfirm();
    claimFt3dInteractionMode('ability-targeting', () => { cancelAbilityTargeting(); });
    mirrorAbilityTargetSelection(null, 'ability-targeting-arm');
    abilityTargetingPickDeduper.reset();
    abilityTargetingRuntime.sceneLegal = null;
    const resolvedProfile = resolveFt3dTargetingProfile(profile, caster);
    if (extension && extension.rangeId !== resolvedProfile.rangeId) {
        const feet = abilityRangeFeetForId(extension.rangeId);
        if (typeof feet === 'number' && Number.isFinite(feet)) {
            resolvedProfile.rangeId = extension.rangeId;
            resolvedProfile.rangeFeet = feet;
            resolvedProfile.rangeLabel = `${feet} ft`;
        }
    }
    if (extension && extension.multiplier > 1 && resolvedProfile.rangeFeet !== null) {
        resolvedProfile.rangeFeet = resolvedProfile.rangeFeet * extension.multiplier;
        resolvedProfile.rangeLabel = `${resolvedProfile.rangeFeet} ft`;
    }
    resolvedProfile.supportsInventoryTargets = abilityAllowsInventoryTargets(ability);
    resolvedProfile.allowInventoryTargets = Boolean(resolvedProfile.inventoryTargetTypes?.length);
    const armed = abilityTargetingLifecycle.arm({
        casterId,
        abilityId,
        abilityName: typeof ability === 'string' ? (ability || 'Ability') : String(ftItemsCol(ability, 'item_name') ?? 'Ability'),
        actionPointCost: (invocation.kind === 'variable-1-choice' && invocation.value === 'damage_only') || typeof ability === 'string'
            ? null
            : (() => {
                const cost = abilityActionPointCost(ability, caster);
                return typeof cost === 'number' && Number.isFinite(cost) ? Math.max(0, Math.trunc(cost)) : null;
            })(),
        profile: resolvedProfile,
        invocation,
        candidateEntityIds: perceivedAbilityTargetCandidateIds(abilityId, casterId, resolvedProfile),
        grantSourceIds: typeof ability === 'string' ? [] : abilityGrantSourceIds(caster, abilityId),
        ...(extension?.originIds.length ? { rangeOriginIds: extension.originIds, rangeOriginFeet: extension.originFeet } : {}),
    });
    abilityTargetingRuntime.deferredDispatch = deferredDispatch
        ? { intentId: armed.intentId, run: deferredDispatch }
        : null;
    // Saved targets are a convenience draft, never a pre-cast command. Preload only legal perceived
    // candidates up to this ability's ceiling; the player still reviews/manipulates and confirms them.
    let draft = armed;
    // Held items and individual darts require explicit picks; neither inherits saved creature targets.
    const savedTargets = resolvedProfile.heldItemTargetTypes?.length || resolvedProfile.projectilePool
        ? [] : liveSlottedTargets(pcItemId(caster), abilityId);
    for (const saved of savedTargets.slice(0, resolvedProfile.maxTargets)) {
        const staged = abilityTargetingLifecycle.stagePick(armed.intentId, saved.id);
        if (staged) draft = staged;
    }
    mirrorAbilityTargetSelection(draft, 'ability-targeting-saved-draft');
    // Only entering a new precast starts audio. Skills resolve their essence in ftHumKeyFor.
    if (!rearming) ftAbilityHumStart(abilityId, ability);
    publishAbilityTargetingIntent();
    repaintActionBars(casterId);
}

/** True when the FT3D actor dock is mounted for this caster — the only surface where a creature-targeting
 *  intent can be picked on (the scene picks, the dock bar shows the draft). Sheet-only contexts keep
 *  their immediate cast dispatch. */
function ft3dDockPresentFor(casterId: string): boolean {
    const safe = String(casterId).replace(/["\\]/g, '');
    return !!document.querySelector(`[data-ft3d-actor-dock] ${AB_PANE_SEL}[data-ft-caster-id="${safe}"]`);
}

/** Replay one of generateAbilityButton()'s fixed call templates after a target is confirmed. The
 *  ability-button module already executes these exact data-ft-use-snippet values for ordinary sheet
 *  clicks; deferring that established invocation is what preserves spell markers, weapon sources, and
 *  input controls without teaching the targeting lifecycle every legacy POST shape at once. */
function runGeneratedAbilitySnippet(snippet: string): void {
    if (!snippet) return;
    try {
        Function(snippet)();
    } catch (error) {
        refuseUnported('ability-click:dispatch-failed', { error: String(error) }, 'This ability could not be dispatched. Try again.');
    }
}

/** The ONE dispatch for an ability/skill button that is not a slot-grid rune (skill chips in the Skills
 *  tray + Skills tab, skill slots, the Movement tab's Mount/Force Mount/Ride buttons). Owner 2026-08-27:
 *  "abilities buried in the character sheet should be sourced by the same fold-out targeting logic as the
 *  action bar" — before this every one of them called useAbility(id) blind against whatever happened to be
 *  .targeted. Arms the same creature-targeting draft the slot click arms when the ability/skill supports
 *  it and the FT3D dock is up; returns false when the caller should fall back to the immediate cast.
 *  Re-clicking the same button while its draft is armed CONFIRMS (mirrors the slot `sameActive` rule) —
 *  for a contested skill with nothing picked that is the plain roll against the GM's DC. */
export interface AbilityClickDispatchOptions {
    noFetch?: boolean;
    damageOnly?: true;
    /** Exact legacy invocation to replay only after the ability's target draft is confirmed. */
    deferredDispatch?: (() => void) | null;
}

/** Refuse a known-unavailable action before a click can claim targeting or open cast options.
 * Unknown cache verdicts keep the existing cast-time gate. Use the clicked source's component-free
 * mode so an innate/charged spell is never judged as its ordinary somatic cast. */
function actionBarAbilityClickRefused(casterId: string, abilityId: string, button?: HTMLElement | null): boolean {
    const caster = casterFromCache(casterId);
    const ability = caster ? findAbility(caster, abilityId) : null;
    if (!ability) return false;
    const gate = (window as Window & {
        ftAbilityPreconditionRefusal?: (request: {
            casterId: string; encounterId: string; ability: unknown; variable1?: unknown; variable2?: unknown;
        }) => string | null;
    }).ftAbilityPreconditionRefusal;
    if (!gate) return false;
    const btn = button ?? ft3dDockAbilityButton(casterId, abilityId);
    const spellType = btn?.getAttribute('data-spell_type') || '';
    const inputId = btn?.getAttribute('data-input_id_reference') || '';
    const inputValue = abilityButtonInput(btn, inputId)?.value;
    const nestedId = btn?.getAttribute('data-nested-inventory-id') || '';
    const charged = spellType === 'charged_spell' || spellType === 'charged_ability';
    // No input element on the bar: a variable-target spend's X is picked on the targeting stepper after this gate, so ask the
    // gate about its smallest pick (1) rather than the spell-level default; confirm re-checks the chosen X.
    const barAmount = inputId && inputValue === undefined && barPicksVariableTargetCharges(ability) ? '1' : undefined;
    const refusal = gate({
        casterId,
        encounterId: unitScalar(caster?.encounter_creatures?.encounter_id),
        ability,
        variable1: nestedId ? 'nested_inventory_source' : spellType || (inputValue ?? barAmount),
        variable2: nestedId || (charged ? btn?.getAttribute('data-source') : spellType ? inputValue : undefined),
    });
    if (!refusal) return false;
    // The initial click opens these pickers; their required creature/form does not exist until Confirm.
    // Keep every other availability refusal, and validate the real choice at cast time.
    if (refusal === 'Your summon selection choice is empty!' && ftLayerHas(ability, 'user_choice_1_summon')) return false;
    if (refusal === 'Select a form to transform into before casting this.' && ftLayerHas(ability, 'user_choice_1_shapeshift')) return false;
    refuseUnported('ability-click:precondition', { actorId: casterId, abilityId, refusal }, refusal);
    return true;
}

export function ftDispatchAbilityClick(casterId: string, abilityId: string, opts: AbilityClickDispatchOptions = {}): boolean {
    const cid = String(casterId || '');
    const aid = String(abilityId || '');
    if (!cid) return false;
    if (!aid) {
        refuseUnported('ability-click:missing-identity', { actorId: cid, abilityId: aid },
            'This action is missing its character or ability. Reopen the sheet and try again.');
        return true;
    }
    // Inspected sheets do not select their subject. Bind through the shared control funnel before
    // either arming a draft or returning to an immediate cast, which reads the sidelog's caster.
    if (typeof window.ftFt3dSelectActor === 'function' && !ftActionBarSelectUnit(cid)) {
        refuseUnported('ability-click:actor-unavailable', { actorId: cid, abilityId: aid },
            'You cannot act as this creature. It must be available and under your control.');
        return true;
    }
    if (!opts.damageOnly && ft3dDockPresentFor(cid) && actionBarAbilityClickRefused(cid, aid)) return true;
    const active = abilityTargetingLifecycle.current();
    const activeDamageOnly = active?.invocation.kind === 'variable-1-choice' && active.invocation.value === 'damage_only';
    if (active && !opts.damageOnly && !activeDamageOnly && active.casterId === cid && active.abilityId.toUpperCase() === aid.toUpperCase()
        && active.profile.heldItemTargetTypes?.length) {
        // A held-item draft has nothing to confirm; re-click is the default cast (Janus resolves the wielded item).
        cancelAbilityTargeting();
        return false;
    }
    if (active && !!opts.damageOnly === !!activeDamageOnly && active.casterId === cid && (active.abilityId.toUpperCase() === aid.toUpperCase()
        || active.profile.projectilePool?.instant?.parentId.toUpperCase() === aid.toUpperCase())) {
        // Handled either way: a min-1 ability re-clicked with nothing picked stays ARMED (confirm returns
        // false) — it must never fall through to the caller's blind legacy cast (Codex review 2026-08-27).
        confirmAbilityTargeting(cid);
        return true;
    }
    if (!ft3dDockPresentFor(cid)) return false;
    const caster = casterFromCache(cid);
    if (!caster) {
        refuseUnported('ability-click:caster-not-held', { actorId: cid, abilityId: aid });
        return true;
    }

    // An equipped/natural weapon reached through its INVENTORY id (action-bar inventory slots, hotkeys,
    // sheet buttons) is a weapon attack, not an ability blob: findAbility() cannot resolve it and the
    // profile derivation below would veto it as targetless. Hand it to the weapon dispatch so a melee
    // weapon arms Reach targeting exactly like the Weapons-tab hit picture does.
    const inventoryWeapon = findInventoryEntry(caster, aid);
    const inventoryWeaponType = inventoryWeapon
        ? String(ftItemsCol(inventoryWeapon, 'item_type') ?? '').toLowerCase()
        : '';
    if (!opts.damageOnly && inventoryWeapon && (inventoryWeaponType === 'weapon' || inventoryWeaponType === 'natural_weapon')) {
        return ftDispatchWeaponClick(cid, aid, 'hit', false, opts.deferredDispatch ?? null);
    }

    // Contested innate skill (Influence vs Empathy …): SKILL_CATALOG.contest_target is the server's own
    // ft_contested_skill_map(). Skills carry no range layer, so they get the synthetic contested profile.
    const cat = window.SKILL_CATALOG || {};
    const skill = cat[aid] || cat[aid.toUpperCase()] || cat[aid.toLowerCase()];
    if (skill) {
        const contestId = String(skill.contest_target || '');
        if (!contestId) return false; // fixed-DC-only skill → immediate roll
        const contest = cat[contestId] || cat[contestId.toUpperCase()];
        cancelActiveAoePlacement();
        abChoiceBranch = null;
        abChoice = null;
        abWeaponMenu = null;
        setItemTargetMenu(null);
        armCreatureTargeting(
            cid, aid, caster, String(skill.item_name || 'Skill'),
            deriveContestedSkillTargetingProfile(String(contest?.item_name || '')),
            { kind: 'plain', abilityId: aid },
            opts.deferredDispatch ?? null,
        );
        return true;
    }

    const ability = findAbility(caster, aid);
    if (!ability) {
        // CORE actions (Pilot / Mount / Force Pilot / Force Mount …) live in no caster pool and no page
        // cache — the legacy path never needed the blob because the server owns the cast. Pull the
        // redacted item_arr into objectCache (object_inc.php) and dispatch again once it lands; only a
        // failed fetch falls back to the immediate cast. Owner 2026-08-27: "clicked Pilot a Creature —
        // no fold-out, couldn't target the mount within reach".
        if (opts.noFetch || typeof window.fetchAndCacheObject !== 'function') return false;
        void window.fetchAndCacheObject(aid).then((blob) => {
            if (blob && ftDispatchAbilityClick(cid, aid, { ...opts, noFetch: true })) return;
            (opts.deferredDispatch ?? (() => ftLegacyAbilityClickAs(cid, aid)))();
        }).catch(() => { (opts.deferredDispatch ?? (() => ftLegacyAbilityClickAs(cid, aid)))(); });
        return true;
    }
    if (opts.damageOnly) {
        const targeting = effectiveAbilityTargetingProfile(caster, ability);
        const weaponProfile = ['weapon', 'natural_weapon'].includes(String(ftItemsCol(ability, 'item_type') ?? '').toLowerCase())
            ? weaponAttackTargetingProfile(caster, ability, aid, 1, false) : null;
        const profile = weaponProfile ?? (targeting.supported ? targeting.profile : null);
        if (!profile) {
            refuseUnported('damage-only:targeting-unavailable', { casterId: cid, abilityId: aid },
                'This damage action cannot select a target here.');
            return true;
        }
        armCreatureTargeting(cid, aid, caster, ability, profile,
            { kind: 'variable-1-choice', abilityId: aid, inputId: '', value: 'damage_only' }, opts.deferredDispatch ?? null);
        return true;
    }
    // Natural weapons have no range layer; their ordinary use shares the weapon's Reach targeting.
    if (String(ftItemsCol(ability, 'item_type') ?? '').toLowerCase() === 'natural_weapon') {
        return ftDispatchWeaponClick(cid, aid, 'hit', false, opts.deferredDispatch ?? null);
    }
    if (window.ftResumeProjectileSpell?.(cid, aid)) return true;
    const heldItemTypes = ownInventoryTargetTypes(ability, caster);
    if (heldItemTypes.length > 0) {
        setItemTargetMenu(null);
        return armHeldItemTargeting(cid, aid, caster, ability, heldItemTypes);
    }
    const inputId = determineAbilityButtonInputId(ability);
    const inputFamily = actionInputFamily(ability, inputId);
    const numericSpec = targetingInputFamilyIsNumeric(inputFamily)
        ? numericActionInputSpec(caster, ability, inputFamily)
        : null;
    const inputElement = inputId
        ? document.getElementById(inputId) as HTMLInputElement | HTMLSelectElement | null
        : null;
    const inputNumericValue = Number(inputElement?.value);
    const requestedNumericValue = Number.isFinite(inputNumericValue) ? inputNumericValue : numericSpec?.value ?? 0;
    const initialNumericValue = numericSpec
        ? Math.max(numericSpec.min, numericSpec.max === ''
            ? requestedNumericValue
            : Math.min(numericSpec.max, requestedNumericValue))
        : 0;
    const dynamicMaxTargets = numericSpec
        ? dynamicActionMaxTargets(ability, inputFamily, String(initialNumericValue))
        : undefined;
    const specialized = ftLayerHas(ability, 'attack_ability_throws_the_throwing_weapon_at_target')
        || ftLayerHas(ability, 'draw_sheathed_weapon_for_attack_ability')
        || ftLayerHas(ability, 'counterspelling_effect')
        // channel_chosen_orb owns its own cast door (the orb picker fires useAbilityVariable2 with
        // the chosen slugs). Targeting must not hijack the click ahead of it — the bar replays the
        // generated snippet instead, which opens the picker. See ability-button.ts ftOrbChoiceCast.
        || ftLayerHas(ability, 'channel_chosen_orb')
        // A summon choice must be configured before placement, even when upcasting owns the primary input.
        || ftLayerHas(ability, 'user_choice_1_summon')
        || ftLayerHas(ability, 'spell_ritual')
        || isAbilityChoiceParent(ability)
        || sanguineDmgPickOptionIds(caster, ability, false).length > 0;
    // A summon owns ground placement even when its invocation has no mounted spell-table input.
    // Keep an exact item/charge callback until placement confirms, just as creature targeting does.
    if (usesGroundSummonPlacement(ability)) {
        abWeaponMenu = null;
        setItemTargetMenu(null);
        const summonId = fixedSummonCreatureId(ability);
        const exactCast = opts.deferredDispatch;
        if (!summonId || !exactCast && (inputId || ftLayerHas(ability, 'spell_ritual'))) {
            claimActionBarOverlayLane(cid, 'ability-choice');
            const choiceInput = exactCast ? `input_${aid}_user_choice_1_summon` : inputId;
            abChoice = { casterId: cid, abilityId: aid, inputId: choiceInput, ritual: !exactCast && ftLayerHas(ability, 'spell_ritual'),
                isAoe: false, summonCast: exactCast ?? undefined };
            actionOptionValueMap().set(actionOptionKey(aid), initialActionOptionValues(caster, ability, choiceInput));
            repaintActionBars(cid);
        } else {
            abChoiceBranch = null;
            abChoice = null;
            const button = ft3dDockAbilityButton(cid, aid);
            const snippet = button?.getAttribute('data-ft-use-snippet') || '';
            void armFt3dSummonPlacement(cid, aid, summonId,
                exactCast ?? (() => snippet ? runGeneratedAbilitySnippet(snippet) : window.useAbility?.(aid)), button);
        }
        return true;
    }
    if (!specialized && !opts.deferredDispatch && armInstantProjectileSpell(cid, aid, numericSpec ? String(initialNumericValue) : undefined,
        '', '', { creatureOnly: true })) return true;
    const dispatch = classifyAbilityDispatchVariant({
        aoe: !!ftLayerValue(ability, 'aoe_type'),
        inputId,
        inputKind: targetingDispatchInputKind(inputFamily),
        trigger: false,
        dedicated: false,
        weaponPinned: false,
        itemTarget: ownInventoryTargetTypes(ability, caster).length > 0,
        markerOrSource: false,
        specialized,
    });
    const profile = effectiveAbilityTargetingProfile(caster, ability, '', dynamicMaxTargets);
    if (dispatch.route !== 'ability-targeting' || !profile.supported) return false;
    const projectilePool = window.ftGetProjectilePool?.(cid, aid);
    if (projectilePool && dispatch.invocation.kind === 'plain') {
        if (projectilePool.remaining < 1) { window.displayWarning?.('No projectiles remaining.'); return true; }
        profile.profile.projectilePool = projectilePool;
        profile.profile.maxTargets = projectilePool.remaining;
    }
    abChoiceBranch = null;
    abChoice = null;
    abWeaponMenu = null;
    setItemTargetMenu(null);
    let invocation: ActiveAbilityTargetingIntent['invocation'];
    if (dispatch.invocation.kind === 'variable-1-number') {
        invocation = {
            kind: 'variable-1-number',
            abilityId: aid,
            inputId: dispatch.invocation.inputId,
            value: initialNumericValue,
            secondaryChoice: ftLayerEntries(ability, 'damage_type').length > 1
                ? choiceEntryUuid(ftLayerEntries(ability, 'damage_type')[0])
                : undefined,
        };
    } else if (dispatch.invocation.kind === 'variable-1-choice') {
        invocation = {
            kind: 'variable-1-choice',
            abilityId: aid,
            inputId: dispatch.invocation.inputId,
            value: String(inputElement?.value || choiceEntryUuid(ftLayerEntries(ability, 'damage_type')[0])),
        };
    } else {
        invocation = { kind: 'plain', abilityId: aid };
    }
    armCreatureTargeting(cid, aid, caster, ability, profile.profile, invocation, opts.deferredDispatch ?? null);
    return true;
}

/** Route a hand-authored/special ability invocation through the same ability-first lifecycle while
 * preserving its exact endpoint/marker callback. */
export function ftDispatchAbilityVariantClick(
    casterId: string,
    abilityId: string,
    deferredDispatch: () => void,
    damageOnly = false,
): boolean {
    if (typeof deferredDispatch !== 'function') return false;
    return ftDispatchAbilityClick(casterId, abilityId, { deferredDispatch, ...(damageOnly ? { damageOnly: true } : {}) });
}

/** Encouraging Spell owns this one reaction picker; its confirmed target/weapon go directly to the native attack lane. */
function armSanguineOpportunity(casterId: string, commit: (targetId: string, weaponId: string) => void): boolean {
    const abilityId = 'D1EADC34-832C-1909-2FC3-722AC857FBEA';
    const caster = casterFromCache(casterId);
    const ability = caster ? findAbility(caster, abilityId) : null;
    if (!caster || !ability || !ft3dDockPresentFor(casterId)) return false;
    if (actionBarAbilityClickRefused(casterId, abilityId)) return false;
    const weapon = defaultWeaponForAttackAbility(caster, ability);
    if (!weapon) return false;
    const profile = weaponPinnedTargetingProfile(caster, ability, weapon.node, weapon.id);
    if (!profile) return false;
    cancelActiveAoePlacement();
    armCreatureTargeting(casterId, abilityId, caster, ability, { ...profile, minTargets: 1, maxTargets: 1 },
        { kind: 'plain', abilityId }, confirmed => {
            const targetId = confirmed.selectedEntityIds[0];
            mirrorAbilityTargetSelection(null, 'sanguine-opportunity-confirm');
            if (targetId) commit(targetId, weapon.id);
        });
    return true;
}

/** Extraordinary spells are absent from the prepared pool. Use their list endpoint's definition;
 * the dialog owns cast options and payment, while this action owns targeting. */
export async function dispatchExtraordinarySpell(
    casterId: string, abilityId: string, definition: unknown, summonId: string, cast: () => void,
): Promise<boolean> {
    const caster = casterFromCache(casterId);
    const ability = itemNodeOf(definition);
    if (!caster || !ability?.items || String(ftItemsCol(ability, 'item_id')).toUpperCase() !== abilityId.toUpperCase()) {
        throw new Error('The selected spell could not be loaded. Reopen the spell picker.');
    }
    // Reference data only: this does not add the spell to any known/prepared ability pool.
    (caster.catalog ??= {})[abilityId] = ability;
    const profile = deriveAbilityTargetingProfile(ability);
    const summon = usesGroundSummonPlacement(ability);
    const area = !!ftLayerValue(ability, 'aoe_type');
    if (!summon && !area && !profile.supported
        && ['self-target', 'targetless-or-implicit', 'auto-target'].includes(profile.reason)) {
        cancelAbilityTargeting(false);
        cancelActiveAoePlacement();
        return false;
    }
    if (!ft3dDockPresentFor(casterId)) throw new Error('Open the Scene action bar to target this spell.');
    if (summon || area) {
        const template = document.createElement('template');
        template.innerHTML = generateAbilityButton(ability, caster);
        const button = template.content.querySelector<HTMLElement>('[data-ability_id]');
        if (!button) throw new Error('The selected spell cannot open placement.');
        if (summon) {
            if (!await armFt3dSummonPlacement(casterId, abilityId,
                summonId || fixedSummonCreatureId(ability), cast, button, ability)) {
                throw new Error('Summon placement is unavailable.');
            }
        } else {
            cancelAbilityTargeting(false);
            window.aoeManager?.startFt3d?.(button);
            const active = window.aoeManager?.ft3dActive;
            if (active?.envelope?.intent?.abilityId?.toUpperCase() !== abilityId.toUpperCase()) {
                throw new Error('Area placement is unavailable.');
            }
            active.castOverride = cast;
        }
        return true;
    }
    if (!profile.supported) throw new Error('Targeting is unavailable for this spell.');
    cancelActiveAoePlacement();
    abChoice = null;
    abChoiceBranch = null;
    abWeaponMenu = null;
    setItemTargetMenu(null);
    armCreatureTargeting(casterId, abilityId, caster, ability, profile.profile,
        { kind: 'plain', abilityId }, cast);
    return true;
}

window.ftDispatchExtraordinarySpell = dispatchExtraordinarySpell;

// World-placement / structure / vehicle item types — map objects, never rows in a caster's backpack.
// Mirrors FT_CASTER_NON_CARRYABLE_ITEM_TYPES (get_weapon_for_attack_ability.php). An item-type gate
// on these describes the map (Ram the Gate → door/building/war_machine/vehicle_land), not own gear.
const NON_CARRYABLE_ITEM_TYPES = new Set<string>([
    'furniture', 'door', 'building',
    'window', 'wall', 'roof', 'floor',
    'vehicle_land', 'vehicle_water', 'vehicle_air', 'war_machine',
    'ore_vein', 'tree', 'trap', 'digsite',
]);

// World-object abilities (Ram the Gate) are targeted by ONE implementation — abtnProjectWorldObjectTarget() in
// ability-button.ts, on the run-snippet click path a dock medallion actually takes. A second branch here would
// bypass this function's dispatch classification (chooser / input / deferred variants) for the same buttons.

/** Client mirror of ability_uses_caster_inventory_target_picker(); server remains authoritative.
 *  Returns the gate's carryable item-types — minus world-object types (Ram the Gate is NOT an own-item
 *  imbue) — and ONLY when `caster` actually carries a matching, non-deleted row. Empty ⇒ the button is
 *  not hijacked into the picker, so object/creature abilities and an empty-handed caster fall through
 *  to normal targeting (and the server's own default-resolution / requires-target veto). */
function ownInventoryTargetTypes(ability: unknown, caster: CachedCaster): string[] {
    if (!ftLayerHas(ability, 'ability_works_only_against_item_type')) return [];
    if (TEAM_TARGET_LAYERS.some((layer) => ftLayerHas(ability, layer))) return [];
    const types = [...new Set(
        ftLayerEntries(ability, 'ability_works_only_against_item_type')
            .map((entry: unknown) => String(entry ?? '').trim().toLowerCase())
            .filter(Boolean)
            .filter((type) => !NON_CARRYABLE_ITEM_TYPES.has(type)),
    )];
    if (types.length === 0) return [];
    // Must-carry: don't hijack the click unless the caster holds something the imbue can target — or another
    // creature is holding one in the open (Frostwind Blade on an ally's sword, owner 2026-09-14). Range-free:
    // the scene pick measures reach and offers the approach move (owner 2026-09-15).
    const allowed = new Set(types);
    const carriesMatch = inventoryRowsDeep(caster).some((entry) => {
        const type = String(ftItemsCol(entry, 'item_type') ?? '').toLowerCase();
        const deleted = Number(ftInventoryField?.(entry, 'inventory_deleted') ?? 0);
        return allowed.has(type) && deleted === 0;
    });
    return carriesMatch || heldItemHolders(caster, types).length > 0 ? types : [];
}

interface HeldItemTargetGroup { holder: CachedCaster; holderId: string; entries: CasterItemNode[] }

function encounterCreatureIdOf(row: unknown): string {
    const record = recordOf(row);
    const raw = recordOf(record?.encounter_creatures)?.encounter_creature_id ?? record?.encounter_creature_id;
    return String((Array.isArray(raw) ? raw[0] : raw) ?? '').trim().toUpperCase();
}

function combatSizeFeetOf(row: unknown): number | undefined {
    const sizeId = String(sizeIdOf(row) ?? '').toUpperCase();
    if (!sizeId || typeof SIZE_TYPES === 'undefined') return undefined;
    const size = Number(SIZE_TYPES.find(entry => String(entry.item_id).toUpperCase() === sizeId)?.base_size);
    return Number.isFinite(size) && size > 0 ? size : undefined;
}

/** Other creatures within the ability's range and the allowed-type items each has EQUIPPED. Client mirror of
 *  Janus (validate_and_adjust_targets): a held row has no map position, so reach is measured to its holder,
 *  and another creature's stowed gear is refused. Server stays authoritative (sight, line of effect). */
function heldItemTargetsInRange(caster: CachedCaster, ability: unknown, allowedTypes: readonly string[]): HeldItemTargetGroup[] {
    const encounterArr = (window as unknown as { ft3dEncounterArrCache?: { arr?: { encounter_creatures_arr?: unknown; catalog?: unknown } } | null })
        .ft3dEncounterArrCache?.arr;
    const rows = Array.isArray(encounterArr?.encounter_creatures_arr) ? encounterArr.encounter_creatures_arr as CachedCaster[] : [];
    const casterId = encounterCreatureIdOf(caster);
    if (!casterId || rows.length === 0) return [];
    const rangeId = String(ftLayerValue(ability, 'range') ?? '').trim().toUpperCase();
    const authored = abilityRangeFeetForId(rangeId);
    // Self / object / missing range never reaches another creature's hands (Janus vetoes it) — own gear only.
    if (authored === undefined) return [];
    // Reach resolves from the caster exactly like Janus's 5 ft default.
    const rangeFeet = authored ?? Math.max(0, Number(ftLayerValue(caster, 'reach')) || 5);
    const origin = rows.find(row => encounterCreatureIdOf(row) === casterId) ?? caster;
    const at = (row: unknown) => ({ x: posCoord(row, 'x_pos') ?? 0, y: posCoord(row, 'y_pos') ?? 0, z: posCoord(row, 'z_pos') ?? 0 });
    const allowed = new Set(allowedTypes);
    const catalog = recordOf(encounterArr?.catalog);
    const groups: Array<HeldItemTargetGroup & { distance: number }> = [];
    for (const row of rows) {
        const holderId = encounterCreatureIdOf(row);
        if (!holderId || holderId === casterId) continue;
        if (Number(recordOf(row.encounter_creatures)?.encounter_creature_private ?? 0) === 1) continue;
        const distance = combatCenterDistanceFt(at(origin), at(row), {
            sourceSizeFeet: combatSizeFeetOf(origin), targetSizeFeet: combatSizeFeetOf(row), depthAuthored: true,
        });
        if (!(distance <= rangeFeet)) continue;
        const entries = heldEquippedRows(row).filter((entry) => allowed.has(heldItemTypeOf(entry, catalog)));
        if (entries.length > 0) groups.push({ holder: row, holderId, entries, distance });
    }
    return groups.sort((a, b) => a.distance - b.distance);
}

/** A held row's item_type: its own items bucket, else the encounter catalog (leaned encounter rows). */
function heldItemTypeOf(entry: CasterItemNode, catalog: Record<string, unknown> | null): string {
    const direct = ftItemsCol(entry, 'item_type');
    if (direct) return String(direct).toLowerCase();
    const itemId = String(ftInventoryField?.(entry, 'inventory_item_id') ?? '').toUpperCase();
    return String(recordOf(recordOf(catalog?.[itemId])?.items)?.item_type ?? '').toLowerCase();
}

/** What another creature visibly holds: equipped, live, public rows (Janus refuses stowed gear). */
function heldEquippedRows(row: CachedCaster): CasterItemNode[] {
    return inventoryRowsDeep(row).filter((entry) => inventoryEquipped(entry, row)
        && Number(ftInventoryField?.(entry, 'inventory_deleted') ?? 0) === 0
        && ![1, 2].includes(Number(ftInventoryField?.(entry, 'inventory_private') ?? 0)));
}

function heldItemInventoryId(entry: CasterItemNode): string {
    const raw = ftInventoryField?.(entry, 'inventory_id') ?? entry.inventory_id;
    return String((Array.isArray(raw) ? raw[0] : raw) ?? '').trim().toUpperCase();
}

interface HeldItemHolder { holder: CachedCaster; holderId: string; equipped: CasterItemNode[]; allowed: CasterItemNode[] }

/** Every other non-private creature holding at least one allowed-type item in the open, range-free:
 *  the FT3D scene owns reach, line of effect and the approach move. */
function heldItemHolders(caster: CachedCaster, allowedTypes: readonly string[], includeUnarmed = false): HeldItemHolder[] {
    const encounterArr = (window as unknown as { ft3dEncounterArrCache?: { arr?: { encounter_creatures_arr?: unknown; catalog?: unknown } } | null })
        .ft3dEncounterArrCache?.arr;
    const rows = Array.isArray(encounterArr?.encounter_creatures_arr) ? encounterArr.encounter_creatures_arr as CachedCaster[] : [];
    const casterId = encounterCreatureIdOf(caster);
    const catalog = recordOf(encounterArr?.catalog);
    const allowed = new Set(allowedTypes);
    const out: HeldItemHolder[] = [];
    for (const row of rows) {
        const holderId = encounterCreatureIdOf(row);
        if (!holderId || holderId === casterId) continue;
        if (Number(recordOf(row.encounter_creatures)?.encounter_creature_private ?? 0) === 1) continue;
        const equipped = heldEquippedRows(row);
        const matching = equipped.filter((entry) => allowed.has(heldItemTypeOf(entry, catalog)));
        if (matching.length > 0 || includeUnarmed) out.push({ holder: row, holderId, equipped, allowed: matching });
    }
    return out;
}

/** Arm the FT3D creature pick for a held-item imbue: click a creature → radial of its equipped slots →
 *  the chosen row's inventory id is the target. False when there is no scene to pick in or the ability's
 *  range never reaches another creature (self/object ranges keep the fold-out / default own item). */
function armHeldItemTargeting(casterId: string, abilityId: string, caster: CachedCaster, ability: CasterItemNode, types: readonly string[]): boolean {
    if (types.length === 0 || !ft3dDockPresentFor(casterId)) return false;
    const rangeId = String(ftLayerValue(ability, 'range') ?? '').trim().toUpperCase();
    const authored = abilityRangeFeetForId(rangeId);
    if (authored === undefined) return false;
    cancelActiveAoePlacement();
    abChoiceBranch = null;
    abChoice = null;
    abWeaponMenu = null;
    armCreatureTargeting(casterId, abilityId, caster, ability, {
        kind: 'creature-targets',
        aimLine: 'none',
        minTargets: 1,
        maxTargets: 1,
        rangeId,
        rangeFeet: authored === Infinity ? null : authored,
        rangeLabel: rangeId === ABILITY_TARGETING_REACH_RANGE_ID ? 'Reach'
            : authored === Infinity ? 'Unlimited' : authored === null ? 'Range set by server' : `${authored} ft`,
        heldItemTargetTypes: [...types],
    }, { kind: 'plain', abilityId });
    return true;
}

/** Resolve a held-item pick to one inventory row. A creature id (party frame / initiative click) resolves
 *  only when that holder has exactly one allowed item in hand; otherwise the radial must choose. */
function resolveHeldItemTargetPick(active: ActiveAbilityTargetingIntent, entityId: string): { holderId: string; inventoryId: string } | { refusal: string } {
    const caster = casterFromCache(active.casterId);
    const types = active.profile.heldItemTargetTypes ?? [];
    const holders = caster ? heldItemHolders(caster, types) : [];
    const byHolder = holders.find((h) => h.holderId === entityId);
    const holder = byHolder ?? holders.find((h) => h.allowed.some((entry) => heldItemInventoryId(entry) === entityId));
    const holderName = holder && typeof getCreatureName === 'function' ? String(getCreatureName(holder.holder) || 'That creature') : 'That creature';
    if (!holder) return { refusal: `choose a ${types.join(' or ')} a creature has equipped.` };
    if (!active.candidateEntityIds.includes(holder.holderId)) return { refusal: `you cannot perceive what ${holderName} is holding.` };
    const sceneLegal = abilityTargetingRuntime.sceneLegal;
    if (sceneLegal?.intentId === active.intentId && !sceneLegal.entityIds.has(holder.holderId)) {
        return { refusal: `${holderName} is out of reach or behind cover — move within range first.` };
    }
    if (byHolder) {
        const only = byHolder.allowed.length === 1 ? byHolder.allowed[0] : undefined;
        if (!only) return { refusal: `click ${holderName} in the scene and choose which item from their equipped slots.` };
        return { holderId: byHolder.holderId, inventoryId: heldItemInventoryId(only) };
    }
    return { holderId: holder.holderId, inventoryId: entityId };
}

function stageHeldItemTargetPick(active: ActiveAbilityTargetingIntent, entityId: string, pickId: string): boolean {
    const resolved = resolveHeldItemTargetPick(active, entityId);
    if ('refusal' in resolved) {
        showAbilityTargetingFeedback(active.intentId, 'incompatible-target', resolved.refusal);
        return false;
    }
    if (!abilityTargetingPickDeduper.accept(active.intentId, resolved.inventoryId, pickId)) return true;
    const staged = abilityTargetingLifecycle.selectHeldItem(active.intentId, resolved.inventoryId);
    if (!staged) return false;
    clearSingleTargetAutoConfirm();
    mirrorAbilityTargetSelection(staged, 'ability-targeting-held-item');
    publishAbilityTargetingIntent();
    // The fold-out offered the same choice; the cast consumes both.
    if (abItemTargetMenu?.casterId === staged.casterId) setItemTargetMenu(null);
    confirmAbilityTargeting(staged.casterId);
    return true;
}

/** Scene radial for a held-item imbue (window.parent.ftHeldItemTargetRadial, called by the FT3D iframe on a
 *  creature click): every equipped slot of that creature, allowed items enabled, the rest shown disabled so
 *  the player can see what is in hand. `choose` hands the inventory id back to the scene, which owns range
 *  and the reach approach. */
function heldItemTargetRadial(rawHolderId: string, choose: (inventoryId: string) => void): { title: string; state: string; actions: SceneObjectAction[] } | null {
    const active = abilityTargetingLifecycle.current();
    const types = active?.profile.heldItemTargetTypes ?? [];
    const holderId = String(rawHolderId ?? '').trim().toUpperCase();
    const caster = active ? casterFromCache(active.casterId) : null;
    if (!active || !caster || types.length === 0 || !holderId) return null;
    const holder = heldItemHolders(caster, types, true).find((h) => h.holderId === holderId);
    if (!holder) return null;
    const name = typeof getCreatureName === 'function' ? String(getCreatureName(holder.holder) || 'Creature') : 'Creature';
    const allowedIds = new Set(holder.allowed.map(heldItemInventoryId));
    const registry = (window as unknown as { FT_ICON_REGISTRY?: Record<string, { tooltip?: string } | undefined> }).FT_ICON_REGISTRY;
    const slotOf = (entry: CasterItemNode): string => String(ftInventoryField?.(entry, 'equipped') ?? '').trim().toUpperCase();
    const typeLabel = types.map((type) => type.replace(/_/g, ' ')).join(' or ');
    const ordered = [...holder.equipped].sort((a, b) => (
        Number(allowedIds.has(heldItemInventoryId(b))) - Number(allowedIds.has(heldItemInventoryId(a)))
        || (ITEM_TARGET_SLOT_PRIORITY[slotOf(a)] ?? 99) - (ITEM_TARGET_SLOT_PRIORITY[slotOf(b)] ?? 99)
    ));
    const slotAction = (entry: CasterItemNode): SceneObjectAction => {
        const inventoryId = heldItemInventoryId(entry);
        const rawName = ftInventoryField?.(entry, 'inventory_name');
        const itemName = String((Array.isArray(rawName) ? rawName[0] : rawName) || ftItemsCol(entry, 'item_name') || 'Item');
        const slot = slotOf(entry);
        const slotName = registry?.[slot]?.tooltip || 'Equipped';
        const enabled = allowedIds.has(inventoryId);
        return {
            label: itemName,
            icon: slot || 'equipment',
            enabled,
            reason: enabled ? `${slotName} — cast ${active.abilityName} on it` : `${slotName} — ${active.abilityName} only targets a ${typeLabel}`,
            run: () => { if (enabled) choose(inventoryId); },
        };
    };
    // The wheel holds ten slots beside Cancel; overflow folds into a submenu.
    const actions = ordered.length > 10
        ? [...ordered.slice(0, 9).map(slotAction), { label: 'More', icon: 'equipment', run: () => {}, children: () => ordered.slice(9).map(slotAction) }]
        : ordered.map(slotAction);
    if (actions.length === 0) {
        actions.push({ label: 'Nothing equipped', icon: 'equipment', enabled: false, reason: `${name} has nothing equipped`, run: () => {} });
    }
    return { title: name, state: `${active.abilityName}: choose an equipped ${typeLabel}`, actions };
}

/** Bootstrap-tooltip attrs (lazily shown by the delegated mouseover handler in template.js) instead of
 *  a native `title` — matches the rune ability buttons. */
function tip(text: string, placement = 'left'): string {
    const t = escapeHtml(text);
    return `data-bs-toggle="tooltip" data-bs-placement="${placement}" data-bs-animation="false" data-bs-original-title="${t}" aria-label="${t}"`;
}

const MIN_BARS = 1;
const MAX_BARS = 5;
const ROW_SLOTS = 14;            // fixed buttons per row (first 10 → 1-0 hotkeys later). 16→14 (owner 2026-06-27):
                                // the full-screen dock must sit BETWEEN the side drawers (info+log are commonly
                                // open); at the readable ~77px button size only ~14 fit the ~1160px band on a
                                // 1080p desktop. Slots 14-15 are orphaned (owner approved) — data persists, just
                                // not rendered.
const DEFAULT_DIVIDERS = [10];   // boundary: a vertical divider AFTER the Nth button (1-based) when unset.
const DIVIDER_TRACK = '11px';    // grid column width of a divider track
const VALID_SIZES = ['s', 'm', 'l'];
type ActionBarSize = 's' | 'm' | 'l';
const LOCK_COOKIE = 'ft_action_bars_locked'; // client-side UI pref (like ability view-mode/size)

// "Special" universal abilities (short rest / rest / resolve / spot-hidden / snap-out / stop-bleeding /
// custom-*) render on the core-abilities card with a data-ability_id but NOT the .btn_ability_ranges
// class the bar's drag looks for, and they don't resolve via findAbility (they're not in the blob's
// ability buckets). This registry lets the bar (a) recognise their buttons as draggable and (b) render
// them in a slot — a fixed icon + handleAbilityBtnSingleClick. Ids/icons mirror object-card/card-shell +
// render-player-card. Bound as slot_type 'special'. Spot-Hidden's AOE preview stays on the card; Resolve
// retains its shared triggered-ability affordance in the slot as well as on the General Abilities flyout.
const SPECIAL_ABILITIES: Record<string, { icon: string; label: string; outOfCombatOnly?: boolean }> = {
    '4744CC19-87A9-41D7-9285-ABD36470EC81': { icon: 'short_rest_2', label: 'Short Rest', outOfCombatOnly: true },
    '5B363C9C-3207-4C45-A863-5506DDC06116': { icon: 'rest', label: 'Rest', outOfCombatOnly: true },
    'B2D3417B-2700-11CB-E17D-38DECAF66F1A': { icon: 'spot_hidden_creatures', label: 'Spot Hidden Creatures' },
    '5B518ED7-4CEF-72BE-F20B-912598267714': { icon: 'search_for_hidden_inventory', label: 'Search for Hidden Inventory' },
    'B2BC3219-C745-7050-51DD-892951F0026B': { icon: 'resolve', label: 'Resolve' },
    '28D6D12E-A33F-4D3F-A668-C0C9037F5BC1': { icon: 'snap_outta_it', label: 'Snap Out of It' },
    '904F852B-443B-24E4-7B71-F785BB66433B': { icon: 'stop_the_bleeding', label: 'Stop Bleeding' },
    'C79A906F-BA10-B705-1CB9-6A7C5DFAEBEF': { icon: 'custom_full_ability', label: 'Custom Full Ability' },
    'E350D4F5-B29D-17C0-4AB4-0CAA0398C74A': { icon: 'custom_ability', label: 'Custom Ability' },
    '41FF250E-4146-C8C2-1324-DD6911BDB372': { icon: 'custom_quick_ability', label: 'Custom Quick Ability' },
    'EEB78637-0F73-8369-D230-8C29512B759E': { icon: 'custom_free_ability', label: 'Custom Free Ability' },
    'BF71B9FC-32EF-29EF-9560-7219EBE600CD': { icon: 'custom_reaction_ability', label: 'Custom Reaction Ability' },
    // Core-attacks actions (handleAbilityBtnSingleClick ability buttons, same as above).
    'C6FD9254-7968-86FC-5FDC-8E87914DEDE4': { icon: 'disarm', label: 'Disarm' },
    'D1EADC34-832C-1909-2FC3-722AC857FBEA': { icon: 'opportunity_attack', label: 'Opportunity Attack' },
    '9AE1980E-783E-16F5-E8C4-904435FC0A69': { icon: 'shove', label: 'Shove' },
    '6D6CCCC6-01F5-965C-FCF4-DBDF4DBCCD93': { icon: 'grapple', label: 'Grapple' },
};
const SHORT_REST_ID = '4744CC19-87A9-41D7-9285-ABD36470EC81';
const RESOLVE_ABILITY_ID = 'B2BC3219-C745-7050-51DD-892951F0026B';
function specialAbility(id: string): { icon: string; label: string; outOfCombatOnly?: boolean } | undefined {
    return SPECIAL_ABILITIES[String(id || '').toUpperCase()];
}
/** Is the caster's encounter in combat?
 *
 *  ⚠ in_combat is an `encounters` COLUMN, not an encounter_creatures one — it appears nowhere in
 *  build_encounter_creature_arr()'s column list, fetch_encounter_creatures(), or
 *  update_encounter_creature_stats(). This used to read caster.encounter_creatures.in_combat, which
 *  is always undefined, so it returned false for EVERYONE and both of its call sites below were
 *  silently dead: the out-of-AP attack slot never rang inactive, and Short Rest / Rest never rang
 *  inactive during combat (both owner-requested 2026-06-26). Delegates to the encounter-resolving
 *  reader (encounterCache → nested fallback) so there is ONE definition of "in combat".
 *  (Found 2026-08-09 while gating the Machinist orb strip on the same condition.) */
function casterInCombat(caster: CachedCaster): boolean {
    return casterIsInCombat(caster);
}
/** Unwrapped remaining action points (encounter_creatures.aps). */
function casterApsRemaining(caster: CachedCaster): number {
    return Number(ftEncounterCreatureField(caster.encounter_creatures, 'aps') ?? 0) || 0;
}
function attackSlotUnusable(caster: CachedCaster, head = false): boolean {
    return casterInCombat(caster) && (casterApsRemaining(caster) < 1 || (head
        && Number(ftEncounterCreatureField(caster.encounter_creatures, 'head_attack') ?? 0) >= 1
        && !ftLayerHas(caster, 'multiple_head_attacks')));
}
function syncActionBarAttackSlots(caster: CachedCaster, container: HTMLElement): void {
    container.querySelectorAll<HTMLElement>('[data-ft-ap-slot]').forEach(slot => {
        slot.classList.toggle('is-unusable', attackSlotUnusable(caster, slot.dataset.ftApSlot === 'head'));
    });
    for (const [kind, column] of [['weapon', 'weapon_attack'], ['head', 'head_attack']] as const) {
        const value = String(ftEncounterCreatureField(caster.encounter_creatures, column) ?? 0);
        container.querySelectorAll<HTMLElement>(`[data-ft-ab-action="adjust-${kind}-attack"]`).forEach(button => {
            const label = button.closest('.ft-demo-attack-adjust')?.querySelector('b');
            if (label && label.textContent !== value) label.textContent = value;
        });
    }
}
// Grant cast-type markers that ride the cast as use_ability_variable_1 — the server keys the item-grant
// gate, the mana-pool skip AND the charge accounting off them (use_ability_pre_conditions): at-will is
// never deactivated, innate-per-rest + charged casts pay CHARGES (the object's), none pay the mana pool.
// A bound slot loses its spell_type, so recover it from computed.creature.castable — item grants live in
// LIKE-NAMED castable buckets (castable.at_will_spell / .innate_spell_per_long_rest / .charged_spell / …)
// with spell_type + source stamped on the ENTRY; spells_known/curses_known hold the native, marker-less
// spells (the old spells_known-only scan missed every item grant → false mana rings). SOURCE-BOUND slots
// only, on an EXACT source match (the slot's source_id was captured from the entry's own source at bind
// time): a source-less native binding must never inherit an item grant's marker for the same spell id
// (that would skip the mana pool), and a two-item grant must never pair item B's type with item A's stale
// source (wrong charge target) — both Codex review findings, 2026-07-13. (owner bug 2026-07-13: the bar
// sent NO marker → the server's unequipped-grant gate never fired → hotbarred item spell kept casting
// after unequip.)
const GRANT_SPELL_TYPES = new Set(['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell', 'charged_ability']);
// The types the SERVER recognizes as variable_1 cast markers (use_ability_pre_conditions: the grant gate
// + the mana-pool skip) — today the same set as GRANT_SPELL_TYPES.
const GRANT_CAST_MARKERS = new Set(['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell', 'charged_ability']);
function boundSpellGrantType(caster: CachedCaster, abilityId: string, sourceId: string | null): string | null {
    const want = String(abilityId || '').toUpperCase();
    const srcU = sourceId ? String(sourceId).toUpperCase() : '';
    if (want === '' || srcU === '') return null;
    const cast = caster?.computed?.creature?.castable as Record<string, unknown> | undefined;
    if (!cast || typeof cast !== 'object') return null;
    for (const pool of Object.values(cast)) {
        if (!Array.isArray(pool)) continue;
        for (const rawSpell of pool) {
            const spell = recordOf(rawSpell);
            if (String(spell?.item_id ?? '').toUpperCase() !== want) continue;
            if (String(spell?.source ?? '').toUpperCase() !== srcU) continue;
            const t = String(spell?.spell_type ?? '');
            if (GRANT_SPELL_TYPES.has(t)) return t;
        }
    }
    return null;
}
/** Short Rest's glyph reflects its remaining charges (short_rest_0/1/2), like the core-abilities card. */
function shortRestIcon(caster: CachedCaster): string {
    for (const e of abilityEntriesOfType(caster, 'full_ability')) {
        if (String(abilityEntryId(e) || '').toUpperCase() === SHORT_REST_ID) {
            const c = Number(abilityEntryCharges(e, 0)) || 0;
            return c >= 2 ? 'short_rest_2' : (c === 1 ? 'short_rest_1' : 'short_rest_0');
        }
    }
    return 'short_rest_0';
}

// Non-ability "bar actions" (custom onclick, no data-ability_id) that can be pinned to a slot: Add AP /
// Add Reaction (no-arg globals) and Clear Trigger Overlays (needs the ec/enc/camp ids on the button, which
// clearTriggersFromCoreAbilitiesButton(this) reads). Bound as slot_type 'action'; the source buttons carry
// data-ft-bar-action (render-player-card.ts) and the bar slot re-emits it for move/unbind.
interface BarAction {
    icon: string;
    label: string;
    attrs(caster: CachedCaster): string;
}

const BAR_ACTIONS: Record<string, BarAction> = {
    add_ap: { icon: 'add_ap', label: 'Add AP', attrs: () => `data-ft-ab-action="add-ap"` },
    add_reaction: { icon: 'add_reaction', label: 'Add Reaction', attrs: () => `data-ft-ab-action="add-reaction"` },
    clear_triggers: {
        icon: 'clear_triggers', label: 'Clear Trigger Overlays',
        attrs: (caster: CachedCaster) => {
            const ecArr = (caster?.encounter_creatures && !Array.isArray(caster.encounter_creatures)) ? caster.encounter_creatures : {};
            const u = (v: unknown) => String((Array.isArray(v) ? v[0] : v) ?? '');
            const ecId = u(ecArr.encounter_creature_id), encId = u(ecArr.encounter_id);
            const campId = u(caster?.player_characters?.attached_campaign_id) || u(ecArr.campaign_id);
            return `data-ft-clear-triggers-ec="${escapeHtml(ecId)}" data-ft-clear-triggers-enc="${escapeHtml(encId)}" data-ft-clear-triggers-camp="${escapeHtml(campId)}" data-ft-ab-action="clear-triggers"`;
        },
    },
};
function barAction(key: string): BarAction | undefined {
    return BAR_ACTIONS[String(key || '').toLowerCase()];
}

// The compact subset the owner identified in Core Abilities: the five custom action-economy buttons,
// followed by Add AP / Add Reaction / Clear Trigger Overlays. Keep the ordering identical to the banner.
const ACTION_POINT_FOLD_ABILITIES = [
    'C79A906F-BA10-B705-1CB9-6A7C5DFAEBEF',
    'E350D4F5-B29D-17C0-4AB4-0CAA0398C74A',
    '41FF250E-4146-C8C2-1324-DD6911BDB372',
    'EEB78637-0F73-8369-D230-8C29512B759E',
    'BF71B9FC-32EF-29EF-9560-7219EBE600CD',
] as const;
const ACTION_POINT_FOLD_ACTIONS = ['add_ap', 'add_reaction', 'clear_triggers'] as const;

/** AP-pill fold-up: the same core action-economy controls as the character sheet, lined up in one dock row. */
function renderActionPointFoldout(casterId: string, caster: CachedCaster): string {
    if (actionPointFoldOpen.get(casterId) !== true) return '';
    const cid = escapeHtml(casterId);
    const abilities = ACTION_POINT_FOLD_ABILITIES.map((abilityId) => {
        const spec = specialAbility(abilityId);
        if (!spec) return '';
        return `<button type="button" class="ft-action-apfold__btn ft-action-apfold__btn--ability"`
            + ` data-ft-ab-action="single-ability" data-ft-caster-id="${cid}" data-ft-ability-id="${abilityId}" data-ft-info-item-id="${abilityId}" ${tip(spec.label, 'top')}>`
            + `${iconMap(spec.icon, 32, 32, false)}</button>`;
    }).join('');
    const actions = ACTION_POINT_FOLD_ACTIONS.map((key) => {
        const action = barAction(key);
        if (!action) return '';
        return `<button type="button" class="ft-action-apfold__btn ft-action-apfold__btn--adjust" ${action.attrs(caster)} ${tip(action.label, 'top')}>`
            + `${iconMap(action.icon, 32, 32, false)}</button>`;
    }).join('');
    return `<div class="ft-action-choicebar ft-action-apfold" role="group" aria-label="Action point controls">`
        + `<div class="ft-action-apfold__buttons">${abilities}${actions}</div>`
        + `<button type="button" class="ft-action-choicebar__cancel ft-action-apfold__close" ${tip('Close', 'top')} data-ft-ab-action="ap-fold-close" data-ft-caster-id="${cid}">&#10005;</button>`
        + `</div>`;
}

function isLocked(): boolean {
    return typeof getCookie === 'function' && getCookie(LOCK_COOKIE) === '1';
}

// ── color theme (per-PC; stored on the action_bars rows, character-global like size/slot_count) ────────
// The frame/slot/accent palette is driven by CSS custom properties on .ft-action-bars (defaults = the
// neutral "dark" theme, defined in ensureActionBarStyles). A theme is EITHER a preset name (whose var
// overrides live in AB_THEMES) OR a "#rrggbb" custom accent (tints rim+accent over the dark frame). The
// chosen theme is applied as an inline style on .ft-action-bars, so presets and custom share one path.
const DEFAULT_THEME = 'dark';
interface ABTheme { label: string; swatch: string; vars: Record<string, string>; }
const AB_THEMES: Record<string, ABTheme> = {
    dark: { label: 'Dark', swatch: '#9fb0cc', vars: {} }, // {} → uses the CSS defaults
    bronze: {
        label: 'Bronze', swatch: '#e0aa5a', vars: {
            '--ftab-bg-1': '#2b241a', '--ftab-bg-2': '#1c160f', '--ftab-bg-3': '#110d08', '--ftab-border': '#0a0805',
            '--ftab-rim-rgb': '146,110,62', '--ftab-accent-rgb': '228,170,90',
            '--ftab-slot-top': 'rgba(44,38,28,.55)', '--ftab-slot-bot': 'rgba(8,6,5,.8)',
        },
    },
    arcane: {
        label: 'Arcane', swatch: '#aa82f5', vars: {
            '--ftab-bg-1': '#241f33', '--ftab-bg-2': '#191427', '--ftab-bg-3': '#0e0b18', '--ftab-border': '#080612',
            '--ftab-rim-rgb': '120,90,170', '--ftab-accent-rgb': '170,130,245',
            '--ftab-slot-top': 'rgba(40,34,54,.55)', '--ftab-slot-bot': 'rgba(8,6,12,.8)',
        },
    },
    emerald: {
        label: 'Emerald', swatch: '#5ac88c', vars: {
            '--ftab-bg-1': '#1c2a22', '--ftab-bg-2': '#132019', '--ftab-bg-3': '#0a130d', '--ftab-border': '#04100a',
            '--ftab-rim-rgb': '80,150,110', '--ftab-accent-rgb': '90,200,140',
            '--ftab-slot-top': 'rgba(28,44,34,.55)', '--ftab-slot-bot': 'rgba(5,12,8,.8)',
        },
    },
    crimson: {
        label: 'Crimson', swatch: '#e66e6e', vars: {
            '--ftab-bg-1': '#2c1d1d', '--ftab-bg-2': '#1f1313', '--ftab-bg-3': '#130a0a', '--ftab-border': '#100404',
            '--ftab-rim-rgb': '160,80,80', '--ftab-accent-rgb': '230,110,110',
            '--ftab-slot-top': 'rgba(46,30,30,.55)', '--ftab-slot-bot': 'rgba(12,6,6,.8)',
        },
    },
};

/** The PC's chosen theme — read from the first bar carrying one (theme is the same across a PC's bars; we
 *  write it to every bar on change). Falls back to the dark default. */
function readTheme(caster: CachedCaster): string {
    for (const b of readBars(caster)) {
        const t = b.theme;
        if (typeof t === 'string' && t !== '') return t;
    }
    return DEFAULT_THEME;
}
/** "#rrggbb" (or "rrggbb") → "r,g,b" channels for rgba(var(--…)); null if not a 6-hex color. */
function hexToRgbChannels(hex: string): string | null {
    const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex || '').trim());
    if (!m) return null;
    const n = parseInt(m[1]!, 16);
    return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}
/** Inline CSS-var declarations for a theme: a preset name → its var map; a "#rrggbb" → custom accent (rim +
 *  accent tinted to the chosen color over the dark frame). '' = the default (CSS defaults apply). */
function themeStyle(theme: string): string {
    if (theme && theme[0] === '#') {
        const ch = hexToRgbChannels(theme);
        return ch ? `--ftab-rim-rgb:${ch};--ftab-accent-rgb:${ch};` : '';
    }
    const t = AB_THEMES[theme];
    if (!t) return '';
    return Object.entries(t.vars).map(([k, v]) => `${k}:${v}`).join(';');
}

function normSize(s: unknown): 's' | 'm' | 'l' { return typeof s === 'string' && VALID_SIZES.includes(s) ? s as 's' | 'm' | 'l' : 'm'; }
function normCount(n: unknown): number {
    const v = Number(n);
    return Number.isFinite(v) ? Math.max(1, Math.min(50, Math.floor(v))) : ROW_SLOTS;
}

/** Bar width is a character-global preference. The schema stores it on each row, so read the first valid
 *  value and stamp every row together when it changes (the same pattern used by the color theme). */
function readBarSize(caster: CachedCaster): ActionBarSize {
    for (const bar of readBars(caster)) {
        if (typeof bar.size === 'string' && VALID_SIZES.includes(bar.size)) return bar.size as ActionBarSize;
    }
    return 'm';
}

/** The outer map dock owns the actual responsive width. Reflect the persisted preference there while leaving
 *  in-sheet action bars alone; CSS still caps every dock to its available viewport on tablet and mobile. */
function applyDockSize(container: HTMLElement, size: ActionBarSize): void {
    const dock = container.closest<HTMLElement>('.ft3d-actionbar-dock, [data-ft3d-actor-dock]');
    if (dock) dock.dataset.ftBarSize = size;
}

function readBars(caster: CachedCaster): ABBar[] {
    return Array.isArray(caster.action_bars) ? caster.action_bars : [];
}

function casterFromCache(casterId: string): CachedCaster | null {
    return window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined || null;
}

function publishAbilityTargetingHitEstimates(
    active: ActiveAbilityTargetingIntent,
    facts: Map<string, AbilityTargetingCandidateFact>,
): void {
    if (active.profile.aimLine === 'none') return;
    const caster = casterFromCache(active.casterId);
    const ability = caster ? findAbility(caster, active.abilityId) : null;
    if (!caster || !ability) return;
    const weapon = active.profile.weaponAttack
        ? findInventoryEntry(caster, active.profile.weaponAttack.inventoryId) : ability;
    if (!weapon) return;
    const rollType = String(ftLayerValue(weapon, 'roll_type') ?? '').toUpperCase();
    const spellAttack = rollType === 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C' // spell ranged attack
        || rollType === '5740CA49-006E-A7A0-B46B-6DDED6CD546C'; // spell melee attack
    const weaponRangedAttack = rollType === '3F85CA97-F9B2-A83A-FDE8-E523FD99346C';
    const weaponMeleeAttack = rollType === '2FE8DFB7-FE61-4862-AF71-3523262350A0';
    if (!spellAttack && !weaponRangedAttack && !weaponMeleeAttack) return;
    const skillVariable = spellAttack
        ? 'bending_weapon_skill'
        : weaponAttackSkill(weapon, caster, { value: ftLayerValue, entries: ftLayerEntries },
            active.profile.weaponAttack?.throwing);
    const skills = caster.computed?.creature?.skills;
    const skillEntry = skills && typeof skills === 'object' && !Array.isArray(skills)
        ? (skills as Record<string, Record<string, unknown>>)[skillVariable]
        : undefined;
    const tierLevel = Math.max(0, Math.trunc(Number(skillEntry?.tier_level ?? 0)));
    const tierDie = tierLevel > 0 ? tierLevel + 3 : 0;
    const essenceModifier = spellAttack
        ? Number(caster.computed?.creature?.spellcasting?.essence_mod ?? 0)
        : Number(ftLayerValue(caster, 'body') ?? 0);
    const flatModifier = (Number.isFinite(essenceModifier) ? essenceModifier : 0)
        + (Number(skillEntry?.skill_rolls_mod ?? 0) || 0)
        + (Number(skillEntry?.skill_roll_mod ?? 0) || 0);
    const advDisStacks = (Number(skillEntry?.adv_dis_stacks ?? 0) || 0) + (tierLevel === 0 ? -1 : 0);
    const encounterRows = (() => {
        const cached = (window as Window & {
            ft3dEncounterArrCache?: { arr?: { encounter_creatures_arr?: unknown } } | null;
        }).ft3dEncounterArrCache?.arr?.encounter_creatures_arr;
        return Array.isArray(cached) ? cached : [];
    })();
    const targetDefense = (entityId: string): number | null => {
        const cachedTarget = casterFromCache(entityId);
        const cachedDefense = cachedTarget ? Number(ftLayerValue(cachedTarget, 'ec_total')) : NaN;
        if (Number.isFinite(cachedDefense)) return cachedDefense;
        const row = encounterRows.find(candidate => {
            if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return false;
            const record = candidate as Record<string, unknown>;
            const encounter = record.encounter_creatures;
            const encounterRecord = encounter && typeof encounter === 'object' && !Array.isArray(encounter)
                ? encounter as Record<string, unknown>
                : null;
            const rawId = encounterRecord?.encounter_creature_id ?? record.encounter_creature_id;
            const id = String(Array.isArray(rawId) ? rawId[0] : rawId ?? '').trim().toUpperCase();
            return id === entityId;
        });
        const defense = row ? Number(ftLayerValue(row, 'ec_total')) : NaN;
        return Number.isFinite(defense) ? defense : null;
    };
    const estimates = [...facts.values()].flatMap(fact => {
        if (window.FateTide?.ft3dPerceivedCreatureIds?.has(fact.entityId) !== true) return [];
        const rawDefense = targetDefense(fact.entityId);
        if (rawDefense === null) return [];
        // Flanking (melee, per target): +1 ADV stack when an ally stands on the far side. The 3D scene
        // supplies the geometric fact; the server re-derives it authoritatively on the roll.
        const targetAdvDisStacks = advDisStacks + (fact.flanked ? 1 : 0);
        return [{
            entityId: fact.entityId,
            advDisStacks: targetAdvDisStacks,
            hitChancePercent: estimateSkillAttackHitChance({
            tierDie,
            flatModifier,
            advDisStacks: targetAdvDisStacks,
                // Cover is transient environmental AC. It changes whether a landed attack
                // penetrates full AC, not the EC threshold represented by this hit estimate.
                defense: rawDefense,
            }),
        }];
    });
    window.dispatchEvent(new CustomEvent('ft:ability-targeting-estimates', {
        detail: {
            schema: ABILITY_TARGETING_ESTIMATES_SCHEMA,
            version: active.intentId,
            estimates,
        },
    }));
}

/** action_bars are keyed on the PC item_id (character-global), so the write must target it — NOT the
 *  pane's data-ft-caster-id (which is the encounter_creature_id in an encounter). */
function pcItemId(caster: CachedCaster): string {
    return String(caster?.player_characters?.item_id || caster?.items?.item_id || '');
}

/** Resolve an ability_id to its FULL nested ability (items + item_layer_types) for rendering. The blob's
 *  ability buckets are LEANED pointer shells (top-level item_id; items.item_id null) — the full def lives
 *  in root.catalog. ftFindFullNestedItem is the canonical resolver; fallback caster.catalog; spells/mentas
 *  scan the castable/known pools. An id that won't resolve renders dimmed (--missing), never an error. */
function findAbility(caster: CachedCaster | null, abilityId: string): CasterItemNode | null {
    const id = String(abilityId || '');
    if (!id) return null;
    const finder = window.ftFindFullNestedItem;
    if (typeof finder === 'function') {
        try {
            const full = itemNodeOf(finder(id));
            if (full && full.items) return full;
        } catch { /* fall through */ }
    }
    const cat = caster?.catalog;
    if (cat && typeof cat === 'object') {
        const e = cat[id] || cat[id.toUpperCase()] || cat[id.toLowerCase()];
        if (e && e.items) return e;
    }
    const creature = caster?.computed?.creature;
    if (creature) {
        const U = id.toUpperCase();
        const pools: unknown[][] = [];
        if (creature.castable && Array.isArray(creature.castable.spells_known)) pools.push(creature.castable.spells_known);
        for (const k of ['spells_known', 'curses_known', 'mentas']) {
            if (Array.isArray(creature[k])) pools.push(creature[k]);
        }
        for (const pool of pools) {
            for (const rawEntry of pool) {
                const e = itemNodeOf(rawEntry);
                if (!e) continue;
                const eid = e?.items?.item_id || e?.item_id;
                if (eid && String(eid).toUpperCase() === U && e?.items) return e;
            }
        }
    }
    return null;
}

/** consume_nested_unit_per_use is stamped on the ability ENTRY (in computed.creature.abilities.<type>),
 *  not on the ability's own nest — findAbility() above returns the nest, so it can't carry the flag.
 *  Resolve it here by scanning the entry buckets for a match on ability_id (+ source when known) so a
 *  consumable (ammo/potion/coating) bound to an action slot still consumes a unit on use. Mirrors the
 *  entry-level read buildRow()/buildIconSection() do in ability-row.ts. */
function boundAbilityConsumesNestedUnit(caster: CachedCaster, abilityId: string, sourceId: string | null): boolean {
    const buckets = caster?.computed?.creature?.abilities;
    if (!buckets || typeof buckets !== 'object') return false;
    const idU = String(abilityId || '').toUpperCase();
    if (idU === '') return false;
    const srcU = sourceId ? String(sourceId).toUpperCase() : '';
    for (const type of Object.keys(buckets)) {
        const list = buckets[type];
        if (!Array.isArray(list)) continue;
        for (const e of list) {
            if (e?.consume_nested_unit_per_use !== true) continue;
            if (String(abilityEntryId(e) || '').toUpperCase() !== idU) continue;
            // When the slot recorded a source, require it to match so the right stack is decremented;
            // when it didn't, accept the id match (a lone consumable grant).
            if (srcU !== '' && e?.source && String(e.source).toUpperCase() !== srcU) continue;
            return true;
        }
    }
    return false;
}

/** Is this ability id still GRANTED to the caster right now — present in a live ability bucket
 *  (computed.creature.abilities.*) or castable pool (computed.creature.castable.*)? Item-granted
 *  spells/abilities (at-will / innate / charged, and weapon attack abilities) leave these pools the
 *  moment their source item is unequipped or removed (layer_inventory_abilities_onto_creature skips
 *  unequipped items server-side). findAbility() below resolves the DEFINITION from the catalog by id
 *  regardless, so a hotbar slot bound to an item-granted ability would otherwise still render live
 *  after the item is gone. Used to grey such slots out (renderMissingSlot). Native, source-less
 *  bindings are NOT checked against this (they stay resolved by definition). (owner bug 2026-07-13) */
function abilityGrantPresent(caster: CachedCaster, abilityId: string): boolean {
    const want = String(abilityId || '').toUpperCase();
    if (want === '') return false;
    const creature = caster?.computed?.creature as Record<string, unknown> | undefined;
    if (!creature || typeof creature !== 'object') return false;
    // abilityEntryId resolves every entry shape (bare-uuid / {item_id} / {ability_id} / {item_arr} /
    // nested {items:{item_id}}), so it covers both the leaned pointer rows and the full nests.
    const hit = (list: unknown): boolean => {
        if (!Array.isArray(list)) return false;
        for (const e of list) {
            if (String(abilityEntryId(e) || '').toUpperCase() === want) return true;
        }
        return false;
    };
    const scanContainer = (container: unknown): boolean => {
        if (!container || typeof container !== 'object') return false;
        for (const list of Object.values(container as Record<string, unknown>)) {
            if (hit(list)) return true;
        }
        return false;
    };
    // Grant/ability buckets + baked castable pools (where equipped-item grants live).
    if (scanContainer(creature.abilities)) return true;
    if (scanContainer(creature.castable)) return true;
    // Top-level creature-compile pools (EC / creature-blob shape) — mirror findAbility()'s reach so a
    // legitimately-known ability represented only here is never falsely greyed (Codex P2, 2026-07-13).
    for (const k of ['spells_known', 'curses_known', 'mentas']) {
        if (hit(creature[k])) return true;
    }
    return false;
}

/** The chromeless skill button for a bound skill (resolved via window.SKILL_CATALOG). '' if unresolved. */
function skillSlotInner(skillId: string): string {
    const cat = window.SKILL_CATALOG || {};
    const meta = cat[skillId] || cat[String(skillId).toUpperCase()] || cat[String(skillId).toLowerCase()];
    if (!meta) return '';
    const sid = escapeHtml(skillId);
    const iconKey = String(meta.variable_name || skillId);
    const name = String(meta.item_name || 'Skill');
    return `<button type="button" class="ft-action-skill-btn" data-ft-ab-action="use-ability" data-ft-ability-id="${sid}"`
        + ` data-ft-info-item-id="${sid}" ${tip(name, 'top')}>${iconPh(iconKey, 48, 48)}</button>`;
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const AUTOMATIC_NPC_OVERRUN_ABILITY_ID = '1DC3B3DE-BD4C-B77A-429D-12C7F2B2439D';
const AUTOMATIC_NPC_DODGE_ABILITY_ID = '62323E36-5B5E-0114-2750-ED864773B062';
const AUTOMATIC_NPC_ABILITY_TYPES = ACTIONABLE_ABILITY_TYPES.filter((type) =>
    type !== 'attack_ability' && type !== 'opportunity_attack_ability');

/** Find an inventory entry (top-level or nested in a container) by inventory_id, in the caster blob. */
function findInventoryEntry(caster: CachedCaster, invId: string): CasterItemNode | null {
    return findCarriedInventoryEntry<CasterItemNode>(caster, invId, { entries: ftLayerEntries, inventoryField: ftInventoryField });
}
function inventoryEquipped(entry: CasterItemNode, caster: CachedCaster): boolean {
    return readInventoryEquippedToken(entry, caster) !== '';
}

function automaticAbilityBar(caster: CachedCaster, abilityTypes: readonly string[], includeSkillAbilities: boolean): ABBar | null {
    const seen = new Set<string>();
    let authoredIndex = 0;
    const abilities = abilityTypes
        .flatMap((abilityType) => abilityEntriesOfType(caster, abilityType))
        .map((entry) => {
            const entryIndex = authoredIndex++;
            const abilityId = String(abilityEntryId(entry) || '').trim();
            const nest = resolveAbilityNest(entry, caster);
            return {
                abilityId,
                authoredIndex: entryIndex,
                powerLevel: nest && typeof nest === 'object' ? plTotal(nest) : undefined,
                skillSource: abilitySkillSource(caster, entry),
            };
        })
        .filter((entry) => {
            const key = entry.abilityId.toUpperCase();
            // Movement owns Dodge; keep its grant/catalog definition without adding a hotbar slot.
            if (key === '' || seen.has(key) || key === AUTOMATIC_NPC_DODGE_ABILITY_ID
                || (!includeSkillAbilities && (entry.skillSource !== null || key === AUTOMATIC_NPC_OVERRUN_ABILITY_ID))) return false;
            seen.add(key);
            return true;
        })
        .sort((left, right) => {
            const leftHasPowerLevel = left.powerLevel !== undefined;
            const rightHasPowerLevel = right.powerLevel !== undefined;
            if (leftHasPowerLevel !== rightHasPowerLevel) return leftHasPowerLevel ? -1 : 1;
            if (leftHasPowerLevel && rightHasPowerLevel) {
                const powerLevelOrder = right.powerLevel! - left.powerLevel!;
                if (powerLevelOrder !== 0) return powerLevelOrder;
            }
            return left.authoredIndex - right.authoredIndex;
        });
    if (abilities.length === 0) return null;

    return {
        bar_index: 0,
        size: 'm',
        slot_count: ROW_SLOTS,
        position: 'bottom',
        slots: abilities.slice(0, ROW_SLOTS).map((ability, slotIndex) => ({
            slot_index: slotIndex,
            ability_id: ability.abilityId,
            slot_type: 'ability',
        })),
    };
}

/**
 * Encounter creatures do not own persisted PC hotbars. Project their creature-native active abilities
 * in descending Power Level order; skill-derived actions live in the grouped ability-island flyout. A
 * creature with no native actions keeps the equipped-weapon fallback. This is render-only: it never
 * mutates caster.action_bars and cannot create mmdb.action_bars rows.
 */
function automaticEncounterCreatureBars(caster: CachedCaster): ABBar[] | null {
    const encounterCreatureId = String(caster?.encounter_creatures?.encounter_creature_id ?? '').trim();
    const playerCharacterId = String(caster?.player_characters?.item_id ?? '').trim();
    if (!encounterCreatureId || playerCharacterId) return null;

    const casterType = String(
        caster?.encounter_creatures?.item_type
        ?? ftItemsCol(caster, 'item_type')
        ?? '',
    ).trim().toLowerCase();
    const abilityBar = automaticAbilityBar(
        caster,
        casterType === 'lair' ? ['lair_ability'] : AUTOMATIC_NPC_ABILITY_TYPES,
        casterType === 'lair',
    );
    if (abilityBar) return [abilityBar];

    const equippedWeapons: Array<{ inventoryId: string; name: string }> = [];
    const scan = (entries: unknown): void => {
        if (!Array.isArray(entries)) return;
        for (const rawEntry of entries) {
            const entry = itemNodeOf(rawEntry);
            if (!entry) continue;
            const rawId = typeof ftInventoryField === 'function'
                ? ftInventoryField(entry, 'inventory_id')
                : entry.inventory_id;
            const inventoryId = String((Array.isArray(rawId) ? rawId[0] : rawId) ?? '').trim();
            const weaponType = typeof ftLayerValue === 'function'
                ? String(ftLayerValue(entry, 'weapon_type') ?? '').trim()
                : '';
            if (inventoryId && inventoryEquipped(entry, caster)
                && (weaponType !== '' || inventoryAttackAction(caster, entry, inventoryId) !== null)) {
                const rawName = typeof ftInventoryField === 'function'
                    ? ftInventoryField(entry, 'inventory_name')
                    : entry.inventory_name;
                equippedWeapons.push({
                    inventoryId,
                    name: String((Array.isArray(rawName) ? rawName[0] : rawName) ?? inventoryId),
                });
            }
            scan(entry.inventory_in_container);
        }
    };
    scan(caster.inventory);
    equippedWeapons.sort((left, right) => left.name.localeCompare(right.name)
        || left.inventoryId.localeCompare(right.inventoryId));

    return [{
        bar_index: 0,
        size: 'm',
        slot_count: ROW_SLOTS,
        position: 'bottom',
        slots: equippedWeapons.slice(0, ROW_SLOTS).map((weapon, slotIndex) => ({
            slot_index: slotIndex,
            ability_id: weapon.inventoryId,
            slot_type: 'inventory',
        })),
    }];
}

// Attack-type glyphs + the melee weapon_type id (verbatim from the Weapons tab, weapon-tables.ts) — the
// top-left badge on an equipped weapon shows melee / ranged / throw, matching what the click fires.
const MELEE_WEAPON_TYPE_ID = '1364260D-E37C-4257-AD16-5E745107811A';
const ICON_ATTACK_MELEE = '2FE8DFB7-FE61-4862-AF71-3523262350A0';
const ICON_ATTACK_RANGED = '3F85CA97-F9B2-A83A-FDE8-E523FD99346C';

function weaponAttackTargetingProfile(
    caster: CachedCaster,
    weapon: CasterItemNode,
    invId: string,
    maxTargets: number,
    forceThrown = false,
): AbilityTargetingProfile | null {
    const weaponType = String(ftLayerValue(weapon, 'weapon_type') ?? '').toUpperCase();
    const weaponReach = Number(ftLayerValue(weapon, 'weapon_reach'));
    const explicitRanged = String(ftLayerValue(weapon, 'roll_type') ?? '').toUpperCase() === ICON_ATTACK_RANGED;
    // Natural missiles such as Boulder Throw author the normal range UUID, whereas manufactured
    // weapons author range_end. Keep the numeric weapon range first; resolve the UUID only for
    // an explicitly ranged attack so ordinary melee weapons retain their reach contract.
    const numericRangeEnd = Number(ftLayerValue(weapon, 'range_end'));
    const authoredRange = explicitRanged
        ? abilityRangeFeetForId(String(ftLayerValue(weapon, 'range') ?? ''))
        : null;
    const rawRangeEnd = numericRangeEnd > 0 ? numericRangeEnd : Number(authoredRange);
    const throwPct = Number(ftLayerValue(caster, 'throwing_distance_changed_by_percentage')) || 0;
    const rangeEnd = forceThrown ? rawRangeEnd * (1 + throwPct / 100) : rawRangeEnd;
    const melee = !forceThrown && !explicitRanged && (weaponType === MELEE_WEAPON_TYPE_ID
        || (!weaponType && Number.isFinite(weaponReach) && weaponReach > 0 && !(rangeEnd > 0)));
    if (melee) {
        const casterReach = Math.max(0, Number(ftLayerValue(caster, 'reach')) || 5);
        const extension = Number.isFinite(weaponReach) ? Math.max(0, weaponReach - 5) : 0;
        const rangeFeet = casterReach + extension;
        return {
            kind: 'creature-targets',
            aimLine: 'melee-attack',
            weaponAttack: { inventoryId: String(invId).toUpperCase(), throwing: forceThrown },
            minTargets: 1,
            maxTargets,
            rangeId: ABILITY_TARGETING_REACH_RANGE_ID,
            rangeFeet,
            rangeLabel: rangeFeet === casterReach ? 'Reach' : `${rangeFeet} ft reach`,
        };
    }
    if (Number.isFinite(rangeEnd) && rangeEnd > 0) {
        return {
            kind: 'creature-targets',
            aimLine: 'ranged-attack',
            weaponAttack: { inventoryId: String(invId).toUpperCase(), throwing: forceThrown },
            projectileKind: paintedWallProjectileKind('3F85CA97-F9B2-A83A-FDE8-E523FD99346C', String(ftItemsCol(weapon, 'item_id') ?? '')),
            minTargets: 1,
            maxTargets,
            rangeId: `weapon:${String(invId).toUpperCase()}`,
            rangeFeet: rangeEnd,
            rangeLabel: `${rangeEnd} ft`,
        };
    }
    return null;
}

/** Janus resolves an attack_ability to the explicitly pinned weapon BEFORE its range gate. Most weapon
 *  maneuvers therefore carry no range of their own (Cleave has only max_targets:3); their effective range
 *  is Reach + the weapon's reach extension for melee, or range_end for ranged weapons. Mirror that
 *  composite here so the targeting UI does not misclassify a valid weapon attack as targetless and fire it
 *  immediately with an empty target_id. All non-range vetoes from deriveAbilityTargetingProfile remain
 *  authoritative: AOE, self/object, item-picker, auto-target, and dynamic target-count shapes stay out. */
function weaponPinnedTargetingProfile(
    caster: CachedCaster,
    ability: CasterItemNode,
    weapon: CasterItemNode,
    invId: string,
    forceThrown = false,
): AbilityTargetingProfile | null {
    const direct = deriveAbilityTargetingProfile(ability);
    if (!direct.supported && direct.reason !== 'targetless-or-implicit') return null;
    const itemType = String(ftItemsCol(ability, 'item_type') ?? '').toLowerCase();
    if (itemType !== 'attack_ability' && itemType !== 'opportunity_attack_ability') return null;

    let maxTargets = direct.supported ? direct.profile.maxTargets : 1;
    if (!direct.supported) {
        const rawMax = ftLayerValue(ability, 'max_targets');
        if (rawMax === '[x]') return null;
        maxTargets = rawMax === undefined || rawMax === null || rawMax === '' ? 1 : Number(rawMax);
        if (!Number.isInteger(maxTargets) || maxTargets < 1 || maxTargets > 1000) return null;
    }

    const weaponProfile = weaponAttackTargetingProfile(caster, weapon, invId, maxTargets,
        forceThrown || !!ftLayerValue(ability, 'attack_ability_throws_the_throwing_weapon_at_target'));
    if (weaponProfile) return weaponProfile;
    return direct.supported ? direct.profile : null;
}

type EffectiveAbilityTargetingProfile =
    | { supported: true; profile: AbilityTargetingProfile }
    | { supported: false };

/** The ATTACHMENT GATE, client side — the mirror of ft_weapon_matches_ability_attachment_gate()
 *  in includes/functions/janus/encounter/weapon_priority_helpers.php. TWO axes, OR'd:
 *    attached_equipped_weapon — explicit weapon template UUIDs, and
 *    attached_weapon_form     — natural-weapon anatomy forms (bite / claw / tail / ...).
 *  Both are read here exactly as the server reads them, so the fold-out the player sees and the
 *  weapon the server actually swings cannot disagree. Adding a form gate WITHOUT this mirror would
 *  have shown the ability under weapons the server then refused. */
function abilityAttachmentAxisSet(ability: unknown, layerName: string): Set<string> | null {
    if (!ftLayerHas(ability, layerName)) return null;
    const set = new Set(
        ftLayerEntries(ability, layerName).map((entry: unknown) => uuidOf(entry).toUpperCase()),
    );
    set.delete('');
    return set.size > 0 ? set : null;
}

const abilityAttachmentFormSet = (ability: unknown): Set<string> | null =>
    abilityAttachmentAxisSet(ability, 'attached_weapon_form');
const abilityAttachmentIdSet = (ability: unknown): Set<string> | null =>
    abilityAttachmentAxisSet(ability, 'attached_equipped_weapon');

/** True when the ability restricts which weapon may carry it on EITHER axis.
 *  Both axes go through the NORMALIZED set, matching ft_ability_has_weapon_attachment_gate():
 *  an id layer holding only blank or deleted entries must read as absent, not as a filter
 *  nothing can satisfy. */
function abilityHasWeaponAttachmentGate(ability: unknown): boolean {
    return abilityAttachmentIdSet(ability) !== null || abilityAttachmentFormSet(ability) !== null;
}

/** Does this weapon satisfy the ability's attachment gate? `weaponNode` is the compiled weapon /
 *  inventory node, which carries its own natural_weapon_form layer — a manufactured weapon has
 *  none and so simply fails the form axis. */
function weaponMatchesAttachmentGate(ability: unknown, templateId: string, weaponNode: unknown): boolean {
    const tpl = String(templateId ?? '').toUpperCase();

    const ids = abilityAttachmentIdSet(ability);
    if (tpl !== '' && ids?.has(tpl)) return true;

    const forms = abilityAttachmentFormSet(ability);
    if (!forms) return false;
    const form = uuidOf(ftLayerValue(weaponNode, 'natural_weapon_form')).toUpperCase();
    return form !== '' && forms.has(form);
}

/** Mirror Janus' unpinned weapon walk so attack runes on the N-sheet get the range/reach of the weapon
 * the server will actually roll. */
function defaultWeaponForAttackAbility(caster: CachedCaster, ability: CasterItemNode): { id: string; node: CasterItemNode } | null {
    const slotOf = (entry: CasterItemNode): string => String(ftInventoryField(entry, 'equipped') ?? '').toUpperCase();
    const idOf = (entry: CasterItemNode): string => String(ftInventoryField(entry, 'inventory_id') ?? '').trim();
    const templateOf = (entry: CasterItemNode): string => String(
        ftInventoryField(entry, 'inventory_item_id') ?? ftItemsCol(entry, 'item_id') ?? '',
    ).toUpperCase();
    const handOrder = [HAND_SLOT_BOTH, HAND_SLOT_MAIN, HAND_SLOT_OFF];
    const inventory = inventoryRowsDeep(caster)
        .filter((entry) => Number(ftInventoryField(entry, 'inventory_private') ?? 0) !== 2)
        .filter((entry) => handOrder.includes(slotOf(entry)))
        .filter((entry) => ['weapon', 'natural_weapon'].includes(String(ftItemsCol(entry, 'item_type') ?? '').toLowerCase()))
        .sort((left, right) => handOrder.indexOf(slotOf(left)) - handOrder.indexOf(slotOf(right)));
    const compiledNaturals = handOrder
        .map((slot) => naturalWeaponInSlot(caster, slot))
        .filter((entry): entry is NaturalHandWeapon => entry !== null);
    // The canonical encounter row can briefly repaint over a freshly compiled caster with its lean
    // projection. The hand cluster already embodies the last fully resolved weapon choice, while that
    // lean row may no longer carry computed.creature.abilities.natural_weapon. Re-resolve the visible
    // natural hand id through the shared object cache so a click during that window still targets with
    // the same Claw/Bite the player can see instead of falling through to an empty-target legacy POST.
    const casterId = String(caster.encounter_creatures?.encounter_creature_id ?? '').trim();
    const safeCasterId = casterId.replace(/["\\]/g, '');
    const pane = safeCasterId
        ? document.querySelector<HTMLElement>(
            `[data-ft3d-actor-dock] ${AB_PANE_SEL}[data-ft-caster-id="${safeCasterId}"]`,
        )
        : null;
    const renderedNaturals: NaturalHandWeapon[] = [];
    if (pane) {
        for (const hand of ['main', 'off']) {
            const cell = pane.querySelector<HTMLElement>(
                `.ft-action-slot--natural[data-ft-hand-slot="${hand}"][data-ft-natural-weapon-id]`,
            );
            const id = String(cell?.getAttribute('data-ft-natural-weapon-id') ?? '').trim();
            const node = id ? findAbility(caster, id) : null;
            if (id && node && !renderedNaturals.some((entry) => entry.id.toUpperCase() === id.toUpperCase())) {
                // slotId here is the CELL the limb was painted into, not necessarily its own
                // equipment_slot (a borrowed or mirrored claw occupies a hand it doesn't name). This
                // list only feeds attachment-gate matching and fallback ORDER, which never read it.
                renderedNaturals.push({ id, node, slotId: hand === 'main' ? HAND_SLOT_MAIN : HAND_SLOT_OFF });
            }
        }
    }
    const naturals = [...renderedNaturals, ...compiledNaturals].filter((entry, index, rows) =>
        rows.findIndex((candidate) => candidate.id.toUpperCase() === entry.id.toUpperCase()) === index);

    if (abilityHasWeaponAttachmentGate(ability)) {
        const inv = inventory.find((entry) => weaponMatchesAttachmentGate(ability, templateOf(entry), entry));
        if (inv) return { id: idOf(inv) || templateOf(inv), node: inv };
        const natural = naturals.find((entry) => weaponMatchesAttachmentGate(ability, String(entry.id), entry.node));
        return natural ?? null;
    }

    const both = inventory.find((entry) => slotOf(entry) === HAND_SLOT_BOTH);
    if (both) return { id: idOf(both), node: both };
    const main = inventory.find((entry) => slotOf(entry) === HAND_SLOT_MAIN);
    if (!main && naturals.length > 0) return naturals[0]!;
    if (main) return { id: idOf(main), node: main };
    const off = inventory.find((entry) => slotOf(entry) === HAND_SLOT_OFF);
    if (off) return { id: idOf(off), node: off };
    return naturals[0] ?? null;
}

/**
 * Twin / Bouncing (sanguine, owner 2026-07-22): validate_and_adjust_targets' $ft_extra_targets — an armed caster may take ONE more target
 * on a direct single-target spell (and on an additional-target upcast's ceiling), the two capped at +1 together. Eligibility is the cast
 * gate's twin (cast-range-gate.ts sanguineExtraTargets, via window.ftSanguineExtraTargets) judged on the AUTHORED ability; an armed
 * ritual cast gets nothing (PHP refuses the bonus for 'spell_ritual').
 */
function sanguineTargetBonus(caster: CachedCaster, ability: CasterItemNode): number {
    if (String(ftItemsCol(ability, 'item_type') ?? '').toLowerCase() !== 'spell') return 0;
    const casterId = String(unitScalar(caster.encounter_creatures?.encounter_creature_id) ?? '');
    const abilityId = String(ftItemsCol(ability, 'item_id') ?? '').toUpperCase();
    const ritual = window.ftAbRitualArmed instanceof Set && window.ftAbRitualArmed.has(abilityId);
    const read = (window as Window & { ftSanguineExtraTargets?: (casterId: string, ability: unknown, variable1?: unknown) => 0 | 1 }).ftSanguineExtraTargets;
    return read ? read(casterId, ability, ritual ? 'spell_ritual' : null) : 0;
}

function effectiveAbilityTargetingProfile(
    caster: CachedCaster,
    ability: CasterItemNode,
    pinnedWeaponId = '',
    dynamicMaxTargets?: number,
): EffectiveAbilityTargetingProfile {
    let targetingAbility = Number.isInteger(dynamicMaxTargets) && Number(dynamicMaxTargets) >= 1
        ? ({
            ...(ability as Record<string, unknown>),
            item_layer_types: {
                ...(recordOf((ability as Record<string, unknown>).item_layer_types) ?? {}),
                max_targets: [String(dynamicMaxTargets)],
            },
        } as CasterItemNode)
        : ability;
    const casterId = encounterCreatureIdOf(caster);
    const extension = (window as Window & { ftCastRangeExtension?: (casterId: string, ability: unknown) =>
        { unknown: string } | { rangeId: string } | null }).ftCastRangeExtension?.(casterId, ability);
    if (extension && !('unknown' in extension) && extension.rangeId !== String(ftLayerValue(targetingAbility, 'range') ?? '').toUpperCase())
        targetingAbility = { ...targetingAbility, item_layer_types: { ...recordOf(targetingAbility.item_layer_types), range: extension.rangeId } } as CasterItemNode;
    const itemType = String(ftItemsCol(ability, 'item_type') ?? '').toLowerCase();
    // source_id is never a targeting gate. When the generated invocation genuinely pins an attack to
    // a weapon, it only refines the shared profile (that weapon's reach/range); targeting still owns the
    // click and the exact source-aware invocation is deferred until confirmation. Grant/provenance sources
    // that are not weapons intentionally fall through to the normal equipped-weapon resolution below.
    if (pinnedWeaponId && (itemType === 'attack_ability' || itemType === 'opportunity_attack_ability')) {
        const pinned = findInventoryEntry(caster, pinnedWeaponId) ?? findAbility(caster, pinnedWeaponId);
        const pinnedType = String(pinned ? ftItemsCol(pinned, 'item_type') ?? '' : '').toLowerCase();
        if (pinned && (pinnedType === 'weapon' || pinnedType === 'natural_weapon')) {
            const profile = weaponPinnedTargetingProfile(caster, targetingAbility, pinned, pinnedWeaponId);
            if (profile) return { supported: true, profile };
        }
    }
    const direct = deriveAbilityTargetingProfile(targetingAbility);
    // Twin / Bouncing: an armed caster's single-target spell (and an additional-target upcast's computed ceiling) takes one more.
    if (direct.supported && itemType === 'spell' && (dynamicMaxTargets !== undefined || direct.profile.maxTargets === 1)) {
        const bonus = sanguineTargetBonus(caster, ability);
        if (bonus > 0) return { supported: true, profile: { ...direct.profile, maxTargets: direct.profile.maxTargets + bonus } };
    }
    if (direct.supported) return direct;
    if (direct.reason !== 'targetless-or-implicit') return { supported: false };
    if (itemType !== 'attack_ability' && itemType !== 'opportunity_attack_ability') return { supported: false };
    const weapon = defaultWeaponForAttackAbility(caster, ability);
    if (!weapon) return { supported: false };
    const profile = weaponPinnedTargetingProfile(caster, targetingAbility, weapon.node, weapon.id);
    return profile ? { supported: true, profile } : { supported: false };
}

/** Shared no-blind-POST dispatch for the Weapons-tab hit/throw pictures and plain weapon slots. */
function weaponArmRefusal(caster: CachedCaster, weapon: CasterItemNode, inventoryId: string, abilityId: string | null, throwing: boolean): string | null {
    if (throwing && weaponInLobMode(String(unitScalar(caster.encounter_creatures?.encounter_creature_id)), inventoryId)) return 'weapon:family:throw-lob';
    const ability = abilityId ? findAbility(caster, abilityId) : plainWeaponAbility(inventoryId);
    if (!ability) return 'weapon:attack-definition-unavailable';
    const read = { value: ftLayerValue, entries: ftLayerEntries, inventoryField: ftInventoryField, itemField: ftItemsCol,
        computedValue: ftComputedValue, itemType: (row: Record<string, unknown>) => String(ftItemsCol(row, 'item_type') ?? ''),
        // Confirmation hydrates missing references once, before the shared family check and dice.
        deferStatusDefinitions: true,
        skillEntries: pageSkillEntries(),
        resolveItem: (id: string) => fullDefinition(recordOf(recordOf(weapon.catalog)?.[id]), true)
            ?? fullDefinition(recordOf(findAbility(caster, id)), true)
            ?? fullDefinition(recordOf(window.FateTide?.objectCache instanceof Map ? window.FateTide.objectCache.get(id) : null), true) };
    const natural = !ftInventoryField(weapon, 'inventory_id') && String(ftItemsCol(weapon, 'item_type') ?? '') === 'natural_weapon';
    const binding = natural
        ? bindNaturalWeaponAttack(ability as Record<string, unknown>, caster as Record<string, unknown>, inventoryId, read)
        : bindExplicitWeaponAttack(ability as Record<string, unknown>, caster as Record<string, unknown>, inventoryId, read, throwing);
    return 'reason' in binding ? binding.reason : explicitWeaponFamilyRefusal(binding, caster as Record<string, unknown>, read, throwing);
}

export function ftDispatchWeaponClick(
    casterId: string,
    weaponId: string,
    variant: 'hit' | 'throw' | 'damage' = 'hit',
    preserveWeaponMenu = false,
    deferredDispatch: (() => void) | null = null,
): boolean {
    const cid = String(casterId || '');
    const wid = String(weaponId || '');
    if (!cid || !wid || !ft3dDockPresentFor(cid)) return false;
    claimActionBarOverlayLane(cid, 'weapon');
    const active = abilityTargetingLifecycle.current();
    if (active && active.casterId === cid && active.abilityId.toUpperCase() === wid.toUpperCase()) {
        confirmAbilityTargeting(cid);
        return true;
    }
    const caster = casterFromCache(cid);
    if (!caster) return false;
    const weapon = findInventoryEntry(caster, wid) ?? findAbility(caster, wid);
    if (!weapon) return false;
    if (variant === 'hit' && weaponInThrowMode(cid, wid, weapon)) variant = 'throw';
    if (variant === 'throw') {
        const reason = weaponArmRefusal(caster, weapon, wid, null, true);
        if (reason) { reportWeaponAttackRefusal(reason, { actorId: cid, weaponInventoryId: wid }); return true; }
    }
    const profile = weaponAttackTargetingProfile(caster, weapon, wid, 1, variant === 'throw');
    if (!profile) return false;
    if (variant === 'throw') profile.throwLob = weaponInLobMode(cid, wid);
    abChoiceBranch = null;
    abChoice = null;
    if (!preserveWeaponMenu) abWeaponMenu = null;
    setItemTargetMenu(null);
    armCreatureTargeting(
        cid,
        wid,
        caster,
        weapon,
        profile,
        { kind: 'plain', abilityId: wid },
        deferredDispatch ?? (variant === 'throw'
            ? () => window.useAbilityWithWeapon?.(wid, wid, null, 'throw_weapon')
            : () => window.useAbility?.(wid)),
    );
    return true;
}

/** For an equipped weapon, the attack the slot click should fire + its type glyph. Mirrors the Weapons
 *  tab: a THROWING attack (the bound attack ability carries attack_ability_throws_the_throwing_weapon…)
 *  throws via useAbilityVariable1(id,'throw_weapon'); otherwise the weapon's normal hit via useAbility(id),
 *  melee or ranged by weapon_type. null when the item has no firing attack (e.g. equipped armor). */
function inventoryAttackAction(caster: CachedCaster, entry: CasterItemNode, invId: string): { attrs: string; iconId: string } | null {
    const bind = resolveInventoryBinding(caster, invId);
    // NPCs need not grant a separate attack ability for each equipped weapon. The weapon
    // itself supplies its basic attack; a granted binding only refines it (e.g. throwing).
    const itemType = String(ftItemsCol(entry, 'item_type') ?? '').toLowerCase();
    if (!bind && itemType !== 'weapon' && itemType !== 'natural_weapon') return null;
    const ab = bind ? findAbility(caster, bind.abilityId) : null;
    const lv = ftLayerValue;
    const id = escapeHtml(invId);
    const casterId = String(caster.encounter_creatures?.encounter_creature_id ?? '');
    if (weaponInThrowMode(casterId, invId, entry)) {
        return { attrs: `data-ft-ab-action="use-weapon" data-ft-ability-id="${id}" data-ft-weapon-mode="throw"`, iconId: 'throw_weapon' };
    }
    const throws = ab && typeof lv === 'function' && !!lv(ab, 'attack_ability_throws_the_throwing_weapon_at_target');
    if (throws) {
        return { attrs: `data-ft-ab-action="use-ability-variable1" data-ft-ability-id="${id}" data-ft-variable1="throw_weapon"`, iconId: 'throw_weapon' };
    }
    const wt = typeof lv === 'function' ? String(lv(entry, 'weapon_type') ?? '') : '';
    // `use-weapon`, not `use-ability`: the slot carries the INVENTORY id, and only ftDispatchWeaponClick()
    // can turn an inventory weapon into a targeting profile (a melee weapon arms Reach targeting). Routed
    // as `use-ability` the real Dagger slot armed nothing and fell through to a blind legacy POST that the
    // no-target gate silently swallowed — owner 2026-09-01, verified on staging. Fallback is unchanged:
    // a weapon without a targeting profile still fires useAbility(inventory_id) immediately.
    return { attrs: `data-ft-ab-action="use-weapon" data-ft-ability-id="${id}"`, iconId: wt === MELEE_WEAPON_TYPE_ID ? ICON_ATTACK_MELEE : ICON_ATTACK_RANGED };
}

/** A bound INVENTORY item (e.g. a weapon): ALWAYS render the item's own icon (renderInventoryThumbnail),
 *  NOT the bare attack rune. When it's an equipped weapon with a firing attack, clicking runs the weapon's
 *  normal attack via useAbility(inventory_id) — the SAME path as the Weapons-tab equipped-weapon hit button
 *  — so the slot shows the WEAPON and still attacks. The icon dims when the item isn't equipped. Press-hold
 *  opens item info; the button is draggable within the bar for move/unbind (onAbilityPointerDown recognises
 *  it via .ft-action-inv-btn). (Previously equipped weapons rendered generateAbilityButton, so a correct
 *  slot_type:'inventory' binding LOOKED like "it bound the attack ability, not the item".) */
function renderInventorySlot(caster: CachedCaster, invId: string, pos: string, variant?: string): string {
    const entry = findInventoryEntry(caster, invId);
    if (!entry) return renderMissingSlot(pos, 'This item is no longer available');
    const equipped = inventoryEquipped(entry, caster);
    // The thumbnail is DECORATION only here — the slot button owns the click (useAbility) and the bar owns
    // drag. So render it INERT: dom_id:false avoids a second id="inventory_<uuid>" colliding with the real
    // inventory-tab tile, exclude_dropzone strips the .dropzone behavior, and we strip data-inventory_id so
    // the global target-select click walk (targeting-combat.ts: toggles .targeted on any data-inventory_id
    // ancestor) doesn't treat a click on the bar as picking a map target.
    let thumb = (typeof window.renderInventoryThumbnail === 'function')
        ? window.renderInventoryThumbnail(entry, {}, {
            flow_layout: true, show_overlay: false, use_size_based_width: false,
            thumbnail_width: 44, thumbnail_height: 44, is_draggable: false, show_log_message: 0, show_icons: false,
            dom_id: false, exclude_dropzone: true,
        })
        : '';
    thumb = thumb.replace(/\sdata-inventory_id="[^"]*"/, '');
    const nameRaw = (typeof ftInventoryField === 'function' ? ftInventoryField(entry, 'inventory_name') : entry.inventory_name);
    const name = String((Array.isArray(nameRaw) ? nameRaw[0] : nameRaw) ?? 'Item');
    // Click behaviour:
    //  • Equipped weapon with a firing attack → fire it (the correct useAbility variant — a throwing
    //    weapon throws, a hand weapon hits — see inventoryAttackAction).
    //  • UNEQUIPPED item that has an equipment slot → equip it (server equips into a free hand, or swaps
    //    the off-hand / two-handed item out first; AP + Fast Hands are handled server-side).
    //  • Anything else (equipped non-weapon, slotless consumable) → no use-action yet (use-on-click is a
    //    later pass), but the button stays draggable + press-holdable for info.
    const lv = ftLayerValue;
    const equippable = typeof lv === 'function' ? !!lv(entry, 'equipment_slot') : false;
    // Throw-variant slot (bound from the Weapons-tab throw tile via source_id 'throw_weapon'): force the
    // THROW action + glyph regardless of the weapon's DEFAULT attack, so a throwable weapon (e.g. dagger)
    // can live on the bar TWICE — once as its melee/ranged hit, once as a throw.
    const atkDefault = equipped ? inventoryAttackAction(caster, entry, invId) : null;
    const atk = (variant === 'throw_weapon' && equipped)
        ? { attrs: `data-ft-ab-action="use-ability-variable1" data-ft-ability-id="${escapeHtml(invId)}" data-ft-variable1="throw_weapon"`, iconId: 'throw_weapon' }
        : atkDefault;
    let clickAttr = '';
    if (atk) {
        clickAttr = ` ${atk.attrs}`;
    } else if (!equipped && equippable) {
        clickAttr = ` data-ft-ab-action="equip-inventory" data-ft-inventory-id="${escapeHtml(invId)}"`;
    }

    // Corner badges (equipped only): the attack-type glyph top-LEFT (melee/ranged/throw — matches the
    // click), and the equipment-slot glyph top-RIGHT (a clear "equipped, in this slot" readout). Both are
    // pointer-events:none so they never steal the click/drag.
    let badges = '';
    if (atk) {
        badges += `<span class="ft-action-inv-corner ft-action-inv-corner--tl" aria-hidden="true">${iconMap(atk.iconId, 15, 15)}</span>`;
    }
    if (equipped) {
        const rawSlot = (typeof ftInventoryField === 'function' ? ftInventoryField(entry, 'equipped') : entry.equipped);
        const slotId = String((Array.isArray(rawSlot) ? rawSlot[0] : rawSlot) ?? '');
        if (UUID_RE.test(slotId)) {
            badges += `<span class="ft-action-inv-corner ft-action-inv-corner--tr" aria-hidden="true">${iconMap(slotId, 15, 15)}</span>`;
        }
    }

    // The same one-AP hint is patched on resource changes; clicks still reach the browser cast gate.
    const unusable = atk && attackSlotUnusable(caster) ? ' is-unusable' : '';

    // Weapon fold-out marker (dock only, via onAbilityChoiceClickIntercept): an equipped weapon whose
    // DEFAULT attack fires gets its click intercepted to open the per-weapon attack fold-out instead of
    // firing immediately. The forced throw-variant slot keeps its direct throw (it IS a specific attack
    // choice already). In-sheet bars never intercept, so the inline onclick still fires there.
    const foldable = !!atkDefault && variant !== 'throw_weapon';
    let foldAttr = foldable ? ` data-ft-weapon-foldout="${escapeHtml(invId)}"` : '';
    // NESTED-EFFECT item (owner 2026-07-19): not a weapon firing attacks, but the row grants active
    // abilities of its own — a healing potion's "Drink …" / "Throw …" quick_abilities, a coating, a
    // charged wondrous item. Click opens the same fold-out listing those abilities. Only when the slot has
    // no other click job: an unequipped-but-equippable item keeps its equip-on-click (you equip first,
    // THEN click again for the menu — matching the weapon gesture), and a weapon keeps its attack fold-out.
    if (!foldable && !clickAttr && nestedFoldEntries(caster, invId).length > 0) {
        foldAttr = ` data-ft-nested-foldout="${escapeHtml(invId)}"`;
    }

    const dim = equipped ? '' : ' ft-action-slot--inv-unequipped';
    return `<div class="ft-action-slot ft-action-slot--filled ft-action-slot--inv${dim}${unusable}" ${pos}${atk ? ' data-ft-ap-slot="attack"' : ''}>`
        + `<button type="button" class="ft-action-inv-btn"${clickAttr}${foldAttr} data-ft-info-item-id="${escapeHtml(invId)}" ${tip(name, 'top')}>${thumb}${badges}</button>`
        + `</div>`;
}

/** A slot whose bound thing no longer resolves — a deleted/unprepared spell, a sold/removed item, a skill
 *  no longer known. Renders a visible "unknown" glyph (help-circle-outline — iconMap's own ? fallback) inside
 *  a dimmed/greyed slot, so it reads as "this ability is gone" instead of looking like an empty/unbound slot
 *  (the old `--missing` div was empty → indistinguishable from a free slot). The slot still drags for unbind/
 *  move so the player can clear or re-point the stale binding. (owner 2026-06-27) */
function renderMissingSlot(pos: string, label: string): string {
    return `<div class="ft-action-slot ft-action-slot--missing" ${pos} ${tip(label, 'top')}>`
        + `${iconMap('help-circle-outline', 30, 30, false)}</div>`;
}

/** One slot cell, explicitly placed in the shared grid (grid-row = bar index +1, grid-column = computed).
 *  Carries data-ft-ab-bar + data-ft-ab-slot so the drag system knows its (bar, slot) without DOM walking. */
function renderSlotCell(caster: CachedCaster, slot: ABSlot | undefined, slotIdx: number, barIdx: number, gridCol: number, hotKey = ''): string {
    // data-ft-ab-key = the effective key (dock only); the keydown listener fires `[data-ft-ab-key="<k>"] button`.
    const keyAttr = hotKey ? ` data-ft-ab-key="${escapeHtml(hotKey)}"` : '';
    const pos = `style="grid-row:${barIdx + 1};grid-column:${gridCol};" data-ft-ab-slot="${slotIdx}" data-ft-ab-bar="${barIdx}"${keyAttr}`;
    if (slot && slot.ability_id) {
        if (slot.slot_type === 'inventory') {
            return renderInventorySlot(caster, slot.ability_id, pos, slot.source_id);
        }
        if (slot.slot_type === 'special') {
            const spec = specialAbility(slot.ability_id);
            if (spec) {
                const sid = escapeHtml(slot.ability_id);
                // Short Rest's glyph tracks its remaining charges; the rest use their fixed registry icon.
                const icon = String(slot.ability_id).toUpperCase() === SHORT_REST_ID ? shortRestIcon(caster) : spec.icon;
                // Short Rest / Rest are OUT-of-combat only → ring the slot inactive while in combat so the player
                // doesn't click and get a Janus warning (owner 2026-06-26). Special buttons aren't .btn_ability_ranges,
                // so use the generic slot-level .is-unusable (red border + dim).
                const unusable = !!spec.outOfCombatOnly && casterInCombat(caster);
                const button = String(slot.ability_id).toUpperCase() === RESOLVE_ABILITY_ID
                    ? renderSimpleTriggerAbilityButton(slot.ability_id, 'ft-action-special-btn', iconMap(icon, 32, 32), caster, {
                        title: spec.label,
                        extraAttrs: { type: 'button', 'data-ft-info-item-id': slot.ability_id },
                    })
                    : `<button type="button" class="ft-action-special-btn" data-ability_id="${sid}" data-ft-info-item-id="${sid}" data-ft-ab-action="single-ability" data-ft-ability-id="${sid}" ${tip(spec.label, 'top')}>${iconMap(icon, 32, 32)}</button>`;
                return `<div class="ft-action-slot ft-action-slot--filled ft-action-slot--special${unusable ? ' is-unusable' : ''}" ${pos}>`
                    + button
                    + `</div>`;
            }
            return renderMissingSlot(pos, 'This ability is no longer available');
        }
        if (slot.slot_type === 'action') {
            const act = barAction(slot.ability_id);
            if (act) {
                const key = escapeHtml(slot.ability_id);
                return `<div class="ft-action-slot ft-action-slot--filled ft-action-slot--special" ${pos}>`
                    + `<button type="button" class="ft-action-special-btn" data-ft-bar-action="${key}" ${act.attrs(caster)} ${tip(act.label, 'top')}>${iconMap(act.icon, 32, 32)}</button>`
                    + `</div>`;
            }
            return renderMissingSlot(pos, 'This action is no longer available');
        }
        if (slot.slot_type === 'skill') {
            const inner = skillSlotInner(slot.ability_id);
            if (inner) return `<div class="ft-action-slot ft-action-slot--filled ft-action-slot--skill" ${pos}>${inner}</div>`;
            return renderMissingSlot(pos, 'This skill is no longer available');
        }
        // Item-sourced grant gate: a slot bound to an ability that came FROM an item (source_id set —
        // an equipped weapon's attack, or a wand/bead's at-will/charged spell) is only usable while
        // that grant is live. Once the source item is unequipped/removed the ability drops out of the
        // caster's ability + castable pools (server recompile). We DON'T flat-grey it (that threw away
        // the icon + its color coding — owner: "they're all grey, we color code them more"): instead we
        // keep rendering the ability's REAL color-coded rune, dimmed with an amber "unequipped" ring
        // (ft-action-slot--unavailable), so you still recognize which power it is. The cast itself is
        // hard-blocked server-side (use_ability_pre_conditions) — but that gate ONLY fires when the cast
        // carries a grant marker in use_ability_variable_1, so the render below MUST send one (it not
        // sending one was the "tooltip says unequipped but clicking still casts" bug) — a click then
        // just surfaces the rejection. Source-less (native) bindings skip this entirely. (owner 2026-07-13)
        const grantGone = !!slot.source_id && !abilityGrantPresent(caster, slot.ability_id);
        const ab = findAbility(caster, slot.ability_id);
        if (ab) {
            // Pass the stored source so weapon attacks fire against the right weapon (draw/throw/nested
            // variants are recomputed by generateAbilityButton from the ability's layers + this source).
            // iconSize:'l' → L2 rune detail tier (more differentiating art); display stays slot-sized via
            // .ft-action-slot .ft-rune-mount{width/height:100%} (owner 2026-06-26).
            // Bound slots lose their spell_type → recover it and RIDE IT ON THE CAST (use_ability_variable_1),
            // exactly like the spells island passes it: the server's item-grant gate, mana-pool skip and charge
            // accounting all key off the marker. Doubles as castFreeOfMana so at-will/innate/charged don't
            // false-ring on mana.
            const grantType = boundSpellGrantType(caster, slot.ability_id, slot.source_id ?? null);
            // grantGone → the pools no longer hold the entry, so the TRUE type is unrecoverable; send
            // at_will_spell anyway. WHICH marker only matters when a cast PROCEEDS — the server gate rejects
            // on bucket-absence for any of them, which is the whole point of this click (surface the warning).
            const spellType = (grantType !== null && GRANT_CAST_MARKERS.has(grantType))
                ? grantType
                : (grantGone ? 'at_will_spell' : null);
            const consumeNested = boundAbilityConsumesNestedUnit(caster, slot.ability_id, slot.source_id ?? null);
            let abBtn = generateAbilityButton(ab, caster, { iconSize: 'l', spellType, source: slot.source_id ?? null, consumeNestedUnitPerUse: consumeNested, castFreeOfMana: grantType !== null, barChoosesAmount: true, showChargePill: true });
            const ownItemTypes = ownInventoryTargetTypes(ab, caster);
            if (ownItemTypes.length > 0) {
                abBtn = abBtn.replace(
                    'data-ability_id=',
                    `data-ft-item-target-types="${escapeHtml(ownItemTypes.join(','))}" data-ability_id=`,
                );
            }
            // CHARGE-short: a charged ability whose investable budget is empty → the SAME red "can't use" ring (AP/mana
            // already handled inside generateAbilityButton). abilityPoolShort reads the shared budget, so a protected
            // holder's empty pool — usable, it spends nothing — is never ringed. (owner 2026-06-26: "snipe the easy ones
            // like charges".)
            if (abilityPoolShort(caster, ab) && !abBtn.includes('ft-ability-btn-ap-short')) {
                abBtn = abBtn.replace('btn_ability_ranges', 'btn_ability_ranges ft-ability-btn-ap-short');
            }
            // Item grant no longer live → keep the real color-coded icon but mark it unavailable
            // (dim + amber ring); the server hard-blocks the cast, the click just surfaces the reason.
            const unavailable = grantGone ? ' ft-action-slot--unavailable' : '';
            const gone = grantGone ? ` ${tip('The item that granted this is no longer equipped', 'top')}` : '';
            // Feature B: saved creature-target thumbnails overlaid on the ability icon (empty string when none).
            const tgtOverlay = renderSlotTargetsOverlay(caster, slot.ability_id);
            return `<div class="ft-action-slot ft-action-slot--filled${unavailable}"${gone} ${pos}>${abBtn}${tgtOverlay}</div>`;
        }
        return renderMissingSlot(pos, grantGone ? 'The item that granted this is no longer equipped' : 'This ability is no longer available');
    }
    return `<div class="ft-action-slot ft-action-slot--empty" ${pos}></div>`;
}

// ── hotkeys (Increment 1: default WoW-style 1-0 on the primary bar's first 10 slots) ──────────────
// Bindings are POSITIONAL — tied to (bar, slot), not to the ability sitting there — matching BG3/WoW, so
// they survive ability swaps. v1 ships defaults only: bar 0 (the primary/bottom bar), slots 0-9 → 1,2,…,9,0.
// Per-slot custom rebinding (the BG3 "configure" flow + settings tray) is Increment 2. Dock-only: the keydown
// listener fires the full-screen dock's bar, and the badges only render there.
const HOTKEY_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const FT3D_ACTION_KEY_DEFAULTS: Readonly<Record<string, string>> = Object.freeze({
    action_bar: 'b',
    skills: 'k',
    main_hand: 'f',
    off_hand: 'g',
    hiding: 'c',
    character: 'n',
    inventory: 'i',
});

/** The campaign shell owns general shortcuts. Other routes retain the historical fallbacks. */
function ft3dActionBinding(action: string): string {
    const fallback = FT3D_ACTION_KEY_DEFAULTS[action] ?? '';
    if (typeof window.ftGetFt3dKeyBinding !== 'function') return fallback;
    const binding = window.ftGetFt3dKeyBinding(action);
    return typeof binding === 'string' ? binding : fallback;
}

function ft3dActionBindingMatches(event: KeyboardEvent, action: string): boolean {
    if (typeof window.ftMatchFt3dKeyBinding === 'function') {
        return window.ftMatchFt3dKeyBinding(event, action);
    }
    const fallback = FT3D_ACTION_KEY_DEFAULTS[action] ?? '';
    return !!fallback && !event.ctrlKey && !event.metaKey && !event.altKey
        && String(event.key).toLowerCase() === fallback;
}

function ft3dActionBindingDisplay(action: string): string {
    const binding = ft3dActionBinding(action);
    if (typeof window.ftFormatFt3dKeyBinding === 'function') return window.ftFormatFt3dKeyBinding(binding);
    return keyDisplay(binding);
}

function reservedGlobalKeys(): Set<string> {
    const configured = typeof window.ftGetFt3dReservedActionKeys === 'function'
        ? window.ftGetFt3dReservedActionKeys()
        : ['i', 'k', 'b', 'f', 'g'];
    return new Set(configured.filter((key) => /^[a-z0-9]$/.test(key)));
}

/** Positional default key for a slot: bar 0 (primary), slots 0-9 → 1,2,…,9,0. '' = no default. */
function defaultKeyForSlot(barIdx: number, slotIdx: number): string {
    if (barIdx === 0 && slotIdx >= 0 && slotIdx < HOTKEY_DIGITS.length) return HOTKEY_DIGITS[slotIdx]!;
    return '';
}

/** The EFFECTIVE (normalized, lowercase) key bound to a slot. A stored `hot_key` is authoritative — including
 *  the '' sentinel, which means "explicitly unbound" and SUPPRESSES the positional default (so rebinding a
 *  default digit onto another slot doesn't leave a phantom binding here). Absent → the positional default. */
function effectiveKeyForSlot(slot: ABSlot | undefined, barIdx: number, slotIdx: number): string {
    const hk = slot?.hot_key;
    // A legacy binding on a now-RESERVED key (f/g became the hand slots 2026-08-27) is treated as unbound:
    // no badge, never fired — the reserved key always goes to its global owner.
    if (typeof hk === 'string') return reservedGlobalKeys().has(hk) ? '' : hk;
    const fallback = defaultKeyForSlot(barIdx, slotIdx);
    return reservedGlobalKeys().has(fallback) ? '' : fallback;
}

/** Display form: digits as-is, single letters uppercased (BG3/WoW look). */
function keyDisplay(k: string): string { return /^[a-z]$/.test(k) ? k.toUpperCase() : k; }

/** Keys RESERVED for global UI shortcuts — never bindable to an action slot, and never fired as one.
 *  'i' = fullscreen inventory dock toggle; 'k' = Skills quick-tray. Reserving them here (used
 *  by BOTH the bind path and the fire path) prevents a slot from double-firing alongside its global UI. */
/** The two hand slots read their bindings from the campaign shell's general-key registry. They render as
 *  .ft-action-slot[data-ft-ab-key], so the same real-button fire path below drives pointer and keyboard use. */

/** Normalize a pressed key to a storable binding ('' if not bindable — modifiers/F-keys/Enter/reserved/etc.). */
function normalizeBindKey(key: string): string {
    const k = /^[a-z0-9]$/i.test(key) ? key.toLowerCase() : '';
    return reservedGlobalKeys().has(k) ? '' : k;
}

/** Inject a top-CENTER key badge into a rendered slot cell. The cell is a single-root string ending in the
 *  slot's outer </div>, so we splice the badge in just before it — works for every slot variant (filled /
 *  inventory / special / empty) without threading the badge through each branch. Top-left/right corners are
 *  already used by the attack-type + equipment-slot glyphs, hence top-center. */
function withKeyBadge(cellHtml: string, key: string): string {
    if (!key) return cellHtml;
    const badge = `<span class="ft-action-key-badge" aria-hidden="true">${escapeHtml(keyDisplay(key))}</span>`;
    return cellHtml.replace(/<\/div>\s*$/, `${badge}</div>`);
}

/** Configure-mode overlay covering a FILLED slot: click → that slot starts listening for its key. Sits on top
 *  (pointer-events) so the underlying ability button can't fire while you're rebinding. */
function withBindOverlay(cellHtml: string, casterId: string, barIdx: number, slotIdx: number, key: string): string {
    const listening = !!bindListening && bindListening.casterId === casterId
        && bindListening.barIdx === barIdx && bindListening.slotIdx === slotIdx;
    const label = listening ? 'Press a key…' : (key ? `⌨ ${escapeHtml(keyDisplay(key))}` : '⌨ Set key');
    const overlay = `<button type="button" class="ft-action-bind-overlay${listening ? ' is-listening' : ''}"`
        + ` data-ft-ab-action="bind-slot" data-ft-caster-id="${escapeHtml(casterId)}" data-ft-bar-idx="${barIdx}" data-ft-slot-idx="${slotIdx}">${label}</button>`;
    return cellHtml.replace(/<\/div>\s*$/, `${overlay}</div>`);
}

/** Bind key K to (barIdx, slotIdx): first FREE K from every slot that currently resolves to it (custom OR
 *  positional default → set to the '' sentinel), then stamp it on the target. Persists via the normal layout
 *  save. No-op if the target slot is empty (hot_key only lives on a stored, ability-bearing slot row). */
function assignHotKey(casterId: string, barIdx: number, slotIdx: number, key: string): void {
    const caster = casterFromCache(casterId);
    if (!caster) return;
    const bars = readBars(caster).map((b) => ({ ...b, slots: (b.slots || []).map((s) => ({ ...s })) }));
    bars.forEach((b, bi) => (b.slots || []).forEach((s) => {
        if (effectiveKeyForSlot(s, bi, Number(s.slot_index)) === key) s.hot_key = '';
    }));
    const target = bars[barIdx] && (bars[barIdx]!.slots || []).find((s) => Number(s.slot_index) === slotIdx);
    if (!target) return;
    target.hot_key = key;
    persist(casterId, bars);
}

/** Document-level keydown. Two modes: (1) a slot is LISTENING for its key (configure flow) → capture + bind;
 *  (2) normal play → fire the FULL-SCREEN DOCK slot whose effective key matches, by re-using its real button
 *  click (targeting / AP / equip / throw stay in one place). Idempotent install. */
function ensureActionBarHotkeys(): void {
    if (typeof document === 'undefined' || window._ftAbHotkeys) return;
    window._ftAbHotkeys = true;
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        // 1) BIND CAPTURE — precedence over everything when a slot is waiting for its key.
        if (bindListening) {
            if (e.key === 'Escape') { const c = bindListening.casterId; bindListening = null; e.preventDefault(); repaintActionBars(c); return; }
            const bk = normalizeBindKey(e.key);
            if (!bk) return; // modifier / F-key / Enter — ignore, keep listening
            e.preventDefault();
            const t = bindListening; bindListening = null;
            assignHotKey(t.casterId, t.barIdx, t.slotIdx, bk); // persist() repaints
            return;
        }
        // 2) NORMAL FIRE — configurable shell actions may intentionally use Ctrl/Alt/Shift. Character
        // slot bindings remain plain alphanumerics and never hijack browser-modified shortcuts.
        if (e.metaKey || e.repeat) return;
        const ae = document.activeElement as HTMLElement | null;
        if (ae) { const tag = ae.tagName; if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || ae.isContentEditable) return; }
        const dock = document.querySelector<HTMLElement>('.ft3d-actionbar-dock');
        if (!dock) return;                                     // dock-only — no full-screen bar → key passes through
        if (dock.hidden) return;                               // minimized (B) → a hidden bar never fires
        if (dock.querySelector('.ft-action-bars.is-binding')) return; // configuring this bar → don't fire abilities
        // Fixed launchers/hand slots FIRST: their keys are reserved (normalizeBindKey → ''), so route them
        // through the same real buttons as pointer input before looking for a configurable action slot.
        if (ft3dActionBindingMatches(e, 'skills')) {
            const skills = dock.querySelector<HTMLElement>('.ft-action-skills-launch');
            if (!skills) return;                               // this caster has no skills
            e.preventDefault();
            skills.click();
            return;
        }
        const hand = ft3dActionBindingMatches(e, 'main_hand')
            ? 'main'
            : (ft3dActionBindingMatches(e, 'off_hand') ? 'off' : '');
        if (!hand && (e.ctrlKey || e.altKey)) return;
        const k = hand ? ft3dActionBinding(hand === 'main' ? 'main_hand' : 'off_hand') : normalizeBindKey(e.key);
        if (!k) return;
        const btn = hand
            ? (dock.querySelector<HTMLElement>(`.ft-action-slot[data-ft-action-binding="${hand === 'main' ? 'main_hand' : 'off_hand'}"] button`)
                ?? dock.querySelector<HTMLElement>(`.ft-action-slot[data-ft-hand-slot="${hand}"] button`))
            : dock.querySelector<HTMLElement>(`.ft-action-slot[data-ft-ab-key="${k}"] button`);
        if (!btn) return;                                      // unbound / empty slot — do nothing
        e.preventDefault();
        const slot = btn.closest('.ft-action-slot');
        slot?.classList.add('ft-action-slot--keyfire');
        setTimeout(() => slot?.classList.remove('ft-action-slot--keyfire'), 160);
        btn.dataset.ftActionKeyfire = '1';
        try { btn.click(); } finally { delete btn.dataset.ftActionKeyfire; }
    });
}

function ensureActionBarDelegation(): void {
    if (window._ftAbDelegation) return;
    window._ftAbDelegation = true;
    document.addEventListener('click', (e: MouseEvent) => {
        const el = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-ft-ab-action]') : null;
        if (!el) return;
        const action = el.dataset.ftAbAction || '';
        // Most controls carry their caster explicitly. Compact projection tiles (hands, future dock
        // surfaces) still live inside the authoritative action-bar pane, so recover that identity rather
        // than falling through to an immediate targetless cast when a renderer omits the duplicate attr.
        const cid = el.dataset.ftCasterId
            || el.closest<HTMLElement>(AB_PANE_SEL)?.getAttribute('data-ft-caster-id')
            || '';
        const aid = el.dataset.ftAbilityId || '';
        if (action === 'toggle-skills') { e.preventDefault(); ftActionBarToggleSkills(cid); return; }
        if (action === 'unit-tab') {
            e.preventDefault();
            if (!(el as HTMLButtonElement).disabled) ftActionBarSelectUnit(el.dataset.ftUnitId || '');
            return;
        }
        if (action === 'toggle-ap-fold') { e.preventDefault(); ftActionBarToggleActionPointFold(cid); return; }
        if (action === 'ap-fold-close') { e.preventDefault(); ftActionBarToggleActionPointFold(cid, false); return; }
        if (action === 'toggle-settings') { e.preventDefault(); ftActionBarToggleSettings(cid); return; }
        if (action === 'toggle-lock') { e.preventDefault(); ftActionBarToggleLock(cid); return; }
        if (action === 'add-row') { e.preventDefault(); ftActionBarAddRow(cid); return; }
        if (action === 'remove-row') { e.preventDefault(); ftActionBarRemoveRow(cid); return; }
        if (action === 'add-divider') { e.preventDefault(); ftActionBarAddDivider(cid); return; }
        if (action === 'toggle-bind-mode') { e.preventDefault(); ftActionBarToggleBindMode(cid); return; }
        if (action === 'set-size') { e.preventDefault(); ftActionBarSetSize(cid, el.dataset.ftBarSize || ''); return; }
        if (action === 'set-theme') { e.preventDefault(); ftActionBarSetTheme(cid, el.dataset.ftTheme || ''); return; }
        if (action === 'dance-remove' || action === 'dance-move') {
            e.preventDefault();
            const index = Number(el.dataset.ftDanceIdx);
            const dir = Number(el.dataset.ftDanceDir || 0);
            ftActionBarEditDancePlaylist(cid, (list) => {
                if (!Number.isInteger(index) || index < 0 || index >= list.length) return;
                if (action === 'dance-remove') { list.splice(index, 1); return; }
                const to = index + dir;
                if (to < 0 || to >= list.length) return;
                [list[index], list[to]] = [list[to]!, list[index]!];
            });
            return;
        }
        if (action === 'bind-slot') {
            e.preventDefault();
            ftActionBarBindSlot(cid, Number(el.dataset.ftBarIdx || 0), Number(el.dataset.ftSlotIdx || 0));
            return;
        }
        if (action === 'choice-step') {
            e.preventDefault();
            const intentId = Number(el.closest<HTMLElement>('[data-ft-ability-intent-id]')?.dataset.ftAbilityIntentId || 0);
            if (intentId) {
                const valueEl = el.parentElement?.querySelector<HTMLElement>('[data-ft-targeting-option-value]');
                if (valueEl) {
                    const min = Number(el.dataset.ftMin || 0);
                    const max = el.dataset.ftMax === undefined
                        ? Number.POSITIVE_INFINITY
                        : Number(el.dataset.ftMax);
                    const next = Math.max(min, Math.min(max,
                        Number(valueEl.dataset.ftTargetingOptionValue || min) + Number(el.dataset.ftDelta || 0)));
                    (window.ftAbilityTargetingSetNumericOption ?? setAbilityTargetingNumericOption)(intentId, next);
                    return;
                }
            }
            ftAbChoiceStep(el, Number(el.dataset.ftDelta || 0));
            return;
        }
        if (action === 'projectile-add' || action === 'projectile-undo') {
            e.preventDefault(); e.stopPropagation();
            window.aoeManager?.addFt3dProjectile?.(action === 'projectile-undo');
            return;
        }
        if (action === 'summon-cast-now') {
            e.preventDefault(); e.stopPropagation();
            window.aoeManager?.castFt3dSummonsNow?.();
            return;
        }
        if (action === 'targeting-remove') {
            e.preventDefault(); e.stopPropagation();
            (window.ftAbilityTargetingStagePick ?? stageAbilityTargetPick)(
                Number(el.dataset.ftIntentId || 0),
                el.dataset.ftEntityId || '',
                `fold-remove:${Date.now()}:${el.dataset.ftEntityId || ''}`,
                true,
            );
            return;
        }
        if (action === 'aoe-upcast-step') { e.preventDefault(); ftActionBarAoeUpcastStep(cid, aid, Number(el.dataset.ftDelta || 0)); return; }
        if (action === 'aoe-ready') { e.preventDefault(); ftActionBarAoeReady(cid, aid); return; }
        if (action === 'aoe-confirm') {
            e.preventDefault();
            // Confirm only the placement this row was painted for — a stale row must not cast a newer one.
            const liveIntent = window.aoeManager?.ft3dActive?.envelope?.intent;
            if (liveIntent && String(liveIntent.controllerId || liveIntent.casterId || '').toUpperCase() === cid.toUpperCase()
                && String(liveIntent.abilityId || '').toUpperCase() === aid.toUpperCase()) window.aoeManager?.confirmFt3d?.();
            return;
        }
        if (action === 'aoe-cancel') { e.preventDefault(); window.aoeManager?.cancelFt3d?.(); return; }
        if (action === 'toggle-ritual') { e.preventDefault(); ftActionBarToggleRitual(cid, aid); return; }
        // No preventDefault: let the native checkbox toggle; just mirror its state onto the open choice.
        if (action === 'toggle-quick-ritual') { if (abChoice) abChoice.quickRitual = (el as HTMLInputElement).checked; return; }
        if (action === 'choice-cancel') { e.preventDefault(); ftActionBarChoiceCancel(cid); return; }
        if (action === 'choice-confirm') { e.preventDefault(); ftActionBarChoiceConfirm(cid, (el.dataset.ftMode as 'normal' | 'ritual') || 'normal'); return; }
        if (action === 'weapon-fold-fire') { e.preventDefault(); ftActionBarWeaponFoldFire(cid, aid, el.dataset.ftInventoryId || ''); return; }
        if (action === 'arcane-arrow-fire') { e.preventDefault(); ftActionBarArcaneArrowFire(cid, el.dataset.ftInventoryId || '', el.dataset.ftScrollId || ''); return; }
        if (action === 'weapon-fold-drop' || action === 'weapon-fold-throw-object') {
            e.preventDefault();
            const invId = String(el.dataset.ftInventoryId || '');
            if (!invId) return;
            cancelAbilityTargeting(false);
            abWeaponMenu = null;
            const carry = window as Window & { ftDropInventoryAtFeet?: (id: string) => boolean; ftThrowObjectStart?: (id: string) => boolean };
            if (action === 'weapon-fold-drop') carry.ftDropInventoryAtFeet?.(invId);
            else carry.ftThrowObjectStart?.(invId);
            repaintActionBars(cid);
            return;
        }
        // General Attacks → held-item Throw: a throwing weapon arms its own thrown attack (one-shot — the
        // weapon's sticky Hit/Throw mode is left alone; the button's Direct/Lob pick becomes the weapon's flight
        // choice, which only ever governs throws); anything else arms the generic Throw Object placement.
        if (action === 'general-throw') {
            e.preventDefault();
            const invId = String(el.dataset.ftInventoryId || '');
            if (!cid || !invId) return;
            cancelAbilityTargeting(false);
            abWeaponMenu = null;
            if (el.dataset.ftThrowKind === 'weapon') {
                const key = weaponModeKey(cid, invId);
                if (el.dataset.ftThrowFlight === 'lob') lobWeaponModes.add(key);
                else lobWeaponModes.delete(key);
                ftDispatchWeaponClick(cid, invId, 'throw');
                return;
            }
            (window as Window & { ftThrowObjectStart?: (id: string) => boolean }).ftThrowObjectStart?.(invId);
            repaintActionBars(cid);
            return;
        }
        if (action === 'weapon-fold-lob') {
            e.preventDefault();
            if (!abWeaponMenu || abWeaponMenu.casterId !== cid || abWeaponMenu.kind !== 'weapon') return;
            const key = weaponModeKey(cid, abWeaponMenu.invId);
            if (el.dataset.ftFlight === 'lob') lobWeaponModes.add(key);
            else lobWeaponModes.delete(key);
            const wasTargeting = abilityTargetingLifecycle.current()?.casterId === cid;
            cancelAbilityTargeting(false);
            if (wasTargeting) ftActionBarWeaponFoldBasic(cid, abWeaponMenu.invId, true);
            // A REFUSED re-arm (an unported throw) returns before repainting — the toggle must still show the new mode.
            repaintActionBars(cid);
            return;
        }
        if (action === 'weapon-fold-mode') {
            e.preventDefault();
            if (!abWeaponMenu || abWeaponMenu.casterId !== cid || abWeaponMenu.kind !== 'weapon') return;
            const caster = casterFromCache(cid);
            const weapon = caster ? findInventoryEntry(caster, abWeaponMenu.invId) : null;
            if (!caster || !weapon || !ftLayerHas(weapon, 'item_throwable') || !inventoryEquipped(weapon, caster)) return;
            const key = weaponModeKey(cid, abWeaponMenu.invId);
            if (el.dataset.ftMode === 'throw') thrownWeaponModes.add(key);
            else thrownWeaponModes.delete(key);
            const wasTargeting = abilityTargetingLifecycle.current()?.casterId === cid;
            cancelAbilityTargeting(false);
            if (wasTargeting) ftActionBarWeaponFoldBasic(cid, abWeaponMenu.invId, true);
            // A REFUSED re-arm (an unported throw) returns before repainting — the toggle must still show the new mode.
            repaintActionBars(cid);
            return;
        }
        if (action === 'weapon-fold-close') { e.preventDefault(); ftActionBarWeaponFoldClose(cid); return; }
        if (action === 'item-target-fire') { e.preventDefault(); ftActionBarItemTargetFire(cid, aid, el.dataset.ftInventoryId || ''); return; }
        if (action === 'item-target-close') { e.preventDefault(); ftActionBarItemTargetClose(cid); return; }
        if (action === 'creature-target-remove') {
            e.preventDefault(); e.stopPropagation();
            const paneCasterId = el.closest(AB_PANE_SEL)?.getAttribute('data-ft-caster-id') || '';
            ftActionBarRemoveCreatureTarget(el.dataset.ftPcId || '', aid, el.dataset.ftCreatureId || '', paneCasterId);
            return;
        }
        if (action === 'top-tab') { e.preventDefault(); ftActionBarTopTab(cid, (el.dataset.ftTab || 'abilities') as TopTab); return; }
        if (action === 'equip-inventory') { e.preventDefault(); ftActionBarEquipInventory(el.dataset.ftInventoryId || ''); return; }
        if (action === 'use-ability') {
            e.preventDefault();
            if (!ftDispatchAbilityClick(cid, aid)) window.useAbility?.(aid);
            return;
        }
        if (action === 'use-weapon') {
            e.preventDefault();
            if (!ftDispatchWeaponClick(cid, aid)) window.useAbility?.(aid);
            return;
        }
        if (action === 'use-ability-variable1') {
            e.preventDefault();
            const variable1 = el.dataset.ftVariable1 || '';
            if (variable1 === 'throw_weapon' && ftDispatchWeaponClick(cid, aid, 'throw')) return;
            window.useAbilityVariable1?.(aid, variable1);
            return;
        }
        if (action === 'single-ability') {
            e.preventDefault();
            // A normal slotted special supersedes the AP panel; buttons inside that panel deliberately
            // leave it open so the GM can make several AP/reaction adjustments before pressing ✕.
            if (!el.closest('.ft-action-apfold') && actionPointFoldOpen.delete(cid)) {
                setTimeout(() => repaintActionBars(cid), 0);
            }
            if (!ftDispatchAbilityClick(cid, aid)) ftLegacyAbilityClickAs(cid, aid);
            return;
        }
        if (action === 'add-ap') { e.preventDefault(); window.addAPToCreature?.(); return; }
        if (action === 'add-reaction') { e.preventDefault(); window.addReactionToCreature?.(); return; }
        if (action === 'adjust-weapon-attack') {
            e.preventDefault();
            callActionBarGlobal(Number(el.dataset.ftDelta || 0) > 0 ? 'addAttackToCaster' : 'reduceAttackToCaster');
            return;
        }
        if (action === 'adjust-head-attack') {
            e.preventDefault();
            callActionBarGlobal(Number(el.dataset.ftDelta || 0) > 0 ? 'addHeadAttackToCaster' : 'reduceHeadAttackToCaster');
            return;
        }
        // The context disc's turn-owner badge reuses the query-only combat-widget camera bridge; the
        // portrait's End Turn strip and the out-of-AP prompt end the turn through combat-controls.
        if (action === 'focus-turn-creature') {
            e.preventDefault();
            const entityId = el.dataset.ftEntityId || '';
            if (entityId) {
                window.dispatchEvent(new CustomEvent('ft:combat-widget-focus', {
                    cancelable: true,
                    detail: { encounterCreatureId: entityId },
                }));
            }
            return;
        }
        if (action === 'end-turn') { e.preventDefault(); (window as unknown as { endTurn?: (casterId?: string) => unknown }).endTurn?.(cid); return; }
        if (action === 'clear-triggers') { e.preventDefault(); window.clearTriggersFromCoreAbilitiesButton?.(el); }
    });
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement
            && e.target.matches('[data-ft-aoe-upcast-input], .ft-action-targetingbar input.ft-abchoice-ctl')) {
            e.preventDefault();
            e.stopPropagation();
            e.target.blur();
        }
    });
    document.addEventListener('change', (e: Event) => {
        if (e.target instanceof HTMLInputElement && e.target.matches('[data-ft-aoe-upcast-input]')) {
            const input = e.target;
            const value = Math.max(Number(input.min), Math.min(Number(input.max), Math.trunc(input.valueAsNumber) || Number(input.min)));
            input.value = String(value);
            setActionBarAoeUpcastLevel(input.dataset.ftCasterId || '', input.dataset.ftAbilityId || '', value);
            return;
        }
        if (e.target instanceof HTMLInputElement && e.target.matches('[data-ft-targeting-inventory]')) {
            const intentId = Number(e.target.closest<HTMLElement>('[data-ft-ability-intent-id]')?.dataset.ftAbilityIntentId || 0);
            setAbilityInventoryTargets(intentId, e.target.checked);
            return;
        }
        const targetingOption = e.target instanceof HTMLInputElement
            ? e.target.closest<HTMLInputElement>('.ft-action-targetingbar input.ft-abchoice-ctl')
            : null;
        if (targetingOption) {
            const intentId = Number(targetingOption.closest<HTMLElement>('[data-ft-ability-intent-id]')?.dataset.ftAbilityIntentId || 0);
            const minimum = targetingOption.min !== '' ? Number(targetingOption.min) : 0;
            const maximum = targetingOption.max !== '' ? Number(targetingOption.max) : Infinity;
            const value = Math.max(minimum, Math.min(maximum, Math.trunc(targetingOption.valueAsNumber) || minimum));
            targetingOption.value = String(value);
            if (intentId) setAbilityTargetingNumericOption(intentId, value);
            return;
        }
        const targetingChoice = e.target instanceof HTMLSelectElement
            ? e.target.closest<HTMLSelectElement>('.ft-action-targetingbar select[data-ft-targeting-choice]')
            : null;
        if (targetingChoice) {
            const intentId = Number(targetingChoice.closest<HTMLElement>('[data-ft-ability-intent-id]')?.dataset.ftAbilityIntentId || 0);
            if (intentId) {
                if (targetingChoice.dataset.ftTargetingChoice === 'secondary-damage-type') {
                    setAbilityTargetingSecondaryChoice(intentId, targetingChoice.value);
                } else {
                    setAbilityTargetingChoiceOption(intentId, targetingChoice.value);
                }
            }
            return;
        }
        const danceAdd = e.target instanceof Element ? e.target.closest<HTMLSelectElement>('[data-ft-ab-dance-add]') : null;
        if (danceAdd) {
            const key = danceAdd.value;
            if (key) ftActionBarEditDancePlaylist(danceAdd.dataset.ftCasterId || '', (list) => { list.push(key); });
            return;
        }
        const el = e.target instanceof Element ? e.target.closest<HTMLInputElement>('[data-ft-ab-custom-color]') : null;
        if (!el) return;
        ftActionBarSetCustomColor(el.dataset.ftCasterId || '', el.value);
    });
}

/** Divider positions for display: blob key present → literal (incl. empty); absent (fresh PC) → default. */
function currentDividers(caster: CachedCaster): number[] {
    const raw = caster?.action_bar_dividers;
    const list = Array.isArray(raw) ? raw.map((n: unknown) => Number(n)) : DEFAULT_DIVIDERS.slice();
    return Array.from(new Set(list.filter((n: number) => n >= 1 && n <= ROW_SLOTS - 1))).sort((a, b) => a - b);
}

// Skills launcher — a bordered button at the LEFT of the meta row (left of the status cluster) that toggles
// the floating Skills popup (renderSkillsPopup). Mirrors the status cluster's manage button look so the two
// read as a matched pair. Only rendered when the caster actually has skills.
function renderSkillsLauncher(casterId: string, isOpen: boolean): string {
    const cid = escapeHtml(casterId);
    const binding = ft3dActionBinding('skills');
    const display = ft3dActionBindingDisplay('skills');
    const label = binding ? `Skills (${display})` : 'Skills';
    return `<button type="button" class="ft-action-skills-launch${isOpen ? ' is-open' : ''}" ${tip(label, 'top')} aria-label="${escapeHtml(label)}"${binding ? ` aria-keyshortcuts="${escapeHtml(binding)}"` : ''} aria-pressed="${isOpen ? 'true' : 'false'}" data-ft-ab-action="toggle-skills" data-ft-caster-id="${cid}">${iconMap('skill', 20, 20, false)}</button>`;
}

// The floating Skills quick-tray (sibling panel above the bar, like the settings tray). Two tiers, modeled on
// the ability island: a skill-TYPE selector row (bottom, innate by default) folds out the selected type's
// skill chips in the row above it. Each chip rolls via useAbility, is draggable onto the bar (slot_type
// 'skill') so a player can pin it without "reaching" for the Skills tab, opens info on press-hold, and pops in
// its ADV/DIS + tier badges on hover. The full detail stays in the Skills sheet tab. '' when the popup is
// closed (or the caster has no trained skills).
function renderSkillsPopup(caster: CachedCaster, casterId: string): string {
    if (skillsPopupOpen.get(casterId) !== true) return '';
    const tray = renderActionBarSkillsTray(caster, casterId, skillsPopupType.get(casterId) || DEFAULT_SKILL_TYPE, skillsPopupShowAll.get(casterId) === true);
    if (!tray) return '';
    return `<div class="ft-action-skills-popup" data-ft-ab-resource="skills">${tray}</div>`;
}

// ── BG3-style LEFT cluster (campaign dock only, owner 2026-08-27) ─────────────────────────────────
// Hangs OFF the dock's left edge (position:absolute; right:100%) so it never shifts the centred slot
// grid: dynamically bound universal launchers stacked beside the dynamically bound MAIN and OFF
// hand slots showing what is actually equipped. The hand slots reuse renderInventorySlot, so a click
// opens the same weapon fold-out / fires the same attack as a bar-bound weapon, press-hold opens info,
// and the corner badges read melee/ranged/throw + slot. They carry NO data-ft-ab-bar, so the bar's
// drag/unbind system ignores them (you can't drag a hand off the bar). A two-handed weapon sits in the
// main slot and greys the off slot.
const HAND_SLOT_MAIN = 'CEF334BF-DE96-1BEA-F72A-A4C98FBF36AD';
const HAND_SLOT_OFF = '4FF85144-67EC-9D87-AE28-15137A1A2481';
const HAND_SLOT_BOTH = 'E6D02ACB-D3E9-DD8B-762A-31FE8C8D898D';
const NATURAL_SLOT_HEAD = '3805BDD7-3461-DDDB-F445-922A5A992987';
const NATURAL_SLOT_TAIL = '16ABAE95-5D18-474D-ADFF-8C20FC2439F1';

type NaturalHandWeapon = {
    id: string;
    node: CasterItemNode;
    /** the limb's OWN equipment_slot, uppercased */
    slotId: string;
};

/** Natural weapons are intrinsic attack abilities, not inventory rows, so equippedInSlot() can never
 * find them. Mirror the Weapons tab's finalized-cluster precedence and resolve each lean ref to its full
 * definition, then read the weapon's own equipment_slot. Deduped by UUID and returned IN ORDER, because
 * the hand assignment is a set operation (a second main-hand claw is borrowed into the off hand), not a
 * per-slot lookup. This keeps the FT3D hand dock in parity with the character-sheet equipment paper doll
 * without pretending an intrinsic weapon is inventory. */
function resolvedNaturalWeapons(caster: CachedCaster): NaturalHandWeapon[] {
    const abilitiesRaw = caster.computed?.creature?.abilities?.natural_weapon;
    const abilities = Array.isArray(abilitiesRaw)
        ? abilitiesRaw
        : (abilitiesRaw !== null && abilitiesRaw !== undefined && abilitiesRaw !== '' ? [abilitiesRaw] : []);
    const layerEntries = abilities.length === 0 && ftLayerHas(caster, 'natural_weapon')
        ? ftLayerEntries(caster, 'natural_weapon')
        : [];
    const derivedRaw = caster.computed?.creature?.natural_weapon;
    const derived = Array.isArray(derivedRaw)
        ? derivedRaw
        : (derivedRaw !== null && derivedRaw !== undefined && derivedRaw !== '' ? [derivedRaw] : []);
    const seen = new Set<string>();
    const out: NaturalHandWeapon[] = [];

    for (const entry of [...abilities, ...layerEntries, ...derived]) {
        const id = String(abilityEntryId(entry) || '').trim();
        if (id === '' || seen.has(id.toUpperCase())) continue;
        seen.add(id.toUpperCase());

        let node = itemNodeOf(entry);
        const slotOf = (n: CasterItemNode | null): string => (n && typeof ftLayerValue === 'function'
            ? String(ftLayerValue(n, 'equipment_slot') ?? '').toUpperCase()
            : '');
        // A lean ref carries no equipment_slot; resolve it against the warm cache before reading.
        if (slotOf(node) === '' && typeof window.ftFindFullNestedItem === 'function') {
            try { node = itemNodeOf(window.ftFindFullNestedItem(id)) || node; } catch { /* cache miss */ }
        }
        const resolvedSlot = slotOf(node);
        if (resolvedSlot !== '' && node) out.push({ id, node, slotId: resolvedSlot });
    }
    return out;
}

/** The first natural weapon whose OWN equipment_slot names `slotId` (a head Bite, a tail Slap, a
 *  both-hands maw), or null. */
function naturalWeaponInSlot(caster: CachedCaster, slotId: string): NaturalHandWeapon | null {
    return resolvedNaturalWeapons(caster).find((n) => n.slotId === slotId) ?? null;
}

/** Every natural weapon naming one of `slots`, in order — the pool the hand assignment draws from. */
function naturalWeaponsInSlots(caster: CachedCaster, slots: string[]): NaturalHandWeapon[] {
    return resolvedNaturalWeapons(caster).filter((n) => slots.includes(n.slotId));
}

/** Same picture projection as the equipment paper doll: own picture first, then the weapon
 * classification carrier. Natural weapons commonly keep their art on that carrier. */
function naturalWeaponThumbUrl(entry: CasterItemNode): string {
    const picOf = window.ftPrimaryPictureOf;
    if (typeof picOf !== 'function') return '';
    const urlOf = (node: unknown): string => {
        const pic = node ? recordOf(picOf(node)) : null;
        const raw = pic?.picture_url ?? pic?.pic_url ?? '';
        return String(Array.isArray(raw) ? raw[0] ?? '' : raw);
    };
    const own = urlOf(entry);
    if (own !== '') return own;
    const layers = recordOf(entry.item_layer_types);
    const rawCarriers = layers?.weapon_classification;
    const carriers = Array.isArray(rawCarriers) ? rawCarriers : (rawCarriers ? [rawCarriers] : []);
    for (const carrier of carriers) {
        const url = urlOf(carrier);
        if (url !== '') return url;
    }
    return '';
}

function equippedInSlot(caster: CachedCaster, slotId: string): string {
    for (const entry of readInventoryRows(caster, 'inventory')) {
        const s = readInventoryEquippedToken(entry, caster).toUpperCase();
        if (s !== slotId) continue;
        const rawId = typeof ftInventoryField === 'function' ? ftInventoryField(entry, 'inventory_id') : entry.inventory_id;
        const id = String((Array.isArray(rawId) ? rawId[0] : rawId) ?? '').trim();
        if (id) return id;
    }
    return '';
}

/** The controlled caster's swings — equipped hand weapons, then natural weapons — for the scene's object
 *  radial ("Attack" on a wall, door or chest). Each option fires exactly what its hand tile falls back to
 *  (useAbility(id)) at the explicit object; Janus owns range, AP and legality. */
function objectAttackOptions(casterId: string): Array<{ id: string; name: string }> {
    const caster = casterFromCache(casterId);
    if (!caster) return [];
    const out: Array<{ id: string; name: string }> = [];
    const seen = new Set<string>();
    const add = (id: string, name: string): void => {
        if (!id || seen.has(id.toUpperCase())) return;
        seen.add(id.toUpperCase());
        out.push({ id, name });
    };
    for (const slot of [HAND_SLOT_BOTH, HAND_SLOT_MAIN, HAND_SLOT_OFF]) {
        const invId = equippedInSlot(caster, slot);
        const entry = invId ? findInventoryEntry(caster, invId) : null;
        if (!entry || String(ftItemsCol(entry, 'item_type') ?? '').toLowerCase() !== 'weapon') continue;
        const name = typeof ftInventoryField === 'function' ? ftInventoryField(entry, 'inventory_name') : null;
        add(invId, String(name || ftItemsCol(entry, 'item_name') || 'Weapon'));
    }
    // Every natural weapon, slotted or not — the default unarmed Slam (the fist) is identity-only in the
    // root catalog, so the hand-slot resolver (which needs an equipment_slot) would drop it. Same sources
    // and precedence as renderNaturalWeapons() in weapon-tables.ts.
    const list = (value: unknown): unknown[] => Array.isArray(value)
        ? value : (value !== null && value !== undefined && value !== '' ? [value] : []);
    const curated = list(caster.computed?.creature?.abilities?.natural_weapon);
    const naturals = [
        ...curated,
        ...(curated.length === 0 && ftLayerHas(caster, 'natural_weapon') ? list(ftLayerEntries(caster, 'natural_weapon')) : []),
        ...list(caster.computed?.creature?.natural_weapon),
    ];
    for (const entry of naturals) add(String(abilityEntryId(entry) || '').trim(), abilityEntryName(entry) || 'Unarmed strike');
    return out;
}

function renderHandSlot(
    caster: CachedCaster,
    casterId: string,
    invId: string,
    natural: NaturalHandWeapon | null,
    slotId: string,
    key: string,
    label: string,
    ghost: boolean,
    bindingAction: 'main_hand' | 'off_hand' | null,
): string {
    const slotName = slotId === HAND_SLOT_MAIN ? 'main'
        : slotId === HAND_SLOT_OFF ? 'off'
            : slotId === NATURAL_SLOT_HEAD ? 'head' : 'tail';
    const bindingAttr = bindingAction ? ` data-ft-action-binding="${bindingAction}"` : '';
    const keyAttr = `data-ft-ab-key="${escapeHtml(key)}" data-ft-hand-slot="${slotName}"${bindingAttr}`;
    const handLabel = key && bindingAction ? `${label} (${ft3dActionBindingDisplay(bindingAction)})` : label;
    let cell: string;
    if (invId) {
        cell = renderInventorySlot(caster, invId, keyAttr);
        // renderInventorySlot owns the item-name tooltip. Keep that useful identity and append the
        // hand + CURRENT viewer binding; ft:ft3d-keybindings-changed repaints this whole cluster.
        const handWeapon = findInventoryEntry(caster, invId);
        const modeLabel = handWeapon && weaponInThrowMode(casterId, invId, handWeapon) ? ' — Throw mode' : '';
        const handSuffix = escapeHtml(` — ${handLabel}${modeLabel}`);
        cell = cell
            .replace(/data-bs-original-title="([^"]*)"/, `data-bs-original-title="$1${handSuffix}"`)
            .replace(/aria-label="([^"]*)"/, `aria-label="$1${handSuffix}"`);
        if (key) {
            cell = cell.replace('<button type="button"', `<button type="button" aria-keyshortcuts="${escapeHtml(key)}"`);
        }
        if (ghost) {
            // Match the equipment grid's both-hands treatment: the off hand shows a faded copy of the
            // actual item, rather than looking empty. It is occupancy feedback only, so the shadow copy
            // is disabled; attacks / fold-outs remain owned by the main-hand tile.
            cell = cell
                .replace('class="ft-action-slot', 'class="ft-action-slot ft-action-slot--hand-ghost')
                .replace('<button type="button"', '<button type="button" disabled aria-disabled="true" tabindex="-1"');
        }
    } else if (natural) {
        const name = String(ftItemsCol(natural.node, 'item_name') ?? 'Natural Weapon');
        const url = naturalWeaponThumbUrl(natural.node);
        const face = url !== ''
            ? `<img class="token-img" src="${escapeHtml(url)}" alt="${escapeHtml(name)}" draggable="false">`
            : iconMap('natural_weapon', 36, 36, false);
        const rollType = String(ftLayerValue(natural.node, 'roll_type') ?? '');
        const badges = (rollType !== ''
            ? `<span class="ft-action-inv-corner ft-action-inv-corner--tl" aria-hidden="true">${iconMap(rollType, 15, 15, false)}</span>`
            : '')
            + `<span class="ft-action-inv-corner ft-action-inv-corner--tr" aria-hidden="true">${iconMap(slotId, 15, 15, false)}</span>`;
        const headSpent = slotId === NATURAL_SLOT_HEAD
            && casterInCombat(caster)
            && Number(ftEncounterCreatureField(caster.encounter_creatures, 'head_attack') ?? 0) >= 1
            && !ftLayerHas(caster, 'multiple_head_attacks');
        const unusable = attackSlotUnusable(caster, slotId === NATURAL_SLOT_HEAD) ? ' is-unusable' : '';
        const readiness = headSpent ? ' — head attack already used this turn' : '';
        cell = `<div class="ft-action-slot ft-action-slot--filled ft-action-slot--natural${unusable}" ${keyAttr} data-ft-ap-slot="${slotId === NATURAL_SLOT_HEAD ? 'head' : 'attack'}" data-ft-natural-weapon-id="${escapeHtml(natural.id)}">`
            + `<button type="button" class="ft-action-natural-btn" data-ft-ab-action="use-weapon" data-ft-natural-foldout="${escapeHtml(natural.id)}" data-ft-caster-id="${escapeHtml(casterId)}" data-ft-ability-id="${escapeHtml(natural.id)}"`
            + ` data-ft-info-item-id="${escapeHtml(natural.id)}"${key ? ` aria-keyshortcuts="${escapeHtml(key)}"` : ''} ${tip(`${name} — ${handLabel}${readiness}`, 'top')}>${face}${badges}</button></div>`;
        if (ghost) {
            cell = cell
                .replace('class="ft-action-slot', 'class="ft-action-slot ft-action-slot--hand-ghost')
                .replace('<button type="button"', '<button type="button" disabled aria-disabled="true" tabindex="-1"');
        }
    } else {
        // Empty hand: the slot glyph, no click.
        cell = `<div class="ft-action-slot ft-action-slot--empty ft-action-slot--hand-empty" ${keyAttr} ${tip(`${handLabel} — empty`, 'top')}>`
            + `${iconMap(slotId, 26, 26, false)}</div>`;
    }
    // Same gold menu-active glow the bar grid gives a slot (renderActionBar): a hand whose weapon fold-out
    // is open, or whose attack is the one being targeted. The grid pass never sees these cluster cells, so
    // without this the only visible trace of an open G/F fold-out was a dark tile (owner 2026-09-14).
    const handWeaponId = (invId || natural?.id || '').toUpperCase();
    const targeting = abilityTargetingLifecycle.current();
    const handActive = !ghost && handWeaponId !== '' && (
        (!!abWeaponMenu && abWeaponMenu.casterId === casterId && abWeaponMenu.invId.toUpperCase() === handWeaponId)
        || (!!targeting && targeting.casterId === casterId && targeting.abilityId.toUpperCase() === handWeaponId)
    );
    if (handActive) cell = cell.replace('class="ft-action-slot', 'class="ft-action-slot ft-action-slot--menu-active');
    cell = cell.replace('class="ft-action-slot', 'class="ft-action-slot ft-action-slot--hand');
    return withKeyBadge(cell, key);
}

// ── UNIT TABS — the SUMMONS tab re-homed onto the dock (owner 2026-09-01) ───────────────────────
// A controller's summons are UNITS you tab between, BG3-style, instead of a pane inside the PC sheet.
// The strip sits on top of the hands cluster: the HOME (controller) tab is pinned first, then one tab
// per living summon. Clicking a tab routes through the campaign shell's ONE actor funnel
// (window.ftFt3dSelectActor → ftSetActiveCaster + mountFt3dActorActionBar), so the unit card, hotbar,
// perception POV, inventory dock and websocket subscription all pivot together — and the home tab is
// always there to tab back to.
// Data is blob-first and needs NO server query: the controller row bakes caster_summons = [{id, name}]
// (fetch_encounter_creatures.php — the same list the sheet's pill strip reads; keyed by controller, NOT
// by encounter, so it is the membership authority), enriched from casterCache rows (the websocket
// realias caches every creature in the encounter before this repaint) for name, portrait, dead and
// turn state. A baked summon with NO cached row is in ANOTHER encounter: it cannot act on this map, so
// its tab shows disabled rather than vanishing — the one case the old summons-tab DB query existed for.

type UnitTabRow = CachedCaster & { permissions?: { player?: unknown } };

interface UnitTabEntry {
    id: string;
    name: string;
    row: UnitTabRow | null;
    home: boolean;
}

function unitScalar(value: unknown): string {
    let v = value;
    while (Array.isArray(v)) v = v[0];
    return v === null || v === undefined ? '' : String(v).trim();
}

function unitValidId(value: unknown): string {
    const id = unitScalar(value);
    return id && typeof checkIfValidUUID === 'function' && checkIfValidUUID(id) ? id : '';
}

/** The creature this row is acting FOR: its controller when it is a summon, else itself. */
function unitHomeId(row: CachedCaster | null | undefined, selfId: string): string {
    return unitValidId(row?.encounter_creatures?.controller_encounter_creature_id) || selfId;
}

/** The current encounter ROSTER — the one snapshot that decides whether a unit is HERE. Two retained
 *  copies exist (the websocket encounterCache and the campaign shell's ft3dEncounterArrCache); take the
 *  newer by wire version so neither stale copy can outvote the other. null = no roster known. */
function unitRoster(caster: CachedCaster): { rows: UnitTabRow[]; ids: Set<string> } | null {
    const encId = unitScalar(caster.encounter_creatures?.encounter_id);
    if (!encId) return null;
    const held = recordOf(window.FateTide?.encounterCache?.get(encId));
    const campaign = (window as Window & {
        ft3dEncounterArrCache?: { encounterId?: unknown; arr?: unknown } | null;
    }).ft3dEncounterArrCache;
    const campaignArr = campaign && unitScalar(campaign.encounterId).toUpperCase() === encId.toUpperCase()
        ? recordOf(campaign.arr) : null;
    const version = (snap: Record<string, unknown> | null): number => Number(snap?._ws_version ?? -1);
    const pick = (held && campaignArr)
        ? (version(campaignArr) > version(held) ? campaignArr : held)
        : (held ?? campaignArr);
    const rows = pick?.encounter_creatures_arr;
    if (!Array.isArray(rows)) return null;
    const ids = new Set<string>();
    for (const r of rows as UnitTabRow[]) {
        const id = unitValidId(r?.encounter_creatures?.encounter_creature_id);
        if (id) ids.add(id.toUpperCase());
    }
    return { rows: rows as UnitTabRow[], ids };
}

/** Enrichment row for a unit (name / portrait / dead / turn): the live casterCache alias first (the
 *  websocket realias refreshes every roster row there BEFORE this repaint), else the roster copy.
 *  Presence here never decides actability — unitRoster does. */
function unitCachedRow(id: string, roster: { rows: UnitTabRow[] } | null): UnitTabRow | null {
    if (!id) return null;
    const cache = window.FateTide?.casterCache;
    let row = cache?.get(id) as UnitTabRow | undefined;
    if (!row && typeof window.ftSeedCasterFromEncounterCache === 'function') {
        // Pure alias of the cached encounter row into casterCache — no event, no repaint (caster-cache.ts).
        try { window.ftSeedCasterFromEncounterCache(id); } catch { /* cold cache */ }
        row = cache?.get(id) as UnitTabRow | undefined;
    }
    if (row) return row;
    const wanted = id.toUpperCase();
    return roster?.rows.find((r) => (
        unitScalar(r?.encounter_creatures?.encounter_creature_id).toUpperCase() === wanted
    )) ?? null;
}

function unitIsDead(row: UnitTabRow | null): boolean {
    return Number(unitScalar(row?.encounter_creatures?.encounter_creature_dead) || 0) === 1;
}

function unitName(row: UnitTabRow | null, fallback: string): string {
    try {
        const n = row && typeof getCreatureName === 'function' ? String(getCreatureName(row) || '') : '';
        if (n) return n;
    } catch { /* fall through */ }
    return fallback || 'Creature';
}

/** Baked thin list on the controller row: [{encounter_creature_id, encounter_creature_name}]. */
function unitBakedSummons(home: UnitTabRow | null): Array<{ id: string; name: string }> | null {
    const raw = home?.encounter_creatures?.caster_summons;
    if (!Array.isArray(raw)) return null;
    const out: Array<{ id: string; name: string }> = [];
    for (const entry of raw) {
        const rec = recordOf(entry);
        const id = unitValidId(rec?.encounter_creature_id);
        if (!id) continue;
        out.push({ id, name: unitScalar(rec?.encounter_creature_name) });
    }
    return out;
}

/** Fallback membership when a cached controller blob predates the caster_summons bake: the roster
 *  rows whose controller is the home creature. */
function unitEncounterSummons(homeId: string, roster: { rows: UnitTabRow[] } | null): Array<{ id: string; name: string }> {
    if (!roster) return [];
    const wanted = homeId.toUpperCase();
    const out: Array<{ id: string; name: string }> = [];
    for (const r of roster.rows) {
        if (unitValidId(r?.encounter_creatures?.controller_encounter_creature_id).toUpperCase() !== wanted) continue;
        const id = unitValidId(r?.encounter_creatures?.encounter_creature_id);
        if (id) out.push({ id, name: unitName(r, '') });
    }
    return out;
}

function unitPortraitHtml(row: UnitTabRow | null, name: string): string {
    const prefix = name.trim().slice(0, 2) || '??';
    let url = '';
    try {
        const pic = row && typeof selectCreaturePicture === 'function' ? selectCreaturePicture(row, { use_picture_thumbnail: true }) : null;
        url = pic && typeof pictureField0 === 'function' ? String(pictureField0(pic, 'pic_url') || '') : '';
    } catch { /* initials */ }
    return `<span class="ft-action-units__initials" aria-hidden="true">${escapeHtml(prefix)}</span>`
        + (url ? `<img class="ft-action-units__pic" src="${escapeHtml(url)}" alt="" draggable="false" onerror="this.remove()">` : '');
}

/** '' when the dock's caster neither has summons nor IS one — a lone unit needs no tab strip. */
function renderUnitTabs(caster: CachedCaster, casterId: string): string {
    const activeU = casterId.toUpperCase();
    const homeId = unitHomeId(caster, casterId);
    const roster = unitRoster(caster);
    const homeRow: UnitTabRow | null = homeId.toUpperCase() === activeU ? (caster as UnitTabRow) : unitCachedRow(homeId, roster);
    let summons = unitBakedSummons(homeRow);
    if (summons === null) summons = unitEncounterSummons(homeId, roster);
    // The unit being driven always keeps its tab (even dead/unlisted) so there is a way home from it.
    if (homeId.toUpperCase() !== activeU && !summons.some((s) => s.id.toUpperCase() === activeU)) {
        summons.unshift({ id: casterId, name: unitName(caster as UnitTabRow, '') });
    }
    const entries: UnitTabEntry[] = [{ id: homeId, name: unitName(homeRow, 'Home'), row: homeRow, home: true }];
    const seen = new Set<string>([homeId.toUpperCase()]);
    for (const s of summons) {
        const key = s.id.toUpperCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const row = key === activeU ? (caster as UnitTabRow) : unitCachedRow(s.id, roster);
        // Dead summons drop off the strip (they cannot act; initiative skips them too) unless driven.
        if (key !== activeU && unitIsDead(row)) continue;
        entries.push({ id: s.id, name: unitName(row, s.name), row, home: false });
    }
    if (entries.length < 2) return '';
    const cid = escapeHtml(casterId);
    // HERE = in the current roster snapshot. A stale casterCache alias (a summon that moved away or
    // was recompiled out) must never make a tab actionable on its own; without any roster (a sheet
    // outside an encounter) fall back to "we have a row".
    const isHere = (entry: UnitTabEntry): boolean => (
        roster ? roster.ids.has(entry.id.toUpperCase()) : !!entry.row
    );
    const buttons = entries.map((entry) => {
        const isActive = entry.id.toUpperCase() === activeU;
        const elsewhere = !isActive && !isHere(entry);
        const dead = unitIsDead(entry.row);
        let turn = false;
        try { turn = !!entry.row && casterIsCurrentTurn(entry.row); } catch { /* no encounter binding */ }
        const isPc = String(entry.row?.player_characters?.item_id ?? '').trim() !== '';
        const num = (!isPc && entry.row && typeof ftEncounterCreatureField === 'function')
            ? Number(ftEncounterCreatureField(entry.row.encounter_creatures, 'creature_number') ?? 0) : 0;
        const state = elsewhere ? ' — in another encounter' : (dead ? ' — dead' : (turn ? ' — its turn' : ''));
        const label = `${entry.name}${entry.home ? ' (home)' : ''}${state}`;
        const cls = 'ft-action-units__tab'
            + (entry.home ? ' is-home' : '')
            + (isActive ? ' is-active' : '')
            + (turn ? ' is-turn' : '')
            + (elsewhere ? ' is-elsewhere' : '')
            + (dead ? ' is-dead' : '');
        // Pressed-button group rather than ARIA tabs: there are no tabpanels — the whole dock swaps.
        return `<button type="button" class="${cls}" aria-pressed="${isActive ? 'true' : 'false'}"`
            + ` ${tip(label, 'top')} aria-label="${escapeHtml(label)}"${elsewhere ? ' disabled' : ''}`
            + ` data-ft-ab-action="unit-tab" data-ft-unit-id="${escapeHtml(entry.id)}" data-ft-caster-id="${cid}">`
            + unitPortraitHtml(entry.row, entry.name)
            + (num > 0 ? `<span class="ft-action-units__num" aria-hidden="true">${num}</span>` : '')
            + `</button>`;
    }).join('');
    return `<div class="ft-action-units" role="group" aria-label="Controlled units">${buttons}</div>`;
}

// A newborn changes the controller's tabs, without rebuilding its spells or hands.
window.addEventListener('ft:summons-changed', event => {
    const changed = new Set<string>((event as CustomEvent<{ creatureIds: string[] }>).detail.creatureIds);
    for (const hands of document.querySelectorAll<HTMLElement>('.ft-action-hands[data-ft-caster-id]')) {
        const id = hands.dataset.ftCasterId ?? '';
        const caster = window.FateTide?.casterCache?.get(id) as CachedCaster | undefined;
        if (!caster) continue;
        const controller = unitValidId(caster.encounter_creatures?.controller_encounter_creature_id).toUpperCase();
        if (!changed.has(id.toUpperCase()) && !changed.has(controller)) continue;
        const html = renderUnitTabs(caster, id), previous = hands.querySelector(':scope > .ft-action-units');
        if (html) {
            const template = document.createElement('template'); template.innerHTML = html;
            if (previous) previous.replaceWith(template.content); else hands.prepend(template.content);
        } else previous?.remove();
        hands.classList.toggle('has-units', !!html);
        // Instant summons spend resources; timed ones retire Casting. Update
        // those small projections alongside the tabs, keeping spell rows mounted.
        const bar = hands.closest<HTMLElement>('.ft-action-bars');
        if (!bar) continue;
        const container = bar.parentElement;
        if (container) patchActionBarResources(caster, id, container);
        for (const [selector, html, parent] of [
            ['.ft-action-status', window.renderActionBarStatusCluster?.(id, caster) ?? '', '.ft-action-bars-top__left'],
            ['.ft-action-context', window.renderActionBarContextualActions?.(id, caster) ?? '', '.ft-action-bars-top__right'],
        ]) {
            const previous = bar.querySelector(selector!);
            if ((previous?.outerHTML ?? '') === html) continue;
            const template = document.createElement('template'); template.innerHTML = html!;
            if (previous) previous.replaceWith(template.content); else bar.querySelector(parent!)?.prepend(template.content);
            const mounted = bar.querySelector<HTMLElement>(selector!);
            if (mounted) { window.ftMountRuneIcons?.(mounted); window.ftMountIcons?.(mounted); }
        }
        if (container) delete (container as HTMLElement & { _ftAbLastHtml?: string })._ftAbLastHtml;
        ftUpdateActivityBars(id);
    }
    scheduleHandsPlacement();
});

/** Pivot the dock to another controlled unit (home creature or one of its summons). Routes through
 *  the campaign shell's actor funnel when present so POV / perception / inventory move with it; the
 *  bare caster funnel is the fallback on surfaces without the shell. Returns whether a pivot was
 *  accepted (the shell fails closed on roster membership + compiled player permission). */
export function ftActionBarSelectUnit(unitId: string): boolean {
    const id = unitValidId(unitId);
    if (!id) return false;
    const active = String(window.FateTide?.activeCasterId || '').trim();
    const w = window as Window & {
        ftFt3dSelectActor?: (id: string) => unknown;
        ftSetActiveCaster?: (id: string) => unknown;
    };
    // A sheet-tab click can set activeCasterId without moving the FT3D dock. Only skip the shell
    // pivot when that dock also belongs to this creature.
    if (active.toUpperCase() === id.toUpperCase()
        && (typeof w.ftFt3dSelectActor !== 'function' || ft3dDockPresentFor(id))) return true;
    if (typeof w.ftFt3dSelectActor === 'function') return w.ftFt3dSelectActor(id) === true;
    if (typeof w.ftSetActiveCaster !== 'function') return false;
    const row = unitCachedRow(id, null);
    if (row && typeof ftHasPlayerPermission === 'function' && !ftHasPlayerPermission(row)) return false;
    void w.ftSetActiveCaster(id);
    return true;
}

/** The legacy cast (handleAbilityBtnSingleClick → useAbility) acts as the ACTIVE caster, never the clicked tile's: a GM
 *  clicking Hide on NPC B's card while NPC A was active hid A (fix-bug 2026-09-28). Every tile fallback comes here:
 *  pivot to the tile's creature through the funnel above first, and cast NOTHING when it refuses — never as somebody
 *  else. A tile that names no creature keeps the legacy behaviour. */
export function ftLegacyAbilityClickAs(casterId: string, abilityId: string): void {
    // Only a tile that names NO creature keeps the legacy cast; a named but unusable id casts nothing.
    if (String(casterId ?? '').trim() !== '' && !ftActionBarSelectUnit(casterId)) return;
    window.handleAbilityBtnSingleClick?.(abilityId);
}

/** Cycle the dock's unit tabs (the shell's Next/Previous unit keys). Returns false when there is
 *  nothing to cycle — or the pivot was refused — so the caller lets the key fall through to normal
 *  focus navigation. */
export function ftActionBarCycleUnit(direction: number): boolean {
    const dock = document.querySelector<HTMLElement>('[data-ft3d-actor-dock]');
    if (!dock || dock.hidden) return false;
    const tabs = Array.from(dock.querySelectorAll<HTMLButtonElement>('.ft-action-units__tab:not(:disabled)'));
    if (tabs.length < 2) return false;
    const current = Math.max(0, tabs.findIndex((t) => t.classList.contains('is-active')));
    const next = tabs[(current + (direction < 0 ? -1 : 1) + tabs.length) % tabs.length];
    if (!next) return false;
    return ftActionBarSelectUnit(next.dataset.ftUnitId || '');
}

function renderHandsCluster(caster: CachedCaster, casterId: string, inventoryOpen: boolean, extraLaunchers = ''): string {
    const cid = escapeHtml(casterId);
    // Second launcher row: Sneak / Hide — the movement tab's own stealth logic, rendered as tiles.
    let stealth = '';
    try { stealth = renderStealthRow(caster); } catch { /* movement helpers not ready */ }
    if (stealth) {
        const hidingKey = ft3dActionBinding('hiding');
        const hidingDisplay = ft3dActionBindingDisplay('hiding');
        stealth = stealth
            .replace('<button ', `<button data-ft3d-hiding-toggle${hidingKey ? ` aria-keyshortcuts="${escapeHtml(hidingKey)}"` : ''} `)
            .replace(/data-bs-original-title="([^"]*)"/, (_match, title: string) =>
                `data-bs-original-title="${escapeHtml(hidingKey ? `${title} (${hidingDisplay})` : title)}"`);
    }
    const both = equippedInSlot(caster, HAND_SLOT_BOTH);
    const equippedMain = equippedInSlot(caster, HAND_SLOT_MAIN);
    const equippedOff = equippedInSlot(caster, HAND_SLOT_OFF);
    // Match the equipment paper doll (buildEquipmentColumn, inventory.ts): inventory wins in its exact
    // hand; a both-hands natural weapon occupies both only when no inventory occupies either hand; and
    // single-hand naturals are assigned as a SET, not per-cell (owner 2026-09-02) —
    //  • one claw, both hands bare  → main real, off a ghost copy (one limb drawn twice, not two weapons)
    //  • dagger in main, one claw   → the claw slides into the still-free off hand for REAL, which is the
    //                                 two-weapon-fighting pairing the engine now honours
    //                                 (calculate_equipped_counts.php).
    const mainFree = !both && !equippedMain;
    const offFree = !both && !equippedOff;
    const naturalBoth = mainFree && offFree
        ? naturalWeaponInSlot(caster, HAND_SLOT_BOTH)
        : null;
    let naturalMain: NaturalHandWeapon | null = null;
    let naturalOff: NaturalHandWeapon | null = null;
    let naturalOffIsMirror = false;
    if (naturalBoth) {
        naturalMain = naturalBoth;
        naturalOff = naturalBoth;
        naturalOffIsMirror = true;
    } else {
        // POOL, not two singular lookups: a creature with TWO main-hand claws must put the first in the
        // main hand and BORROW the second into the off hand. Asking naturalWeaponInSlot(MAIN) twice
        // returns the same limb, so the off hand would have been ghosted instead of genuinely armed —
        // disagreeing with both paper dolls and with calculate_equipped_counts (Codex review, 2026-09-02).
        const pool = naturalWeaponsInSlots(caster, [HAND_SLOT_MAIN, HAND_SLOT_OFF]);
        const take = (want: string): NaturalHandWeapon | null => {
            const i = pool.findIndex((n) => n.slotId === want);
            return i >= 0 ? (pool.splice(i, 1)[0] ?? null) : null;
        };
        if (mainFree) naturalMain = take(HAND_SLOT_MAIN);
        if (offFree) naturalOff = take(HAND_SLOT_OFF);
        // A free hand with no natural weapon of its own borrows the other hand's unclaimed one.
        if (mainFree && !naturalMain && pool.length > 0) naturalMain = pool.shift() ?? null;
        if (offFree && !naturalOff && pool.length > 0) naturalOff = pool.shift() ?? null;
        // Exactly one limb with BOTH hands bare → mirror it, main real / off ghosted — UNLESS it is a
        // PAIRED limb (fist/slam, claw): two fists are two real weapons (owner 2026-09-03;
        // shared/paired-limb-forms.ts, twin of calculate_equipped_counts.php).
        if (mainFree && offFree && (naturalMain === null) !== (naturalOff === null)) {
            const solo = naturalMain ?? naturalOff;
            naturalMain = solo;
            naturalOff = solo;
            const soloForm = solo && typeof ftLayerValue === 'function'
                ? String(ftLayerValue(solo.node, 'natural_weapon_form') ?? '')
                : '';
            naturalOffIsMirror = !(isPairedLimb(uuidOf(soloForm), solo?.id ?? '')
                && bodyPairsLimbs(typeof ftLayerValue === 'function' ? ftLayerValue(caster, 'quadruped') : null, ftItemsCol(caster, 'item_type')));
        }
    }
    const naturalHead = naturalWeaponInSlot(caster, NATURAL_SLOT_HEAD);
    const naturalTail = naturalWeaponInSlot(caster, NATURAL_SLOT_TAIL);
    const main = both || equippedMain;
    const off = both || equippedOff;
    const mainKey = ft3dActionBinding('main_hand');
    const offKey = ft3dActionBinding('off_hand');
    const extraNaturals = [
        naturalHead ? { natural: naturalHead, slotId: NATURAL_SLOT_HEAD, label: 'Head' } : null,
        naturalTail ? { natural: naturalTail, slotId: NATURAL_SLOT_TAIL, label: 'Tail' } : null,
    ].filter((entry): entry is NonNullable<typeof entry> => entry !== null);
    const extraNaturalRow = extraNaturals.length > 0
        ? `<div class="ft-action-hands__slots ft-action-hands__slots--natural-extra">${extraNaturals.map((entry) =>
            renderHandSlot(caster, casterId, '', entry.natural, entry.slotId, '', entry.label, false, null)).join('')}</div>`
        : '';
    // Portrait instead of a generic glyph so the cluster says WHO you are driving (owner 2026-08-27).
    // NPCs (no player_characters row) get the encounter creature number bottom-right, mirroring
    // renderCreatureThumbnail's badge, so "Troll 3" is unambiguous on a bar full of trolls.
    let sheetName = '';
    try { sheetName = typeof getCreatureName === 'function' ? String(getCreatureName(caster) || '') : ''; } catch { /* */ }
    // Match the Party frame: this is a compact name prefix, not first/last initials. "Artin
    // Goblinchaser" therefore reads "Ar" in both places instead of becoming the ambiguous "AG".
    const namePrefix = sheetName.trim().slice(0, 2) || '??';
    const initialsHtml = `<span class="ft-action-bars__portrait-initials" aria-hidden="true">${escapeHtml(namePrefix)}</span>`;
    let portraitUrl = '';
    let portraitUsesNativeAspect = false;
    let portraitRatio = 1;
    try {
        const pic = typeof selectCreaturePicture === 'function' ? selectCreaturePicture(caster, { use_picture_thumbnail: true }) : null;
        portraitUrl = pic && typeof pictureField0 === 'function' ? String(pictureField0(pic, 'pic_url') || '') : '';
        // A cropped PC thumbnail is deliberately a portrait and keeps the compact fixed-width treatment.
        // Every other selected row is full artwork; encounter_arr carries its source dimensions, so let
        // the sheet launcher widen/narrow to that ratio instead of cropping landscape creatures into a
        // portrait slit. Square dimensions may be elided by build_picture_arr and correctly stay square.
        const isThumbnail = Number(pictureField0(pic, 'pic_thumbnail') ?? 0) === 1;
        const pictureWidth = Number(pictureField0(pic, 'picture_width'));
        const pictureHeight = Number(pictureField0(pic, 'picture_height'));
        if (portraitUrl && !isThumbnail) {
            portraitUsesNativeAspect = true;
            portraitRatio = pictureWidth > 0 && pictureHeight > 0 ? pictureWidth / pictureHeight : 1;
        }
    } catch { /* fall through to the initials */ }
    // The initials stay underneath a real image, so a failed/lazy image can simply remove itself and
    // reveal the same useful fallback instead of dropping back to an anonymous person glyph. A native-art
    // sheet carries measured inline width/height (sizeNativePortraits); drop those with the class so the
    // fallback returns to the fixed 72px tile.
    const portrait = initialsHtml + (portraitUrl
        ? `<img class="ft-action-bars__portrait" src="${escapeHtml(portraitUrl)}" alt="" draggable="false" onerror="this.parentNode.classList.remove('has-portrait','ft-action-bars__sheet--native-art');delete this.parentNode.dataset.ftPortraitRatio;this.parentNode.style.removeProperty('width');this.parentNode.style.removeProperty('height');this.remove();window.ftPlaceHandsCluster&amp;&amp;window.ftPlaceHandsCluster()">`
        : '');
    const isPc = String(caster?.player_characters?.item_id ?? '').trim() !== '';
    const creatureNum = (!isPc && typeof ftEncounterCreatureField === 'function')
        ? Number(ftEncounterCreatureField(caster?.encounter_creatures, 'creature_number') ?? 0) : 0;
    const numBadge = creatureNum > 0 ? `<span class="ft-action-bars__portrait-num" aria-hidden="true">${creatureNum}</span>` : '';
    // Keep the creature's display name (encounter naming, e.g. "Troll 5") while making the portrait's
    // action + CURRENT binding discoverable on hover. Rebinding repaints the cluster, so this never goes stale.
    const characterKey = ft3dActionBinding('character');
    const characterLabel = characterKey ? `Character sheet (${ft3dActionBindingDisplay('character')})` : 'Character sheet';
    const sheetTooltip = sheetName ? `${sheetName} — ${characterLabel}` : characterLabel;
    const nativeArtClass = portraitUsesNativeAspect ? ' ft-action-bars__sheet--native-art' : '';
    // The ratio ships as data, not as a CSS aspect-ratio: the cluster is a grid whose `auto` column sizes
    // from the sheet's intrinsic contribution, and a button whose only children are absolutely positioned
    // contributes 0 width — `width:auto;aspect-ratio` therefore collapsed the tile to nothing (the Werewolf
    // "We" bug, 2026-09-01). sizeNativePortraits() measures the stack and writes width/height instead.
    const nativeArtStyle = portraitUsesNativeAspect ? ` data-ft-portrait-ratio="${portraitRatio.toFixed(4)}"` : '';
    const sheetButton = `<button type="button" class="btn btn-dark border ft-action-bars__btn ft-action-bars__sheet${portraitUrl ? ' has-portrait' : ''}${nativeArtClass}"${nativeArtStyle}`
        + ` ${tip(sheetTooltip, 'top')} aria-label="${escapeHtml(characterLabel)}"${characterKey ? ` aria-keyshortcuts="${escapeHtml(characterKey)}"` : ''} data-ft3d-open-actor-sheet data-ft-caster-id="${cid}">${portrait}${numBadge}</button>`;
    const sheet = renderPortraitTurnFrame(caster, casterId, sheetButton);
    const inventoryKey = ft3dActionBinding('inventory');
    const inventoryLabel = inventoryKey ? `Inventory (${ft3dActionBindingDisplay('inventory')})` : 'Inventory';
    const inventory = `<button type="button" class="btn btn-dark border ft-action-bars__btn ft-action-bars__inventory ft3d-inventory-toggle"`
        + ` ${tip(inventoryLabel, 'top')} aria-label="${escapeHtml(inventoryLabel)}"${inventoryKey ? ` aria-keyshortcuts="${escapeHtml(inventoryKey)}"` : ''} aria-pressed="${inventoryOpen ? 'true' : 'false'}">${iconMap('inventory', 18, 18, false)}</button>`;
    // BG3 arrangement (owner 2026-08-27): portrait LEFT spanning the cluster's height; RIGHT a stack of
    // the two hand slots on top and the launcher row (inventory now, more later) tucked under them at the
    // same width.
    // Ships INLINE and is promoted to the absolute BG3 placement by placeHandsCluster() only once it
    // has measured that the cluster actually fits beside the dock. Failing safe toward "in the flow"
    // means a missed placement pass costs a nicety, never the whole cluster (owner 2026-08-31).
    // Unit tabs ride on top of the cluster (grid-column 1 / -1), so the strip repaints, folds inline
    // and re-measures with the cluster it belongs to.
    let unitTabs = '';
    try { unitTabs = renderUnitTabs(caster, casterId); } catch { /* strip is a nicety; never sink the bar */ }
    return `<div class="ft-action-hands is-inline${unitTabs ? ' has-units' : ''}" data-ft-caster-id="${cid}">`
        + unitTabs
        + sheet
        + `<div class="ft-action-hands__stack">`
        + extraNaturalRow
        + `<div class="ft-action-hands__slots">`
        + renderHandSlot(caster, casterId, main, naturalMain, HAND_SLOT_MAIN, mainKey, 'Main hand', false, mainKey ? 'main_hand' : null)
        + renderHandSlot(caster, casterId, off, naturalOff, HAND_SLOT_OFF, offKey, 'Off hand', !!both || naturalOffIsMirror, offKey ? 'off_hand' : null)
        + `</div>`
        + `<div class="ft-action-hands__launchers">${inventory}${extraLaunchers}</div>`
        + (stealth ? `<div class="ft-action-hands__launchers ft-action-hands__stealth">${stealth}</div>` : '')
        + `</div></div>`;
}

/** The portrait carries turn CONTROL in combat (owner 2026-09-15; it used to ride the corner disc): the
 *  movement budget drains around the frame, and on this caster's own turn an End Turn strip sits across
 *  the bottom. The picture above the strip still opens the sheet — a click target must never swap
 *  meaning mid-session, and an ended turn cannot be taken back. Out of combat the wrapper is inert.
 *  The data-ft-movement-* attributes feed the scene iframe's drag preview (receiveMovementBudgetPreview). */
function renderPortraitTurnFrame(caster: CachedCaster, casterId: string, sheetButton: string): string {
    let turn: ReturnType<typeof portraitTurnState> = null;
    try { turn = portraitTurnState(caster); } catch { turn = null; }
    if (!turn) return `<div class="ft-action-hands__portrait">${sheetButton}</div>`;
    const fill = Math.max(0, Math.min(1, turn.fillPct));
    const speedLabel = turn.speedLabel || 'walking';
    const movementLabel = `${speedLabel} movement: ${turn.remainingFeet} feet ready`;
    const endTurn = turn.myTurn && turn.canEndTurn;
    const cls = 'ft-action-hands__portrait is-combat'
        + (turn.myTurn ? ' is-my-turn' : '')
        + (endTurn ? ' has-end-turn' : '')
        + (endTurn && turn.outOfAp ? ' is-hurry' : '')
        + (turn.bankedPct > 1 ? ' is-overbanked' : '');
    const rim = `<span class="ft-action-hands__move-rim" data-ft-move-budget-fill data-ft-authoritative-fill="${fill.toFixed(6)}" style="--ft-move-fill:${fill.toFixed(4)}" aria-hidden="true"></span>`;
    // The mode is named only when it is not walking — the mesh already shows a walker walking.
    const readout = `<span class="ft-action-hands__move-readout" aria-hidden="true"><strong data-ft-move-budget-feet>${turn.remainingFeet}</strong><small>ft${turn.speedLabel ? ` ${escapeHtml(turn.speedLabel)}` : ''}</small></span>`;
    const strip = endTurn
        ? `<button type="button" class="ft-action-hands__end-turn" data-ft-ab-action="end-turn" data-ft-caster-id="${escapeHtml(casterId)}" aria-label="End turn${turn.roundNo ? `, round ${turn.roundNo}` : ''}">End Turn</button>`
        : '';
    return `<div class="${cls}" data-ft-move-budget-entity="${escapeHtml(turn.entityId)}" data-ft-move-budget-encounter="${escapeHtml(turn.encounterId)}"`
        + ` data-ft-movement-base-speed="${turn.baseSpeed}" data-ft-movement-banked-pct="${turn.bankedPct}" data-ft-movement-aps="${turn.aps}"`
        + ` data-ft-movement-speed-variable="${escapeHtml(turn.speedVariable)}" data-ft-movement-speed-label="${escapeHtml(speedLabel)}"`
        + ` data-ft-authoritative-movement-feet="${turn.remainingFeet}" data-ft-authoritative-movement-label="${escapeHtml(movementLabel)}">`
        + sheetButton + rim + readout + strip
        + `<span class="visually-hidden" data-ft-move-budget-label>${escapeHtml(movementLabel)}</span>`
        + `</div>`;
}

/** The ADDITIONAL, louder End Turn (owner 2026-09-15): once this caster has no action points left on its
 *  own turn, a prompt floats above the bar. Reactions deliberately do not hold it back — a banked
 *  reaction is spent on somebody else's turn, not a reason to keep this one open. */
function renderEndTurnPrompt(caster: CachedCaster, casterId: string): string {
    let turn: ReturnType<typeof portraitTurnState> = null;
    try { turn = portraitTurnState(caster); } catch { turn = null; }
    if (!turn || !turn.outOfAp || !turn.canEndTurn) return '';
    return `<div class="ft-end-turn-prompt">`
        + `<button type="button" class="ft-end-turn-prompt__btn" data-ft-ab-action="end-turn" data-ft-caster-id="${escapeHtml(casterId)}" aria-label="No action points left — end turn">`
        + `<span class="ft-end-turn-prompt__glyph" aria-hidden="true">${turnHourglassArt()}</span>`
        + `<span class="ft-end-turn-prompt__text"><strong>End Turn</strong><small>No action points left</small></span>`
        + `</button></div>`;
}

/** The dock's End Turn controls (portrait strip + out-of-AP prompt) as one comparable key; '' out of combat. */
function dockTurnControlsKey(caster: CachedCaster): string {
    let turn: ReturnType<typeof portraitTurnState> = null;
    try { turn = portraitTurnState(caster); } catch { turn = null; }
    if (!turn) return '';
    return `${turn.myTurn ? 1 : 0}${turn.canEndTurn ? 1 : 0}${turn.outOfAp ? 1 : 0}`;
}

/** A turn change must repaint the dock's End Turn controls (Loudwire, 2026-09-24: End Turn stayed up after the
 *  turn moved on). The bar repaints on caster updates, but the turn pointer travels on the ENCOUNTER: a compact
 *  encounter_stats_update patches it and only fires ft:encounter-turn-state, and a full encounter_update repaints
 *  the dock (ftRealiasCastersFromEncounterBlob) BEFORE it installs the new encounter into either cache. So after
 *  every turn/encounter event settles, repaint any dock pane whose painted turn key no longer matches — the key
 *  gate keeps an ordinary cast from rebuilding the dock a second time. */
function reconcileDockTurnControls(): void {
    document.querySelectorAll<HTMLElement & { _ftAbTurnKey?: string }>(
        '[data-ft3d-actor-dock] .ft-pc-sheet-pane[data-ft-pc-pane="action_bar"][data-ft-caster-id]',
    ).forEach((pane) => {
        if (pane._ftAbTurnKey === undefined) return;
        const casterId = pane.getAttribute('data-ft-caster-id') || '';
        const caster = casterId ? casterFromCache(casterId) : null;
        if (caster && dockTurnControlsKey(caster) !== pane._ftAbTurnKey) renderActionBar(caster, casterId, pane);
    });
}
if (!window._ftDockTurnReconcileBound) {
    window._ftDockTurnReconcileBound = true;
    // Deferred: the FT3D shell's own listeners re-point ft3dEncounterArrCache (which the portrait reads first)
    // during these same events, so compare only once every synchronous listener has run.
    // One pending pass coalesces a burst (a full update fires both events).
    let pending = 0;
    const reconcileSoon = () => {
        if (pending) return;
        pending = window.setTimeout(() => { pending = 0; reconcileDockTurnControls(); }, 0);
    };
    window.addEventListener('ft:encounter-turn-state', reconcileSoon);
    window.addEventListener('ft:encounter-state-received', reconcileSoon);
}

// ── hand-cluster placement: measured, not breakpointed ───────────────────────────────────────────
// The cluster hangs off the dock's LEFT edge (position:absolute; right:100%). The dock is centred and
// capped at --ft3d-actionbar-width, so the room to its left is (stage - dock) / 2 — a function of the
// stage width, the bar size AND any drawer that narrows the stage, which no single media query can
// express. Measure it instead: promote to the absolute placement only when the cluster demonstrably
// fits, otherwise leave it inline in .ft-action-bars__main where it is always fully visible.
const HANDS_DOCK_GAP = 6;      // matches the absolute rule's margin-right
const HANDS_DEMOTE_INSET = 8;  // already hung out there: fold back only once it really runs out of room
const HANDS_PROMOTE_INSET = 12; // hysteresis, so an animating drawer can't chatter across the threshold
const handsPlacementDocks = new WeakSet<HTMLElement>();
let handsPlacementFrame = 0;
let handsPlacementRunning = false;

/** Size a native-art sheet tile (full creature artwork, not a cropped PC thumbnail) to the cluster's
 *  height at the picture's own ratio. Done by measurement rather than CSS `aspect-ratio`: the tile sits in
 *  an `auto` grid column and holds only absolutely-positioned children, so its intrinsic contribution is
 *  0 and the column collapsed with it (Werewolf "We" initials bug, 2026-09-01). The right-hand stack
 *  alone decides the row height, so its offsetHeight is a stable source. Width is clamped so a wide
 *  landscape piece can never turn the launcher into a banner; a hidden dock measures 0 and keeps the
 *  fallback 72px tile until the next placement pass. */
const PORTRAIT_MIN_WIDTH = 44;
const PORTRAIT_MAX_RATIO = 2;
function sizeNativePortraits(hands: HTMLElement): void {
    const sheet = hands.querySelector<HTMLElement>('.ft-action-bars__sheet--native-art');
    const stack = hands.querySelector<HTMLElement>('.ft-action-hands__stack');
    if (!sheet || !stack) return;
    const ratio = Number(sheet.dataset.ftPortraitRatio);
    const height = stack.offsetHeight;
    if (!(ratio > 0) || height <= 0) return;
    const width = Math.round(height * Math.min(ratio, PORTRAIT_MAX_RATIO));
    sheet.style.height = `${height}px`;
    sheet.style.width = `${Math.max(PORTRAIT_MIN_WIDTH, width)}px`;
}

function placeHandsCluster(): void {
    if (handsPlacementRunning) return; // our own class toggle can resize the dock → re-entrant RO
    handsPlacementRunning = true;
    try {
        document.querySelectorAll<HTMLElement>('.ft-action-hands').forEach((hands) => {
            const dock = hands.closest<HTMLElement>('[data-ft3d-actor-dock]');
            if (!dock) { hands.classList.add('is-inline'); return; }
            // Size the portrait BEFORE judging fit, so the width we measure is the width we will show.
            sizeNativePortraits(hands);
            const wasInline = hands.classList.contains('is-inline');
            let fits = false;
            try {
                // Always judge from the ABSOLUTE geometry, whichever mode we are currently in — the
                // inline box is narrower (tighter padding/gap), so deciding from it would flip straight
                // back and oscillate. The dock's own width is CSS-fixed (min(var, 100% - 1.5rem)), so
                // taking the cluster out of the flow cannot move dock.left and make this reading lie.
                // Only pay for the write-before-read when we are actually in the other mode.
                if (wasInline) hands.classList.remove('is-inline');
                const width = hands.offsetWidth;
                const dockLeft = dock.getBoundingClientRect().left;
                fits = width > 0
                    && (dockLeft - HANDS_DOCK_GAP - width) >= (wasInline ? HANDS_PROMOTE_INSET : HANDS_DEMOTE_INSET);
            } catch { fits = false; }
            hands.classList.toggle('is-inline', !fits);
        });
    } finally {
        handsPlacementRunning = false;
    }
}

function scheduleHandsPlacement(): void {
    if (handsPlacementFrame) return;
    handsPlacementFrame = requestAnimationFrame(() => {
        handsPlacementFrame = 0;
        placeHandsCluster();
    });
}

/** Bind the geometry sources once per dock. Three of them, because each misses what the others catch:
 *  - `resize` for the window itself;
 *  - a ResizeObserver on the dock AND on its containing block (the stage). Observing only the dock is
 *    not enough — the dock is width-CAPPED, so a drawer that narrows the stage moves `dock.left`
 *    while leaving the dock's own size untouched, and a size observer never fires (Codex, 2026-08-31);
 *  - a MutationObserver for the attribute flips that change the dock's box without resizing anything:
 *    `hidden` (revealed on selection — the first mount measures a zero-size box), the bar size, and
 *    the inventory-dock state. */
function ensureHandsPlacementObservers(dock: HTMLElement | null): void {
    if (!dock || handsPlacementDocks.has(dock)) return;
    handsPlacementDocks.add(dock);
    window.addEventListener('resize', scheduleHandsPlacement, { passive: true });
    if (typeof ResizeObserver === 'function') {
        try {
            const ro = new ResizeObserver(scheduleHandsPlacement);
            ro.observe(dock);
            const stage = dock.offsetParent;
            if (stage instanceof HTMLElement) ro.observe(stage);
        } catch { /* older engines */ }
    }
    if (typeof MutationObserver === 'function') {
        try {
            new MutationObserver(scheduleHandsPlacement).observe(dock, {
                attributes: true,
                attributeFilter: ['hidden', 'style', 'data-ft-bar-size', 'data-ft-inventory-dock-open'],
            });
        } catch { /* older engines */ }
    }
}

// Far-right controls: the dock-only Inventory launcher immediately LEFT of Settings. Settings toggles
// renderSettingsTray (lock / bar config / shortcut binding). Inventory deliberately lives here instead
// of on the mini-map rim: it is player/action-bar chrome, not a map-level control.
/** Paint the context disc into the FT3D HUD mount (identical-markup skip so a repaint never flashes it).
 *  '' from renderTurnHalo (no encounter binding, no clock) empties the mount and `:empty` collapses it. */
/** The disc's context overlay REPLACES the iframe minimap's campaign-hud card (and its status chip),
 *  opening on hover/focus in both modes. Flag the
 *  minimap host so ft3d-minimap.ts keeps the duplicate card hidden while a halo is up — and apply BOTH
 *  states immediately, so dropping the halo un-hides them without waiting for the minimap's next render
 *  (Codex review 2026-08-29). */
function syncMinimapCampaignCard(suppressed: boolean): void {
    try {
        const frameDoc = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]')?.contentDocument ?? null;
        const minimapHost = frameDoc?.querySelector<HTMLElement>('[data-ft3d-minimap]') ?? null;
        if (!minimapHost) return;
        minimapHost.dataset.ft3dCampaignHudSuppressed = suppressed ? '1' : '0';
        const card = minimapHost.querySelector<HTMLElement>('[data-ft3d-campaign-hud]');
        const status = minimapHost.querySelector<HTMLElement>('[data-ft3d-hud-status]');
        if (card) card.hidden = true; // Retired context card; its data still feeds the encounter circle.
        if (status) status.hidden = suppressed;
    } catch { /* cross-origin or not yet navigated — nothing to suppress */ }
}

type TurnHaloMovementPreviewPayload = {
    schema: 'ft.ft3d.movement-budget-preview.v1';
    encounterId: string;
    entityId: string;
    phase: 'update' | 'commit' | 'clear';
    sequence: number;
    distanceFt: number;
    accepted: boolean;
    inputSource?: 'ft3d-drag' | 'ft3d-path' | 'keyboard-path';
};

const turnHaloCommitRestoreTimers = new WeakMap<HTMLElement, number>();

function projectTurnHaloMovementBudget(halo: HTMLElement, distanceFt: number, accepted: boolean): {
    fillPct: number;
    remainingFeet: number;
    apSpent: number;
} {
    const baseSpeed = Math.max(0, Number(halo.dataset.ftMovementBaseSpeed) || 0);
    let pct = Math.max(0, Number(halo.dataset.ftMovementBankedPct) || 0);
    let aps = Math.max(0, Math.floor(Number(halo.dataset.ftMovementAps) || 0));
    let apSpent = 0;
    const distance = accepted ? Math.max(0, distanceFt) : 0;
    const speedVariable = halo.dataset.ftMovementSpeedVariable || '';
    if (baseSpeed > 0 && distance > 1e-9) {
        if (speedVariable === 'speed_jumping' || speedVariable === 'teleport') {
            if (pct <= 1e-9 && aps > 0) {
                aps -= 1;
                apSpent += 1;
            }
            pct = 0;
        } else {
            let costPct = distance / baseSpeed;
            while (costPct > 1e-9) {
                if (pct <= 1e-9) {
                    if (aps <= 0) break;
                    aps -= 1;
                    apSpent += 1;
                    pct = 1;
                }
                const paid = Math.min(pct, costPct);
                pct -= paid;
                costPct -= paid;
            }
            if (pct < 1e-9) pct = 0;
        }
    }
    const fillPct = baseSpeed > 0 ? (pct > 1e-9 ? Math.min(1, pct) : (aps > 0 ? 1 : 0)) : 0;
    return { fillPct, remainingFeet: fillPct * baseSpeed, apSpent };
}

function restoreTurnHaloMovementPreview(halo: HTMLElement): void {
    const fill = halo.querySelector<HTMLElement>('[data-ft-move-budget-fill]');
    if (!fill) return;
    const authoritative = Math.max(0, Math.min(1, Number(fill.dataset.ftAuthoritativeFill) || 0));
    fill.style.setProperty('--ft-move-fill', authoritative.toFixed(4));
    const label = halo.querySelector<HTMLElement>('[data-ft-move-budget-label]');
    if (label) label.textContent = halo.dataset.ftAuthoritativeMovementLabel || '';
    const feet = halo.querySelector<HTMLElement>('[data-ft-move-budget-feet]');
    if (feet) feet.textContent = String(Math.max(0, Math.round(Number(halo.dataset.ftAuthoritativeMovementFeet) || 0)));
    halo.classList.remove('is-move-previewing', 'is-move-preview-committed');
    delete halo.dataset.ftMovePreviewPhase;
    delete halo.dataset.ftMovePreviewSource;
}

/** Paint the scene iframe's local drag projection directly onto the portrait's movement rim. This avoids
 * rebuilding the action bar on every pointer frame while keeping the cached caster/WS row authoritative. */
function receiveTurnHaloMovementPreview(event: MessageEvent): void {
    if (event.origin !== window.location.origin) return;
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    if (!frame?.contentWindow || event.source !== frame.contentWindow) return;
    const envelope = recordOf(event.data);
    if (envelope?.type !== 'ft3d-campaign-movement-budget-preview-v1') return;
    const payload = recordOf(envelope.payload) as TurnHaloMovementPreviewPayload | null;
    if (!payload || payload.schema !== 'ft.ft3d.movement-budget-preview.v1') return;
    const phase = payload.phase;
    const sequence = Number(payload.sequence);
    const entityId = String(payload.entityId || '').trim().toUpperCase();
    const encounterId = String(payload.encounterId || '').trim().toUpperCase();
    if (!['update', 'commit', 'clear'].includes(phase)
        || !Number.isSafeInteger(sequence) || sequence < 1 || !entityId || !encounterId) return;
    const halo = Array.from(document.querySelectorAll<HTMLElement>('[data-ft-move-budget-entity]'))
        .find(candidate => (candidate.dataset.ftMoveBudgetEntity || '').toUpperCase() === entityId
            && (candidate.dataset.ftMoveBudgetEncounter || '').toUpperCase() === encounterId);
    if (!halo) return;
    const previousSequence = Number(halo.dataset.ftMovePreviewSequence) || 0;
    if (sequence < previousSequence) return;

    if (phase === 'clear') {
        // A committed drop is followed immediately by the drag controller's ordinary clear. Hold the
        // predicted result until the authoritative encounter push replaces it instead of snapping back.
        if (halo.dataset.ftMovePreviewPhase !== 'commit') {
            halo.dataset.ftMovePreviewSequence = String(sequence);
            restoreTurnHaloMovementPreview(halo);
        }
        return;
    }
    halo.dataset.ftMovePreviewSequence = String(sequence);

    const distanceFt = Number(payload.distanceFt);
    if (!Number.isFinite(distanceFt) || typeof payload.accepted !== 'boolean') return;
    const { fillPct, remainingFeet, apSpent } = projectTurnHaloMovementBudget(
        halo,
        distanceFt,
        payload.accepted,
    );
    const clamped = Math.max(0, Math.min(1, fillPct));
    const fill = halo.querySelector<HTMLElement>('[data-ft-move-budget-fill]');
    if (!fill) return;
    fill.style.setProperty('--ft-move-fill', clamped.toFixed(4));
    const speedLabel = halo.dataset.ftMovementSpeedLabel || 'Movement';
    const feet = Math.max(0, Math.round(remainingFeet));
    const aps = Math.max(0, Math.floor(apSpent));
    const previewLabel = `${speedLabel} movement preview: ${feet} feet ready${aps > 0 ? ` after spending ${aps} action point${aps === 1 ? '' : 's'}` : ''}`;
    const label = halo.querySelector<HTMLElement>('[data-ft-move-budget-label]');
    if (label) label.textContent = previewLabel;
    const feetReadout = halo.querySelector<HTMLElement>('[data-ft-move-budget-feet]');
    if (feetReadout) feetReadout.textContent = String(feet);
    halo.dataset.ftMovePreviewPhase = phase;
    halo.dataset.ftMovePreviewSource = payload.inputSource || 'ft3d-drag';
    halo.classList.toggle('is-move-previewing', phase === 'update');
    halo.classList.toggle('is-move-preview-committed', phase === 'commit');

    const priorTimer = turnHaloCommitRestoreTimers.get(halo);
    if (priorTimer !== undefined) window.clearTimeout(priorTimer);
    if (phase === 'commit') {
        const timer = window.setTimeout(() => {
            if (halo.isConnected && halo.dataset.ftMovePreviewSequence === String(sequence)) {
                restoreTurnHaloMovementPreview(halo);
            }
        }, 6000);
        turnHaloCommitRestoreTimers.set(halo, timer);
    }
}

window.addEventListener('message', receiveTurnHaloMovementPreview);

function paintTurnHaloPortal(casterId: string): void {
    const mount = document.querySelector<HTMLElement & { _ftHaloLastHtml?: string; _ftHaloLastKey?: string }>('[data-ft3d-turn-halo-mount]');
    if (!mount) return;
    // The disc belongs to the encounter, including GM view before any actor is selected.
    // These are view pointers only; no creature is fabricated or fetched to paint the clock.
    const caster = casterFromCache(casterId) ?? { encounter_creatures: {
        encounter_id: String(window.ftGetActiveEncounterId?.() || ''),
        campaign_id: String(window.ftGetCampaignId?.() || window.FateTide?.campaign?.id || ''),
    } };
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    const syncEncounterInfo = frame ? bindEncounterInfoCircle(mount, frame,
        () => hasCampaignPermission(casterFromCache(casterId)) || window.FateTide?.user?.isGM === true) : null;
    // Repaint only when an INPUT changed (turnHaloKey) — iconMap markup carries per-call ids, so a
    // byte-compare of the HTML repainted the disc on every caster update and killed its :hover/focus.
    const key = turnHaloKey(caster, casterId);
    const keyUnchanged = key !== '' && mount._ftHaloLastKey === key && mount.innerHTML !== '';
    // ⛔ Reconcile the iframe BEFORE the key early-return (Codex review 2026-08-29): the minimap can
    // re-render or the frame reload without any halo input changing, which brought the duplicate card
    // back and left it there. This is idempotent, so running it on every paint is free.
    syncMinimapCampaignCard(keyUnchanged ? mount.innerHTML !== '' : true);
    if (keyUnchanged) {
        positionTurnHaloMount();
        return;
    }
    mount._ftHaloLastKey = key;
    const html = renderTurnHalo(caster, casterId);
    syncMinimapCampaignCard(html !== '');
    if (mount._ftHaloLastHtml !== html) {
        const restoreFocus = document.activeElement === mount.querySelector('.ft-turn-halo');
        mount._ftHaloLastHtml = html;
        replaceHydratedHtml(mount, html);
        if (html) window.ftMountIcons?.(mount);
        if (restoreFocus) mount.querySelector<HTMLElement>('.ft-turn-halo')?.focus({ preventScroll: true });
    }
    syncEncounterInfo?.();
    positionTurnHaloMount();
}

/** Bind encounter-level refresh even on a fresh GM page that has never rendered an action bar. */
function initTurnHaloPortal(): void {
    if (!document.querySelector('[data-ft3d-turn-halo-mount]')) return;
    ensureActionBarStyles();
    const repaintDocked = () => {
        const id = document.querySelector<HTMLElement>('#ft3d_campaign_actor_dock [data-ft-caster-id]')?.getAttribute('data-ft-caster-id') || '';
        paintTurnHaloPortal(id);
    };
    if (!window._ftHaloResizeBound) {
        window._ftHaloResizeBound = true;
        window.addEventListener('resize', positionTurnHaloMount, { passive: true });
        // The minimap lives in the scene iframe and can re-lay itself out (map expand, frame reload)
        // without a window resize. Measuring two rects is cheap; a 1 s follow keeps the disc tucked.
        window.setInterval(positionTurnHaloMount, 1000);
        // The action bar only repaints on caster/encounter updates; the campaign CLOCK moves without
        // one (push/pull_time → ft:campaign-state-received, plus the live tick). Re-key on those so the
        // sun/moon and the clock label follow the time (owner 2026-08-28) — cheap, since the key gate
        // means nothing repaints unless a value actually changed.
        window.addEventListener('ft:campaign-state-received', repaintDocked);
        document.addEventListener('ft:campaign-state-received', repaintDocked);
        // The derived clock's painter (campaign-clock-runtime.ts) fires this every tick it repaints the
        // Campaign time widget — including the forced repaint on each re-base after a GM push. The disc
        // reads that same derived time, so a minute boundary is the moment to re-key; seconds would churn.
        window.addEventListener('ft:campaign-clock-tick', event => {
            if ((event as CustomEvent<{ minuteChanged?: boolean }>).detail?.minuteChanged) repaintDocked();
        });
        // Encounter pushes (turn / weather / environment / combat state) arrive as their own events,
        // not as a caster update. In particular, go-to-creature-turn publishes the mutation-time
        // truth as ft:encounter-turn-state; observers do not receive a caster update to incidentally
        // repaint the disc. The iframe minimap card refreshes a beat later, so repaint again shortly
        // after full/environment projections.
        // Debounced trailing pass: a burst of encounter events would otherwise queue one 800 ms
        // timer each (Codex review 2026-08-29).
        let trailing = 0;
        const repaintSoon = () => {
            repaintDocked();
            window.clearTimeout(trailing);
            trailing = window.setTimeout(repaintDocked, 800);
        };
        window.addEventListener('ft:encounter-turn-state', repaintDocked);
        window.addEventListener('ft:encounter-state-received', repaintSoon);
        window.addEventListener('ft:encounter-lighting-changed', repaintSoon);
        document.addEventListener('click', event => {
            if (event.target instanceof Element && event.target.closest('[data-ft-turn-widget-action="clear-caster"]')) {
                // Clear-caster's delegated handlers release the dock in this same event dispatch.
                window.setTimeout(repaintDocked, 0);
            }
        });
        window.setInterval(repaintDocked, 10000);
    }
    repaintDocked();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initTurnHaloPortal, { once: true });
else window.setTimeout(initTurnHaloPortal, 0);

/** Park the context disc just off the minimap's lower-right rim (owner 2026-09-15 — it used to hang off
 *  the dock's right edge and push the log up). The minimap renders inside the same-origin scene iframe,
 *  so its viewport box is the frame's rect offset by the iframe's own. The mount is position:fixed in
 *  the full-width HUD, so those viewport coordinates ARE its top/left. No minimap (frame not loaded,
 *  another route) → the top-right corner, still clear of the log on the floor. */
const HALO_SIZE = 88;
function positionTurnHaloMount(): void {
    const mount = document.querySelector<HTMLElement>('[data-ft3d-turn-halo-mount]');
    if (!mount) return;
    if (!mount.innerHTML) {
        mount.hidden = true;
        return;
    }
    mount.hidden = false;
    const edge = 10;
    let left = window.innerWidth - HALO_SIZE - edge;
    let top = edge;
    try {
        const iframe = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
        const ring = iframe?.contentDocument?.querySelector<HTMLElement>('[data-ft3d-minimap] .ft3d-minimap__frame') ?? null;
        const inner = ring?.getBoundingClientRect();
        if (iframe && inner && inner.width > 0 && inner.height > 0) {
            const outer = iframe.getBoundingClientRect();
            const cx = outer.left + inner.left + inner.width / 2;
            const cy = outer.top + inner.top + inner.height / 2;
            const radius = Math.max(inner.width, inner.height) / 2;
            // 65° below the horizontal on the ring's right side — the pocket the owner marked. The bezel
            // tabs sit ~20% outside the frame radius, so reach past them rather than onto them, and stay
            // inside the viewport's right-edge strip (Elevation tab).
            const angle = 65 * Math.PI / 180;
            const reach = radius * 1.2 + HALO_SIZE / 2 + 12;
            left = cx + Math.cos(angle) * reach - HALO_SIZE / 2;
            top = cy + Math.sin(angle) * reach - HALO_SIZE / 2;
        }
    } catch { /* cross-origin or mid-navigation — keep the corner fallback */ }
    left = Math.max(edge, Math.min(left, window.innerWidth - HALO_SIZE - edge));
    top = Math.max(edge, Math.min(top, window.innerHeight - HALO_SIZE - edge));
    mount.style.left = `${Math.round(left)}px`;
    mount.style.top = `${Math.round(top)}px`;
    // The overlay drops below the disc; on a short viewport (or an expanded map pushing the disc down)
    // flip it above whichever side has more room, and cap its height to that room (Codex review).
    const below = window.innerHeight - (top + HALO_SIZE) - 16;
    const above = top - 16;
    const up = below < 260 && above > below;
    mount.classList.toggle('is-overlay-up', up);
    mount.style.setProperty('--halo-ov-max-h', `${Math.max(140, Math.round(up ? above : below))}px`);
}

function renderControls(
    casterId: string,
    inDock: boolean,
    inventoryOpen: boolean,
    canEdit: boolean,
    campaignDock = false,
): string {
    const cid = escapeHtml(casterId);
    const open = settingsOpen.get(casterId) === true;
    const inventoryKey = ft3dActionBinding('inventory');
    const inventoryLabel = inventoryKey ? `Inventory (${ft3dActionBindingDisplay('inventory')})` : 'Inventory';
    // Campaign dock: Inventory + Character sheet moved to the LEFT hand cluster (renderHandsCluster,
    // owner 2026-08-27, BG3-style); the right keeps only minimize + settings.
    const inventory = (inDock && !campaignDock)
        ? `<button type="button" class="btn btn-dark border ft-action-bars__btn ft-action-bars__inventory ft3d-inventory-toggle"`
            + ` ${tip(inventoryLabel)} aria-label="${escapeHtml(inventoryLabel)}"${inventoryKey ? ` aria-keyshortcuts="${escapeHtml(inventoryKey)}"` : ''} aria-pressed="${inventoryOpen ? 'true' : 'false'}">${iconMap('inventory', 18, 18, false)}</button>`
        : '';
    const settings = canEdit
        ? `<button type="button" class="btn btn-dark border ft-action-bars__btn ft-action-bars__settings${open ? ' is-open' : ''}"`
            + ` ${tip('Bar settings')} data-ft-ab-action="toggle-settings" data-ft-caster-id="${cid}">&#9881;</button>`
        : '';
    const campaignControls = campaignDock
        ? `<button type="button" class="btn btn-dark border ft-action-bars__btn ft-action-bars__minimize"`
            + ` ${tip(`Minimize action bar (${ft3dActionBindingDisplay('action_bar')})`)} data-ft3d-close-actor aria-label="Minimize action bar">&minus;</button>`
        : '';
    // Campaign context disc: NOT in this row — portalled into the FT3D HUD's [data-ft3d-turn-halo-mount]
    // and parked beside the minimap. Painted here because this is the repaint path that already runs on
    // every caster/encounter update. (End Turn itself lives on the hands-cluster portrait.)
    if (campaignDock) paintTurnHaloPortal(casterId);
    return `<div class="ft-action-bars__controls">`
        + inventory
        + campaignControls
        + settings
        + `</div>`;
}

// The settings tray (shown above the bar when the gear is open): lock toggle, bar-config (+/−/add-divider,
// hidden when locked), and the keybind "Configure shortcuts" toggle. '' when the gear is closed.
function renderSettingsTray(casterId: string, barCount: number, inDock: boolean): string {
    if (settingsOpen.get(casterId) !== true) return '';
    const cid = escapeHtml(casterId);
    const locked = isLocked();
    const binding = bindMode.get(casterId) === true;
    const lockBtn = `<button type="button" class="btn btn-sm btn-dark border ft-ab-settings__btn" data-ft-ab-action="toggle-lock" data-ft-caster-id="${cid}">${locked ? '🔒 Locked' : '🔓 Unlocked'}</button>`;
    const minusDis = barCount <= MIN_BARS ? ' disabled' : '';
    const plusDis = barCount >= MAX_BARS ? ' disabled' : '';
    const editGroup = locked ? '' : (
        `<button type="button" class="btn btn-sm btn-dark border ft-ab-settings__btn"${plusDis} data-ft-ab-action="add-row" data-ft-caster-id="${cid}">&plus; Bar</button>`
        + `<button type="button" class="btn btn-sm btn-dark border ft-ab-settings__btn"${minusDis} data-ft-ab-action="remove-row" data-ft-caster-id="${cid}">&minus; Bar</button>`
        + `<button type="button" class="btn btn-sm btn-dark border ft-ab-settings__btn" data-ft-ab-action="add-divider" data-ft-caster-id="${cid}">&#124;&plus; Divider</button>`
    );
    // Keybinds are dock-only, so the Configure button only appears there (on the in-sheet bar it'd be a dead
    // control — its bind overlays never render because `binding` is gated on inDock).
    const bindBtn = inDock
        ? `<button type="button" class="btn btn-sm border ft-ab-settings__btn ft-ab-settings__configure${binding ? ' is-active' : ''}" data-ft-ab-action="toggle-bind-mode" data-ft-caster-id="${cid}">${binding ? '✓ Done' : '⌨ Configure shortcuts'}</button>`
        : '';
    const hint = (inDock && binding) ? `<span class="ft-ab-settings__hint">Click a slot, then press a key. Esc cancels.</span>` : '';
    // Theme row: preset swatches + a custom-color well. Dark is the default; a custom "#rrggbb" tints the
    // accent over the dark frame. Persisted per-PC on the bar rows (readTheme / ftActionBarSetTheme).
    const caster = casterFromCache(casterId);
    const curTheme = caster ? readTheme(caster) : DEFAULT_THEME;
    const swatches = Object.entries(AB_THEMES).map(([key, t]) =>
        `<button type="button" class="ft-ab-theme-sw${curTheme === key ? ' is-active' : ''}" style="--sw:${t.swatch}"`
        + ` ${tip(t.label, 'top')} data-ft-ab-action="set-theme" data-ft-caster-id="${cid}" data-ft-theme="${escapeHtml(key)}"></button>`).join('');
    const customActive = curTheme.charAt(0) === '#';
    const customVal = customActive ? curTheme : '#88a0c8';
    const customSw = `<label class="ft-ab-theme-custom${customActive ? ' is-active' : ''}"${customActive ? ` style="--sw:${escapeHtml(customVal)}"` : ''} ${tip('Custom color', 'top')}>`
        + `<input type="color" value="${escapeHtml(customVal)}" data-ft-ab-custom-color="1" data-ft-caster-id="${cid}">`
        + `<span aria-hidden="true">🎨</span></label>`;
    const curSize = caster ? readBarSize(caster) : 'm';
    const sizeButtons = ([
        ['s', 'Compact'],
        ['m', 'Standard'],
        ['l', 'Wide'],
    ] as Array<[ActionBarSize, string]>).map(([size, label]) =>
        `<button type="button" class="btn btn-sm border ft-ab-settings__btn ft-ab-size__btn${curSize === size ? ' is-active' : ''}"`
        + ` data-ft-ab-action="set-size" data-ft-caster-id="${cid}" data-ft-bar-size="${size}" aria-pressed="${curSize === size}">${label}</button>`).join('');
    const layoutSection = `<div class="ft-ab-settings__section ft-ab-settings__section--layout">${lockBtn}${editGroup}</div>`;
    const sizeSection = `<div class="ft-ab-settings__section ft-ab-size"><span class="ft-ab-size__lbl">Width</span><div class="ft-ab-size__choices" role="group" aria-label="Action bar width">${sizeButtons}</div></div>`;
    const shortcutSection = (bindBtn || hint)
        ? `<div class="ft-ab-settings__section ft-ab-settings__section--shortcuts">${bindBtn}${hint}</div>`
        : '';
    const themeRow = `<div class="ft-ab-theme"><span class="ft-ab-theme__lbl">Theme</span>${swatches}${customSw}</div>`;
    return `<div class="ft-action-settings"><div class="ft-action-settings__row">${layoutSection}${sizeSection}${shortcutSection}</div>${themeRow}${caster ? renderDancePlaylistRow(casterId, caster) : ''}</div>`;
}

// ── dance playlist (owner 2026-09-16 — the FT3D /dance picker) ────────────────────────────────────────────
// Character-global like the theme, but stored in action_bar_settings (a playlist is not a bar row). /dance
// chains these in order, looping the list; one entry dances it until the character moves; none dances the
// first in DANCE_CLIPS. The chaining itself lives in ft3d-emote-clips.ts danceProgram().

function readDancePlaylist(caster: CachedCaster): string[] {
    return normalizeDancePlaylist(caster.action_bar_settings?.dance_playlist);
}

function renderDancePlaylistRow(casterId: string, caster: CachedCaster): string {
    const cid = escapeHtml(casterId);
    const playlist = readDancePlaylist(caster);
    const labelOf = (key: string): string => DANCE_CLIPS.find(dance => dance.key === key)?.label ?? key;
    const chips = playlist.map((key, index) => {
        const label = escapeHtml(labelOf(key));
        const button = (action: string, glyph: string, title: string, disabled: boolean, dir = ''): string =>
            `<button type="button" class="ft-ab-dance__btn" ${tip(title, 'top')} aria-label="${title} ${label}"${disabled ? ' disabled' : ''}`
            + ` data-ft-ab-action="${action}" data-ft-caster-id="${cid}" data-ft-dance-idx="${index}"${dir ? ` data-ft-dance-dir="${dir}"` : ''}>${glyph}</button>`;
        return `<li class="ft-ab-dance__chip"><span class="ft-ab-dance__n">${index + 1}</span>${label}`
            + button('dance-move', '&lsaquo;', 'Move earlier:', index === 0, '-1')
            + button('dance-move', '&rsaquo;', 'Move later:', index === playlist.length - 1, '1')
            + button('dance-remove', '&times;', 'Remove', false)
            + '</li>';
    }).join('');
    const full = playlist.length >= DANCE_PLAYLIST_MAX;
    const options = DANCE_CLIPS.map(dance => `<option value="${escapeHtml(dance.key)}">${escapeHtml(dance.label)}</option>`).join('');
    const add = `<select class="ft-ab-dance__add" aria-label="Add a dance to the playlist" data-ft-ab-dance-add="1" data-ft-caster-id="${cid}"${full ? ' disabled' : ''}>`
        + `<option value="">${full ? 'Playlist full' : '+ Add dance'}</option>${options}</select>`;
    const hint = playlist.length === 0 ? `<span class="ft-ab-dance__hint">/dance plays ${escapeHtml(DANCE_CLIPS[0]?.label ?? 'a dance')}</span>`
        : playlist.length === 1 ? '<span class="ft-ab-dance__hint">held until you move</span>'
        : '<span class="ft-ab-dance__hint">in order, on repeat</span>';
    return `<div class="ft-ab-dance"><span class="ft-ab-theme__lbl">Dances</span><ol class="ft-ab-dance__list">${chips}</ol>${add}${hint}</div>`;
}

/** Replace this PC's dance playlist: optimistic cache write + repaint, then persist the one setting. */
export function ftActionBarSetDancePlaylist(casterId: string, playlist: readonly string[]): void {
    const clean = normalizeDancePlaylist(playlist);
    persistActionBarPreferences(casterId, { action_bar_settings: { dance_playlist: clean } });
}

function ftActionBarEditDancePlaylist(casterId: string, edit: (list: string[]) => void): void {
    const caster = casterFromCache(casterId);
    if (!caster) return;
    const list = readDancePlaylist(caster);
    edit(list);
    ftActionBarSetDancePlaylist(casterId, list);
}

// ── ability CHOICE bar (BG3-style) ───────────────────────────────────────────────────────────────
// An ability whose generateAbilityButton stamps a non-empty data-input_id_reference needs an extra choice
// before it fires (upcast level / shapeshift form / summon / damage type / variable charges|mana). In the
// spell/ability TABLES that control sits next to the row; in the full-screen DOCK there's no table, so the
// referenced input_<id>_<layer> element doesn't exist and the click can't read a value. This panel renders
// the SAME canonical control (same id) above the bar; Cast then replays the button's own fire snippet, which
// now finds its input. No firing logic is reimplemented — the panel just supplies the missing input.

// The single class the panel's value-carrying control wears, so Cast can read it WITHOUT a canonical id
// (which would collide with the open PC-sheet spell table's identical input id).
const CHOICE_CTL_CLASS = 'ft-abchoice-ctl';
// SECONDARY value control (use_ability_variable_2): the damage-type pick on an ability whose
// PRIMARY control already carries the [x] amount (Elemental Attack: count + frost/fire/…).
const CHOICE_CTL2_CLASS = 'ft-abchoice-ctl2';
// ability_choice BRANCH picker. NOT a variable at all — it re-targets the cast itself: an
// ability_choice parent (Enhance Ability) has no effect layers of its own, so Cast fires the PICKED
// CHILD. That is the same semantics the PC-sheet spell table reaches by swapping the parent's cast
// button for the child's (query_ability_choice_inc.php); the action bar has no HTML to swap, so it
// retargets the fire call instead. Its own class keeps it out of the variable_1/variable_2 readers.
const CHOICE_BRANCH_CLASS = 'ft-abchoice-branch';

/** An ability_choice PARENT — a picker, not a castable ability. */
function isAbilityChoiceParent(ab: CasterItemNode | null | undefined): boolean {
    return !!ab && ftLayerEntries(ab, 'ability_choice').length > 0;
}

/** Branch options {id,name} for a choice parent, from a SERVER-RESOLVED list. The layer itself holds
 *  bare UUIDs and the branch defs are in NO client cache root, so ftItemNameById / catalogIdentityById
 *  both come back empty and a layer-derived select renders UUID labels (smoke A5/C2, 2026-07-28).
 *  Two server sources carry real names: the castable payload's `ability_choices` (spells) and the
 *  ability nest's `ability_choice_options` (abilities — the same key ability-row.ts reads). */
interface BranchOption {
    id: string;
    name: string;
    input_family?: string;
    spell_level?: number;
    options?: Array<{ id: string; name: string }>;
}

/** The branch picked in the OPEN panel. Module-level so it survives the repaint that rebuilds the
 *  control for the picked child — but it is deliberately a SINGLE entry, not a map: only one choice
 *  panel is ever open, and a per-parent map let a stale pick survive closing the panel and silently
 *  preselect itself when that parent was reopened (or opened by a DIFFERENT caster), so a slot
 *  re-click could cast a branch the current player never chose. Keyed by caster+parent and cleared
 *  whenever the panel changes. [[Codex review 2026-07-28 P2 #7]] */
let abChoiceBranch: { casterId: string; abilityId: string; branchId: string } | null = null;

function branchPickKeyMatches(casterId: string, abilityId: string): boolean {
    return !!abChoiceBranch
        && abChoiceBranch.casterId === casterId
        && abChoiceBranch.abilityId.toUpperCase() === String(abilityId).toUpperCase();
}
function pickedBranchId(casterId: string, abilityId: string): string {
    return branchPickKeyMatches(casterId, abilityId) ? (abChoiceBranch?.branchId ?? '') : '';
}

function abilityChoiceOptions(caster: CachedCaster, ab: CasterItemNode, abilityId: string): BranchOption[] {
    const out: BranchOption[] = [];
    const push = (rows: unknown): boolean => {
        if (!Array.isArray(rows) || rows.length === 0) return false;
        for (const row of rows) {
            const r = recordOf(row);
            if (!r) continue;
            const id = String(r.id ?? r.item_id ?? '');
            const name = String(r.name ?? r.item_name ?? '');
            if (id === '') continue;
            const branch: BranchOption = { id, name: name || id };
            if (typeof r.input_family === 'string') branch.input_family = r.input_family;
            if (r.spell_level !== undefined) branch.spell_level = Number(r.spell_level) || 0;
            if (Array.isArray(r.options)) {
                branch.options = r.options
                    .map((o) => recordOf(o))
                    .filter((o): o is Record<string, unknown> => !!o)
                    .map((o) => ({ id: String(o.id ?? ''), name: String(o.name ?? '') }))
                    .filter((o) => o.id !== '');
            }
            out.push(branch);
        }
        return out.length > 0;
    };
    // Spells: the row this ability occupies in computed.creature.castable.
    const want = String(abilityId || '').toUpperCase();
    const castable = caster?.computed?.creature?.castable as Record<string, unknown> | undefined;
    for (const pool of Object.values(castable ?? {})) {
        if (!Array.isArray(pool)) continue;
        for (const rawRow of pool) {
            const row = recordOf(rawRow);
            if (!row) continue;
            if (String(row.item_id ?? row.id ?? '').toUpperCase() !== want) continue;
            if (push(row.ability_choices)) return out;
        }
    }
    // Abilities: server-populated on the nest by ensure_ability_choice_options_on_ability_arr().
    if (push((ab as Record<string, unknown> | null)?.ability_choice_options)) return out;
    // Last resort — ids only. Better a usable picker with ugly labels than no picker at all.
    for (const entry of ftLayerEntries(ab, 'ability_choice')) {
        const id = choiceEntryUuid(entry);
        if (id !== '') out.push({ id, name: id });
    }
    return out;
}

/** uuid of a layer entry (shell nest {items:{item_id}} or bare ref). */
function choiceEntryUuid(e: unknown): string {
    if (typeof e === 'string') return e;
    return String(ftItemsCol(e, 'item_id') ?? recordOf(e)?.item_id ?? '');
}

type ActionInputFamily = AbilityButtonInputFamily;

/** Resolve the mechanic behind a canonical input id. `_spell_upcastable` is historically overloaded:
 * it can mean a real spell level, a variable target ceiling, or a variable charge payment. */
function actionInputFamily(ab: CasterItemNode | null | undefined, inputId: string): ActionInputFamily {
    return determineAbilityButtonInputFamily(ab, inputId);
}

function targetingInputFamilyIsNumeric(family: ActionInputFamily): family is AbilityTargetingNumericOptionKind {
    return family === 'spell-level' || family === 'variable-targets'
        || family === 'variable-charges' || family === 'variable-mana';
}

function targetingDispatchInputKind(family: ActionInputFamily): 'spell-level' | 'numeric' | 'damage-type' | 'other' {
    return family === 'spell-level' ? 'spell-level'
        : targetingInputFamilyIsNumeric(family) ? 'numeric'
            : family === 'damage-type' ? 'damage-type' : 'other';
}

function dynamicActionMaxTargets(ab: CasterItemNode, family: ActionInputFamily, rawValue: string): number | undefined {
    if (!targetingInputFamilyIsNumeric(family)) return undefined;
    return deriveAbilityTargetingNumericMaxTargets(ab, family, rawValue);
}

function variableChargeAvailable(caster: CachedCaster, ab: CasterItemNode): number | null {
    const poolId = uuidOf(ftLayerValue(ab, 'consumes_ability_charges'))
        || uuidOf(ftLayerValue(ab, 'add_charges_to_passive_ability'))
        || uuidOf(ftLayerValue(ab, 'add_charges_to_ability'));
    if (!poolId) return null;
    for (const passive of abilityEntriesOfType(caster, 'passive_ability')) {
        if (!passiveActive(passive) || abilityEntryId(passive).toUpperCase() !== poolId.toUpperCase()) continue;
        return sheetChargeBudget(caster, passive);
    }
    return 0;
}

/** The red "can't use" ring / dimmed fold-out socket: a charged ability whose investable budget is empty. The shared budget, not
 *  the raw pool, decides — a protected holder's empty pool still offers its max (sheetChargeBudget), exactly as the sheet's
 *  abilityChargeShortReason and the engine gate allow. Chargeless abilities ('NA' → NaN) are never short. */
function abilityPoolShort(caster: CachedCaster, entry: unknown): boolean {
    const max = Number(abilityEntryMaxCharges(entry, 'NA')), current = Number(abilityEntryCharges(entry, 'NA'));
    return Number.isFinite(max) && max > 0 && Number.isFinite(current) && sheetChargeBudget(caster, entry) <= 0;
}

/** `damageTypePick`: the choicebar's Sanguine Elemental / Transmuted pick (sanguineDmgPickOptionIds admits a volley parent through its
 *  projectile) — it rides the volley's variable_2, where the engine's next_spell_damage_type_choice reads it first (prepareNextSpell).
 *  `hydrated`: the one fetch-and-ask-again round an armed caster's unheld status definition gets (the resolver's own retry shape).
 *  `creatureOnly`: the first-click lane; area volleys retain their configured placement flow. */
function armInstantProjectileSpell(casterId: string, spellId: string, castLevel?: string, spellType = '', damageTypePick = '',
    options: { hydrated?: boolean; creatureOnly?: boolean; sourceId?: string } = {}): boolean {
    const caster = casterFromCache(casterId);
    const parent = caster ? findAbility(caster, spellId) : null;
    if (!caster || !parent) return false;
    const children = ftLayerEntries(parent, 'free_ability').map(uuidOf).filter(Boolean);
    if (children.length !== 1) return false;
    const read = (key: string) => ftLayerValue(parent, key);
    if (instantProjectileCount(ftItemsCol(parent, 'item_type'), read, 1, castLevel) === null) return false;
    const childId = children[0]!;
    const child = findAbility(caster, childId);
    if (options.creatureOnly && child && ftLayerValue(child, 'aoe_type')) return false;
    if (!child) {
        void window.fetchAndCacheObject?.(childId).then(() => {
            if (!findAbility(caster, childId)) { window.displayWarning?.('Projectile definition could not be loaded.'); return; }
            if (!armInstantProjectileSpell(casterId, spellId, castLevel, spellType, damageTypePick, options)) {
                // An unheld area child is now known: replay its real control to open the configured
                // area flow, rather than casting the Self parent or silently dropping the click.
                const button = ft3dDockAbilityButton(casterId, spellId);
                if (button) button.click();
                else window.displayWarning?.('Choose this projectile spell from the action bar to configure its area.');
            }
        });
        return true;
    }
    // Sanguine Boost (next_spell_upcast_levels) armed on the caster: the volley is cast at the BOOSTED level, so the pool offered is the
    // boosted one — the engine sizes it the same way (ability-use.ts, the volley block) and bills the chosen level; the FT3D stage reads
    // the same helpers for its area growth (FT3DAOEPlacement casterNextSpellArea). A caster with NO armed carrier takes the plain pool and
    // nothing is judged (nextSpellVolleyPool); an armed caster's unheld status definition is fetched once and the pool asked again, exactly
    // as the missing child above is — never an uncaught hand-back on the sheet.
    const nextSpellRead = { value: (row: Record<string, unknown> | null, key: string) => ftLayerValue(row, key),
        has: (row: Record<string, unknown> | null, key: string) => ftLayerValue(row, key) != null,
        statuses: (row: Record<string, unknown>) => ftCreaturePayloadStatuses(row) as Record<string, unknown>[],
        definition: (id: string) => (window.ftFindFullNestedItem?.(id) ?? null) as Record<string, unknown> | null };
    let judged: { count: number | null; boost: number };
    try {
        judged = nextSpellVolleyPool(nextSpellRead, caster as unknown as Record<string, unknown>, parent as unknown as Record<string, unknown>,
            ftLayerValue(child, 'ability_charges'), castLevel, spellType);
    } catch (error) {
        const missing = error instanceof HandBack ? notHeldDefinitionId(error.reason) : undefined;
        if (!missing) throw error;
        if (!options.hydrated) {
            void window.fetchAndCacheObject?.(missing).then(() => armInstantProjectileSpell(casterId, spellId, castLevel, spellType, damageTypePick, { ...options, hydrated: true }));
            return true;
        }
        window.displayWarning?.('A Sanguine buff could not be loaded; the volley is offered without it.');
        judged = { count: instantProjectileCount(ftItemsCol(parent, 'item_type'), read, ftLayerValue(child, 'ability_charges'), castLevel), boost: 0 };
    }
    const count = judged.count;
    if (count === null) return false;
    const instant: InstantProjectileCast = { parentId: spellId,
        variable1: spellType || castLevel || String(ftLayerValue(parent, 'spell_level') ?? ''),
        // Grants retain their source in variable_2. Other marker casts carry a level;
        // ordinary casts carry the Sanguine damage-type pick there.
        ...(isSpellGrantMode(spellType) ? (options.sourceId ? { variable2: options.sourceId } : {})
            : spellType && castLevel ? { variable2: castLevel }
            : !spellType && FT_TARGET_UUID_RE.test(damageTypePick) ? { variable2: damageTypePick.toUpperCase() } : {}) };
    const pool: ProjectilePool = { remaining: count, total: count, instant };
    abChoice = null;
    abChoiceBranch = null;
    if (ftLayerValue(child, 'aoe_type')) {
        const button = document.createElement('button');
        Object.assign(button.dataset, { ability_id: childId, ftCasterId: casterId, item_type: 'free_ability',
            bsOriginalTitle: String(ftItemsCol(parent, 'item_name') ?? 'Projectiles'), ftInstantProjectilePool: JSON.stringify(pool) });
        for (const key of ['aoe_type', 'aoe_distance', 'aoe_height', 'aoe_depth', 'range', 'persistent_aoe', 'aoe_follows_target', 'arcing_trajectory', 'ability_does_not_effect_inventory']) {
            const value = ftLayerValue(child, key);
            if (value !== null && value !== undefined) button.dataset[key] = String(value);
        }
        window.aoeManager?.startFt3d?.(button);
    } else {
        const profile = effectiveAbilityTargetingProfile(caster, child, '', count);
        if (!profile.supported) { window.displayWarning?.('Projectile targeting is unavailable.'); return true; }
        profile.profile.projectilePool = pool;
        profile.profile.maxTargets = count;
        const inputId = determineAbilityButtonInputId(parent);
        const family = actionInputFamily(parent, inputId);
        const invocation: ActiveAbilityTargetingIntent['invocation'] = !spellType && targetingInputFamilyIsNumeric(family)
            ? { kind: 'variable-1-number', abilityId: spellId, inputId,
                value: Number(castLevel || numericActionInputSpec(caster, parent, family).value) }
            : { kind: 'plain', abilityId: childId };
        cancelActiveAoePlacement();
        armCreatureTargeting(casterId, childId, caster, child, profile.profile, invocation);
    }
    repaintActionBars(casterId);
    return true;
}

// Generated sheet/quick-cast invocations share the dock's arming path. The bridge keeps
// this bundle's lifecycle unique; confirmed volleys go straight to ftUseProjectileAssignments.
window.ftArmInstantProjectileSpell = (casterId, spellId, castLevel, spellType, damageTypePick, sourceId) =>
    ft3dDockPresentFor(casterId) && armInstantProjectileSpell(casterId, spellId, castLevel, spellType, damageTypePick,
        { creatureOnly: true, sourceId });

window.ftResumeProjectileSpell = (casterId: string, spellId: string, onlyIfIdle = false): boolean => {
    // A late grant cannot replace a newer action. Read the live draft, not cached/hidden sheet DOM.
    if (onlyIfIdle && (abilityTargetingLifecycle.current() || window.aoeManager?.ft3dActive
        || (abChoice && abChoice.abilityId !== spellId))) return true;
    const caster = casterFromCache(casterId);
    const parent = caster ? findAbility(caster, spellId) : null;
    if (!caster || !parent || !ftLayerHas(parent, 'spell_upcast_additional_projectiles_created')) return false;
    if (instantProjectileCount(ftItemsCol(parent, 'item_type'), key => ftLayerValue(parent, key), 1) !== null) return false;
    const children = ftLayerEntries(parent, 'free_ability').map(uuidOf).filter(Boolean);
    // An ambiguous multi-kind grant needs its authored choice UI.
    if (children.length !== 1) return false;
    const childId = children[0];
    if (!childId) return false;
    const pool = window.ftGetProjectilePool?.(casterId, childId);
    const child = findAbility(caster, childId);
    if (!pool?.remaining || !child) return false;
    if (!ftLayerValue(child, 'aoe_type')) return ftDispatchAbilityClick(casterId, childId);
    const button = document.createElement('button');
    button.dataset.ability_id = childId;
    button.dataset.ftCasterId = casterId;
    button.dataset.item_type = 'free_ability';
    button.dataset.bsOriginalTitle = String(ftItemsCol(child, 'item_name') ?? 'Projectiles');
    for (const key of ['aoe_type', 'aoe_distance', 'aoe_height', 'aoe_depth', 'range', 'persistent_aoe', 'aoe_follows_target', 'arcing_trajectory', 'ability_does_not_effect_inventory']) {
        const value = ftLayerValue(child, key);
        if (value !== null && value !== undefined) button.dataset[key] = String(value);
    }
    window.aoeManager?.startFt3d?.(button);
    return !!window.aoeManager?.ft3dActive;
};

window.ftGetProjectilePool = (casterId: string, abilityId: string): ProjectilePool | null => {
    const caster = casterFromCache(casterId);
    if (!caster) return null;
    const entry = abilityEntriesOfType(caster, 'free_ability')
        .find(value => abilityEntryId(value).toUpperCase() === abilityId.toUpperCase());
    const sourceId = String(recordOf(entry)?.source ?? '').toUpperCase();
    if (!entry || !sourceId) return null;
    const source = ftCreatureStatuses(caster, 'target').find(value =>
        String(recordOf(value)?.status_id ?? '').toUpperCase() === sourceId);
    // Compiled status snapshots embed the granting item and omit status_item_id.
    // Raw status deltas still carry that column, so accept both authoritative shapes.
    const statusItem = itemNodeOf(recordOf(source)?.status_arr);
    const parentId = String(recordOf(source)?.status_item_id ?? statusItem?.items?.item_id ?? '');
    const parent = statusItem?.items ? statusItem : parentId ? findAbility(caster, parentId) : null;
    // Follow the actual grant, never guess from the child's display name or charge count.
    if (!parent || !ftLayerHas(parent, 'spell_upcast_additional_projectiles_created')) return null;
    if (!ftLayerEntries(parent, 'free_ability').some(value => uuidOf(value).toUpperCase() === abilityId.toUpperCase())) return null;
    const remaining = Number(abilityEntryCharges(entry, 0));
    const total = Number(abilityEntryMaxCharges(entry, remaining));
    if (!Number.isInteger(remaining) || remaining < 0 || remaining > 1000) return null;
    return { remaining, total: Math.max(remaining, Number.isInteger(total) ? total : remaining) };
};

function numericActionInputSpec(
    caster: CachedCaster,
    ab: CasterItemNode,
    family: ActionInputFamily,
): { label: string; min: number; max: number | ''; value: number } {
    if (family === 'spell-level') {
        const level = abilityCastBaseLevel(ab);
        return { label: 'Cast at level', min: level, max: targetingMaxSpellLevel(caster, level, ab), value: level };
    }
    if (family === 'variable-mana') {
        const mana = Math.max(0, Math.trunc(sheetManaBudget(caster)));
        const min = mana > 0 ? 1 : 0;
        return { label: 'Mana invested', min, max: mana, value: min };
    }
    if (family === 'variable-charges') {
        const available = variableChargeAvailable(caster, ab);
        const value = available ?? 0;
        return { label: 'Charges spent', min: 0, max: available === null ? '' : available, value };
    }
    if (family === 'variable-targets') {
        const available = variableChargeAvailable(caster, ab);
        const min = available === 0 ? 0 : 1;
        return {
            label: String(ftLayerValue(ab, 'consumes_charges_qty') ?? '') === '[x]' ? 'Targets / charges' : 'Targets',
            min,
            max: available === null ? '' : available,
            value: min,
        };
    }
    return { label: 'Amount', min: 0, max: '', value: 1 };
}

type ActionOptionValues = { primary: string; secondary: string };
function actionOptionValueMap(): Map<string, ActionOptionValues> {
    if (!(window.ftAbOptionValues instanceof Map)) window.ftAbOptionValues = new Map<string, ActionOptionValues>();
    return window.ftAbOptionValues;
}
function actionOptionKey(abilityId: string): string { return String(abilityId).toUpperCase(); }
function initialActionOptionValues(caster: CachedCaster, ab: CasterItemNode, inputId: string): ActionOptionValues {
    const family = actionInputFamily(ab, inputId);
    let primary = '';
    if (family === 'damage-type') primary = choiceEntryUuid(ftLayerEntries(ab, 'damage_type')[0]);
    else if (family !== 'none' && family !== 'summon' && family !== 'shapeshift') {
        primary = String(numericActionInputSpec(caster, ab, family).value);
    }
    const secondary = family !== 'damage-type' && ftLayerEntries(ab, 'damage_type').length > 1
        ? choiceEntryUuid(ftLayerEntries(ab, 'damage_type')[0]) : '';
    return { primary, secondary };
}
function currentActionOptionValues(caster: CachedCaster, ab: CasterItemNode, inputId: string, abilityId: string): ActionOptionValues {
    return actionOptionValueMap().get(actionOptionKey(abilityId))
        ?? initialActionOptionValues(caster, ab, inputId);
}
interface ActionBarChoiceOption {
    id: string;
    name: string;
}

type SummonPreview = { name: string; pictureUrl: string; widthFt: number; heightFt: number };

function ft3dDockAbilityButton(casterId: string, abilityId: string): HTMLElement | null {
    const cid = String(casterId).toUpperCase();
    const aid = String(abilityId).toUpperCase();
    return Array.from(document.querySelectorAll<HTMLElement>(
        '[data-ft3d-actor-dock] [data-ft-caster-id] .btn_ability_ranges[data-ability_id]',
    )).find(button => (
        String(button.closest<HTMLElement>('[data-ft-caster-id]')?.dataset.ftCasterId || '').toUpperCase() === cid
        && String(button.dataset.ability_id || '').toUpperCase() === aid
    )) ?? null;
}

function loadSummonPreview(creatureId: string, caster: CachedCaster | null = null): SummonPreview {
    const id = String(creatureId).trim();
    const cache = window.FateTide?.objectCache as Map<string, unknown> | undefined;
    let creature: unknown = cache?.get(id) ?? cache?.get(id.toUpperCase()) ?? null;
    // Adoption holds summon definitions in the encounter/caster catalogs. Arming
    // uses the same synchronous reader as the ability dock, including lean refs.
    if (!creature) creature = findAbility(caster, id);
    const fallbackName = String(typeof ftItemNameById === 'function' ? ftItemNameById(id) ?? '' : '').trim();
    let name = String(creature ? ftItemsCol(creature, 'item_name') ?? '' : '').trim() || fallbackName || 'Summon';
    let widthFt = 5;
    let heightFt = 5;
    let pictureUrl = '';
    if (creature) {
        try {
            const markup = renderCreatureThumbnail(creature, { use_picture_thumbnail: false });
            const template = document.createElement('template');
            template.innerHTML = String(markup || '').trim();
            const token = template.content.querySelector<HTMLElement>('[data-width_ft]');
            const renderedWidth = Number(token?.dataset.width_ft);
            const renderedHeight = Number(token?.dataset.height_ft);
            if (renderedWidth > 0) widthFt = renderedWidth;
            if (renderedHeight > 0) heightFt = renderedHeight;
            pictureUrl = String(
                template.content.querySelector<HTMLImageElement>('img[src]')?.getAttribute('src')
                ?? template.content.querySelector<HTMLVideoElement>('video[poster]')?.getAttribute('poster')
                ?? '',
            ).trim();
        } catch {
            // The canonical renderer can be unavailable during very early bootstrap; SizeType is the fallback.
        }
        if (widthFt === 5 && heightFt === 5) {
            const sizeId = String(ftLayerValue(creature, 'size') ?? '').toUpperCase();
            const size = SIZE_TYPES.find(entry => String(entry.item_id).toUpperCase() === sizeId);
            if (size && Number(size.base_size) > 0) widthFt = heightFt = Number(size.base_size);
        }
        if (!pictureUrl && typeof window.ftPrimaryPictureOf === 'function') {
            const picture = recordOf(window.ftPrimaryPictureOf(creature));
            pictureUrl = String(picture?.picture_url ?? picture?.pic_url ?? '').trim();
        }
        name = name || 'Summon';
    }
    return { name, pictureUrl, widthFt, heightFt };
}

/** RollType::getId('summon_roll') (additions.php). */
const SUMMON_ROLL_TYPE_ID = 'E5016E3A-6A12-2287-5F48-5CFAC863E967';

/** How many summon spots a cast asks for (owner 2026-09-16). A Summon Roll with no dice summons exactly its mod
 *  (Summon Skeletal Minions: 3). A rolled Summon Roll (dice × die + mod, e.g. 1d4) asks for up to its MAXIMUM and
 *  may cast early — the roll uses the spots in order. A tagged mod or any other summon → 1 spot. Janus spreads any
 *  body beyond the chosen spots a body-width apart (janus_summon_desired_point). */
function summonCountPlan(ability: CasterItemNode | null | undefined): { count: number; rolled: boolean } {
    const single = { count: 1, rolled: false };
    if (!ability || String(ftLayerValue(ability, 'roll_type') ?? '').toUpperCase() !== SUMMON_ROLL_TYPE_ID) return single;
    const mod = String(ftLayerValue(ability, 'mod') ?? '').trim();
    const modValue = /^-?\d+$/.test(mod) ? Number(mod) : mod === '' ? 0 : null;
    const dice = Number(ftLayerValue(ability, 'dice')) || 0;
    const die = Number(ftLayerValue(ability, 'die')) || 0;
    if (modValue === null) return single;
    if (dice > 0 && die > 0) {
        const max = Math.min(50, dice * die + modValue);
        return max > 1 ? { count: max, rolled: true } : single;
    }
    return modValue > 1 ? { count: Math.min(50, modValue), rolled: false } : single;
}

/** The ability's placement button when its dock button is no longer mounted. A spell cast from the ability-island
 *  fly-up loses its medallion as soon as the option panel opens (claimActionBarOverlayLane closes the island), so the
 *  summon Confirm found no button and fell back to a targetless cast — "has a range and requires a target" (owner
 *  2026-10-07, Summon Woodland Being). Built only while this caster's Scene dock is up; wrapped so the placement still
 *  reads the caster from closest('[data-ft-caster-id]'). */
function detachedDockAbilityButton(
    casterId: string, caster: CachedCaster | null, ability: CasterItemNode | null,
): HTMLElement | null {
    if (!caster || !ability?.items || !ft3dDockPresentFor(casterId)) return null;
    const host = document.createElement('div');
    host.dataset.ftCasterId = casterId;
    host.innerHTML = generateAbilityButton(ability, caster);
    return host.querySelector<HTMLElement>('[data-ability_id]');
}

async function armFt3dSummonPlacement(
    casterId: string,
    abilityId: string,
    creatureId: string,
    cast: () => void,
    sourceButton: HTMLElement | null = null,
    abilityDefinition: CasterItemNode | null = null,
): Promise<boolean> {
    const caster = casterFromCache(casterId);
    const ability = abilityDefinition ?? (caster ? findAbility(caster, abilityId) : null);
    const button = ft3dDockAbilityButton(casterId, abilityId) ?? sourceButton
        ?? detachedDockAbilityButton(casterId, caster, ability);
    const manager = window.aoeManager;
    if (!button || typeof manager?.startFt3dSummon !== 'function') return false;
    const plan = summonCountPlan(ability);
    const preview = loadSummonPreview(creatureId, caster);
    const active = abilityTargetingLifecycle.current();
    if (active) cancelAbilityTargeting(false);
    mirrorAbilityTargetSelection(null, 'ft3d-summon-placement-arm');
    manager.startFt3dSummon(button, {
        creatureId,
        ...preview,
        count: plan.count,
        countRolled: plan.rolled,
        cast,
    });
    repaintActionBars(casterId);
    return true;
}

/** Character-sheet-table parity for damage choices: the canonical table renderer emits a static
 *  Tom Select whose options carry their icon HTML in data-icon and keep the visible row icon-only.
 *  Keep the action-bar control id-less (the table may already own the canonical input id), but use
 *  the same option model/classes so every fold-up gets the same picker and accessible tooltips. */
function actionBarDamageTypeSelect(
    options: ActionBarChoiceOption[],
    selectedValue: string,
    extraClass = '',
    extraAttrs = '',
): string {
    const selectedUpper = String(selectedValue).toUpperCase();
    let opts = '';
    for (const option of options) {
        if (!option.id) continue;
        const selected = option.id.toUpperCase() === selectedUpper ? ' selected' : '';
        const icon = iconMap(option.id, 25, 25, false);
        opts += `<option value="${escapeHtml(option.id)}" data-icon="${escapeHtml(icon)}" title="${escapeHtml(option.name || 'Damage Type')}"${selected}></option>`;
    }
    const classes = ['ts-select', 'ts-auto-width', 'icon-only-damage-select', 'ft-ab-damage-select', extraClass]
        .filter(Boolean).join(' ');
    // The dock is bottom-anchored and scroll-clipped. Body-mount + upward placement lets the menu
    // overlay the scene instead of increasing the action-bar frame's scroll height.
    return `<select class="${classes}" title="Choose Damage Type" data-ts-dropdown-parent="body" data-ts-dropdown-direction="up"${extraAttrs}>${opts}</select>`;
}

function damageTypeOptionsFromAbility(ab: CasterItemNode): ActionBarChoiceOption[] {
    const options: ActionBarChoiceOption[] = [];
    for (const entry of ftLayerEntries(ab, 'damage_type')) {
        const id = choiceEntryUuid(entry);
        if (!id) continue;
        let name = String(ftItemsCol(entry, 'item_name') ?? '');
        if (name === '' && typeof ftItemNameById === 'function') name = String(ftItemNameById(id) ?? '');
        options.push({ id, name: name || id });
    }
    return options;
}

/** Text choice twin of the character-sheet summon/shapeshift picker. Static resolved options keep
 *  this out of the AJAX path, while Tom Select supplies search and consistent keyboard behavior.
 *  Action-bar menus body-mount and open upward so no picker contributes to the dock's scroll size. */
function actionBarTextChoiceSelect(
    options: ActionBarChoiceOption[],
    selectedValue: string,
    placeholder: string,
    extraClass: string,
): string {
    const selectedUpper = String(selectedValue).toUpperCase();
    let opts = `<option value="">${escapeHtml(placeholder)}</option>`;
    for (const option of options) {
        if (!option.id) continue;
        const selected = option.id.toUpperCase() === selectedUpper ? ' selected' : '';
        opts += `<option value="${escapeHtml(option.id)}"${selected}>${escapeHtml(option.name || option.id)}</option>`;
    }
    return `<select class="ts-select ts-auto-width ft-ab-choice-select ${extraClass}"`
        + ` placeholder="${escapeHtml(placeholder)}" title="${escapeHtml('Choose ' + placeholder)}"`
        + ` data-show-icons="false" data-ts-dropdown-parent="body" data-ts-dropdown-direction="up">${opts}</select>`;
}

/** A summon / shapeshift / damage-type <select> (class-tagged, no id). Leading blank → the player must pick. */
function abChoiceSelect(
    ab: CasterItemNode,
    layer: string,
    requirePick = true,
    ctlClass: string = CHOICE_CTL_CLASS,
    selectedValue = '',
): string {
    if (layer === 'damage_type') {
        return actionBarDamageTypeSelect(damageTypeOptionsFromAbility(ab), selectedValue, ctlClass);
    }
    const options: ActionBarChoiceOption[] = [];
    for (const e of ftLayerEntries(ab, layer)) {
        const uid = choiceEntryUuid(e);
        if (!uid) continue;
        // Bare-UUID refs (damage_type entries ship as plain UUID strings) carry no nested
        // item_name — resolve via the session catalog before degrading to the raw UUID.
        let nm = String(ftItemsCol(e, 'item_name') ?? '');
        if (nm === '' && typeof ftItemNameById === 'function') {
            nm = String(ftItemNameById(uid) ?? '');
        }
        if (nm === '') nm = uid;
        options.push({ id: uid, name: nm });
    }
    const placeholder = layer.includes('shapeshift') ? 'Shapeshift'
        : layer.includes('summon') ? 'Summon'
            : requirePick ? 'Choose' : '';
    return actionBarTextChoiceSelect(options, selectedValue, placeholder, ctlClass);
}

/** Damage-type selector embedded in the live creature-targeting fold-up. Its selected value comes from
 * the targeting intent so unrelated repaints cannot reset the cast to the first authored type. */
function targetingDamageTypeSelect(
    ab: CasterItemNode,
    selectedValue: string,
    role: 'damage-type' | 'secondary-damage-type' = 'damage-type',
): string {
    return actionBarDamageTypeSelect(
        damageTypeOptionsFromAbility(ab),
        selectedValue,
        '',
        ` data-ft-targeting-choice="${role}"`,
    );
}
// ── Sanguine Elemental/Transmuted (next_spell_damage_type_choice) ────────────────────────────────
// The predicate + allowed-set reader live in ../sanguine-damage-type-pick so the action bar, the
// PC-sheet spell table and the AOE confirm all ask the SAME question (they drifted when this logic
// was private to this file — the buff only ever surfaced here). The pick rides use_ability_variable_2
// and the SERVER re-authorizes it, so this select is display-only sugar.

/** A cast whose use_ability_variable_2 is already spoken for by its SOURCE row — a consumable
 *  (data-nested-inventory-id) or an item-granted spell (data-source, fired as
 *  useAbilityVariable2(id, spellType, source)). The Sanguine pick can't share that slot, so the
 *  picker must never be offered there: the confirm would replace the source with the damage-type
 *  UUID and a charged cast would stop consuming its item charges. A MARKER cast with no source
 *  (plain at-will / innate-per-rest) is fine — the confirm keeps the marker in variable_1 and the
 *  pick rides variable_2. [[Codex review 2026-07-24 P1 — data-source was previously unchecked]] */
function abBtnItemSourced(btn: HTMLElement): boolean {
    return !!btn.getAttribute('data-nested-inventory-id') || !!btn.getAttribute('data-source');
}

/** The player's damage-type pick, per ability, so a REPAINT doesn't silently reset it. The AOE panel
 *  repaints on every upcast step (and on the ritual toggle), which would otherwise rebuild the select
 *  at its first option and cast the wrong type. Cleared when the choice bar closes (cast or cancel).
 *  [[Codex review 2026-07-24 P2]] */
const sanguineDmgPicks = new Map<string, string>();

/** The secondary damage-type <select> for the Sanguine buff's offered set. Tagged CHOICE_CTL2_CLASS so
 *  the confirm reads it as variable_2, and stamped data-ft-sanguine-dmg-for so the AOE confirm (which
 *  has no choicebar of its own to read) can find it. Defaults to the first option (no blank flash-gate). */
function damageTypeChoiceSelectHtml(ids: string[], abilityId: string): string {
    if (ids.length === 0) return '';
    const aidU = String(abilityId).toUpperCase();
    const held = sanguineDmgPicks.get(aidU);
    return actionBarDamageTypeSelect(
        ids.map((id) => ({ id, name: sanguineDamageTypeName(id) })),
        held || ids[0] || '',
        CHOICE_CTL2_CLASS,
        ` data-ft-sanguine-dmg-for="${escapeHtml(aidU)}"`,
    );
}

/** Remember the picked ability_choice BRANCH and repaint, so the panel can rebuild its cast-time
 *  control from that branch (their input families genuinely differ — see branchChoiceControl). */
function onAbilityChoiceBranchChange(e: Event): void {
    const el = e.target as HTMLElement | null;
    if (!el || typeof el.closest !== 'function') return;
    const sel = el.closest<HTMLSelectElement>(`.ft-action-choicebar select.${CHOICE_BRANCH_CLASS}`);
    if (!sel || !abChoice) return;
    abChoiceBranch = { casterId: abChoice.casterId, abilityId: abChoice.abilityId, branchId: String(sel.value || '') };
    // The picked CHILD is held by no caster bucket (a menta branch, an un-held spell branch): intern its definition so the confirm's
    // findAbility(child) resolves — creature targeting / area placement / the browser lane need it (2026-10-07, Psion v12 mentas).
    const branchId = String(sel.value || '');
    if (/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(branchId)) {
        const repaintCaster = abChoice.casterId;
        void window.fetchAndCacheObject?.(branchId).then(() => repaintActionBars(repaintCaster), () => { /* the confirm falls back to the legacy fire */ });
    }
    const values = actionOptionValueMap().get(actionOptionKey(abChoice.abilityId));
    if (values) actionOptionValueMap().set(actionOptionKey(abChoice.abilityId), { ...values, primary: '' });
    repaintActionBars(abChoice.casterId);
}

/** Persist ordinary fold-up controls across action-bar/AOE repaints. AOE placement repaints whenever its
 * staged target set changes, so DOM-only values would otherwise jump back to their defaults mid-cast. */
function onActionOptionValueChange(e: Event): void {
    if (!abChoice) return;
    const el = e.target instanceof Element
        ? e.target.closest<HTMLInputElement | HTMLSelectElement>(`.ft-action-choicebar .${CHOICE_CTL_CLASS}, .ft-action-choicebar .${CHOICE_CTL2_CLASS}`)
        : null;
    if (!el) return;
    const key = actionOptionKey(abChoice.abilityId);
    const current = actionOptionValueMap().get(key) ?? { primary: '', secondary: '' };
    actionOptionValueMap().set(key, el.classList.contains(CHOICE_CTL2_CLASS)
        ? { ...current, secondary: String(el.value ?? '') }
        : { ...current, primary: String(el.value ?? '') });
}

/** Remember the pick so the next repaint re-renders it as selected. */
function onSanguineDmgPickChange(e: Event): void {
    const el = e.target as HTMLElement | null;
    if (!el || typeof el.closest !== 'function') return;
    const sel = el.closest<HTMLSelectElement>('select[data-ft-sanguine-dmg-for]');
    if (!sel) return;
    const aidU = String(sel.getAttribute('data-ft-sanguine-dmg-for') || '').toUpperCase();
    if (aidU) sanguineDmgPicks.set(aidU, String(sel.value || ''));
}

/** A −/number/+ stepper (class-tagged, no id); the +/− buttons step their sibling input. */
function abStepper(min: number, max: number | '', value: number): string {
    const maxAttr = max === '' ? '' : ` max="${max}"`;
    return `<div class="ft-ab-choice-stepper">`
        + `<button type="button" class="ft-ab-choice-step" data-ft-ab-action="choice-step" data-ft-delta="-1">&minus;</button>`
        + `<input class="ft-ab-choice-num ${CHOICE_CTL_CLASS}" type="number" min="${min}"${maxAttr} value="${value}">`
        + `<button type="button" class="ft-ab-choice-step" data-ft-ab-action="choice-step" data-ft-delta="1">&plus;</button>`
        + `</div>`;
}
/** Small "what does upcasting do?" badges for the upcast control — one per upcast effect the spell carries
 *  (damage dice, +targets, +projectiles, +AOE ft, +duration), each showing the PER-LEVEL amount with an icon +
 *  tooltip. Mirrors the spell-form upcast layers (base_spell_form_inc.php) and the list-display readout. '' when
 *  the spell declares no upcast effects. [[owner 2026-06-27]] */
function upcastEffectBadges(ab: CasterItemNode, levelMultiplier = 1): string {
    if (!ab || levelMultiplier <= 0) return '';
    const sval = (k: string): string => {
        const v = ftLayerValue(ab, k);
        return v === undefined || v === null ? '' : String(v).trim();
    };
    const multiplied = (k: string, fallback = 0): number =>
        (Number(ftLayerValue(ab, k)) || fallback) * levelMultiplier;
    const badge = (icon: string, text: string, tipText: string): string =>
        `<span class="ft-upeffect">${icon ? iconMap(icon, 13, 13, tipText) : ''}<span>${escapeHtml(text)}</span></span>`;
    const damageTypes = ftLayerEntries(ab, 'damage_type').map(entry => {
        const id = choiceEntryUuid(entry);
        const nestedName = String(ftItemsCol(entry, 'item_name') ?? '').trim();
        return nestedName || (id && typeof ftItemNameById === 'function' ? String(ftItemNameById(id) ?? '').trim() : '');
    }).filter(Boolean);
    const damageTypeLabel = [...new Set(damageTypes)].join('/');
    const out: string[] = [];
    // Damage: {dice_upcast}d{die_upcast} [+ mod_upcast] — same shape the list-display upcast readout uses.
    if (ftLayerHas(ab, 'die_upcast')) {
        let dmg = `+${multiplied('dice_upcast', 1)}d${sval('die_upcast')}`;
        if (ftLayerHas(ab, 'mod_upcast')) dmg += `+${multiplied('mod_upcast')}`;
        out.push(badge('damage_roll', `${dmg}${damageTypeLabel ? ` ${damageTypeLabel}` : ''}`, 'Extra damage from this upcast'));
    } else if (ftLayerHas(ab, 'mod_upcast')) {
        out.push(badge('damage_roll', `+${multiplied('mod_upcast')}${damageTypeLabel ? ` ${damageTypeLabel}` : ''}`, 'Extra damage from this upcast'));
    }
    if (ftLayerHas(ab, 'spell_upcast_additional_targets_int')) out.push(badge('spell_upcast_additional_targets_int', `+${multiplied('spell_upcast_additional_targets_int')}`, 'Additional targets from this upcast'));
    if (ftLayerHas(ab, 'spell_upcast_additional_projectiles_created')) out.push(badge('spell_upcast_additional_projectiles_created', `+${multiplied('spell_upcast_additional_projectiles_created')}`, 'Additional projectiles from this upcast'));
    if (ftLayerHas(ab, 'spell_upcast_increase_aoe_ft')) out.push(badge('spell_upcast_increase_aoe_ft', `+${multiplied('spell_upcast_increase_aoe_ft')} ft`, 'Larger area from this upcast'));
    if (ftLayerHas(ab, 'spell_upcast_duration_type') && ftLayerHas(ab, 'spell_upcast_duration_int')) out.push(badge('spell_upcast_duration_type', `+${multiplied('spell_upcast_duration_int')} ${sval('spell_upcast_duration_type')}`, 'Longer duration from this upcast'));
    if (out.length === 0) return '';
    return `<div class="ft-action-choicebar__upeffects">${out.join('')}</div>`;
}

/** The control + label for a PICKED ability_choice BRANCH, driven by the server-resolved
 *  input_family / spell_level / options on the branch row (the client can't read the child's own
 *  layers — its def is catalogized nowhere the client can see). Mirrors buildChoiceControl's
 *  families; falls back to no control when the branch declares none. */
function branchChoiceControl(caster: CachedCaster, branch: BranchOption, selectedValue = ''): { label: string; html: string } {
    const family = String(branch.input_family ?? '');
    if (family === '') return { label: '', html: '' };
    const selectFrom = (label: string): { label: string; html: string } => {
        return {
            label,
            html: actionBarTextChoiceSelect(branch.options ?? [], selectedValue, label, CHOICE_CTL_CLASS),
        };
    };
    if (family === 'spell_upcastable') {
        const lvl = Number(branch.spell_level) || 0;
        return { label: 'Cast at level', html: abStepper(lvl, '', lvl) };
    }
    if (family === 'user_choice_1_shapeshift') return selectFrom('Transform into');
    if (family === 'user_choice_1_summon') return selectFrom('Summon');
    if (family === 'user_choice_1_damage_type') {
        return {
            label: 'Damage type',
            html: actionBarDamageTypeSelect(branch.options ?? [], selectedValue, CHOICE_CTL_CLASS),
        };
    }
    if (family === 'variable_mana_dice') {
        // The shared mana budget (numericActionInputSpec's twin): a protected holder at 0 mana still invests up to its Spirit.
        const mana = Math.max(0, Math.trunc(sheetManaBudget(caster)));
        const min = mana >= 1 ? 1 : 0;
        return { label: 'Mana invested', html: abStepper(min, mana, min) };
    }
    return { label: 'Amount', html: abStepper(0, '', 1) };
}

/** The control + its label for a given canonical input-id family, built from the ability's layers. */
function buildChoiceControl(
    caster: CachedCaster,
    ab: CasterItemNode,
    inputId: string,
    abilityId = '',
    selectedValue = '',
): { label: string; html: string } {
    if (!inputId) return { label: '', html: '' };   // a pure ritual spell (no extra value) — buttons only
    const family = actionInputFamily(ab, inputId);
    if (family === 'spell-level') {
        const lvl = abilityCastBaseLevel(ab);
        const maxLvl = targetingMaxSpellLevel(caster, lvl, ab);
        const held = upcastLevelMap().get(String(abilityId).toUpperCase());
        const selected = Math.max(lvl, Math.min(maxLvl, Number(held) || lvl));
        // The effect badges describe the selected delta, not a misleading permanent +1-level preview.
        return { label: 'Cast at level', html: abStepper(lvl, maxLvl, selected)
            + upcastEffectBadges(ab, selected - lvl) };
    }
    if (family === 'shapeshift') {
        return { label: 'Transform into', html: abChoiceSelect(ab, 'user_choice_1_shapeshift', true, CHOICE_CTL_CLASS, selectedValue) };
    }
    if (family === 'summon') {
        return { label: 'Summon', html: abChoiceSelect(ab, 'user_choice_1_summon', true, CHOICE_CTL_CLASS, selectedValue) };
    }
    if (family === 'damage-type') {
        return { label: 'Damage type', html: abChoiceSelect(ab, 'damage_type', false, CHOICE_CTL_CLASS, selectedValue) };
    }
    // status_effect_applied_is_caster_choice (Psion Override Emotion, 2026-10-07): the pick rides variable_1; blank = the first entry.
    if (family === 'status-effect') {
        return { label: 'Status effect', html: actionBarTextChoiceSelect(statusChoiceOptions(ab), selectedValue, 'Status Effect', CHOICE_CTL_CLASS) };
    }
    if (family === 'variable-mana' || family === 'variable-charges' || family === 'variable-targets' || family === 'amount') {
        const spec = numericActionInputSpec(caster, ab, family);
        const held = Number(selectedValue);
        const selected = selectedValue.trim() !== '' && Number.isFinite(held)
            ? Math.max(spec.min, spec.max === '' ? held : Math.min(spec.max, held))
            : spec.value;
        return { label: spec.label, html: abStepper(spec.min, spec.max, selected) };
    }
    return { label: '', html: '' };
}

/** The set of ability ids the player has armed for ritual casting. Shared with the AOE confirm (pre-template
 *  bundle) via window — the Ritual choice lives in the OPTION system, the AOE flow only READS it. */
function ritualArmedSet(): Set<string> {
    if (!(window.ftAbRitualArmed instanceof Set)) window.ftAbRitualArmed = new Set<string>();
    return window.ftAbRitualArmed;
}

/** Upcast LEVEL the player chose in the AOE fold-up, keyed by UPPER ability id. Shared with AOEManager
 *  and FT3DAOEPlacement for preview sizing and casting. Only one AOE
 *  placement is active at a time, so this holds at most one live entry. [[owner 2026-06-27 AOE upcast bridge]] */
function upcastLevelMap(): Map<string, number> {
    if (!(window.ftAbUpcastLevel instanceof Map)) window.ftAbUpcastLevel = new Map<string, number>();
    return window.ftAbUpcastLevel;
}

/** Quick Ritual availability: the caster's mana discount for a normal-time cast of a ritual spell.
 *  Returns the discount (>0) when an ACTIVE charged passive carrying ritual_spell_mana_discount
 *  (e.g. Quick Ritual, granted by Ritual Expert) can pay its guarded charge cost; 0 otherwise.
 *  This offers the checkbox; the cast lane still refuses Quick Ritual until its browser contract is ported. */
function quickRitualDiscount(caster: CachedCaster): number {
    for (const pa of abilityEntriesOfType(caster, 'passive_ability')) {
        if (!passiveActive(pa)) continue;
        const nest = resolveAbilityNest(pa, caster);
        if (!nest || !ftLayerHas(nest, 'ritual_spell_mana_discount')) continue;
        if ((Number(abilityEntryCharges(pa, 0)) || 0) < sheetChargeCost(caster, 1)) continue;
        const d = Number(ftLayerValue(nest, 'ritual_spell_mana_discount')) || 1;
        if (d > 0) return d;
    }
    return 0;
}

/** Slow-cast set mirrored from casting_roll.php: a NORMAL cast with one of these casting times is
 *  DELAYED (casting status now, mana spent later at finish_casting) — the Quick Ritual opt-in can't
 *  ride to the finish invocation, so the checkbox must not be offered. */
const QR_SLOW_CAST_TIMES = new Set(['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours']);

function renderAbilityTargetCard(intentId: number, entityId: string): string {
    const token = FT_TARGET_UUID_RE.test(entityId)
        ? document.querySelector<HTMLElement>(`.encounter-creature[data-encounter_creature_id="${entityId}"]`)
        : null;
    const live = creatureFaceFromToken(token);
    const target = creatureRowById(entityId);
    const cachedName = target && typeof getCreatureName === 'function' ? String(getCreatureName(target) || '') : '';
    const thumb = creatureAvatarUrl(target) || live.thumb;
    const name = live.name || cachedName || String(target ? ftItemsCol(target, 'item_name') ?? '' : '') || 'Target';
    const face = thumb
        ? `<img class="ft-action-targetingbar__portrait" src="${escapeHtml(thumb)}" alt="${escapeHtml(name)}">`
        : `<span class="ft-action-targetingbar__portrait ft-action-targetingbar__portrait--none">${escapeHtml(targetInitials(name))}</span>`;
    const currentRaw = target ? ftComputedValue(target, 'vitality', 'v_current') : undefined;
    const maxRaw = target ? ftComputedValue(target, 'vitality', 'effective_v_max') : undefined;
    const pctRaw = target ? ftComputedValue(target, 'vitality', 'v_bar_v_percent') : token?.dataset.v_bar_v_percent;
    const current = Number(currentRaw);
    const maximum = Number(maxRaw);
    const pct = Math.max(0, Math.min(100, Number(pctRaw) || 0));
    const tokenLabel = token?.querySelector<HTMLElement>('.hp-bar')?.dataset.hpLabel || '';
    const vitalityLabel = Number.isFinite(current) && Number.isFinite(maximum) && maximum > 0
        ? `${current} / ${maximum}`
        : tokenLabel;
    const tooltip = `${name}${vitalityLabel ? ` • ${vitalityLabel}` : ''} • Click to remove`;
    return `<button type="button" class="ft-action-targetingbar__target" aria-label="Remove ${escapeHtml(name)}" ${tip(tooltip, 'top')}`
        + ` data-ft-ab-action="targeting-remove" data-ft-intent-id="${intentId}" data-ft-entity-id="${escapeHtml(entityId)}">`
        + `${face}<span class="ft-action-targetingbar__vbar"><i style="width:${pct}%"></i></span>`
        + `<span class="ft-action-targetingbar__remove" aria-hidden="true">&#10005;</span></button>`;
}

/** Cross-bundle AOE stage bridge. Area membership is derived by scene geometry, summarized in the
 * options header, and refreshed whenever a moving/falling placement crosses the volume. */
export function ftActionBarSetAoeTargets(
    abilityId: string,
    targets: readonly Ft3dAoeTargetPreview[],
): void {
    aoeStagedTargets.clear();
    const abilityKey = String(abilityId).trim().toUpperCase();
    if (abilityKey) {
        targets.forEach(target => {
            if (target?.entityId) aoeStagedTargets.set(target.entityId.toUpperCase(), { ...target });
        });
    }
    if (abChoice?.isAoe && (!abilityKey || abChoice.abilityId.toUpperCase() === abilityKey)) {
        repaintActionBars(abChoice.casterId);
    }
}

function abilityCastBaseLevel(ability: CasterItemNode | null): number {
    return ability ? Math.max(0, Number(ftLayerValue(ability,
        ftItemsCol(ability, 'item_type') === 'menta' ? 'menta_level' : 'spell_level')) || 0) : 0;
}

function targetingMaxSpellLevel(caster: CachedCaster, minimum: number, ability: CasterItemNode | null = null): number {
    if (ability && ftItemsCol(ability, 'item_type') === 'menta') {
        // Mentas invest Psi, not mana. Keep both the AOE and creature-target steppers on the held pool.
        const psi = abilityEntriesOfType(caster, 'passive_ability').find(entry =>
            abilityEntryId(entry).toUpperCase() === '6A8183E1-349E-3960-B465-6C863097E193');
        const current = psi ? sheetChargeBudget(caster, psi) : 0;
        return current >= minimum ? current : minimum;
    }
    // Wizard v9 spell_mana_discount: a level costs (level − discount) mana, so the pool affords up to mana + discount.
    const mana = sheetManaBudget(caster) + (ability ? sheetSpellManaDiscount(caster, ability) : 0);
    // A normal numeric upcast spends its chosen level as mana. Bounding the stepper to the current
    // pool makes the affordance update with combat state and prevents an obviously unaffordable level
    // from reaching precheck; Janus still re-validates after concurrent resource changes.
    return Number.isFinite(mana) && mana >= minimum ? Math.floor(mana) : minimum;
}

/** Combined staged-target + option panel for an armed creature ability. */
function renderAbilityTargetingBar(casterId: string, caster: CachedCaster): string {
    const intent = abilityTargetingLifecycle.current();
    if (!intent || intent.casterId !== casterId) return '';
    const selected = intent.selectedEntityIds.length;
    const maximum = intent.profile.maxTargets;
    const ready = intent.phase === 'ready-to-confirm';
    const rangeIcon = `<span class="ft-action-targetingbar__range" ${tip(intent.profile.rangeLabel, 'top')}>`
        + `${iconMap(intent.profile.rangeId, 18, 18, false)}</span>`;
    const projectile = intent.profile.projectilePool;
    const multiple = maximum > 1 || !!projectile;
    // Say WHY nothing can be picked instead of inviting a click that can never land.
    const sceneLegal = abilityTargetingRuntime.sceneLegal;
    const legalCount = sceneLegal?.intentId === intent.intentId ? sceneLegal.entityIds.size : null;
    const floorRefusal = intent.candidateEntityIds.length === 0 ? abilityTargetingFloorRefusal() : null;
    const targetingHintState = floorRefusal
        ? 'floor-unavailable'
        : intent.candidateEntityIds.length === 0
        ? 'no-candidates'
        : legalCount === 0 ? 'no-legal' : 'ready';
    const optionalTarget = intent.profile.minTargets === 0;
    const heldItemTypes = intent.profile.heldItemTargetTypes ?? [];
    const targetingHint = floorRefusal
        ? `<span ${tip(floorRefusal, 'top')}>perception paused — the encounter floor is unavailable</span>`
        : heldItemTypes.length
        ? (targetingHintState === 'no-candidates'
            ? `no one in sight is holding a ${heldItemTypes.join(' or ')}`
            : `click a creature to choose from its equipped slots${targetingHintState === 'no-legal' ? ' — move within reach' : ''}`)
        : optionalTarget
        ? (targetingHintState === 'ready'
            ? 'pick a creature to contest — rolls on click — or roll vs DC'
            : 'no creature to contest — roll vs DC')
        : targetingHintState === 'no-candidates'
            ? 'no perceived target for this ability'
            : targetingHintState === 'no-legal'
                ? 'no target in range with line of effect'
                : projectile ? 'click to assign a projectile · remove assignments below · Confirm to fire'
                    : multiple ? 'choose targets' : 'choose a target — casts on click';
    let optionControls = '';
    if (intent.invocation.kind === 'variable-1-number') {
        const ability = findAbility(caster, intent.invocation.abilityId);
        const family = ability ? actionInputFamily(ability, intent.invocation.inputId) : 'amount';
        const spec = ability
            ? numericActionInputSpec(caster, ability, family)
            : { label: 'Amount', min: 0, max: '', value: intent.invocation.value };
        const numericMaximum = spec.max === '' ? null : Number(spec.max);
        const maximumValue = numericMaximum ?? Number.POSITIVE_INFINITY;
        const currentValue = Math.max(spec.min, Math.min(maximumValue, intent.invocation.value));
        const maxAttr = spec.max === '' ? '' : ` data-ft-max="${spec.max}"`;
        const isSpellLevel = family === 'spell-level';
        optionControls = `<div class="ft-action-choicebar__upcast ft-action-targetingbar__upcast">`
            + `<span class="ft-action-choicebar__lbl">${escapeHtml(spec.label)}</span>`
            + `<div class="ft-ab-choice-stepper">`
            + `<button type="button" class="ft-ab-choice-step" ${tip(isSpellLevel ? 'Lower the cast level' : `Lower ${spec.label.toLowerCase()}`, 'top')} data-ft-ab-action="choice-step" data-ft-delta="-1" data-ft-min="${spec.min}"${maxAttr}${currentValue <= spec.min ? ' disabled' : ''}>&minus;</button>`
            + `<input class="ft-ab-choice-num ${CHOICE_CTL_CLASS}" type="number" step="1" min="${spec.min}"${spec.max === '' ? '' : ` max="${spec.max}"`} value="${currentValue}" aria-label="${escapeHtml(spec.label)}" data-ft-targeting-option-value="${currentValue}">`
            + `<button type="button" class="ft-ab-choice-step" ${tip(isSpellLevel ? 'Upcast a level' : `Raise ${spec.label.toLowerCase()}`, 'top')} data-ft-ab-action="choice-step" data-ft-delta="1" data-ft-min="${spec.min}"${maxAttr}${numericMaximum !== null && currentValue >= numericMaximum ? ' disabled' : ''}>&plus;</button>`
            + `</div>`
            + (isSpellLevel && ability
                ? upcastDamageReadout(ability, currentValue - abilityCastBaseLevel(ability))
                    + upcastEffectBadges(ability, currentValue - abilityCastBaseLevel(ability))
                : '')
            + `</div>`;
        if (ability && ftLayerEntries(ability, 'damage_type').length > 1) {
            const damageType = intent.invocation.secondaryChoice
                || choiceEntryUuid(ftLayerEntries(ability, 'damage_type')[0]);
            optionControls += `<div class="ft-action-choicebar__upcast ft-action-targetingbar__option">`
                + `<span class="ft-action-choicebar__lbl">Damage type</span>`
                + targetingDamageTypeSelect(ability, damageType, 'secondary-damage-type')
                + `</div>`;
        }
    } else if (intent.invocation.kind === 'variable-1-choice') {
        const ability = findAbility(caster, intent.abilityId);
        if (ability) {
            optionControls = `<div class="ft-action-choicebar__upcast ft-action-targetingbar__option">`
                + `<span class="ft-action-choicebar__lbl">Damage type</span>`
                + targetingDamageTypeSelect(ability, intent.invocation.value)
                + `</div>`;
        }
    }
    const attack = intent.profile.aimLine === 'melee-attack' || intent.profile.aimLine === 'ranged-attack';
    // Any object-capable ability offers the toggle; a restricted ATTACK still shows it disabled so the
    // player learns why a door is unpickable. Other restricted abilities simply omit it.
    const inventoryOption = (attack || intent.profile.supportsInventoryTargets) && !intent.profile.inventoryTargetTypes?.length
        ? `<label class="ft-action-targetingbar__inventory" title="${intent.profile.supportsInventoryTargets
            ? 'Allow this ability to target visible objects as well as creatures. Objects never resist: no attack roll, and they automatically fail contests.'
            : 'This ability is restricted to creatures'}">`
            + `<input type="checkbox" data-ft-targeting-inventory${intent.profile.allowInventoryTargets ? ' checked' : ''}${intent.profile.supportsInventoryTargets ? '' : ' disabled'}>`
            + `<span>Include objects</span></label>`
        : '';
    return `<div class="ft-action-choicebar ft-action-targetingbar" data-ft-ability-intent-id="${intent.intentId}">`
        + `<div class="ft-action-choicebar__head">`
        + `<span class="ft-action-choicebar__title">${escapeHtml(intent.abilityName)}</span>`
        + optionControls
        + inventoryOption
        + `<span class="ft-action-choicebar__hint" data-ft-targeting-hint="${targetingHintState}">${targetingHint}</span>`
        + rangeIcon
        + (projectile ? `<span class="ft-action-targetingbar__count">${selected}/${maximum} projectiles assigned</span>`
            : multiple ? `<span class="ft-action-targetingbar__count" ${tip('Selected targets', 'top')}>${selected}/${maximum}</span>` : '')
        + (projectile ? [...new Set(intent.selectedEntityIds)].map(id => `<span class="ft-projectile-assignment">${renderAbilityTargetCard(intent.intentId, id)}<b>×${intent.selectedEntityIds.filter(selectedId => selectedId === id).length}</b></span>`).join('') : '')
        + (multiple ? `<button type="button" class="ft-action-choicebar__cast" data-ft-ab-action="choice-confirm" data-ft-caster-id="${escapeHtml(casterId)}" data-ft-mode="normal"${ready ? '' : ' disabled aria-disabled="true"'}>Cast</button>` : '')
        + (optionalTarget && !multiple ? `<button type="button" class="ft-action-choicebar__cast" ${tip('Roll against the fixed DC the GM set (no contest)', 'top')} data-ft-ab-action="choice-confirm" data-ft-caster-id="${escapeHtml(casterId)}" data-ft-mode="normal">Roll vs DC</button>` : '')
        + `<button type="button" class="ft-action-choicebar__cancel" ${tip('Cancel targeting', 'top')} data-ft-ab-action="choice-cancel" data-ft-caster-id="${escapeHtml(casterId)}">&#10005;</button>`
        + `</div></div>`;
}

/** Resulting damage at the selected level, e.g. `8d6 Fire` for Fireball at level 3, `9d6 Fire` at 4 — the
 *  VALUE the owner wants beside the level stepper (2026-08-25), not only the delta badge. '' when the
 *  ability has no dice. */
function upcastDamageReadout(ab: CasterItemNode | null, levelDelta: number): string {
    if (!ab) return '';
    const num = (k: string): number => Number(ftLayerValue(ab, k)) || 0;
    const baseDice = num('dice');
    const baseDie = num('die');
    if (!(baseDice > 0) || !(baseDie > 0)) return '';
    const delta = Math.max(0, levelDelta);
    const upDice = num('dice_upcast') * delta;
    const upDie = num('die_upcast');
    const mod = num('mod') + num('mod_upcast') * delta;
    const damageTypes = ftLayerEntries(ab, 'damage_type').map(entry => {
        const id = choiceEntryUuid(entry);
        const nestedName = String(ftItemsCol(entry, 'item_name') ?? '').trim();
        const name = nestedName
            || (id && typeof ftItemNameById === 'function' ? String(ftItemNameById(id) ?? '').trim() : '');
        return { id, name };
    }).filter(entry => entry.name);
    const uniqueDamageTypes = damageTypes.filter((entry, index, entries) => (
        entries.findIndex(candidate => candidate.id === entry.id && candidate.name === entry.name) === index
    ));
    const typeHtml = uniqueDamageTypes.map(entry => (
        `${entry.id ? iconMap(entry.id, 16, 16, false) : ''}<span>${escapeHtml(entry.name)}</span>`
    )).join('<span aria-hidden="true">/</span>');
    let dice = upDice > 0 && upDie === baseDie
        ? `${baseDice + upDice}d${baseDie}`
        : `${baseDice}d${baseDie}${upDice > 0 ? ` + ${upDice}d${upDie}` : ''}`;
    if (mod > 0) dice += ` + ${mod}`;
    return `<span class="ft-action-choicebar__upcast-dmg" ${tip('Damage at this cast level', 'top')}>${escapeHtml(dice)}${typeHtml ? ` ${typeHtml}` : ''}</span>`;
}

/** The option controls an AOE carries into BOTH its fold-ups (placement row, or the Ready fallback when
 *  placement could not start): level stepper + resulting damage + delta badges, Sanguine pick, ritual. */
function renderAoeOptionControls(casterId: string, caster: CachedCaster, abilityId: string): string {
    const ab = findAbility(caster, abilityId);
    const cid = escapeHtml(casterId);
    const aid = escapeHtml(abilityId);
    const aidU = String(abilityId).toUpperCase();
    const choice = abChoice && abChoice.isAoe && abChoice.casterId === casterId
        && String(abChoice.abilityId).toUpperCase() === aidU ? abChoice : null;
    const isUpcastable = !!ab && !choice?.sourceOccupied
        && spellIsLevelUpcastable(ab);   // cantrips carved out (owner 2026-07-24)
    const family = actionInputFamily(ab, choice?.inputId ?? '');
    const optionValues = ab
        ? currentActionOptionValues(caster, ab, choice?.inputId ?? '', abilityId)
        : { primary: '', secondary: '' };
    const baseLvl = abilityCastBaseLevel(ab);
    const maxLvl = targetingMaxSpellLevel(caster, baseLvl, ab);
    const curLvl = Math.max(baseLvl, Math.min(maxLvl, upcastLevelMap().get(aidU) ?? baseLvl));
    const upcastCtl = isUpcastable
        ? `<div class="ft-action-choicebar__upcast ft-action-aoebar__upcast">`
            + `<span class="ft-action-choicebar__lbl">Cast at level</span>`
            + `<div class="ft-ab-choice-stepper">`
            + `<button type="button" class="ft-ab-choice-step" ${tip('Lower the cast level', 'top')} data-ft-ab-action="aoe-upcast-step" data-ft-caster-id="${cid}" data-ft-ability-id="${aid}" data-ft-delta="-1"${curLvl <= baseLvl ? ' disabled' : ''}>&minus;</button>`
            + `<input class="ft-ab-choice-num" type="number" step="1" min="${baseLvl}" max="${maxLvl}" value="${curLvl}" aria-label="Cast at level" data-ft-aoe-upcast-input data-ft-caster-id="${cid}" data-ft-ability-id="${aid}">`
            + `<button type="button" class="ft-ab-choice-step" ${tip('Upcast a level (grows the area)', 'top')} data-ft-ab-action="aoe-upcast-step" data-ft-caster-id="${cid}" data-ft-ability-id="${aid}" data-ft-delta="1"${curLvl >= maxLvl ? ' disabled' : ''}>&plus;</button>`
            + `</div>`
            + upcastDamageReadout(ab, curLvl - baseLvl)
            + `</div>`
        : '';
    const genericCtrl = ab && choice && family !== 'none' && family !== 'spell-level'
        ? buildChoiceControl(caster, ab, choice.inputId, abilityId, optionValues.primary)
        : { label: '', html: '' };
    const genericCtl = genericCtrl.html
        ? `<div class="ft-action-choicebar__upcast ft-action-aoebar__upcast">`
            + `<span class="ft-action-choicebar__lbl">${escapeHtml(genericCtrl.label)}</span>${genericCtrl.html}</div>`
        : '';
    const ownSecondaryDmg = !!ab && family !== 'damage-type'
        && (choice?.spellType ?? '') === ''
        && ftLayerEntries(ab, 'damage_type').length > 1;
    const ownSecondaryDmgCtl = ownSecondaryDmg
        ? `<div class="ft-action-choicebar__upcast"><span class="ft-action-choicebar__lbl">Damage type</span>`
            + abChoiceSelect(ab, 'damage_type', false, CHOICE_CTL2_CLASS, optionValues.secondary) + `</div>`
        : '';
    const armed = ritualArmedSet().has(aidU);
    const ritualBtn = choice?.ritual
        ? `<button type="button" class="ft-action-choicebar__ritual${armed ? ' is-armed' : ''}" ${tip('Cast this as a ritual (toggle)', 'top')} data-ft-ab-action="toggle-ritual" data-ft-caster-id="${cid}" data-ft-ability-id="${aid}">${iconMap('cast_ritual_spell', 16, 16)}<span>${armed ? 'Ritual ✓' : 'Ritual'}</span></button>`
        : '';
    // Sanguine Elemental/Transmuted on an AOE spell (Fireball is the archetype). FT3D placement reads this
    // select by its data-ft-sanguine-dmg-for stamp at accept time. A MARKER AOE spends variable_2 on the
    // upcast level, so it deliberately gets no select. [[Codex review 2026-07-24 P2]]
    const sangAoeIds = (ownSecondaryDmg || choice?.itemSourced || (choice?.spellType ?? '') !== '')
        ? []
        : sanguineDmgPickOptionIds(caster, ab, false);
    const sangAoeCtl = sangAoeIds.length > 0
        ? `<div class="ft-action-choicebar__upcast"><span class="ft-action-choicebar__lbl">Damage type (Sanguine)</span>`
            + damageTypeChoiceSelectHtml(sangAoeIds, abilityId) + `</div>`
        : '';
    const selectedUpcastEffects = isUpcastable ? upcastEffectBadges(ab, curLvl - baseLvl) : '';
    return upcastCtl + genericCtl + selectedUpcastEffects + ownSecondaryDmgCtl + sangAoeCtl + ritualBtn;
}

/** The AOE PLACEMENT fold-up: shown while this caster's area ability is being aimed in the 3D scene.
 *  The web has no right-click cast gesture, so a scene click only PLACES (stages) the area; this row owns
 *  the explicit Confirm AOE / Cancel (owner 2026-08-25 — brought back from the XZ era). Re-clicking the
 *  slotted ability is the Confirm shortcut. Affected targets are a small count, not thumbnails. */
function renderAoePlacementBar(casterId: string, caster: CachedCaster): string {
    const active = window.aoeManager?.ft3dActive;
    const intent = active?.envelope?.intent;
    // controllerId: a placement made for ANOTHER body (a held-target move) confirms from the acting creature's dock
    if (!active || !intent || String(intent.controllerId || intent.casterId || '').toUpperCase() !== String(casterId).toUpperCase()) return '';
    const ab = findAbility(caster, String(intent.abilityId || ''));
    const name = ab ? String(ftItemsCol(ab, 'item_name') ?? intent.abilityName ?? 'Ability') : String(intent.abilityName ?? 'Ability');
    const staged = active.staged ?? null;
    const isSummon = intent.placementKind === 'summon';
    const isTeleport = intent.placementKind === 'teleport';
    const pool = intent.projectilePool;
    // A multi-summon placement (owner 2026-09-16): one spot per summon, each Confirm banks one, the last one casts.
    const summonCount = isSummon ? Number(active.summonCount) || 0 : 0;
    const summonPlaced = summonCount > 1 ? active.summonPoints?.length ?? 0 : 0;
    // A rolled count: summonCount is the roll's maximum; the caster may cast early ("Cast now").
    const summonRolled = summonCount > 1 && active.summonRolled === true;
    const legal = !!staged && staged.placed !== false && active.placementCheck?.state === 'valid'
        && staged.inRange === true && staged.lineOfEffect !== 'blocked';
    const creatures = staged?.targets?.filter(t => t?.kind === 'creature').length ?? 0;
    const inventory = staged?.targets?.filter(t => t?.kind === 'inventory').length ?? 0;
    const hint = active.placementCheck?.state !== 'valid' && active.placementCheck?.message
        ? escapeHtml(active.placementCheck.message)
        : intent.wallAttachment ? 'Drag to paint the wall · Undo stroke or Redraw to revise · Confirm casts · Esc cancels'
        : isTeleport ? `Choose a landing spot within ${intent.rangeFt} ft · Confirm teleports · Esc cancels`
        : pool ? 'Place an impact point, then Add projectile · Confirm fires the assigned volley'
        : summonRolled ? `the roll summons up to ${summonCount} · place a spot for each with double-click or Confirm · Cast now uses the spots in order (extra summons gather around the last) · Esc cancels`
        : summonCount > 1 ? `choose a spot for summon ${Math.min(summonPlaced + 1, summonCount)} of ${summonCount} · double-click or Confirm places it${summonPlaced + 1 >= summonCount ? ' and casts' : ''} · Esc cancels`
        : !staged || staged.placed === false ? `click the ground to place ${isSummon ? 'the summon' : 'the area'} · double-click casts`
        : legal ? `placed — Confirm or double-click to cast · drag or click to move${isSummon ? '' :
            intent.shape === 'square' || intent.shape === 'cube' ? ' · drag the knob to rotate'
                : intent.shape === 'line' || intent.shape === 'cone' ? ' · drag to aim'
                    : intent.shape === 'circle' ? '' : ' · drag the centre knob to raise'} · Esc cancels`
            : staged.lineOfEffect === 'blocked' ? 'blocked — choose another spot' : 'out of range — choose another spot';
    const cid = escapeHtml(casterId);
    const aid = escapeHtml(String(intent.abilityId || ''));
    const count = staged && !isSummon && !isTeleport
        ? `<span class="ft-action-targetingbar__count" ${tip('Affected creatures · inventory', 'top')}>${creatures}${inventory ? ` · ${inventory}` : ''}</span>`
        : '';
    const placementLabel = isSummon ? `Summon ${String(intent.summonName || '').trim()}`.trim() : name;
    const confirmLabel = isTeleport ? 'Confirm teleport' : summonCount > 1 && summonPlaced + 1 < summonCount ? `Place summon ${summonPlaced + 1} here` : pool ? 'Confirm projectile volley' : isSummon ? 'Confirm summon — cast here' : 'Confirm AOE — cast here';
    const canConfirm = pool ? pool.assigned > 0 : legal;
    return `<div class="ft-action-choicebar ft-action-aoebar ft-action-aoebar--placing${pool ? ' ft-action-aoebar--projectiles' : ''}">`
        + `<div class="ft-action-choicebar__head">`
        + `<span class="ft-action-choicebar__title">${escapeHtml(placementLabel)}</span>`
        + (isSummon || isTeleport ? '' : renderAoeOptionControls(casterId, caster, String(intent.abilityId || '')))
        + `<span class="ft-action-choicebar__hint">${hint}</span>`
        + count
        + (pool ? `<span class="ft-action-targetingbar__count">${pool.assigned}/${pool.remaining} projectiles assigned</span>`
            + `<button type="button" class="ft-action-choicebar__cast" data-ft-ab-action="projectile-add"${legal && pool.assigned < pool.remaining ? '' : ' disabled'}>Add projectile</button>`
            + `<button type="button" class="ft-action-choicebar__cancel" data-ft-ab-action="projectile-undo"${pool.assigned ? '' : ' disabled'}>Undo</button>`
            + (active.projectiles ?? []).map((shot, index) => `<span class="ft-projectile-assignment" title="${escapeHtml(shot.targets.map(target => target.name).join(', '))}">Impact ${index + 1} · ${shot.targets.length} targets</span>`).join('')
            : '')
        + (summonCount > 1 ? `<span class="ft-action-targetingbar__count">${summonPlaced}/${summonCount}${summonRolled ? ' max' : ''} summons placed</span>`
            + `<button type="button" class="ft-action-choicebar__cancel" data-ft-ab-action="projectile-undo"${summonPlaced ? '' : ' disabled'}>Undo</button>`
            + (summonRolled ? `<button type="button" class="ft-action-choicebar__cast" data-ft-ab-action="summon-cast-now"${summonPlaced || legal ? '' : ' disabled'}>Cast now</button>` : '')
            : '')
        + `<button type="button" class="ft-action-choicebar__cast ft-action-aoebar__confirm" ${tip(confirmLabel, 'top')} aria-label="${escapeHtml(confirmLabel)}" data-ft-ab-action="aoe-confirm" data-ft-caster-id="${cid}" data-ft-ability-id="${aid}"${canConfirm ? '' : ' disabled aria-disabled="true"'}>${iconMap('confirm_aoe', 18, 18, false)}</button>`
        + `<button type="button" class="ft-action-choicebar__cancel ft-action-aoebar__cancel" ${tip('Cancel placement', 'top')} aria-label="Cancel placement" data-ft-ab-action="aoe-cancel" data-ft-caster-id="${cid}">${iconMap('cancel_aoe', 18, 18, false)}</button>`
        + `</div></div>`;
}

/** The floating choice panel for the caster (or '' when none is open). */
function renderAbilityChoiceBar(casterId: string, caster: CachedCaster): string {
    if (!abChoice || abChoice.casterId !== casterId) return '';
    const ab = findAbility(caster, abChoice.abilityId);
    const name = ab ? String(ftItemsCol(ab, 'item_name') ?? 'Ability') : 'Ability';
    const cid = escapeHtml(casterId);
    const aid = escapeHtml(abChoice.abilityId);

    // Area abilities use the same pre-cast structure as creature targeting: this fold-up owns variants and
    // upcast choices, the scene owns placement and affected-target feedback, and Confirm resolves the staged area.
    if (abChoice.isAoe) {
        // An armed choice without a placement yet can retry the validated route. A refused launch retires
        // this draft; during placement renderAoePlacementBar wins.
        return `<div class="ft-action-choicebar ft-action-aoebar" data-ft-ability-id="${aid}">`
            + `<div class="ft-action-choicebar__head">`
            + `<span class="ft-action-choicebar__title">${escapeHtml(name)}</span>`
            + renderAoeOptionControls(casterId, caster, abChoice.abilityId)
            + `<button type="button" class="ft-action-choicebar__cast" data-ft-ab-action="aoe-ready" data-ft-caster-id="${cid}" data-ft-ability-id="${aid}">Ready</button>`
            + `<button type="button" class="ft-action-choicebar__cancel" ${tip('Cancel', 'top')} data-ft-ab-action="choice-cancel" data-ft-caster-id="${cid}">&#10005;</button>`
            + `</div></div>`;
    }
    const optionValues = ab
        ? currentActionOptionValues(caster, ab, abChoice.inputId, abChoice.abilityId)
        : { primary: '', secondary: '' };
    // `let`: an ability_choice parent REPLACES this with the picked branch's own control below.
    let ctrl = ab
        ? buildChoiceControl(caster, ab, abChoice.inputId, abChoice.abilityId, optionValues.primary)
        : { label: 'Choose', html: '' };
    // SECONDARY damage-type pick: an ability whose primary control is the [x] amount can ALSO
    // carry a user damage-type choice (Elemental Attack). Cast sends amount → variable_1 and the
    // picked type → variable_2 (confirm below). Defaults to the first type (no blank flash-gate).
    // Upcastable summons need BOTH the level and the chosen creature. A pool marker already
    // owns variable_1, so its creature is the primary control (base-level cast) in variable_2.
    const summonPrimary = !!ab && !!abChoice.spellType && ftLayerHas(ab, 'user_choice_1_summon');
    if (summonPrimary) {
        ctrl = buildChoiceControl(caster, ab, `input_${abChoice.abilityId}_user_choice_1_summon`, abChoice.abilityId, optionValues.primary);
    }
    const wantsSummon2 = !!ab && !summonPrimary && !!abChoice.inputId
        && actionInputFamily(ab, abChoice.inputId) !== 'summon'
        && ftLayerHas(ab, 'user_choice_1_summon');
    const wantsDmg2 = !wantsSummon2 && !summonPrimary && !!ab && !!abChoice.inputId
        && !abChoice.inputId.endsWith('_user_choice_1_damage_type')
        && !abChoice.spellType
        && ftLayerEntries(ab, 'damage_type').length > 1;
    // Sanguine Elemental/Transmuted: an armed next_spell_damage_type_choice buff lets the player
    // pick this damaging spell's type from the buff's allowed set (or any type for Transmuted).
    // The shared predicate already refuses a spell that carries its own multi-type picker (that one
    // rides variable_2) → at most one CHOICE_CTL2 select exists.
    const sangIds = wantsDmg2 || wantsSummon2 || summonPrimary ? [] : sanguineDmgPickOptionIds(caster, ab, !!abChoice.itemSourced || !!abChoice.consumeInvId);
    const ctrl2Html = wantsSummon2
        ? `<span class="ft-action-choicebar__lbl">Summon</span>${abChoiceSelect(ab, 'user_choice_1_summon', true, CHOICE_CTL2_CLASS, optionValues.secondary)}`
        : wantsDmg2
        ? `<span class="ft-action-choicebar__lbl">Damage type</span>${abChoiceSelect(ab, 'damage_type', false, CHOICE_CTL2_CLASS, optionValues.secondary)}`
        : sangIds.length > 0
            ? `<span class="ft-action-choicebar__lbl">Damage type (Sanguine)</span>${damageTypeChoiceSelectHtml(sangIds, abChoice.abilityId)}`
            : '';
    // Cast as Ritual: a ritual spell can be cast without a slot/mana (spell_ritual). For a ritual+summon it
    // carries the chosen creature in variable_2. Shown only for ritual spells (alongside the normal Cast).
    const ritualBtn = abChoice.ritual
        ? `<button type="button" class="ft-action-choicebar__ritual" ${tip('Cast as a ritual (no slot)', 'top')} data-ft-ab-action="choice-confirm" data-ft-caster-id="${cid}" data-ft-mode="ritual">${iconMap('cast_ritual_spell', 16, 16)}<span>Ritual</span></button>`
        : '';
    // Quick Ritual checkbox (ritual_spell_mana_discount): a NORMAL-time cast of this ritual spell for
    // less mana, spending 1 charge of the discount passive. Only offered when the caster actually has
    // an active charged provider; applies to the normal Cast, never the (already free) Ritual cast.
    const qrSlowCast = !!ab && QR_SLOW_CAST_TIMES.has(String(ftLayerValue(ab, 'spell_casting_time') ?? ''));
    const qrDiscount = (abChoice.ritual && !qrSlowCast) ? quickRitualDiscount(caster) : 0;
    const quickRitualChk = qrDiscount > 0
        ? `<label class="ft-action-choicebar__quickritual" ${tip(`Spend 1 Quick Ritual charge: cast in normal time for ${qrDiscount} less mana`, 'top')}>`
            + `<input type="checkbox" data-ft-ab-action="toggle-quick-ritual" data-ft-caster-id="${cid}" data-ft-ability-id="${aid}"${abChoice.quickRitual ? ' checked' : ''}>`
            + `<span>${iconMap('ritual_spell_mana_discount', 16, 16)} Quick Ritual (&minus;${qrDiscount} mana)</span></label>`
        : '';
    // ability_choice BRANCH picker — leads the body, because nothing else in the panel means anything
    // until a branch is chosen (the Cast fires the picked CHILD, see ftActionBarChoiceConfirm). An
    // upcastable choice spell keeps its "Cast at level" stepper alongside; the level still rides
    // variable_1, now against the child.
    // NON-MARKER casts only. The server-side half of the marker fix (branch authorized through its
    // granting parent + the innate charge spent against the PARENT's pool) is live and is what the
    // SPELL TABLE path needs — there the child's button is generated server-side by
    // query_ability_choice_inc.php, so it carries the marker, the inventory `source`, AND its own
    // child-keyed input_id_reference, and casts correctly. THIS panel can't: `abChoice` keeps no
    // `source`, and the marker confirm below fires useAbilityVariable1(id, marker) with no slot left
    // for either the inventory row or the branch's own control value — so a charged branch would cast
    // FREE (consume_inventory_charges no-ops without a variable_2 inventory row) and an upcastable
    // branch would silently drop its level. Re-enabling this needs a cast request that can carry
    // marker + source + child value at once. AOE never reaches here (it returns above with its own
    // placement panel). [[Codex review 2026-07-28 P1 #3/#4]]
    let branchHtml = '';
    // …except an AREA parent: its branch is handed to placement with the marker + source on the branch's own
    // generated button (startBranchAoePlacement), so the three-value problem above never arises there.
    if (isAbilityChoiceParent(ab) && (!abChoice.spellType || !!ftLayerValue(ab, 'aoe_type'))) {
        const branches = abilityChoiceOptions(caster, ab as CasterItemNode, abChoice.abilityId);
        const pickedId = pickedBranchId(abChoice.casterId, abChoice.abilityId);
        branchHtml = `<span class="ft-action-choicebar__lbl">Choose option</span>`
            + actionBarTextChoiceSelect(branches, pickedId, 'Choose option', CHOICE_BRANCH_CLASS);
        // The cast-time control belongs to the BRANCH, not the parent — they differ in live data
        // (Command Foe: parent needs nothing, every branch is upcastable; Glyph of Warding:
        // "Explosive Runes" needs a damage type its parent has no notion of). Rebuild `ctrl` from the
        // picked branch's server-resolved input_family, so the value the Cast sends is the one the
        // CHILD actually wants. Nothing is offered until a branch is picked — a control built from
        // the parent would just be a lie. (owner 2026-07-28)
        const picked = branches.find((c) => c.id.toUpperCase() === pickedId.toUpperCase());
        ctrl = picked
            ? branchChoiceControl(caster, picked, optionValues.primary)
            : { label: '', html: '' };
    }
    const renderFamily = ab ? actionInputFamily(ab, abChoice.inputId) : 'none';
    const renderDynamicTargets = ab
        ? dynamicActionMaxTargets(ab, renderFamily, optionValues.primary)
        : undefined;
    const targetProfile = ab
        ? effectiveAbilityTargetingProfile(caster, ab, '', renderDynamicTargets)
        : null;
    const instantVolley = ab && instantProjectileCount(ftItemsCol(ab, 'item_type'), key => ftLayerValue(ab, key), 1) !== null;
    const actionLabel = instantVolley ? 'Assign projectiles' : targetProfile?.supported ? 'Ready' : 'Cast';
    // Layout (owner UX 2026-06-26): title/label/hint on the LEFT of the header, close ✕ pinned top-RIGHT; the
    // action buttons sit on the LEFT of the body right after the control, so the mouse doesn't travel far.
    return `<div class="ft-action-choicebar" data-ft-ability-id="${aid}">`
        + `<div class="ft-action-choicebar__head">`
        + `<span class="ft-action-choicebar__title">${escapeHtml(name)}</span>`
        + (ctrl.label ? `<span class="ft-action-choicebar__lbl">${escapeHtml(ctrl.label)}</span>` : '')
        + (abChoice.ritual ? `<span class="ft-action-choicebar__hint">click the slot again = ${actionLabel}</span>` : '')
        + `<button type="button" class="ft-action-choicebar__cancel" ${tip('Cancel', 'top')} data-ft-ab-action="choice-cancel" data-ft-caster-id="${cid}">&#10005;</button>`
        + `</div>`
        + `<div class="ft-action-choicebar__body">${branchHtml}${ctrl.html}${ctrl2Html}${quickRitualChk}`
        + `<button type="button" class="ft-action-choicebar__cast" data-ft-ab-action="choice-confirm" data-ft-caster-id="${cid}" data-ft-mode="normal">${actionLabel}</button>`
        + ritualBtn
        + `</div></div>`;
}

type ConfiguredAbilityDispatch = {
    abilityId: string;
    mode: 'normal' | 'ritual';
    spellType: string;
    value: string;
    value2: string;
    hasPrimaryControl: boolean;
    hasSecondaryControl: boolean;
    isSummon: boolean;
    summonValue: string;
    consumeInventoryId: string | null;
    quickRitual: boolean;
};

function dispatchConfiguredAbility(config: ConfiguredAbilityDispatch): void {
    const {
        abilityId, mode, spellType, value, value2, hasPrimaryControl, hasSecondaryControl,
        isSummon, summonValue, consumeInventoryId, quickRitual,
    } = config;
    window.openSideLog?.();
    if (mode === 'normal' && quickRitual) window.ftQuickRitualNextCast = true;
    if (mode === 'ritual') {
        if (isSummon && summonValue) window.useAbilityVariable2?.(abilityId, 'spell_ritual', summonValue, consumeInventoryId);
        else window.useAbilityVariable1?.(abilityId, 'spell_ritual', consumeInventoryId);
    } else if (spellType) {
        if (isSummon && summonValue) {
            window.useAbilityVariable2?.(abilityId, spellType, summonValue, consumeInventoryId);
        } else if (hasPrimaryControl && value !== '') {
            window.useAbilityVariable2?.(abilityId, spellType, value, consumeInventoryId);
        } else if (hasSecondaryControl && value2 !== '') {
            window.useAbilityVariable2?.(abilityId, spellType, value2, consumeInventoryId);
        } else {
            window.useAbilityVariable1?.(abilityId, spellType, consumeInventoryId);
        }
    } else if (hasPrimaryControl && hasSecondaryControl && value2 !== '') {
        window.useAbilityVariable2?.(abilityId, value, value2, consumeInventoryId);
    } else if (hasSecondaryControl && value2 !== '') {
        window.useAbilityVariable2?.(abilityId, '', value2, consumeInventoryId);
    } else if (hasPrimaryControl) {
        window.useAbilityVariable1?.(abilityId, value, consumeInventoryId);
    } else {
        window.useAbility?.(abilityId, consumeInventoryId);
    }
    window.ftQuickRitualNextCast = false;
}

/** Confirm the choice and either enter scene targeting or fire a targetless ability. `mode` 'normal' uses the picked value; 'ritual' casts the spell as a ritual.
 *  Per-family firing (matches the spell-table semantics, no shared input id):
 *   • normal + a value control → useAbilityVariable1(id, value)  [upcast level / shapeshift / damage / charges / mana]
 *   • normal + no control      → useAbility(id)                  [pure ritual spell cast normally]
 *   • ritual + summon          → useAbilityVariable2(id, 'spell_ritual', summon)   [variable_2]
 *   • ritual (no summon)       → useAbilityVariable1(id, 'spell_ritual')
 *  A required (blank) select doesn't fire — it flashes. Control is read scoped to the caster's pane. */
export function ftActionBarChoiceConfirm(casterId: string, mode: 'normal' | 'ritual' = 'normal'): void {
    if ((window.ftAbilityTargetingConfirm ?? confirmAbilityTargeting)(casterId)) return;
    if (!abChoice || abChoice.casterId !== casterId) return;
    const parentAbilityId = abChoice.abilityId;
    const summonCast = abChoice.summonCast;
    // The ability actually FIRED. An ability_choice parent retargets to the picked branch below —
    // every other case fires the ability the slot holds.
    let abilityId = parentAbilityId;
    const inputId = abChoice.inputId;
    // AT-WILL / INNATE-PER-REST / CHARGED cast marker. use_ability_variable_1 is already spoken for by the
    // marker (mana-free accounting keys off it), so this panel's picked value has to ride variable_2 — the
    // identical POST shape ability-button.ts and FT3D placement produce via useAbilityVariable1Input1(id,
    // spellType, input). Firing useAbilityVariable1(id, level) here would drop the marker and bill the cast
    // to the mana pool. [[owner 2026-07-24]]
    const spellType = abChoice.spellType || '';
    // NOTE: AOE rituals never reach here — their fold-up has only the Ritual toggle (no Cast), and the cast
    // happens via FT3D placement Confirm or the ability re-click. This handles non-AOE only.
    const safe = String(casterId).replace(/["\\]/g, '');
    const pane = document.querySelector(`.ft-pc-sheet-pane[data-ft-pc-pane="action_bar"][data-ft-caster-id="${safe}"]`);
    const primarySelector = `.ft-action-choicebar input.${CHOICE_CTL_CLASS}, .ft-action-choicebar select.${CHOICE_CTL_CLASS}`;
    const ctl = pane?.querySelector<HTMLInputElement | HTMLSelectElement>(primarySelector)
        || document.querySelector<HTMLInputElement | HTMLSelectElement>(primarySelector);
    const value = ctl ? String(ctl.value ?? '').trim() : '';
    // Secondary pick (upcast level + summon, or Elemental Attack amount + type) — variable_2.
    const secondarySelector = `.ft-action-choicebar select.${CHOICE_CTL2_CLASS}`;
    const ctl2 = pane?.querySelector<HTMLSelectElement>(secondarySelector)
        || document.querySelector<HTMLSelectElement>(secondarySelector);
    const value2 = ctl2 ? String(ctl2.value ?? '').trim() : '';
    const caster = casterFromCache(casterId);
    const parentAbility = caster ? findAbility(caster, parentAbilityId) : null;
    const isSummon = inputId.endsWith('_user_choice_1_summon')
        || (!!parentAbility && ftLayerHas(parentAbility, 'user_choice_1_summon'));
    const summonInSecondary = isSummon && !spellType && actionInputFamily(parentAbility, inputId) !== 'summon';
    const summonValue = isSummon ? (summonInSecondary ? value2 : value) : '';
    if (isSummon && !FT_TARGET_UUID_RE.test(summonValue)) {
        const summonControl = summonInSecondary ? ctl2 : ctl;
        summonControl?.classList.add('ft-abchoice-needpick');
        setTimeout(() => summonControl?.classList.remove('ft-abchoice-needpick'), 600);
        return;
    }
    const isSelect = !!ctl && ctl.tagName === 'SELECT';
    if (isSelect && value === '') {                  // a form/summon/damage choice not yet picked → flash, keep open
        ctl?.classList.add('ft-abchoice-needpick');
        setTimeout(() => ctl?.classList.remove('ft-abchoice-needpick'), 600);
        return;
    }
    // ability_choice branch: RETARGET the cast to the picked child (the parent carries no effect
    // layers — casting it would roll nothing). Unpicked flashes exactly like the summon select.
    const branchSelector = `.ft-action-choicebar select.${CHOICE_BRANCH_CLASS}`;
    const branchSel = pane?.querySelector<HTMLSelectElement>(branchSelector)
        || document.querySelector<HTMLSelectElement>(branchSelector);
    if (branchSel) {
        const branchId = String(branchSel.value ?? '').trim();
        if (branchId === '') {
            branchSel.classList.add('ft-abchoice-needpick');
            setTimeout(() => branchSel.classList.remove('ft-abchoice-needpick'), 600);
            return;
        }
        abilityId = branchId;
    }
    // Consumable whose ability needed a choice: the chosen value(s) occupy variable_1/variable_2, so the
    // inventory stack to decrement rides the dedicated consume_inventory_id arg (trailing, ignored when
    // undefined). undefined for every non-consumable ability. See use-ability.ts / use_ability_inc.php.
    const consumeInvId = abChoice.consumeInvId || null;
    const configuredDispatch: ConfiguredAbilityDispatch = {
        abilityId,
        mode,
        spellType,
        value,
        value2,
        hasPrimaryControl: !!ctl,
        hasSecondaryControl: !!ctl2,
        isSummon,
        summonValue,
        consumeInventoryId: consumeInvId,
        quickRitual: !!abChoice.quickRitual,
    };
    const targetAbility = caster ? findAbility(caster, abilityId) : null;
    // A MARKER cast only reaches a branch through an AREA parent's picker, and only an area branch can carry the
    // marker + item source (on its placement button). A non-area branch would take the ordinary configured fire
    // below, which drops the source — a charged cast would go unchecked and free. Refuse it here, before the
    // area-only block. [[Codex review 2026-09-16 P1]]
    if (spellType && abilityId !== parentAbilityId && (!targetAbility || !ftLayerValue(targetAbility, 'aoe_type'))) {
        window.displayWarning?.('Cast this option from the spell table.');
        return;
    }
    // A picked branch that is itself an AREA ability (Prismatic Wall's layers) is placed, not fired: the
    // parent panel only chose WHICH area. The child's own options fold up again inside its placement row.
    if (mode === 'normal' && abilityId !== parentAbilityId && caster && targetAbility
        && ftLayerValue(targetAbility, 'aoe_type')) {
        const panel = abChoice;
        const branchPick = abChoiceBranch;
        // Cleared BEFORE the start: startFt3d cancels any prior placement, and that cancel clears the
        // choice panel — it must not wipe the child's freshly armed placement options instead.
        abChoiceBranch = null;
        abChoice = null;
        const started = startBranchAoePlacement(casterId, caster, targetAbility, {
            consumeInvId, spellType: spellType || null, sourceId: panel?.sourceId || null, level: value,
        });
        if (started === 'not-area' && spellType) {
            // A marker cast can't take the ordinary configured fire (it would drop the item source) — see the
            // renderAbilityChoiceBar note. The server-built spell-table branch button carries all of it.
            abChoice = panel;
            abChoiceBranch = branchPick;
            window.displayWarning?.('Cast this option from the spell table.');
            repaintActionBars(casterId);
            return;
        } else if (started === 'not-area') {
            abChoice = panel;   // no placeable area on the branch (no range) → the ordinary configured fire
            abChoiceBranch = branchPick;
        } else {
            if (started === 'started') {
                sanguineDmgPicks.delete(String(parentAbilityId).toUpperCase());
                upcastLevelMap().delete(String(parentAbilityId).toUpperCase());
                actionOptionValueMap().delete(actionOptionKey(parentAbilityId));
            } else {
                abChoice = panel;   // placement refused (its warning is shown) → keep the picker open
                abChoiceBranch = branchPick;
            }
            repaintActionBars(casterId);
            return;
        }
    }
    // (a volley parent's only secondary control is the Sanguine damage-type pick: it authors no damage_type / summon list of its own)
    if (mode === 'normal' && !consumeInvId && armInstantProjectileSpell(casterId, abilityId, value, spellType, value2,
        { sourceId: abChoice.sourceId })) return;
    const targetFamily = targetAbility ? actionInputFamily(targetAbility, inputId) : 'none';
    const dynamicMaxTargets = targetAbility
        ? dynamicActionMaxTargets(targetAbility, targetFamily, value)
        : undefined;
    const targetProfile = caster && targetAbility
        ? effectiveAbilityTargetingProfile(caster, targetAbility, '', dynamicMaxTargets)
        : null;
    // A fixed-creature summon reaches this panel for its level / Ritual / Sanguine options and places its own
    // creature exactly like a picked one (owner 2026-10-07).
    const placedSummonId = isSummon ? summonValue : (targetAbility ? fixedSummonCreatureId(targetAbility) : '');
    if (placedSummonId && caster && targetAbility && usesGroundSummonPlacement(targetAbility)) {
        sanguineDmgPicks.delete(String(parentAbilityId).toUpperCase());
        actionOptionValueMap().delete(actionOptionKey(parentAbilityId));
        abChoiceBranch = null;
        abChoice = null;
        void armFt3dSummonPlacement(
            casterId,
            abilityId,
            placedSummonId,
            () => {
                if (summonCast) summonCast();
                else dispatchConfiguredAbility(configuredDispatch);
                upcastLevelMap().delete(String(parentAbilityId).toUpperCase());
            },
        ).then((started) => {
            if (!started) window.displayWarning?.('Summon placement is unavailable.');
        });
        repaintActionBars(casterId);
        return;
    }
    if (caster && targetAbility && targetProfile?.supported) {
        sanguineDmgPicks.delete(String(parentAbilityId).toUpperCase());
        actionOptionValueMap().delete(actionOptionKey(parentAbilityId));
        abChoiceBranch = null;
        abChoice = null;
        armCreatureTargeting(
            casterId,
            abilityId,
            caster,
            targetAbility,
            targetProfile.profile,
            { kind: 'plain', abilityId },
            () => {
                dispatchConfiguredAbility(configuredDispatch);
                upcastLevelMap().delete(String(parentAbilityId).toUpperCase());
            },
        );
        return;
    }
    dispatchConfiguredAbility(configuredDispatch);
    upcastLevelMap().delete(String(parentAbilityId).toUpperCase());
    // Keyed by the SLOT's ability (the id the select was stamped for) — not the retargeted branch.
    sanguineDmgPicks.delete(String(parentAbilityId).toUpperCase());   // the cast consumes the buff
    actionOptionValueMap().delete(actionOptionKey(parentAbilityId));
    abChoiceBranch = null;   // the cast consumes the pick; next open starts unpicked
    abChoice = null;
    repaintActionBars(casterId);
}

/** Close the choice bar (Cancel) and repaint. For an AOE ritual, also cancel the deployed template + clear the
 *  armed flag (the option panel and the placement are the same gesture). */
export function ftActionBarChoiceCancel(casterId: string): void {
    if ((window.ftAbilityTargetingCancel ?? cancelAbilityTargeting)()) return;
    if (abChoice) {
        sanguineDmgPicks.delete(String(abChoice.abilityId).toUpperCase());
        upcastLevelMap().delete(String(abChoice.abilityId).toUpperCase());
        actionOptionValueMap().delete(actionOptionKey(abChoice.abilityId));
        abChoiceBranch = null;
    }
    if (abChoice?.isAoe) {
        ritualArmedSet().delete(String(abChoice.abilityId).toUpperCase());
        upcastLevelMap().delete(String(abChoice.abilityId).toUpperCase());
        window.aoeManager?.cancelFt3d?.();
    }
    abChoice = null;
    setItemTargetMenu(null);
    repaintActionBars(casterId);
}

// ── WEAPON fold-out (BG3-style second bar of attacks for the clicked weapon slot) ────────────────
// Model: abilityEntriesOfType(caster,'attack_ability') filtered to attacks VALID for the clicked
// weapon (attached_equipped_weapon / if_weapon_classification_equipped_active), bucketed by PROVENANCE (the sourceIconCell
// precedence: requires_subclass → requires_class → weapon-classification → entry.source → carrier →
// host), each bucket sorted by sortAbilitySection. A synthetic "Weapon Attack" group with the weapon's
// basic attack leads. Each selected entry binds the exact clicked inventory row.

interface WFoldGroup { key: string; rank: number; label: string; iconHtml: string; entries: unknown[]; }

/** Group-header icon for an inventory-row source: the weapon's own picture art when available,
 *  else its template's registered icon — never the generic multi-weapon glyph. Tooltip via iconMap. */
function wfoldWeaponGroupIcon(caster: CachedCaster, invId: string, invEntry: CasterItemNode, tooltip: string, fallbackIcon = 'weapon'): string {
    const picOf = (typeof window.ftPrimaryPictureOf === 'function') ? window.ftPrimaryPictureOf : null;
    if (picOf && invEntry) {
        const pic = recordOf(picOf(invEntry));
        const picUrl = pic ? String(pic.picture_url ?? pic.pic_url ?? '') : '';
        if (picUrl) {
            const t = escapeHtml(tooltip);
            return `<span class="d-inline-block ft-icon-map" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${t}">`
                + `<img src="${escapeHtml(picUrl)}" width="26" height="26" alt="${t}" style="display:block;object-fit:cover;border-radius:4px;">`
                + `</span>`;
        }
    }
    const reg = (typeof ICON_LOOKUP !== 'undefined') ? ICON_LOOKUP : undefined;
    const ident = sourceIdentity(caster, invId);
    if (ident.iconId && reg && reg[ident.iconId]) return iconMap(ident.iconId, 26, 26, tooltip);
    const tplRaw = (typeof ftInventoryField === 'function' ? ftInventoryField(invEntry, 'inventory_item_id') : invEntry.inventory_item_id);
    const tpl = String((Array.isArray(tplRaw) ? tplRaw[0] : tplRaw) ?? ftItemsCol(invEntry, 'item_id') ?? '');
    if (tpl && reg && reg[tpl]) return iconMap(tpl, 26, 26, tooltip);
    // Last-ditch glyph. A nested-effect item passes its own item_type (potion/coating/…) — falling back
    // to the weapon glyph would badge a healing potion as a sword.
    return iconMap(fallbackIcon, 26, 26, tooltip);
}

/** Group-header icon: the source's own registered art, else its inventory-nested item's art, else the
 *  item-TYPE glyph. Single iconMap tooltip (no wrapper duplicate). */
function wfoldGroupIcon(caster: CachedCaster, id: string, fallbackType: string, tooltip: string): string {
    const reg = (typeof ICON_LOOKUP !== 'undefined') ? ICON_LOOKUP : undefined;
    if (id && reg && reg[id]) return iconMap(id, 26, 26, tooltip);
    if (id) {
        const info = blobSourceIndex(caster).types[id.toLowerCase()];
        if (info?.iconId && reg && reg[info.iconId]) return iconMap(info.iconId, 26, 26, tooltip);
        if (!fallbackType) fallbackType = info?.type || '';
    }
    return iconMap(fallbackType || 'host', 26, 26, tooltip);
}

/** Provenance group for one attack entry — the sourceIconCell precedence turned into a group key +
 *  label + icon. ITEM-sourced attacks are checked FIRST (owner 2026-07-02: "if the attack ability is
 *  sourced from the item that is how it should be batched") — a weapon's own nested attacks, its
 *  classification's attacks and enchantment grants all compile with the inventory ROW as their source,
 *  so they batch under the clicked weapon even when they also carry a weapon-classification gate
 *  (Lacerating Strike used to leak into a "Battleaxes" group inside the Handaxe fold-out).
 *  rank orders the groups: clicked weapon → weapon-classification → class → subclass → other → host. */
function weaponFoldGroupFor(caster: CachedCaster, entry: unknown, ab: CasterItemNode, invId: string, invEntry: CasterItemNode): { key: string; rank: number; label: string; iconHtml: string } {
    const aidU = String(abilityEntryId(entry) ?? '').toUpperCase();
    const resolveSource = (): string | null => {
        let source = recordOf(entry)?.source ?? null;
        if (source === 'NA' || source === '') source = null;
        if (!source) {
            for (const { entry: sk } of requiredSkillTierEntries(ab)) {
                const u = uuidOf(sk);
                if (u) { source = u; break; }
            }
        }
        if (!source && aidU) { const cs = abilitySourceMap(caster)[aidU.toLowerCase()]; if (cs) source = cs; }
        if (!source || (typeof checkIfValidUUID === 'function' && !checkIfValidUUID(String(source)))) return null;
        // Degenerate compile (e.g. Knockout Strike): the entry's source is the ability ITSELF —
        // no provenance info, so let it fall through to the innate group instead of a self-named one.
        if (String(source).toUpperCase() === aidU) return null;
        return String(source);
    };
    const source = resolveSource();
    if (source && blobSourceIndex(caster).types[source.toLowerCase()]?.inv === true) {
        const ident = sourceIdentity(caster, source);
        const nm = ident.name || ident.type || 'Weapon';
        const label = `${nm} Attacks`;
        const isClickedWeapon = source.toUpperCase() === String(invId).toUpperCase();
        const iconHtml = isClickedWeapon
            ? wfoldWeaponGroupIcon(caster, source, findInventoryEntry(caster, source) ?? invEntry, label)
            : wfoldGroupIcon(caster, source, ident.type, label);
        return { key: `src:${source.toLowerCase()}`, rank: isClickedWeapon ? 0 : 4, label, iconHtml };
    }
    const clickedNaturalId = String(ftItemsCol(invEntry, 'item_id') ?? '').toUpperCase();
    const clickedNatural = String(ftItemsCol(invEntry, 'item_type') ?? '').toLowerCase() === 'natural_weapon';
    const attachedToClickedNatural = clickedNatural && clickedNaturalId !== ''
        && weaponMatchesAttachmentGate(ab, clickedNaturalId, invEntry);
    if (attachedToClickedNatural) {
        const weaponName = String(ftItemsCol(invEntry, 'item_name') ?? 'Natural Weapon');
        const label = `${weaponName} Attacks`;
        return {
            key: `natural:${clickedNaturalId.toLowerCase()}`,
            rank: 0,
            label,
            iconHtml: wfoldWeaponGroupIcon(caster, invId, invEntry, label, 'natural_weapon'),
        };
    }
    const sub = ftLayerEntries(ab, 'requires_subclass')[0];
    if (sub) {
        const uid = uuidOf(sub);
        const nm = String(ftItemsCol(sub, 'item_name') ?? '') || sourceIdentity(caster, uid).name || 'Subclass';
        const label = `${nm} Attacks`;
        return { key: `sub:${uid}`, rank: 3, label, iconHtml: wfoldGroupIcon(caster, uid, 'subclass', label) };
    }
    const cls = ftLayerEntries(ab, 'requires_class')[0];
    if (cls) {
        const uid = uuidOf(cls);
        const nm = String(ftItemsCol(cls, 'item_name') ?? '') || sourceIdentity(caster, uid).name || 'Class';
        const label = `${nm} Attacks`;
        return { key: `cls:${uid}`, rank: 2, label, iconHtml: wfoldGroupIcon(caster, uid, 'class', label) };
    }
    const wc = ftLayerEntries(ab, 'if_weapon_classification_equipped_active')[0];
    if (wc) {
        const uid = uuidOf(wc);
        const nm = String(ftItemsCol(wc, 'item_name') ?? '') || sourceIdentity(caster, uid).name || 'Weapon Class';
        const label = `${nm} Attacks`;
        return { key: `wc:${uid}`, rank: 1, label, iconHtml: wfoldGroupIcon(caster, uid, 'weapon_classification', label) };
    }
    if (source) {
        const ident = sourceIdentity(caster, source);
        const nm = ident.name || ident.type || 'Source';
        const label = `${nm} Attacks`;
        return { key: `src:${source.toLowerCase()}`, rank: 4, label, iconHtml: wfoldGroupIcon(caster, source, ident.type, label) };
    }
    return { key: 'host', rank: 5, label: 'Innate Attacks', iconHtml: iconMap('host', 26, 26, 'Innate Attacks') };
}

/** Attack entries usable WITH the clicked weapon. Excludes: draw-sheathed-bound attacks (they only
 *  fire from their sheathed weapon's own entry — the server's draw path pre-empts the weapon pin),
 *  OTHER inventory items' own attacks (an off-hand dagger's nested attack doesn't belong under the
 *  main-hand sword), and attacks whose weapon gates the clicked weapon fails (§ server-mirror note
 *  above). The weapon's item-sourced maneuvers all stay IN — the leading "weapon attack" entry is the
 *  plain swing (useAbility(inventory_id)), which is NOT one of these entries, so nothing is skimmed. */
function weaponFoldEntries(caster: CachedCaster, invEntry: CasterItemNode, invId: string): unknown[] {
    const tplRaw = (typeof ftInventoryField === 'function' ? ftInventoryField(invEntry, 'inventory_item_id') : invEntry.inventory_item_id);
    const wTpl = String((Array.isArray(tplRaw) ? tplRaw[0] : tplRaw) ?? ftItemsCol(invEntry, 'item_id') ?? '').toUpperCase();
    const wClass = String(ftLayerValue(invEntry, 'weapon_classification') ?? '').toUpperCase();
    const invU = String(invId).toUpperCase();
    const out: unknown[] = [];
    for (const entry of abilityEntriesOfType(caster, 'attack_ability')) {
        const aid = String(abilityEntryId(entry) ?? '').toUpperCase();
        if (aid === '') continue;
        const ab = resolveAbilityNest(entry, caster);
        const ability = itemNodeOf(ab);
        if (!ability?.items) continue;
        if (ftLayerHas(ab, 'draw_sheathed_weapon_for_attack_ability')) continue;
        const src = recordOf(entry)?.source ?? null;
        if (typeof src === 'string' && src !== '' && src !== 'NA'
            && blobSourceIndex(caster).types[src.toLowerCase()]?.inv === true
            && src.toUpperCase() !== invU) continue;
        if (abilityHasWeaponAttachmentGate(ab) && !weaponMatchesAttachmentGate(ab, wTpl, invEntry)) continue;
        if (ftLayerHas(ab, 'if_weapon_classification_equipped_active')) {
            const ok = wClass !== '' && ftLayerEntries(ability, 'if_weapon_classification_equipped_active').some((e: unknown) => uuidOf(e).toUpperCase() === wClass);
            if (!ok) continue;
        }
        out.push(entry);
    }
    return out;
}

/** ACTIVE ability buckets a nested-effect item can offer in the fold-out. Mirrors the Abilities-tab
 *  SECTIONS list (ability-section.ts:17) — which deliberately omits passive_ability — plus the attack
 *  buckets and the item-granted spell pools. passive_ability is EXCLUDED on purpose: passives are always-on
 *  (a potion's "while carried" aura), so a medallion for one would be a dead button. lair_ability is absent
 *  because only lairs carry it and it never rides an inventory row. */
const NESTED_FOLD_BUCKETS: readonly string[] = [
    'full_ability', 'ability', 'quick_ability', 'free_ability', 'reaction_ability',
    'attack_ability', 'opportunity_attack_ability', 'curse', 'sanguine', 'menta',
    'ability_choice', ...GRANT_SPELL_TYPES,
];

/** invId(UPPER) → the ACTIVE abilities that inventory row itself grants, built ONCE per caster blob.
 *
 *  Read straight off computed.creature.abilities (where the compile step files inventory-sourced grants —
 *  enchantment_and_inventory_layering.php:634 — and the same bucket boundAbilityConsumesNestedUnit reads)
 *  rather than via abilityEntriesOfType(): that helper de-dupes by ability id ACROSS the whole bucket, so
 *  two separate potion stacks granting the same "Drink"/"Throw" ids would collapse to one and the second
 *  stack's slot would render no menu. De-duping per SOURCE here keeps every stack independent.
 *
 *  RENDER-SCOPED cache: renderActionBar() clears it at the top of every pass, so all of a pass's slots
 *  share one 15-bucket scan (the original perf goal — the win was always per-repaint, not across them)
 *  while nothing survives into the next repaint.
 *
 *  It must NOT be keyed on caster object identity. That was the original bug (owner 2026-07-20: equipping
 *  a potion from the dock left its second click inert): EVERY live update path mutates the cached blob
 *  IN PLACE and preserves its identity — caster-delta.ts patches sections onto it, and both
 *  adoptCasterBlobIntoEncounterRow (caster-update.ts) and encounter-delta.ts swap a row's contents while
 *  keeping the row object. Only a standalone out-of-encounter PC ever gets a new object. So an equip —
 *  which is exactly what moves an inventory row's nested grants INTO computed.creature.abilities — kept
 *  handing back the pre-equip (empty) map, and the fold-out marker was never rendered.
 *  `_ws_version` is no better a key: encounter-delta.ts only carries it forward across an adopt
 *  (line ~184, `typeof row._ws_version !== 'number'`), it never bumps it. */
let nestedFoldIndexCache: { caster: unknown; map: Map<string, unknown[]> } | null = null;
function nestedFoldIndex(caster: CachedCaster): Map<string, unknown[]> {
    // Identity check retained only to keep ONE pass shared across a render (and to guard the rare
    // multi-caster pass); freshness comes from renderActionBar clearing the cache, not from this test.
    if (nestedFoldIndexCache && nestedFoldIndexCache.caster === caster) return nestedFoldIndexCache.map;
    const map = new Map<string, unknown[]>();
    const buckets = caster?.computed?.creature?.abilities as Record<string, unknown> | undefined;
    if (buckets && typeof buckets === 'object') {
        for (const type of NESTED_FOLD_BUCKETS) {
            const list = buckets[type];
            if (!Array.isArray(list)) continue;
            for (const entry of list) {
                const src = recordOf(entry)?.source;
                if (typeof src !== 'string' || src === '' || src === 'NA') continue;
                const aid = String(abilityEntryId(entry) ?? '').toUpperCase();
                if (aid === '') continue;
                if (!itemNodeOf(resolveAbilityNest(entry, caster))?.items) continue;   // unresolvable nest
                const key = src.toUpperCase();
                let arr = map.get(key);
                if (!arr) { arr = []; map.set(key, arr); }
                if (arr.some((e) => String(abilityEntryId(e) ?? '').toUpperCase() === aid)) continue;
                arr.push(entry);
            }
        }
    }
    nestedFoldIndexCache = { caster, map };
    return map;
}

/** Every ACTIVE ability this inventory row itself grants. That single `source === invId` test is the whole
 *  feature: a potion's "Drink …" / "Throw …" are just two nested quick_ability items on the potion (same
 *  shape as a weapon's attack_ability list), so enumerating the nested abilities gives the drink/throw menu
 *  for free — with NO drink-vs-throw classification and no range/AOE heuristic (a self-buff potion that
 *  happens to carry an AOE would false-positive as throwable). Any future nested-effect item (wand,
 *  coating, charged wondrous item) lights up with zero extra code.
 *
 *  Note there is deliberately NO equipped gate here: the compile layer already decided. Nested inventory
 *  abilities only surface onto the caster when the row is equipped OR the item carries
 *  acquire_abilities_while_unequipped (enchantment_and_inventory_layering.php:550 — the designed exception
 *  for ammo/consumables). Re-testing equipped here would BREAK that exception. */
function nestedFoldEntries(caster: CachedCaster, invId: string): unknown[] {
    const invU = String(invId || '').toUpperCase();
    if (invU === '') return [];
    return nestedFoldIndex(caster).get(invU) ?? [];
}

/** One attack medallion in the fold-out: ~popout size (68px) with L2 rune LOD (96px detail tier)
 *  so sigils stay crisp without forcing extra rows. Name-only tooltip; readiness = greyed socket. */
function wfoldAbilityBtn(caster: CachedCaster, casterId: string, invId: string, entry: unknown): string {
    const aid = String(abilityEntryId(entry) ?? '');
    if (!aid) return '';
    const ab = resolveAbilityNest(entry, caster);
    const nm = abilityEntryName(entry) || 'Attack';
    let face = itemNodeOf(ab)?.items ? runeMountHtml(ab, 96, 'ft-rune-ability-btn ft-rune-wfold') : '';
    if (face === '') {
        const reg = (typeof ICON_LOOKUP !== 'undefined') ? ICON_LOOKUP : undefined;
        face = (reg && reg[aid]) ? iconMap(aid, 34, 34, false) : iconPh('attack_ability', 34, 34, false);
    }
    // A Monk stance attack outside its stance: greyed, the tooltip names the stance (the cast gate still refuses).
    const needs = ab ? requiredCasterStatusReason(ab, caster) : '';
    const short = needs ? ' is-status-gated' : abilityPoolShort(caster, entry) ? ' is-short' : '';
    return `<button type="button" class="ft-action-wfold-btn${short}" data-ft-info-item-id="${escapeHtml(aid)}"`
        + ` data-ft-ab-action="weapon-fold-fire" data-ft-caster-id="${escapeHtml(casterId)}" data-ft-ability-id="${escapeHtml(aid)}" data-ft-inventory-id="${escapeHtml(invId)}" ${tip(needs ? `${nm} — ${needs}` : nm, 'top')}>`
        + `${face}</button>`;
}

/** Arrow ammunition classifications an Arcane Arrow can ride (Arrows, Great Arrows) — mirrors arcane_fletcher.php. */
const ARCANE_ARROW_AMMO_CLASSIFICATION_IDS = new Set<string>([
    '166EBD0E-7FC0-7351-5F82-C84F9B48565D',
    'FBBD1092-449F-4322-9AF5-588825C2E3BD',
]);

/** Client mirror of ft_arcane_fletcher_provider(): an ACTIVE passive carrying `arcane_fletcher`. */
function casterHasArcaneFletcher(caster: CachedCaster): boolean {
    return abilityEntriesOfType(caster, 'passive_ability').some((pa) => {
        if (!passiveActive(pa)) return false;
        const nest = resolveAbilityNest(pa, caster);
        return !!nest && ftLayerHas(nest, 'arcane_fletcher');
    });
}

/** Carried, identified, unspent spell scrolls the clicked arrow-firing weapon can carry as an Arcane Arrow.
 *  Empty unless the caster holds Arcane Fletcher. Client hint only — the server re-validates every shot. */
function arcaneArrowScrolls(caster: CachedCaster, weapon: CasterItemNode): CasterItemNode[] {
    const ammo = String(ftLayerValue(weapon, 'uses_ammo_classification') ?? '').toUpperCase();
    if (!ARCANE_ARROW_AMMO_CLASSIFICATION_IDS.has(ammo) || !casterHasArcaneFletcher(caster)) return [];
    return inventoryRowsDeep(caster).filter((row) => {
        if (String(ftItemsCol(row, 'item_type') ?? '').toLowerCase() !== 'scroll') return false;
        if (Number(ftInventoryField?.(row, 'inventory_deleted') ?? 0) !== 0) return false;
        if (Number(ftInventoryField?.(row, 'inventory_unidentified') ?? 0) !== 0) return false;
        const charges = ftLayerValue(row, 'current_item_charges');
        if (sheetChargeCost(caster, 1) > 0 && charges !== null && charges !== undefined && charges !== '' && Number(charges) <= 0) return false;
        const spells = recordOf(recordOf(recordOf(recordOf(row)?.computed)?.enchantment)?.abilities)?.charged_spell;
        return Array.isArray(spells) && spells.length > 0;
    });
}

/** One scroll medallion in the Arcane Arrow group: the scroll's own picture, named in the tooltip. */
function wfoldArcaneArrowBtn(caster: CachedCaster, casterId: string, invId: string, scroll: CasterItemNode): string {
    const idRaw = ftInventoryField?.(scroll, 'inventory_id');
    const scrollId = String((Array.isArray(idRaw) ? idRaw[0] : idRaw) ?? '');
    if (scrollId === '') return '';
    const nameRaw = ftInventoryField?.(scroll, 'inventory_name');
    const name = String((Array.isArray(nameRaw) ? nameRaw[0] : nameRaw) ?? ftItemsCol(scroll, 'item_name') ?? 'Spell Scroll');
    return `<button type="button" class="ft-action-wfold-btn" data-ft-ab-action="arcane-arrow-fire"`
        + ` data-ft-caster-id="${escapeHtml(casterId)}" data-ft-inventory-id="${escapeHtml(invId)}" data-ft-scroll-id="${escapeHtml(scrollId)}"`
        + ` ${tip(`Arcane Arrow: ${name}`, 'top')}>`
        + `${wfoldWeaponGroupIcon(caster, scrollId, scroll, `Arcane Arrow: ${name}`, 'scroll')}</button>`;
}

/** Loose an Arcane Arrow: the bow's plain shot, targeted like any other, posted with the bound scroll. */
export function ftActionBarArcaneArrowFire(casterId: string, invId: string, scrollId: string): void {
    if (!invId || !scrollId) return;
    const dispatch = () => window.useAbilityVariable2?.(invId, 'arcane_arrow', scrollId);
    // Opening the fold-out already armed this bow's PLAIN shot; re-dispatching the same weapon id would
    // confirm that plain intent instead of arming the scroll-bound one.
    if (abilityTargetingLifecycle.current()?.casterId === casterId) cancelAbilityTargeting(false);
    if (ftDispatchWeaponClick(casterId, invId, 'hit', false, dispatch)) return;
    dispatch();
    abWeaponMenu = null;
    repaintActionBars(casterId);
}

/** The floating weapon fold-out panel for the caster ('' when none is open). Reuses the choice-bar
 *  shell (.ft-action-choicebar). Each provenance group is a bordered box with its source ICON
 *  resting on the top border (centered badge); L2 rune medallions fill the row below. Plain weapon
 *  attack = slot re-click (no separate crown button). Corner ✕ closes. */
function renderWeaponFoldout(casterId: string, caster: CachedCaster): string {
    if (!abWeaponMenu || abWeaponMenu.casterId !== casterId) return '';
    const invId = abWeaponMenu.invId;
    const invEntry = abWeaponMenu.kind === 'natural'
        ? findAbility(caster, invId)
        : findInventoryEntry(caster, invId);
    if (!invEntry) return '';                                  // sold / dropped mid-menu
    const cid = escapeHtml(casterId);

    // NESTED-EFFECT item (potion/coating/wondrous — anything that isn't a weapon firing attacks): the
    // choices are the item's own nested abilities. One group, badged with the ITEM's picture and labelled
    // with its name, so a healing potion reads "Healing Potion → [Drink] [Throw]". No equipped test — see
    // nestedFoldEntries(); the entries only exist if the compile layer already allowed them.
    if (abWeaponMenu.kind === 'nested') {
        const entries = nestedFoldEntries(caster, invId);
        if (entries.length === 0) return '';                   // consumed the last unit / lost the grant
        const nameRaw = (typeof ftInventoryField === 'function' ? ftInventoryField(invEntry, 'inventory_name') : invEntry.inventory_name);
        const label = String((Array.isArray(nameRaw) ? nameRaw[0] : nameRaw) ?? 'Item');
        // Medallions go through generateAbilityButton — the SAME dispatcher the bound ability slots use
        // (see renderSlotCell). That is what makes every ability shape fire correctly: an AOE ("Throw
        // Healing Potion" carries range + aoe_type, so it IS one) gets .use-ability-range and deploys its
        // placement template via the document-delegated AOE listener (aoe-range.ts:26) instead of firing
        // blind; item-granted spells ride their grant marker; ritual/counterspell/draw/throw keep their
        // specialized paths; and consumes_unit_per_use still decrements via nested_inventory_source
        // (ability-button.ts:487). A hand-rolled fire path here would silently break all of those.
        const btns = entries.map((entry) => {
            const aid = String(abilityEntryId(entry) ?? '');
            const ab = resolveAbilityNest(entry, caster);
            if (aid === '' || !itemNodeOf(ab)?.items) return '';
            const grantType = boundSpellGrantType(caster, aid, invId);
            const spellType = (grantType !== null && GRANT_CAST_MARKERS.has(grantType)) ? grantType : null;
            const consumeNested = boundAbilityConsumesNestedUnit(caster, aid, invId);
            return `<span class="ft-action-wfold-btn ft-action-wfold-btn--nested" data-ft-wfold-nested-ability="${escapeHtml(aid)}">`
                + generateAbilityButton(ab, caster, {
                    iconSize: 'l', spellType, source: invId,
                    consumeNestedUnitPerUse: consumeNested, castFreeOfMana: grantType !== null,
                    showChargePill: true,
                })
                + `</span>`;
        }).join('');
        if (btns === '') return '';
        const kindIcon = String(ftInventoryField?.(invEntry, 'item_type') ?? '') || 'consumable';
        return `<div class="ft-action-choicebar ft-action-wfold">`
            + `<div class="ft-action-wfold__inner"><div class="ft-action-wfold__groups">`
            + `<div class="ft-action-wfold-group"><div class="ft-action-wfold-group__frame">`
            + `<div class="ft-action-wfold-group__badge">${wfoldWeaponGroupIcon(caster, invId, invEntry, label, kindIcon)}</div>`
            + `<div class="ft-action-wfold-group__list">${btns}</div></div></div>`
            + `</div></div>`
            + `<button type="button" class="ft-action-choicebar__cancel ft-action-wfold__close" ${tip('Close', 'top')} data-ft-ab-action="weapon-fold-close" data-ft-caster-id="${cid}">&#10005;</button>`
            + `</div>`;
    }

    if (abWeaponMenu.kind === 'weapon' && !inventoryEquipped(invEntry, caster)) return ''; // weapon unequipped mid-menu

    // Bucket by provenance. An attack can compile in twice (e.g. a maneuver nested under two equipped
    // weapons' classifications survives the other-inventory filter once per weapon) — dedupe by ability
    // id, keeping the LOWEST-rank (most specific) group.
    const picked = new Map<string, { g: { key: string; rank: number; label: string; iconHtml: string }; entry: unknown }>();
    for (const entry of weaponFoldEntries(caster, invEntry, invId)) {
        const ab = resolveAbilityNest(entry, caster);
        if (!ab || typeof ab !== 'object') continue;
        const g = weaponFoldGroupFor(caster, entry, ab, invId, invEntry);
        const aid = String(abilityEntryId(entry) ?? '').toUpperCase();
        const prev = picked.get(aid);
        if (!prev || g.rank < prev.g.rank) picked.set(aid, { g, entry });
    }
    const groups = new Map<string, WFoldGroup>();
    for (const { g, entry } of picked.values()) {
        let slot = groups.get(g.key);
        if (!slot) { slot = { ...g, entries: [] }; groups.set(g.key, slot); }
        slot.entries.push(entry);
    }
    // Clicked-weapon groups always show the weapon's own picture on the border badge.
    for (const g of groups.values()) {
        if (g.rank === 0) g.iconHtml = wfoldWeaponGroupIcon(caster, invId, invEntry, g.label);
    }

    let groupsHtml = '';
    const ordered = [...groups.values()].sort((a, b) => (a.rank - b.rank) || a.label.localeCompare(b.label));
    for (const g of ordered) {
        const btns = sortAbilitySection(caster, 'attack_ability', g.entries)
            .map((e) => wfoldAbilityBtn(caster, casterId, invId, e)).join('');
        if (btns === '') continue;
        groupsHtml += `<div class="ft-action-wfold-group"><div class="ft-action-wfold-group__frame">`
            + `<div class="ft-action-wfold-group__badge">${g.iconHtml}</div>`
            + `<div class="ft-action-wfold-group__list">${btns}</div></div></div>`;
    }
    // ARCANE ARROW: carried spell scrolls this bow can loose (server: arcane_fletcher.php re-validates).
    if (abWeaponMenu.kind === 'weapon') {
        const scrollBtns = arcaneArrowScrolls(caster, invEntry)
            .map((row) => wfoldArcaneArrowBtn(caster, casterId, invId, row)).join('');
        if (scrollBtns !== '') {
            groupsHtml += `<div class="ft-action-wfold-group"><div class="ft-action-wfold-group__frame">`
                + `<div class="ft-action-wfold-group__badge">${iconMap('arcane_fletcher', 26, 26, 'Arcane Arrow')}</div>`
                + `<div class="ft-action-wfold-group__list">${scrollBtns}</div></div></div>`;
        }
    }

    // ONE toolbar row (owner 2026-10-15): attack mode | throw flight | carry verbs, each an iconMap glyph + short
    // label, sharing the close button's line so the medallion groups get the height.
    const tool = (attrs: string, icon: string, label: string, title: string, pressed?: boolean): string =>
        `<button type="button" class="ft-action-wfold-tool${pressed ? ' is-on' : ''}" ${attrs}`
        + (pressed === undefined ? '' : ` aria-pressed="${pressed}"`) + ` ${tip(title, 'top')}>`
        + `${iconMap(icon, 16, 16, false)}<span>${label}</span></button>`;
    const throwable = abWeaponMenu.kind === 'weapon' && ftLayerHas(invEntry, 'item_throwable');
    const thrown = weaponInThrowMode(casterId, invId, invEntry);
    const lob = thrown && weaponInLobMode(casterId, invId);
    const melee = String(ftLayerValue(invEntry, 'weapon_type') ?? '').toUpperCase() === MELEE_WEAPON_TYPE_ID;
    const modeHtml = throwable ? `<div class="ft-action-wfold__modes" role="group" aria-label="Weapon attack mode">`
        + tool(`data-ft-ab-action="weapon-fold-mode" data-ft-caster-id="${cid}" data-ft-mode="hit"`,
            melee ? 'melee_weapon_type' : 'ranged_weapon_type', melee ? 'Melee' : 'Normal', melee ? 'Melee attacks' : 'Normal attacks', !thrown)
        + tool(`data-ft-ab-action="weapon-fold-mode" data-ft-caster-id="${cid}" data-ft-mode="throw"`,
            'throw_weapon', 'Throw', 'Thrown attacks', thrown)
        + `</div>`
        // Direct/Lob (owner 2026-09-15): a thrown weapon flies straight by default; Lob arcs it over cover.
        + (thrown ? `<div class="ft-action-wfold__modes" role="group" aria-label="Throw flight">`
            + tool(`data-ft-ab-action="weapon-fold-lob" data-ft-caster-id="${cid}" data-ft-flight="direct"`,
                'line', 'Direct', 'Straight throw — needs a clear line', !lob)
            + tool(`data-ft-ab-action="weapon-fold-lob" data-ft-caster-id="${cid}" data-ft-flight="lob"`,
                'arcing_trajectory', 'Lob', 'Arc it over cover (blocked only by a ceiling)', lob)
            + `</div>` : '') : '';
    // Carry verbs on the held item (owner 2026-09-15): Drop at the feet; Throw as an OBJECT for anything that is
    // not a throwing weapon (a shield, a torch, a lantern) — the generic Throw Object lane, weight-ranged.
    const invAttr = `data-ft-caster-id="${cid}" data-ft-inventory-id="${escapeHtml(invId)}"`;
    const carryHtml = abWeaponMenu.kind === 'weapon' && inventoryEquipped(invEntry, caster)
        ? `<div class="ft-action-wfold__modes" role="group" aria-label="Carry">`
            + tool(`data-ft-ab-action="weapon-fold-drop" ${invAttr}`, 'drop_inventory', 'Drop', 'Set it down at your feet')
            + (throwable ? '' : tool(`data-ft-ab-action="weapon-fold-throw-object" ${invAttr}`,
                'throw_inventory', 'Throw', 'Hurl it as an object (range by weight; L while aiming = straight)'))
            + `</div>`
        : '';
    if (groupsHtml === '' && !throwable && carryHtml === '') return '';

    const naturalFoldAttr = abWeaponMenu.kind === 'natural'
        ? ` data-ft-natural-weapon-foldout="${escapeHtml(invId)}"`
        : '';
    const toolsHtml = modeHtml + carryHtml;
    return `<div class="ft-action-choicebar ft-action-wfold"${naturalFoldAttr}>`
        + (toolsHtml ? `<div class="ft-action-wfold__tools">${toolsHtml}</div>` : '')
        + `<div class="ft-action-wfold__inner"><div class="ft-action-wfold__groups">${groupsHtml}</div></div>`
        + `<button type="button" class="ft-action-choicebar__cancel ft-action-wfold__close" ${tip('Close', 'top')} data-ft-ab-action="weapon-fold-close" data-ft-caster-id="${cid}">&#10005;</button>`
        + `</div>`;
}

const ITEM_TARGET_SLOT_PRIORITY: Record<string, number> = {
    'E6D02ACB-D3E9-DD8B-762A-31FE8C8D898D': 0, // both_hands
    'CEF334BF-DE96-1BEA-F72A-A4C98FBF36AD': 1, // main_hand
    '4FF85144-67EC-9D87-AE28-15137A1A2481': 2, // off_hand
};

function inventoryRowsDeep(caster: CachedCaster): CasterItemNode[] {
    const out: CasterItemNode[] = [];
    const seen = new Set<string>();
    const walk = (entries: unknown): void => {
        if (!Array.isArray(entries)) return;
        for (const raw of entries) {
            const entry = itemNodeOf(raw);
            if (!entry) continue;
            const rawId = typeof ftInventoryField === 'function' ? ftInventoryField(entry, 'inventory_id') : entry.inventory_id;
            const id = String((Array.isArray(rawId) ? rawId[0] : rawId) ?? '').toUpperCase();
            if (id !== '' && !seen.has(id)) {
                seen.add(id);
                out.push(entry);
            }
            walk(entry.inventory_in_container);
        }
    };
    walk(caster.inventory);
    return out;
}

function itemTargetButton(entry: CasterItemNode, casterId: string, abilityId: string, holderName = ''): string {
    const rawId = typeof ftInventoryField === 'function' ? ftInventoryField(entry, 'inventory_id') : entry.inventory_id;
    const invId = String((Array.isArray(rawId) ? rawId[0] : rawId) ?? '');
    if (invId === '') return '';
    const rawName = typeof ftInventoryField === 'function' ? ftInventoryField(entry, 'inventory_name') : entry.inventory_name;
    const itemName = String((Array.isArray(rawName) ? rawName[0] : rawName) ?? ftItemsCol(entry, 'item_name') ?? 'Item');
    const name = holderName ? `${itemName} (${holderName})` : itemName;
    let face = typeof window.renderInventoryThumbnail === 'function'
        ? window.renderInventoryThumbnail(entry, {}, {
            flow_layout: true, show_overlay: false, use_size_based_width: false,
            thumbnail_width: 62, thumbnail_height: 62, is_draggable: false, show_log_message: 0,
            show_icons: false, dom_id: false, exclude_dropzone: true, omit_tooltip: true,
        })
        : iconMap(String(ftItemsCol(entry, 'item_type') ?? 'inventory'), 54, 54, false);
    // The medallion owns this click; prevent the global inventory target handler from also toggling it.
    face = face.replace(/\sdata-inventory_id="[^"]*"/g, '');
    return `<button type="button" class="ft-action-wfold-btn ft-action-item-target-btn"`
        + ` data-ft-ab-action="item-target-fire" data-ft-caster-id="${escapeHtml(casterId)}"`
        + ` data-ft-ability-id="${escapeHtml(abilityId)}" data-ft-inventory-id="${escapeHtml(invId)}"`
        + ` data-ft-info-item-id="${escapeHtml(invId)}" ${tip(name, 'top')}>${face}</button>`;
}

function renderItemTargetFoldout(casterId: string, caster: CachedCaster): string {
    if (!abItemTargetMenu || abItemTargetMenu.casterId !== casterId) return '';
    const allowed = new Set(abItemTargetMenu.allowedTypes);
    const candidates = inventoryRowsDeep(caster).filter((entry) => {
        const type = String(ftItemsCol(entry, 'item_type') ?? '').toLowerCase();
        const deleted = Number(ftInventoryField?.(entry, 'inventory_deleted') ?? 0);
        return allowed.has(type) && deleted === 0;
    });
    const equipped = candidates.filter(entry => inventoryEquipped(entry, caster)).sort((a, b) => {
        const slot = (entry: CasterItemNode): string => String(ftInventoryField?.(entry, 'equipped') ?? '').toUpperCase();
        return (ITEM_TARGET_SLOT_PRIORITY[slot(a)] ?? 99) - (ITEM_TARGET_SLOT_PRIORITY[slot(b)] ?? 99);
    });
    const bagged = candidates.filter((entry) => !inventoryEquipped(entry, caster));
    const ability = findAbility(caster, abItemTargetMenu.abilityId);
    const held = ability ? heldItemTargetsInRange(caster, ability, abItemTargetMenu.allowedTypes) : [];
    if (equipped.length === 0 && bagged.length === 0 && held.length === 0) return '';

    const group = (badge: string, entries: CasterItemNode[], holderName = ''): string => {
        if (entries.length === 0) return '';
        const buttons = entries.map((entry) => itemTargetButton(entry, casterId, abItemTargetMenu!.abilityId, holderName)).join('');
        return `<div class="ft-action-wfold-group"><div class="ft-action-wfold-group__frame">`
            + `<div class="ft-action-wfold-group__badge">${badge}</div>`
            + `<div class="ft-action-wfold-group__list">${buttons}</div></div></div>`;
    };
    // One group per creature within range, nearest first, badged with its portrait.
    const heldGroups = held.map(({ holder, entries }) => {
        const name = typeof getCreatureName === 'function' ? String(getCreatureName(holder) || '') : '';
        const url = creatureAvatarUrl(holder);
        const badge = url
            ? `<img src="${escapeHtml(url)}" width="26" height="26" style="border-radius:50%;object-fit:contain" alt="${escapeHtml(name)}" ${tip(name || 'Creature', 'top')}>`
            : iconMap('equipment', 26, 26, name || 'Creature');
        return group(badge, entries, name);
    }).join('');
    const groups = group(iconMap('equipment', 26, 26, 'Equipped'), equipped)
        + group(iconMap('container', 26, 26, 'In Bags'), bagged)
        + heldGroups;
    return `<div class="ft-action-choicebar ft-action-wfold ft-action-item-target-foldout" data-ft-item-target-foldout="${escapeHtml(abItemTargetMenu.abilityId)}">`
        + `<div class="ft-action-wfold__inner"><div class="ft-action-wfold__groups">${groups}</div></div>`
        + `<button type="button" class="ft-action-choicebar__cancel ft-action-wfold__close" ${tip('Close', 'top')}`
        + ` data-ft-ab-action="item-target-close" data-ft-caster-id="${escapeHtml(casterId)}">&#10005;</button></div>`;
}

export function ftActionBarItemTargetFire(casterId: string, abilityId: string, inventoryId: string): void {
    // Retire the scene pick armed beside the fold-out first: cancelling clears the target selection.
    retireHeldItemTargeting(casterId);
    window.ftSetTargetSelection?.(inventoryId, 'action-bar-item-target');
    window.useAbility?.(abilityId);
    setItemTargetMenu(null);
    repaintActionBars(casterId);
}

export function ftActionBarItemTargetClose(casterId: string): void {
    retireHeldItemTargeting(casterId);
    setItemTargetMenu(null);
    repaintActionBars(casterId);
}

function retireHeldItemTargeting(casterId: string): void {
    const active = abilityTargetingLifecycle.current();
    if (active?.casterId === casterId && active.profile.heldItemTargetTypes?.length) cancelAbilityTargeting(false);
}

/** An attached entry replaces the plain swing draft opened with the weapon slot. */
export function ftActionBarWeaponFoldFire(casterId: string, abilityId: string, invId: string): void {
    window.ftThrowMode = null;
    cancelAbilityTargeting(false);
    cancelActiveAoePlacement();
    abChoiceBranch = null;
    abChoice = null;
    abWeaponMenu = null;
    setItemTargetMenu(null);
    const caster = casterFromCache(casterId);
    const ability = caster ? findAbility(caster, abilityId) : null;
    const weapon = caster ? findInventoryEntry(caster, invId) ?? findAbility(caster, invId) : null;
    const thrown = !!caster && !!weapon && weaponInThrowMode(casterId, invId, weapon);
    if (caster && ability && weapon) {
        const reason = weaponArmRefusal(caster, weapon, invId, abilityId, thrown);
        if (reason) { reportWeaponAttackRefusal(reason, { actorId: casterId, abilityId, weaponInventoryId: invId }); repaintActionBars(casterId); return; }
    }
    const profile = caster && ability && weapon ? weaponPinnedTargetingProfile(caster, ability, weapon, invId, thrown) : null;
    if (caster && ability && profile) {
        armCreatureTargeting(casterId, abilityId, caster, ability, profile, { kind: 'plain', abilityId },
            () => window.useAbilityWithWeapon?.(abilityId, invId, null, thrown ? 'throw_weapon' : null));
    } else refuseUnported('weapon:targeting-profile-unavailable', { actorId: casterId, abilityId, weaponInventoryId: invId });
    repaintActionBars(casterId);
}

/** Fire the weapon's PLAIN attack (the hand slot's default action). Mirrors the slot's
 *  pre-fold-out onclick: a throwing weapon throws, anything else hits via useAbility(inventory_id) —
 *  the inventory id is the item used. */
export function ftActionBarWeaponFoldBasic(casterId: string, invId: string, preserveFoldout = false): void {
    const caster = casterFromCache(casterId);
    const entry = caster ? findInventoryEntry(caster, invId) : null;
    const thrown = !!entry && weaponInThrowMode(casterId, invId, entry);
    if (ftDispatchWeaponClick(casterId, invId, thrown ? 'throw' : 'hit', preserveFoldout)) return;
    const atk = (caster && entry) ? inventoryAttackAction(caster, entry, invId) : null;
    if (thrown || (atk && atk.iconId === 'throw_weapon')) window.useAbilityVariable1?.(invId, 'throw_weapon');
    else window.useAbility?.(invId);
    if (!preserveFoldout) abWeaponMenu = null;
    repaintActionBars(casterId);
}

/** Close the weapon fold-out (✕) and repaint. */
export function ftActionBarWeaponFoldClose(casterId: string): void {
    abWeaponMenu = null;
    repaintActionBars(casterId);
}

/** The small-screen top-row tab strip (full-screen dock AND in-sheet bar). Tabs: Abilities / Spells (only when
 *  present) / Status (only when a status cluster exists) / Conditional (only when a contextual/situational action
 *  exists — its presence is the subtle signal). The
 *  settings GEAR is a STANDALONE button at the right (not a tab). Icon-only (tooltip carries the name) to save
 *  width. CSS hides the strip on desktop and shows ONE group per active tab. */
function renderTopTabStrip(casterId: string, active: TopTab, groups: Set<string>, hasStatus: boolean, hasContext: boolean, canEdit: boolean, settingsIsOpen: boolean, inDock: boolean, inventoryOpen: boolean): string {
    const cid = escapeHtml(casterId);
    const tab = (id: TopTab, icon: string, label: string, extra = '') =>
        `<button type="button" class="ft-action-toptab${extra}${active === id ? ' is-active' : ''}" role="tab" aria-label="${escapeHtml(label)}" aria-selected="${active === id}" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${escapeHtml(label)}" data-ft-toptab-btn="${id}" data-ft-ab-action="top-tab" data-ft-caster-id="${cid}" data-ft-tab="${id}">${iconMap(icon, 18, 18, false)}</button>`;
    // Standalone gear (settings) — its own button to the right, separated from the tabs (opens the floating tray).
    const gear = canEdit
        ? `<button type="button" class="ft-action-topgear${settingsIsOpen ? ' is-open' : ''}" aria-label="Bar settings" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="Bar settings" data-ft-ab-action="toggle-settings" data-ft-caster-id="${cid}">${iconMap('settings', 18, 18, false)}</button>`
        : '';
    const inventoryKey = ft3dActionBinding('inventory');
    const inventoryLabel = inventoryKey ? `Inventory (${ft3dActionBindingDisplay('inventory')})` : 'Inventory';
    const inventory = inDock
        ? `<button type="button" class="ft-action-topgear ft-action-topinventory ft3d-inventory-toggle${inventoryOpen ? ' is-open' : ''}" aria-label="${escapeHtml(inventoryLabel)}"${inventoryKey ? ` aria-keyshortcuts="${escapeHtml(inventoryKey)}"` : ''} aria-pressed="${inventoryOpen ? 'true' : 'false'}" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${escapeHtml(inventoryLabel)}">${iconMap('inventory', 18, 18, false)}</button>`
        : '';
    // Spells / Curses / Mentas are SEPARATE tabs, each shown only when the caster possesses that pool.
    return `<div class="ft-action-bars__toptabs" role="tablist">`
        + tab('abilities', 'abilities', 'Abilities')
        + (groups.has('spells') ? tab('spells', 'spell', 'Spells') : '')
        + (groups.has('curses') ? tab('curses', 'curse', 'Curses') : '')
        + (groups.has('mentas') ? tab('mentas', 'menta', 'Mentas') : '')
        + (hasStatus ? tab('status', 'status_effect', 'Status') : '')
        + (hasContext ? tab('conditional', 'trigger', 'Actions', ' ft-action-toptab--cond') : '')
        + inventory
        + gear
        + `</div>`;
}

/** Switch the small-screen top-row tab and repaint. No-op for the same tab (avoids a needless rebuild). */
export function ftActionBarTopTab(casterId: string, tab: TopTab): void {
    if (topTab.get(casterId) === tab) return;
    topTab.set(casterId, tab);
    repaintActionBars(casterId);
}

/** Toggle ritual-armed for an ability (the fold-up's Ritual button). The AOE confirm reads the shared set. */
export function ftActionBarToggleRitual(casterId: string, abilityId: string): void {
    const set = ritualArmedSet();
    const U = String(abilityId).toUpperCase();
    if (set.has(U)) set.delete(U); else set.add(U);
    repaintActionBars(casterId);
}

/** Step the AOE upcast LEVEL in the fold-up (+/−). Clamped at the spell's base level.
 *  Stores the level in the shared Map, resizes FT3D placement (AOEManager.setUpcastLevel), then repaints
 *  the panel so the value and area note update. FT3DAOEPlacement reads the Map at confirm. */
export function ftActionBarAoeUpcastStep(casterId: string, abilityId: string, delta: number): void {
    const caster = casterFromCache(casterId);
    const ab = caster ? findAbility(caster, abilityId) : null;
    const baseLvl = abilityCastBaseLevel(ab);
    setActionBarAoeUpcastLevel(casterId, abilityId,
        (upcastLevelMap().get(String(abilityId).toUpperCase()) ?? baseLvl) + (Number(delta) || 0));
}

/** Typed values and step buttons share bounds, preview updates, and cast state. */
function setActionBarAoeUpcastLevel(casterId: string, abilityId: string, value: number): void {
    const caster = casterFromCache(casterId);
    const ab = caster ? findAbility(caster, abilityId) : null;
    const baseLvl = abilityCastBaseLevel(ab);
    const maxLvl = caster ? targetingMaxSpellLevel(caster, baseLvl, ab) : baseLvl;
    const U = String(abilityId).toUpperCase();
    const map = upcastLevelMap();
    const lvl = Math.max(baseLvl, Math.min(maxLvl, Number.isFinite(value) ? Math.trunc(value) : baseLvl));
    map.set(U, lvl);
    window.aoeManager?.setUpcastLevel?.(abilityId, lvl);
    repaintActionBars(casterId);
}

/** Arm the area-shaped pre-cast fold-up from either the Action Bar click interceptor or AOEManager.
 * Keeping this as one idempotent bridge removes event-phase ordering from the placement lifecycle. */
export function ftActionBarArmAoeChoice(button: HTMLElement): boolean {
    const pane = button.closest(AB_PANE_SEL) as HTMLElement | null;
    const casterId = pane?.getAttribute('data-ft-caster-id') || '';
    const inFt3dCasterSurface = !!button.closest('[data-ft3d-actor-dock]')
        || (!!casterId && ft3dDockPresentFor(casterId));
    if (button.dataset.ftAoeReadyLaunch === '1'
        || !button.classList.contains('use-ability-range')
        || !inFt3dCasterSurface) return false;
    const abilityId = button.getAttribute('data-ability_id') || '';
    if (!casterId || !abilityId) return false;
    const inputId = button.getAttribute('data-input_id_reference') || '';
    const caster = casterFromCache(casterId);
    const ability = caster ? findAbility(caster, abilityId) : null;
    const nestedInventoryId = button.getAttribute('data-nested-inventory-id') || '';
    const sourceId = button.getAttribute('data-source') || '';
    const generatedSnippet = button.getAttribute('data-ft-use-snippet') || '';
    // Consumables have a dedicated consume_inventory_id request field when a choice needs the two
    // variable slots. Only an exact non-consumable source invocation truly occupies variable_2.
    const grantCast = isSpellGrantMode(button.getAttribute('data-spell_type'));
    const sourceOccupiesVariable2 = nestedInventoryId === '' && sourceId !== ''
        && (grantCast || generatedSnippet.includes(sourceId));
    const ritual = !!ability && !sourceOccupiesVariable2 && ftLayerHas(ability, 'spell_ritual');
    const isUpcastable = !grantCast && !sourceOccupiesVariable2 && ((!!ability && spellIsLevelUpcastable(ability))
        || (!ability && inputId.endsWith('_spell_upcastable')));
    const itemSourced = abBtnItemSourced(button);
    const hasOwnInput = inputId !== '' && !sourceOccupiesVariable2
        && !(grantCast && inputId.endsWith('_spell_upcastable'));
    const wantsSanguinePick = !!caster && !!ability
        && sanguineDmgPickOptionIds(caster, ability, itemSourced).length > 0;
    // Plain AOEs skip configuration entirely: the first button click enters placement immediately.
    if (!ritual && !isUpcastable && !wantsSanguinePick && !hasOwnInput) return false;
    claimActionBarOverlayLane(casterId, 'ability-choice');
    abChoice = {
        casterId,
        abilityId,
        inputId,
        ritual,
        isAoe: true,
        aoeButton: button,
        consumeInvId: nestedInventoryId || undefined,
        itemSourced,
        sourceOccupied: sourceOccupiesVariable2,
        spellType: button.getAttribute('data-spell_type') || undefined,
    };
    if (isUpcastable && ability) {
        upcastLevelMap().set(abilityId.toUpperCase(), abilityCastBaseLevel(ability));
    }
    if (caster && ability) {
        actionOptionValueMap().set(actionOptionKey(abilityId), initialActionOptionValues(caster, ability, inputId));
    }
    setTimeout(() => repaintActionBars(casterId), 0);
    return true;
}

function refuseAoeReady(casterId: string, message: string): false {
    abChoice = null;
    abChoiceBranch = null;
    window.displayWarning?.(message);
    repaintActionBars(casterId);
    return false;
}

/** Retry through the normal gate with the exact invocation, even after its fly-up has closed. */
export function ftActionBarAoeReady(casterId: string, abilityId: string): boolean {
    const panes = Array.from(document.querySelectorAll<HTMLElement>(AB_PANE_SEL))
        .filter(node => String(node.getAttribute('data-ft-caster-id') || '').toUpperCase() === casterId.toUpperCase());
    const pane = panes.find(node => node.closest('[data-ft3d-actor-dock]')) ?? panes[0];
    const held = abChoice?.casterId === casterId && abChoice.abilityId.toUpperCase() === abilityId.toUpperCase()
        ? abChoice.aoeButton : undefined;
    const button = held ?? panes.flatMap(node => Array.from(node.querySelectorAll<HTMLElement>('.btn_ability_ranges.use-ability-range[data-ability_id]')))
        .find(node => String(node.dataset.ability_id || '').toUpperCase() === String(abilityId).toUpperCase());
    if (!button || !pane) return refuseAoeReady(casterId, 'Select this character and choose the area ability again; its cast button is no longer available.');
    const start = window.ftStartAoePlacement;
    if (!start) return refuseAoeReady(casterId, 'Area placement is not available on this page. Open the FT3D campaign view and choose the ability again.');
    // Placement/cast readers need the original caster surface as well as the source/marker dataset.
    const host = document.createElement('div');
    host.hidden = true;
    if (!button.isConnected) { host.appendChild(button); pane.appendChild(host); }
    button.dataset.ftAoeReadyLaunch = '1';
    abChoice = null;
    abChoiceBranch = null;
    try {
        return start(button);
    } catch (error) {
        console.error('AOE Ready failed', error);
        window.aoeManager?.cancelFt3d?.();
        return refuseAoeReady(casterId, 'Area placement could not start. Choose this ability again from the FT3D action bar.');
    } finally {
        delete button.dataset.ftAoeReadyLaunch;
        host.remove();
        repaintActionBars(casterId);
    }
}

/** Hand a picked ability_choice BRANCH that is an area ability to FT3D placement. The branch owns no slot
 *  button, so build its canonical one (generateAbilityButton: range, aoe dims, input reference, consumable
 *  source) and mount it in the caster's dock pane only for the start — ftActionBarArmAoeChoice needs the
 *  pane to fold up the CHILD's own options (upcast stepper etc.). Placement keeps the node and casts from
 *  its dataset, so unmounting it is safe. `level` is the cast level already picked in the branch panel —
 *  the start re-seeds the child's stepper at its base, so it is re-applied to the live template.
 *  'not-area' = the branch is not placeable (no range); the caller decides how to fire it. */
function startBranchAoePlacement(
    casterId: string,
    caster: CachedCaster,
    branch: CasterItemNode,
    opts: { consumeInvId: string | null; spellType: string | null; sourceId: string | null; level: string },
): 'started' | 'refused' | 'not-area' {
    const { consumeInvId, spellType, sourceId, level } = opts;
    const template = document.createElement('template');
    // The branch button carries the SLOT's cast shape: an at-will / innate / charged marker (billed against the
    // granting parent server-side) and its item source, exactly as the slot's own generateAbilityButton call does.
    template.innerHTML = generateAbilityButton(branch, caster, {
        spellType,
        source: consumeInvId || sourceId,
        consumeNestedUnitPerUse: !!consumeInvId,
        castFreeOfMana: !!spellType,
    });
    const button = template.content.querySelector<HTMLElement>('.use-ability-range[data-ability_id]');
    const aoe = window.aoeManager;
    if (!button) return 'not-area';
    if (typeof aoe?.startFt3d !== 'function') return 'refused';
    const branchId = String(button.getAttribute('data-ability_id') || '').toUpperCase();
    const safe = String(casterId).replace(/["\\]/g, '');
    const pane = document.querySelector(`[data-ft3d-actor-dock] ${AB_PANE_SEL}[data-ft-caster-id="${safe}"]`)
        ?? document.querySelector(`${AB_PANE_SEL}[data-ft-caster-id="${safe}"]`);
    const host = document.createElement('div');
    host.hidden = true;
    host.setAttribute('data-ft-branch-aoe-launch', '');
    host.appendChild(button);
    pane?.appendChild(host);
    try {
        cancelAbilityTargeting(false);
        aoe.startFt3d(button);
    } finally {
        host.remove();
    }
    const intent = aoe.ft3dActive?.envelope?.intent;
    if (String(intent?.abilityId || '').toUpperCase() !== branchId
        || String(intent?.casterId || '').toUpperCase() !== String(casterId).toUpperCase()) {
        return 'refused';
    }
    if (level !== '' && spellIsLevelUpcastable(branch) && Number.isFinite(Number(level))) {
        upcastLevelMap().set(branchId, Number(level));
        aoe.setUpcastLevel?.(branchId, Number(level));
    } else if (level !== '') {
        // Any other branch control (damage type / form): carry the pick into the child's placement options.
        const current = actionOptionValueMap().get(actionOptionKey(branchId)) ?? { primary: '', secondary: '' };
        actionOptionValueMap().set(actionOptionKey(branchId), { ...current, primary: level });
    }
    return 'started';
}

/** Dedicated spell choices use the normal scene draft and placement, with an explicit native dispatch. */
type ExtraordinaryPlacement = { staged: Ft3dAoePlacementStageEnvelope | null; envelope: Ft3dAoePlacementEnvelope;
    summonPoints?: Ft3dAoePoint[]; castOverride?: ((point: Ft3dAoePoint) => void) | null };
async function armExtraordinarySpell(casterId: string, ability: CasterItemNode, summonPick: string,
    commit: (targets: ExtraordinaryTargets) => void): Promise<boolean> {
    const caster = casterFromCache(casterId);
    if (!caster) return false;
    cancelAbilityTargeting(false);
    cancelActiveAoePlacement();
    const abilityId = String(ftItemsCol(ability, 'item_id') ?? '');
    const summonId = summonPick || fixedSummonCreatureId(ability);
    if (summonId && usesGroundSummonPlacement(ability)) {
        return armFt3dSummonPlacement(casterId, abilityId, summonId, () => {
            const active = window.aoeManager?.ft3dActive as ExtraordinaryPlacement | null | undefined;
            const center = active?.staged?.center ?? active?.summonPoints?.[active.summonPoints.length - 1];
            if (!center) { window.displayWarning?.('Choose where to summon.'); return; }
            commit({ targetIds: [], selection: active?.staged?.targetSelection,
                summon: { point: center, points: active?.summonPoints ?? [], ...(summonPick ? { pick: summonPick } : {}) } });
        }, null, ability);
    }
    if (ftLayerHas(ability, 'aoe_type') && !ftLayerHas(ability, 'aoe_follows_target')) {
        const result = startBranchAoePlacement(casterId, caster, ability, { consumeInvId: null, spellType: null, sourceId: null, level: '' });
        const active = window.aoeManager?.ft3dActive as ExtraordinaryPlacement | null | undefined;
        if (result !== 'started' || !active) return false;
        active.castOverride = () => {
            const stage = active.staged, intent = active.envelope.intent;
            if (!stage || !intent) return;
            const selection = stage.targetSelection;
            commit({ targetIds: [], selection, placementProof: { ...stage, ...intent },
                placed: { targetId: selection,
                    ...(stage.propagation ? { propagation: { casterId, abilityId, targetSelection: selection, mask: stage.propagation } } : {}),
                    ...(stage.wallGeometry ? { paintedWall: { casterId, abilityId, targetSelection: selection, geometry: stage.wallGeometry } } : {}) } });
        };
        return true;
    }
    if (String(ftLayerValue(ability, 'range') ?? '').toUpperCase() === 'D82855E1-4BB9-93EA-6374-B9904318BC1D') {
        commit({ targetIds: [casterId] });
        return true;
    }
    const profile = effectiveAbilityTargetingProfile(caster, ability, '');
    if (!profile.supported) return false;
    armCreatureTargeting(casterId, abilityId, caster, ability, profile.profile, { kind: 'plain', abilityId },
        confirmed => commit({ targetIds: [...confirmed.selectedEntityIds], selection: confirmed.selectedEntityIds.join(',') }));
    return abilityTargetingLifecycle.current()?.abilityId === abilityId;
}

/** Open normal creature targeting or area placement from another surface. */
function armAbilityTargeting(casterId: string, abilityId: string): boolean {
    // TRUE only when a targeting draft / placement for THIS ability is actually open (a refused click — no Psi / AP — also answers
    // "handled" from ftDispatchAbilityClick, Codex review 2026-10-08).
    const up = (v: unknown): string => String(v ?? '').trim().toUpperCase();
    const opened = (): boolean => up(abilityTargetingLifecycle.current()?.abilityId) === up(abilityId)
        || up(window.aoeManager?.ft3dActive?.envelope?.intent?.abilityId) === up(abilityId);
    if (ftDispatchAbilityClick(casterId, abilityId)) return opened();
    const caster = casterFromCache(casterId);
    const ability = caster ? findAbility(caster, abilityId) : null;
    if (!caster || !ability || !ftLayerValue(ability, 'aoe_type')) return false;
    if (actionBarAbilityClickRefused(casterId, abilityId)) return false; // the same pre-cast refusal the slot click runs
    return startBranchAoePlacement(casterId, caster, ability, { consumeInvId: null, spellType: null, sourceId: null, level: '' }) === 'started' && opened();
}

/** Repaint EVERY mounted dock action-bar pane (no caster arg) — the AOE flow calls this (via window) on
 *  placement start/end so the gold "this ability is active" glow appears/clears immediately. */
function repaintAllActionBars(): void {
    if (typeof document === 'undefined') return;
    document.querySelectorAll<HTMLElement>('.ft-pc-sheet-pane[data-ft-pc-pane="action_bar"][data-ft-caster-id]').forEach((el) => {
        const cid = el.getAttribute('data-ft-caster-id') || '';
        const c = cid ? casterFromCache(cid) : null;
        if (c) renderActionBar(c, cid, el);
    });
}

/** Clear any open choice/option panel (called by the AOE confirm after a cast). */
function clearAbChoice(): void {
    if (!abChoice) return;
    const cid = abChoice.casterId;
    actionOptionValueMap().delete(actionOptionKey(abChoice.abilityId));
    abChoiceBranch = null;   // panel identity changed — never carry a stale branch pick over
    abChoice = null;
    repaintActionBars(cid);
}

/** Stepper +/− button → step its sibling number input, clamped to min/max. (Sibling-based, no id lookup.) */
export function ftAbChoiceStep(btn: HTMLElement, delta: number): void {
    const el = btn?.parentElement?.querySelector('input') as HTMLInputElement | null;
    if (!el) return;
    const min = el.min !== '' ? Number(el.min) : 0;
    const max = el.max !== '' ? Number(el.max) : Infinity;
    let v = (Number(el.value) || min) + (Number(delta) || 0);
    v = Math.max(min, Math.min(max, v));
    el.value = String(v);
    if (abChoice) {
        const key = actionOptionKey(abChoice.abilityId);
        const current = actionOptionValueMap().get(key) ?? { primary: '', secondary: '' };
        actionOptionValueMap().set(key, { ...current, primary: String(v) });
    }
    const caster = abChoice ? casterFromCache(abChoice.casterId) : null;
    const ability = caster && abChoice ? findAbility(caster, abChoice.abilityId) : null;
    if (abChoice && ability && actionInputFamily(ability, abChoice.inputId) === 'spell-level') {
        upcastLevelMap().set(abChoice.abilityId.toUpperCase(), v);
        repaintActionBars(abChoice.casterId);
    }
}

/** Read the clicked sheet's control before a duplicate in another open sheet. */
function abilityButtonInput(button: HTMLElement | null | undefined, inputId: string): HTMLInputElement | HTMLSelectElement | null {
    if (!inputId) return null;
    return button?.closest('.ft-pc-sheet-pane')?.querySelector<HTMLInputElement | HTMLSelectElement>(`[id="${CSS.escape(inputId)}"]`)
        ?? document.getElementById(inputId) as HTMLInputElement | HTMLSelectElement | null;
}

/** Capture-phase click intercept: a dock or sheet ability that needs a choice (an extra value OR ritual casting)
 *  opens the choice bar INSTEAD of firing. Registered AFTER the drag click-block so a post-drag click never
 *  opens it. Capture stops the delegated Use handler from firing. AOE abilities (their own
 *  placement flow owns upcast/targeting) and disabled buttons are left alone. Re-clicking the SAME slot while
 *  its panel is open acts as Cast (saves a trip to the button — BG3-style confirm, owner UX request 2026-06-26). */
function onAbilityChoiceClickIntercept(e: MouseEvent): void {
    const t = e.target as HTMLElement | null;
    if (!t || typeof t.closest !== 'function') return;
    const inActionBarDock = !!t.closest('.ft3d-actionbar-dock');
    // A sheet's Use belongs to its creature, including an inspected sheet opened while another
    // creature owns the dock. Select through the shell's existing control gate before routing the cast.
    const chipAbilityButton = t.closest('.ft-action-tgt-chip')
        ?.closest('.ft-action-slot')
        ?.querySelector<HTMLElement>('.btn_ability_ranges[data-ability_id]') ?? null;
    const clickedAbilityButton = chipAbilityButton
        ?? (t.closest('.btn_ability_ranges[data-ability_id]') as HTMLElement | null);
    const clickedPane = clickedAbilityButton
        ?.closest<HTMLElement>('.ft-pc-sheet-pane[data-ft-caster-id]') ?? null;
    const clickedCasterId = clickedPane?.getAttribute('data-ft-caster-id') || '';
    const inFt3dActorDock = !!clickedAbilityButton?.closest('[data-ft3d-actor-dock]');
    const inFt3dSheet = !inFt3dActorDock && !!clickedPane && !!clickedCasterId
        && typeof window.ftFt3dSelectActor === 'function';
    if (inFt3dSheet && !clickedAbilityButton?.hasAttribute('disabled') && !ftActionBarSelectUnit(clickedCasterId)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        refuseUnported('ability-click:actor-unavailable', { actorId: clickedCasterId }, 'You do not control this creature.');
        return;
    }
    const inControlledFt3dSheet = !inFt3dActorDock && !!clickedAbilityButton && !!clickedCasterId
        && ft3dDockPresentFor(clickedCasterId);
    if (clickedAbilityButton && (inFt3dActorDock || inControlledFt3dSheet)) {
        const pane = clickedPane;
        const casterId = pane?.getAttribute('data-ft-caster-id') || '';
        const abilityId = clickedAbilityButton.getAttribute('data-ability_id') || '';
        if (!clickedAbilityButton.hasAttribute('disabled')
            && actionBarAbilityClickRefused(casterId, abilityId, clickedAbilityButton)) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
        }
        if (actionPointFoldOpen.delete(casterId)) setTimeout(() => repaintActionBars(casterId), 0);
        // A different ability owns the dock as soon as it is clicked, even when that next ability has no
        // option row of its own (plain AOEs such as Greasy Sludge are the important case). Previously the
        // old upcast choice survived that no-panel path, so Fatal Bolt and the new AOE both kept a gold glow.
        // Defer only the repaint: the clicked node must remain attached for the delegated cast/AOE handler.
        const displacedChoiceCasterId = abChoice
            && (abChoice.casterId !== casterId
                || abChoice.abilityId.toUpperCase() !== abilityId.toUpperCase())
            ? abChoice.casterId : '';
        if (displacedChoiceCasterId) {
            abChoiceBranch = null;
            abChoice = null;
            setTimeout(() => repaintActionBars(displacedChoiceCasterId), 0);
        }
        const caster = casterId ? casterFromCache(casterId) : null;
        const ability = caster && abilityId ? findAbility(caster, abilityId) : null;
        const itemType = String(ability ? ftItemsCol(ability, 'item_type') ?? '' : '').toLowerCase();
        const itemSourced = abBtnItemSourced(clickedAbilityButton);
        const isAoeButton = clickedAbilityButton.classList.contains('use-ability-range');
        // Clicking Fatal Bolt (or any non-AOE ability) supersedes a cube/cone/etc. immediately, even when
        // the new ability first opens an upcast/options row before its creature-targeting intent is armed.
        if (!isAoeButton) cancelActiveAoePlacement();
        // A fixed-creature summon with something to configure on the dock (an upcast level or other input, the Ritual
        // toggle, a Sanguine pick) opens the option panel first; its Confirm arms ground placement. Arming placement
        // straight from the click skipped the panel, so the dock always cast at base level and never as a ritual
        // (owner 2026-10-07: Summon Giant Constrictor Snake, Summon Phantom Steed …). The open sheet keeps its direct
        // arm — its spell row owns the real level input and its own ritual button. So does an exact item/weapon source
        // invocation: the panel's configured fire cannot carry that source, the generated snippet can.
        const generatedSnippet = clickedAbilityButton.getAttribute('data-ft-use-snippet') || '';
        const sourceId = clickedAbilityButton.getAttribute('data-source') || '';
        const sourceInvocation = sourceId !== '' && generatedSnippet.includes(sourceId);
        const canonicalInputId = clickedAbilityButton.getAttribute('data-input_id_reference') || '';
        const fixedSummonConfiguresOnDock = inFt3dActorDock && !!ability && !sourceInvocation
            && fixedSummonCreatureId(ability) !== '' && usesGroundSummonPlacement(ability)
            && !!(clickedAbilityButton.closest('.ft-action-slot') || clickedAbilityButton.closest('.ft-ability-flyout'))
            && (ftLayerHas(ability, 'spell_ritual')
                || canonicalInputId !== ''
                || sanguineDmgPickOptionIds(caster, ability, itemSourced).length > 0);
        const specialized = !!ability && (
            fixedSummonConfiguresOnDock
            || ftLayerHas(ability, 'attack_ability_throws_the_throwing_weapon_at_target')
            || ftLayerHas(ability, 'draw_sheathed_weapon_for_attack_ability')
            || ftLayerHas(ability, 'counterspelling_effect')
            // Upcastable summons also need a creature choice; let the choice bar collect both.
            || ftLayerHas(ability, 'user_choice_1_summon')
            || ftLayerHas(ability, 'spell_ritual')
            || isAbilityChoiceParent(ability)
            || sanguineDmgPickOptionIds(caster, ability, itemSourced).length > 0
        );
        const inputFamily = actionInputFamily(ability, canonicalInputId);
        const dispatch = classifyAbilityDispatchVariant({
            aoe: isAoeButton,
            inputId: canonicalInputId,
            inputKind: targetingDispatchInputKind(inputFamily),
            trigger: clickedAbilityButton.hasAttribute('data-ft-trigger-use-snippet'),
            dedicated: (itemType === 'sanguine' || itemType === 'spell') && isDedicatedCastSanguine(abilityId),
            // This flag preserves the exact source-aware invocation; it does not decide whether the ability
            // targets. Target eligibility/profile are derived independently below.
            weaponPinned: sourceInvocation,
            itemTarget: clickedAbilityButton.hasAttribute('data-ft-item-target-types'),
            markerOrSource: !!(clickedAbilityButton.getAttribute('data-spell_type')
                || clickedAbilityButton.getAttribute('data-nested-inventory-id')),
            specialized,
        });
        const initialNumericSpec = ability && caster && targetingInputFamilyIsNumeric(inputFamily)
            ? numericActionInputSpec(caster, ability, inputFamily)
            : null;
        const sheetNumericValue = canonicalInputId && inControlledFt3dSheet
            ? Number(abilityButtonInput(clickedAbilityButton, canonicalInputId)?.value)
            : Number.NaN;
        const initialNumericValue = initialNumericSpec
            ? Math.max(initialNumericSpec.min, initialNumericSpec.max === ''
                ? (Number.isFinite(sheetNumericValue) ? sheetNumericValue : initialNumericSpec.value)
                : Math.min(initialNumericSpec.max, Number.isFinite(sheetNumericValue) ? sheetNumericValue : initialNumericSpec.value))
            : 0;
        const initialDynamicMaxTargets = ability && initialNumericSpec
            ? dynamicActionMaxTargets(ability, inputFamily, String(initialNumericValue))
            : undefined;
        const profile = ability && caster
            ? effectiveAbilityTargetingProfile(
                caster,
                ability,
                sourceInvocation ? sourceId : '',
                initialDynamicMaxTargets,
            )
            : null;
        const active = abilityTargetingLifecycle.current();
        const sameActive = !!active && !(active.invocation.kind === 'variable-1-choice' && active.invocation.value === 'damage_only')
            && active.casterId === casterId
            && (active.abilityId.toUpperCase() === abilityId.toUpperCase()
                || active.profile.projectilePool?.instant?.parentId.toUpperCase() === abilityId.toUpperCase());
        // A held-item draft never confirms from its button: re-click falls through (the draft is cancelled
        // below) to the default cast on the wielded item.
        if (sameActive && !active?.profile.heldItemTargetTypes?.length) {
            e.preventDefault();
            e.stopPropagation();
            confirmAbilityTargeting(casterId);
            return;
        }
        // A volley parent's Self range grants darts; it is not the darts' target. Arm the child
        // before generic Self dispatch, with the parent's level control kept in the draft.
        const spellType = clickedAbilityButton.getAttribute('data-spell_type') || '';
        const grantCast = isSpellGrantMode(spellType);
        if (!specialized && (!sourceInvocation || grantCast) && !isAoeButton && (!itemSourced || grantCast)
            && (!spellType || grantCast)
            && !clickedAbilityButton.hasAttribute('disabled')
            && armInstantProjectileSpell(casterId, abilityId, !grantCast && initialNumericSpec ? String(initialNumericValue) : undefined,
                spellType, '', { creatureOnly: true, sourceId })) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        const fixedSummonId = fixedSummonCreatureId(ability);
        if (fixedSummonId && ability && usesGroundSummonPlacement(ability) && !fixedSummonConfiguresOnDock
            && caster && casterId && abilityId && !clickedAbilityButton.hasAttribute('disabled')
            && !clickedAbilityButton.hasAttribute('data-ft-trigger-use-snippet')) {
            e.preventDefault();
            e.stopPropagation();
            abChoiceBranch = null;
            abChoice = null;
            abWeaponMenu = null;
            setItemTargetMenu(null);
            void armFt3dSummonPlacement(
                casterId,
                abilityId,
                fixedSummonId,
                () => generatedSnippet ? runGeneratedAbilitySnippet(generatedSnippet) : window.useAbility?.(abilityId),
                clickedAbilityButton,
            );
            return;
        }
        if (dispatch.route === 'ability-targeting'
            && profile?.supported && ability && caster && casterId && abilityId
            && !clickedAbilityButton.hasAttribute('disabled')) {
            e.preventDefault();
            e.stopPropagation();
            abChoiceBranch = null;
            abChoice = null;
            abWeaponMenu = null;
            setItemTargetMenu(null);
            let invocation: ActiveAbilityTargetingIntent['invocation'];
            if (dispatch.invocation.kind === 'variable-1-number') {
                invocation = {
                    kind: 'variable-1-number',
                    abilityId,
                    inputId: dispatch.invocation.inputId,
                    // A sheet row owns a real canonical input. Preserve the level the player already
                    // selected; the dock has no such input and starts from the authored spell level.
                    value: initialNumericValue,
                    // A spell may author both an upcast level and a damage-type choice. The wire
                    // contract has two variable slots, so keep both decisions in the targeting intent.
                    secondaryChoice: ftLayerEntries(ability, 'damage_type').length > 1
                        ? choiceEntryUuid(ftLayerEntries(ability, 'damage_type')[0])
                        : undefined,
                };
            } else if (dispatch.invocation.kind === 'variable-1-choice') {
                const authoredDefault = choiceEntryUuid(ftLayerEntries(ability, 'damage_type')[0]);
                invocation = {
                    kind: 'variable-1-choice',
                    abilityId,
                    inputId: dispatch.invocation.inputId,
                    // Match numeric upcasting above: a click from the open sheet retains that row's
                    // current select, while the dock starts from the first authored damage type.
                    value: (inControlledFt3dSheet
                        ? abilityButtonInput(clickedAbilityButton, dispatch.invocation.inputId)?.value
                        : '') || authoredDefault,
                };
            } else {
                invocation = { kind: 'plain', abilityId };
            }
            // Targeting and options are one phase: a ranged upcast spell publishes its LOS intent on
            // this first click, and its live level stepper rides in the targeting bar. No Ready gate.
            armCreatureTargeting(casterId, abilityId, caster, ability, profile.profile, invocation);
            return;
        }
        // A generated sheet button can carry a legacy invocation that still composes cleanly with
        // creature targeting (at-will/charged marker, selected form/count, pinned source, throw/draw,
        // normal cast of a ritual-capable spell). Keep the exact generated call and run it only after
        // target confirmation. Interaction-first shapes remain on their own UI: trigger double-click,
        // dedicated casts, ability-choice parents, and Sanguine picks. Level-dependent target ceilings
        // are owned by the live targeting stepper and therefore do not need a separate Ready gate.
        const replayAfterTarget = (inControlledFt3dSheet || inFt3dActorDock)
            && profile?.supported && ability && caster && casterId && abilityId
            && !clickedAbilityButton.hasAttribute('disabled')
            && !isAoeButton
            && !clickedAbilityButton.hasAttribute('data-ft-trigger-use-snippet')
            && !((itemType === 'sanguine' || itemType === 'spell') && isDedicatedCastSanguine(abilityId))
            && !isAbilityChoiceParent(ability)
            && !ftLayerHas(ability, 'user_choice_1_summon')
            && !fixedSummonConfiguresOnDock
            && !ftLayerHas(ability, 'spell_upcast_additional_targets_int')
            && sanguineDmgPickOptionIds(caster, ability, itemSourced).length === 0
            // The dock has no canonical input elements. Let its own choice fold-up collect every
            // non-source option before targeting; replay is safe only when the exact invocation owns
            // variable_2 as a source and therefore cannot compose with another choice anyway.
            && !(inFt3dActorDock && canonicalInputId !== '' && !sourceInvocation)
            && generatedSnippet !== '';
        if (replayAfterTarget) {
            e.preventDefault();
            e.stopPropagation();
            abChoiceBranch = null;
            abChoice = null;
            abWeaponMenu = null;
            setItemTargetMenu(null);
            armCreatureTargeting(
                casterId,
                abilityId,
                caster,
                ability,
                profile.profile,
                { kind: 'plain', abilityId },
                () => runGeneratedAbilitySnippet(generatedSnippet),
            );
            return;
        }
        // Selecting any legacy ability supersedes the draft. Invalidate its version before the old
        // handler runs so a late iframe click cannot leak into that immediate/special dispatch.
        if (active) {
            cancelAbilityTargeting(false);
            setTimeout(() => repaintActionBars(active.casterId), 0);
        }
        // Held-item imbue from a sheet row (the dock has its own fold-out branch below): first click arms the
        // scene pick; a re-click (its draft was just cancelled above) keeps the immediate default cast.
        if (!inActionBarDock && !sameActive && ability && caster && casterId && abilityId
            && !clickedAbilityButton.hasAttribute('disabled')) {
            const heldItemTypes = ownInventoryTargetTypes(ability, caster);
            if (heldItemTypes.length > 0 && armHeldItemTargeting(casterId, abilityId, caster, ability, heldItemTypes)) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
        }
    }
    // Saved-target thumbnails overlay (rather than nest inside) the ability button. Re-dispatch their
    // face click to that ordinary button so it follows the same arm -> review -> confirm lifecycle.
    const savedTargetChip = t.closest('.ft-action-tgt-chip__x')
        ? null
        : t.closest('.ft-action-tgt-chip') as HTMLElement | null;
    if (savedTargetChip) {
        const abilityButton = savedTargetChip.closest('.ft-action-slot')
            ?.querySelector<HTMLElement>('.btn_ability_ranges[data-ability_id]');
        if (abilityButton) {
            e.preventDefault();
            e.stopPropagation();
            abilityButton.click();
            return;
        }
    }
    if (!inActionBarDock) return;                               // remaining fold-out UX is dock-only
    // Slots are stamped with data-ft-item-target-types at render; an island fly-up medallion (the only way
    // to reach an UNBOUND spell) is not, so resolve its types from the cached ability. Without this a
    // fly-up Frostwind Blade skipped the picker and cast targetless (owner report 2026-09-14).
    const itemTargetCandidate = t.closest('.btn_ability_ranges[data-ability_id]') as HTMLElement | null;
    const itemTargetBtn = itemTargetCandidate
        && (itemTargetCandidate.closest('.ft-action-slot') || itemTargetCandidate.closest('.ft-ability-flyout'))
        ? itemTargetCandidate : null;
    const itemTargetPane = itemTargetBtn?.closest(AB_PANE_SEL) as HTMLElement | null | undefined;
    const itemTargetCasterId = itemTargetPane?.getAttribute('data-ft-caster-id') || '';
    const itemTargetAbilityId = itemTargetBtn?.getAttribute('data-ability_id') || '';
    let itemTargetTypes = (itemTargetBtn?.getAttribute('data-ft-item-target-types') || '')
        .split(',').map((value) => value.trim().toLowerCase()).filter(Boolean);
    if (itemTargetBtn && itemTargetTypes.length === 0 && itemTargetCasterId && itemTargetAbilityId) {
        const itemTargetCaster = casterFromCache(itemTargetCasterId);
        const itemTargetAbility = itemTargetCaster ? findAbility(itemTargetCaster, itemTargetAbilityId) : null;
        itemTargetTypes = itemTargetCaster && itemTargetAbility ? ownInventoryTargetTypes(itemTargetAbility, itemTargetCaster) : [];
    }
    if (itemTargetBtn && itemTargetTypes.length > 0) {
        if (itemTargetBtn.closest('.ft-action-bars--readonly') || itemTargetBtn.hasAttribute('disabled')) return;
        const casterId = itemTargetCasterId;
        const abilityId = itemTargetAbilityId;
        const allowedTypes = itemTargetTypes;
        if (!casterId || !abilityId) return;
        e.preventDefault();
        e.stopPropagation();
        if (abItemTargetMenu && abItemTargetMenu.casterId === casterId
            && abItemTargetMenu.abilityId.toUpperCase() === abilityId.toUpperCase()) {
            // Re-click deliberately sends no explicit target. Janus resolves the default: the wielded
            // allowed item, else the first one carried (carried gear is within reach). The scene draft
            // armed with the fold-out was already retired by the ability-targeting block above.
            cancelAbilityTargeting(false);
            window.ftSetTargetSelection?.('', 'action-bar-item-target-default');
            window.useAbility?.(abilityId);
            setItemTargetMenu(null);
            repaintActionBars(casterId);
            return;
        }
        claimActionBarOverlayLane(casterId, 'item-target');
        setItemTargetMenu({ casterId, abilityId, allowedTypes: [...new Set(allowedTypes)] });
        // Same first press also arms the scene pick (owner 2026-09-15): click a creature → radial of its
        // equipped slots → cast on the chosen item. The fold-out stays as the in-reach shortcut.
        const heldCaster = casterFromCache(casterId);
        const heldAbility = heldCaster ? findAbility(heldCaster, abilityId) : null;
        if (heldCaster && heldAbility) armHeldItemTargeting(casterId, abilityId, heldCaster, heldAbility, allowedTypes);
        repaintActionBars(casterId);
        return;
    }
    // WEAPON slot: a pointer click and F/G behave the same on first press — arm the plain attack AND open
    // the attached-attack foldout as an optional override (owner 2026-09-14: a click that armed the swing
    // without folding up hid every weapon ability behind the hotkey). Choosing a foldout entry replaces
    // the active plain targeting intent.
    const invBtn = t.closest('.ft-action-inv-btn[data-ft-weapon-foldout], .ft-action-inv-btn[data-ft-nested-foldout], .ft-action-natural-btn[data-ft-natural-foldout]') as HTMLElement | null;
    if (invBtn && invBtn.closest('.ft-action-slot')) {
        const activeTargeting = abilityTargetingLifecycle.current();
        if (activeTargeting) {
            cancelAbilityTargeting(false);
            setTimeout(() => repaintActionBars(activeTargeting.casterId), 0);
        }
        // Read-only bar (spectator/GM-view without control): the fold-out panel never renders there
        // (canEdit-gated like the choice bar), so don't swallow the click — direct fire as before.
        if (invBtn.closest('.ft-action-bars--readonly')) return;
        const invPane = invBtn.closest(AB_PANE_SEL) as HTMLElement | null;
        const invCasterId = invPane?.getAttribute('data-ft-caster-id') || '';
        const nestedId = invBtn.getAttribute('data-ft-nested-foldout') || '';
        const naturalId = invBtn.getAttribute('data-ft-natural-foldout') || '';
        const kind: 'weapon' | 'natural' | 'nested' = nestedId ? 'nested' : (naturalId ? 'natural' : 'weapon');
        const invId = nestedId || naturalId || invBtn.getAttribute('data-ft-weapon-foldout') || '';
        if (!invCasterId || !invId) return;
        e.preventDefault();
        e.stopPropagation();
        // Match the OPEN menu's kind too: if a row flipped between weapon and nested between repaints,
        // the stale state must not drive a fire decision made from the new marker.
        if (abWeaponMenu && abWeaponMenu.casterId === invCasterId
            && abWeaponMenu.kind === kind
            && abWeaponMenu.invId.toUpperCase() === invId.toUpperCase()) {
            // Nested-effect items have no plain action; their established second click just closes.
            // A weapon re-click (pointer or F/G alike, owner 2026-09-14) restarts the default attack and
            // keeps the alternatives visible; the fold-out's ✕ closes it.
            if (kind === 'nested') ftActionBarWeaponFoldClose(invCasterId);
            else ftActionBarWeaponFoldBasic(invCasterId, invId, true);
            return;
        }
        // Single-open: close the ability choice bar + any in-progress AOE placement first.
        claimActionBarOverlayLane(invCasterId, 'weapon');
        abWeaponMenu = { casterId: invCasterId, invId, kind };
        if (kind === 'nested') repaintActionBars(invCasterId);
        else ftActionBarWeaponFoldBasic(invCasterId, invId, true);
        return;
    }
    const btn = t.closest('.btn_ability_ranges[data-ability_id]') as HTMLElement | null;
    // A bar SLOT button, OR an ability-island fly-up medallion (.ft-ability-flyout). The fly-up is the only
    // way to reach an UNBOUND spell in the full-screen dock and — exactly like a slot — it has no spell
    // TABLE behind it, so the canonical input_<id>_spell_upcastable element doesn't exist there either.
    // Without the fly-up here the intercept bailed, and an upcastable spell cast from it fired through its
    // own snippet against a missing input → empty variable_1 → always the base level, no upcast panel.
    // [[owner 2026-07-24]]
    if (!btn || !(btn.closest('.ft-action-slot') || btn.closest('.ft-ability-flyout'))) return;
    if (btn.hasAttribute('disabled')) return;
    // A dedicated-cast sanguine (Ad-lib / Voidgrasping / Contingency) owns its own fold-out form — its
    // snippet IS the interaction, so never swallow the click for a choice panel. Checked on both attrs
    // because a trigger-wrapped button carries the snippet on data-ft-trigger-use-snippet instead.
    const btnSnippet = (btn.getAttribute('data-ft-use-snippet') || '') + (btn.getAttribute('data-ft-trigger-use-snippet') || '');
    if (btnSnippet.includes('ftSanguineDedicatedCast')) return;
    const pane = btn.closest(AB_PANE_SEL) as HTMLElement | null;
    const casterId = pane?.getAttribute('data-ft-caster-id') || '';
    const abilityId = btn.getAttribute('data-ability_id') || '';
    if (!casterId || !abilityId) return;
    if (window.ftResumeProjectileSpell?.(casterId, abilityId)) {
        e.preventDefault();
        e.stopPropagation();
        return;
    }
    const isAoe = btn.classList.contains('use-ability-range');
    // Re-clicking the ability whose area is being aimed is the Confirm gesture (no right-click on the web).
    // Unplaced/illegal → AOEManager warns and keeps the placement alive.
    const liveAoeIntent = window.aoeManager?.ft3dActive?.envelope?.intent;
    const liveAoeAbilityId = String(liveAoeIntent?.abilityId || '').toUpperCase();
    // …including a choice parent whose picked BRANCH is the area being aimed (startBranchAoePlacement).
    const slotCaster = isAoe && liveAoeIntent ? casterFromCache(casterId) : null;
    const slotAbility = slotCaster ? findAbility(slotCaster, abilityId) : null;
    const liveIsSlotBranch = liveAoeAbilityId !== '' && isAbilityChoiceParent(slotAbility)
        && ftLayerEntries(slotAbility, 'ability_choice').some(entry => choiceEntryUuid(entry).toUpperCase() === liveAoeAbilityId);
    if (isAoe && liveAoeIntent
        && (liveAoeAbilityId === abilityId.toUpperCase() || liveIsSlotBranch)
        && String(liveAoeIntent.casterId || '').toUpperCase() === casterId.toUpperCase()) {
        e.preventDefault();
        e.stopPropagation();
        window.aoeManager?.confirmFt3d?.();
        return;
    }
    // Second click on the same configured ability accepts the defaults/current choices and enters targeting.
    if (abChoice && abChoice.casterId === casterId && abChoice.abilityId === abilityId) {
        e.preventDefault();
        e.stopPropagation();
        // Keyed off the PANEL, not the button: an AOE choice parent opens the ordinary branch panel.
        if (abChoice.isAoe) ftActionBarAoeReady(casterId, abilityId);
        else ftActionBarChoiceConfirm(casterId, 'normal');
        return;
    }
    const inputId = btn.getAttribute('data-input_id_reference') || '';
    const caster = casterFromCache(casterId);
    const ab = caster ? findAbility(caster, abilityId) : null;
    const ritual = !!ab && ftLayerHas(ab, 'spell_ritual');
    // An AREA ability_choice parent (Prismatic Wall) has nothing of its own to place — each branch carries
    // its own area + effect. Placing the parent cast an effectless spell with no branch picker anywhere
    // (owner report 2026-09-16). So it folds up the branch picker like any choice parent, and the confirm
    // launches placement for the picked CHILD (startBranchAoePlacement) — marker casts included, because the
    // branch's generated button carries the marker and item source into placement.
    const aoeChoiceParent = isAoe && isAbilityChoiceParent(ab);
    if (aoeChoiceParent) cancelActiveAoePlacement();
    if (isAoe && !aoeChoiceParent) {
        // Options (upcast / Sanguine / ritual) ride along INTO placement: arm the fold-up state and let the
        // delegated placement route start immediately (owner 2026-08-25 — no Ready step). Plain AOEs arm
        // nothing and fall through the same way.
        ftActionBarArmAoeChoice(btn);
        return;
    }
    // Non-AOE: fold up the option panel (upcast / shapeshift / damage / ritual). Configure ONE ability at a time
    // → cancel any in-progress AOE placement first. Also open for a plain (non-upcastable) damaging spell
    // when the caster has an armed Sanguine damage-type buff, so the type select has somewhere to render.
    const itemSourced = abBtnItemSourced(btn);
    const wantSanguineDmgPick = sanguineDmgPickOptionIds(caster, ab, itemSourced).length > 0;
    // A marker cast (at-will / innate-per-rest / charged) keeps its marker in variable_1 and sends one
    // fold-up choice in variable_2. An exact item/weapon source already owns variable_2, however, so that
    // uncommon three-value shape remains on its generated source-aware invocation.
    const markerCast = (btn.getAttribute('data-spell_type') || '') !== '';
    const exactSourceId = btn.getAttribute('data-source') || '';
    const sourceInvocation = exactSourceId !== '' && btnSnippet.includes(exactSourceId);
    // An ability_choice PARENT must always fold up: it has no effect layers, so firing it straight
    // rolls nothing. It may carry no variable input at all (inputId '') — the branch picker IS the
    // interaction, and the confirm retargets the cast to the chosen child.
    // Marker casts excluded — see renderAbilityChoiceBar: this panel cannot carry marker + inventory
    // source + the branch's own control value at once, so it must not offer the picker for one.
    // Those cast from the spell table, whose server-generated child button carries all three. An AREA parent is
    // the exception (aoeChoiceParent above): its branch placement carries them on the branch button.
    const choiceParent = isAbilityChoiceParent(ab) && (!markerCast || aoeChoiceParent);
    const wantOpen = ritual || (!!inputId && !sourceInvocation) || wantSanguineDmgPick || choiceParent;
    if (!wantOpen) return;
    e.preventDefault();
    e.stopPropagation();
    // data-nested-inventory-id is stamped ONLY on a consumable's button (consumes_unit_per_use). This
    // choice bar spends variable_1/variable_2 on the user's pick, so the source can't ride the normal
    // nested_inventory_source marker — carry it here and hand it to the fire call as consume_inventory_id.
    const consumeInvId = btn.getAttribute('data-nested-inventory-id') || undefined;
    // At-will / innate-per-rest / charged cast marker (bound slots recover it; fly-up medallions carry the
    // pool's own spell_type). The confirm must not clobber it — see ftActionBarChoiceConfirm.
    const spellType = btn.getAttribute('data-spell_type') || undefined;
    claimActionBarOverlayLane(casterId, 'ability-choice');
    abChoice = { casterId, abilityId, inputId, ritual, isAoe: false, consumeInvId, spellType, itemSourced, sourceId: exactSourceId || undefined };
    if (caster && ab) actionOptionValueMap().set(actionOptionKey(abilityId), initialActionOptionValues(caster, ab, inputId));
    if (ab && actionInputFamily(ab, inputId) === 'spell-level') {
        upcastLevelMap().set(abilityId.toUpperCase(), abilityCastBaseLevel(ab));
    }
    repaintActionBars(casterId);
}
/** A nested-effect medallion fires through generateAbilityButton's own onclick (or the delegated AOE
 *  listener), NOT through the action-bar router — so nothing above closes the fold-out afterwards. Close it
 *  here on the way out, in BUBBLE phase and without preventDefault/stopPropagation, so the real dispatch
 *  still runs; the repaint is deferred a tick so an AOE placement has already taken over the pointer. */
function onNestedFoldFireClose(e: MouseEvent): void {
    const t = e.target as HTMLElement | null;
    if (!t || typeof t.closest !== 'function') return;
    if (!t.closest('.ft-action-wfold-btn--nested')) return;
    if (!abWeaponMenu || abWeaponMenu.kind !== 'nested') return;
    const cid = abWeaponMenu.casterId;
    setTimeout(() => {
        if (abWeaponMenu && abWeaponMenu.kind === 'nested' && abWeaponMenu.casterId === cid) {
            abWeaponMenu = null;
            repaintActionBars(cid);
        }
    }, 0);
}

function ensureAbilityChoiceBar(): void {
    if (typeof document === 'undefined' || window._ftAbChoiceBar) return;
    window._ftAbChoiceBar = true;
    document.addEventListener('click', onAbilityChoiceClickIntercept, true);
    document.addEventListener('click', onNestedFoldFireClose);
    document.addEventListener('change', onSanguineDmgPickChange);
    document.addEventListener('change', onAbilityChoiceBranchChange);
    document.addEventListener('change', onActionOptionValueChange);
    // Hooks the AOE flow (pre-template bundle) calls cross-bundle: repaint the dock glow on placement
    // start/end, and clear the option panel after a cast.
    window.ftActionBarRepaintAll = repaintAllActionBars;
    window.ftActionBarClearChoice = clearAbChoice;
    window.ftActionBarSetAoeTargets = ftActionBarSetAoeTargets;
    window.ftAbilityTargetingStagePick = stageAbilityTargetPick;
    // The FT3D iframe's held-item radial reads its wheel from here (twin of ftCreatureRadialActions).
    Object.assign(window, { ftHeldItemTargetRadial: heldItemTargetRadial });
    window.ftAbilityTargetingConfirm = confirmAbilityTargeting;
    // Open an ability's normal targeting (creature pick or area placement) from another surface — the hurl mentas' object wheel.
    Object.assign(window, { ftActionBarArmAbility: armAbilityTargeting, ftActionBarExtraordinarySpell: armExtraordinarySpell });
    // Arm a THROWN weapon attack by id with its flight chosen (owner 2026-09-15): the fold-out's Throw + Direct/Lob
    // chips in one call, for other surfaces (the scene's held-item radial) and the staging smokes.
    window.ftActionBarThrowWeapon = (casterId: string, invId: string, flight: 'direct' | 'lob' = 'direct'): boolean => {
        const key = weaponModeKey(casterId, invId);
        thrownWeaponModes.add(key);
        if (flight === 'lob') lobWeaponModes.add(key); else lobWeaponModes.delete(key);
        return ftDispatchWeaponClick(casterId, invId, 'throw');
    };
    window.ftAbilityTargetingCancel = cancelAbilityTargeting;
    Object.assign(window, { ftArmSanguineOpportunity: armSanguineOpportunity });
    window.ftAbilityTargetingSetNumericOption = setAbilityTargetingNumericOption;
    window.ftAbilityTargetingRefreshCandidates = refreshAbilityTargetCandidates;
    window.ftAbilityTargetingApplySceneLegalCandidates = applyAbilityTargetingSceneLegalCandidates;
    window.ftAbilityTargetingFeedback = showAbilityTargetingFeedback;
    window.ftAbilityTargetingRepublish = publishAbilityTargetingIntent;
    window.ftObjectAttackOptions = objectAttackOptions;
    // The portrait's inline onerror strips the native-art class, which changes the cluster's width —
    // re-measure so a broken image can't leave the cluster promoted into a gap it no longer fits.
    window.ftPlaceHandsCluster = scheduleHandsPlacement;
}

/** Preparation changes spell launchers and an open spell flyout; the bar's slots/resources retain their nodes. */
export function patchActionBarSpells(caster: CachedCaster, casterId: string, container: HTMLElement): void {
    const island = container.querySelector('.ft-action-island');
    if (!island) return;
    const open = abilityIslandOpen.get(casterId), template = document.createElement('template');
    template.innerHTML = renderAbilityIslandIcons(caster, casterId, open, true);
    const next = [...template.content.querySelectorAll<HTMLElement>('[data-ft-island-group="spells"]')];
    const key = (el: Element) => el.getAttribute('onclick');
    for (const old of island.querySelectorAll<HTMLElement>('[data-ft-island-group="spells"]'))
        if (!next.some(el => key(el) === key(old))) { replaceHydratedHtml(old, ''); old.remove(); }
    for (const [index, el] of next.entries()) if (![...island.querySelectorAll('[data-ft-island-group="spells"]')].some(old => key(old) === key(el))) {
        const after = next.slice(index + 1).map(next => [...island.querySelectorAll('[data-ft-island-group="spells"]')].find(old => key(old) === key(next))).find(Boolean);
        island.insertBefore(el, after ?? island.querySelector('[data-ft-island-key="gm_tools"]'));
        window.ftMountIcons?.(el);
    }
    if (open?.key === 'spell') {
        const flyout = container.querySelector<HTMLElement>('.ft-ability-flyout');
        if (flyout) { replaceHydratedHtml(flyout, renderAbilityFlyout(caster, casterId, open)); window.ftMountRuneIcons?.(flyout); }
    }
    const tabs = container.querySelector('.ft-action-bars__toptabs'), spellTab = tabs?.querySelector<HTMLElement>('[data-ft-toptab-btn="spells"]');
    if (next.length && tabs && !spellTab) {
        template.innerHTML = renderTopTabStrip(casterId, topTab.get(casterId) ?? 'abilities', new Set(['spells']), false, false, false, false, false, false);
        const tab = template.content.querySelector('[data-ft-toptab-btn="spells"]');
        if (tab) { tabs.insertBefore(tab, tabs.querySelector('[data-ft-toptab-btn="abilities"]')?.nextSibling ?? null); window.ftMountIcons?.(tab as HTMLElement); }
    } else if (!next.length && spellTab) {
        replaceHydratedHtml(spellTab, ''); spellTab.remove();
        if (topTab.get(casterId) === 'spells') {
            topTab.set(casterId, 'abilities');
            container.querySelector<HTMLElement>('.ft-action-bars')?.setAttribute('data-ft-toptab', 'abilities');
            const tab = tabs?.querySelector('[data-ft-toptab-btn="abilities"]'); tab?.classList.add('is-active'); tab?.setAttribute('aria-selected', 'true');
        }
    }
    delete (container as HTMLElement & { _ftAbLastHtml?: string })._ftAbLastHtml;
}

export function renderActionBar(caster: CachedCaster, casterId: string, container: HTMLElement): void {
    ensureActionBarStyles();
    // The targeting dispatcher keys every action to an explicit caster-bearing pane. Some live campaign
    // repaint paths reuse an existing blank action-bar pane; rendering the controls without repairing its
    // identity leaves visible buttons that can only fall through to the legacy global-sidelog cast. Make
    // the renderer itself uphold the mount contract on every pass, not only the original PHP/DOM creator.
    container.setAttribute('data-ft-pc-pane', 'action_bar');
    container.setAttribute('data-ft-caster-id', String(casterId));
    // Start a fresh nested-fold index for THIS render pass — see nestedFoldIndex(). Both consumers
    // (renderSlotCell → renderInventorySlot, and renderWeaponFoldout below) run downstream of here,
    // so they still share one 15-bucket scan, but nothing is ever carried across repaints.
    nestedFoldIndexCache = null;
    const automaticNpcBars = readBars(caster).length === 0
        ? automaticEncounterCreatureBars(caster)
        : null;
    const hasPlayerControl = typeof ftHasPlayerPermission === 'function'
        && ftHasPlayerPermission(caster);
    // `permissions.player` is the unified actor-control grant: it is stamped for the campaign GM,
    // staff, the creator/permissioned user, and a summon controller. Campaign permission belongs to
    // encounter management and must not independently activate an actor's action-bar controls.
    const canControl = hasPlayerControl;
    const canConfigure = hasPlayerControl && !automaticNpcBars;
    let bars = readBars(caster);
    if (bars.length === 0) {
        bars = automaticNpcBars
            ?? [{ bar_index: 0, size: 'm', slot_count: ROW_SLOTS, position: 'bottom', slots: [] }];
    }
    const barSize = readBarSize(caster);
    applyDockSize(container, barSize);
    const dividers = currentDividers(caster);
    const editable = canConfigure && !isLocked();

    // Shared column sequence across ALL rows: 16 slot columns with a thin divider track interleaved at
    // each divider boundary. Column-aligns every row AND lets each divider be ONE row-spanning element.
    type Col = { kind: 'slot'; i: number } | { kind: 'div'; pos: number };
    const seq: Col[] = [];
    for (let i = 0; i < ROW_SLOTS; i++) {
        seq.push({ kind: 'slot', i });
        if (dividers.includes(i + 1) && i + 1 < ROW_SLOTS) seq.push({ kind: 'div', pos: i + 1 });
    }
    const colTemplate = seq.map((c) => (c.kind === 'slot' ? 'minmax(44px,1fr)' : DIVIDER_TRACK)).join(' ');
    const slotCol: Record<number, number> = {};
    const divCols: Array<{ pos: number; col: number }> = [];
    seq.forEach((c, idx) => {
        if (c.kind === 'slot') slotCol[c.i] = idx + 1;
        else divCols.push({ pos: c.pos, col: idx + 1 });
    });

    // Full-screen dock? Hotkeys + their key badges are dock-only (the in-sheet bar stays click-only,
    // and the summary already shows the HP/Mana/mech bars there). Computed once, reused for resources below.
    const campaignDock = !!container.closest?.('[data-ft3d-actor-dock]');
    const inDock = campaignDock;
    const inventoryHost = container.closest('[data-ft3d-actor-dock]');
    const inventoryOpen = inDock
        && inventoryHost?.getAttribute('data-ft-inventory-dock-open') === '1';
    const binding = inDock && bindMode.get(casterId) === true; // configure-shortcuts mode (dock only)

    let cells = '';
    bars.forEach((bar, barIdx) => {
        const bySlot: Record<number, ABSlot> = {};
        for (const s of (bar.slots || [])) bySlot[Number(s.slot_index)] = s;
        for (let i = 0; i < ROW_SLOTS; i++) {
            const slot = bySlot[i];
            const key = inDock ? effectiveKeyForSlot(slot, barIdx, i) : '';
            let cell = renderSlotCell(caster, slot, i, barIdx, slotCol[i] ?? (i + 1), key);
            if (inDock) cell = withKeyBadge(cell, key);
            // Golden glow on the slot whose ability is being configured — the choice menu (abChoice) OR an
            // in-progress AOE placement (window.ftAoeActiveAbilityId, set by AOEManager). Shows WHICH ability
            // the player is acting on (owner UX 2026-06-27). The slot div is the first element of the cell.
            const slotAbU = slot?.ability_id ? String(slot.ability_id).toUpperCase() : '';
            const aoeActiveU = String(window.ftAoeActiveAbilityId || '').toUpperCase();
            const activeTargeting = abilityTargetingLifecycle.current();
            const menuActive = !!slotAbU && (
                (!!abChoice && abChoice.casterId === casterId && String(abChoice.abilityId).toUpperCase() === slotAbU)
                || (!!activeTargeting && activeTargeting.casterId === casterId && activeTargeting.abilityId.toUpperCase() === slotAbU)
                || (!!aoeActiveU && aoeActiveU === slotAbU)
                // Weapon fold-out open for this slot's weapon (inventory slots carry the inventory_id
                // in ability_id, which is what abWeaponMenu keys on).
                || (!!abWeaponMenu && abWeaponMenu.casterId === casterId && abWeaponMenu.invId.toUpperCase() === slotAbU)
                || (!!abItemTargetMenu && abItemTargetMenu.casterId === casterId && abItemTargetMenu.abilityId.toUpperCase() === slotAbU)
            );
            if (menuActive) cell = cell.replace('class="ft-action-slot', 'class="ft-action-slot ft-action-slot--menu-active');
            // Bind overlay on filled slots AND ghost (key-only) slots, so a retained key can be re-bound/managed.
            const hasCustomKey = typeof slot?.hot_key === 'string' && slot.hot_key !== '';
            if (binding && (slot?.ability_id || hasCustomKey)) cell = withBindOverlay(cell, casterId, barIdx, i, key);
            cells += cell;
        }
    });

    // One element per divider boundary, spanning every row (grid-row: 1 / -1).
    let divs = '';
    for (const d of divCols) {
        divs += `<div class="ft-action-divider${editable ? ' is-editable' : ''}" data-ft-divider-pos="${d.pos}"`
            + ` style="grid-row:1 / -1;grid-column:${d.col};"`
            + `${editable ? ` ${tip('Drag to move · drag off to remove', 'top')}` : ' aria-hidden="true"'}>`
            + `<span class="ft-action-divider__grip"></span></div>`;
    }

    const rowsStyle = `grid-template-columns:${colTemplate};grid-template-rows:repeat(${bars.length},auto);`;
    const controls = (canConfigure || inDock)
        ? renderControls(casterId, inDock, inventoryOpen, canConfigure, campaignDock)
        : '';
    const settingsTray = canConfigure ? renderSettingsTray(casterId, bars.length, inDock) : '';
    // Status-effect cluster ABOVE the bar (BG3-style; migrated off the PC-sheet summary): a bordered
    // manage button (far left) + floating status PNGs/runes, rendered by the info-panel bundle from the
    // SAME blob. '' for non-encounter casters. Sibling row on TOP of .ft-action-bars; hydrated by the
    // ftMountRuneIcons/ftMountIcons calls below + repainted on caster_update with the rest of the bar.
    // ONE SNAPSHOT PER RENDER: every sub-renderer gets the `caster` row this pass is painting (statuses,
    // contextual actions, AP, HP/Mana alike) — never a fresh casterCache read, which can lag or lead this
    // row by one websocket frame and paint a bar that is half old / half new. (2026-08-28 hardening.)
    let statusCluster = window.renderActionBarStatusCluster?.(casterId, caster) || '';
    // Skills launcher (left of the status cluster) + its floating popup. Only when the caster has skills.
    let skillsLauncher = casterHasSkills(caster) ? renderSkillsLauncher(casterId, skillsPopupOpen.get(casterId) === true) : '';
    // Campaign dock (owner 2026-08-27, Codex-consulted): the PANEL LAUNCHERS — skills + manage-statuses —
    // move into the left cluster's launcher row beside Inventory; the live status ICONS (state, not a
    // launcher) stay on the bar's top-left strip. The manage button is lifted out of the info-panel's
    // cluster markup by its class so that bundle needs no change.
    let clusterLaunchers = '';
    if (campaignDock) {
        const m = statusCluster.match(/<button[^>]*class="ft-action-status__edit"[\s\S]*?<\/button>/);
        if (m) { clusterLaunchers += m[0]; statusCluster = statusCluster.replace(m[0], ''); }
        clusterLaunchers = skillsLauncher + clusterLaunchers;
        skillsLauncher = '';
    }
    const skillsPopupHtml = renderSkillsPopup(caster, casterId);
    // Center-top ability island (category icons) + the fly-up strip for the open category. On small screens the
    // island's cats split across the Abilities / Spells tabs (data-ft-island-group per cat); the flyout below
    // carries the OPEN category's group so it only shows under the matching tab.
    const islandOpenState = abilityIslandOpen.get(casterId);
    const island = renderAbilityIslandIcons(caster, casterId, islandOpenState);
    const flyout = renderAbilityFlyout(caster, casterId, islandOpenState);
    const flyoutGroup = islandOpenState ? islandCatGroupForKey(islandOpenState.key) : '';
    // Far-right contextual actions (stop bleeding/burning, escape grapple, stand up, dismount, …) — sits
    // between the last slot and the +/−/lock controls, stacking right-to-left. '' when nothing applies.
    const contextActions = window.renderActionBarContextualActions?.(casterId, caster) || '';
    // Resource readout (HP + Mana + class-mechanic charges) — ONLY in the full-screen XZ dock, where the
    // summary bars aren't on screen. On the in-sheet bar the summary already shows these, so we skip it.
    // (inDock computed above, before the slot loop.)
    let resources = '';
    let actionPointFold = '';
    if (inDock) {
        // AP pips + reaction LEAD the row (fixed-width, combat-only); HP / Mana / class-mechanic bars are
        // flex siblings that share the REMAINING width (HP-only → full; no-mana → HP + class 50/50; all → thirds).
        const apFoldIsOpen = canControl && actionPointFoldOpen.get(casterId) === true;
        const ap = window.renderActionBarAp?.(casterId, caster, canControl, apFoldIsOpen) || '';
        const hpMana = window.renderActionBarHpMana?.(casterId, caster) || '';
        let mech = '';
        try { mech = classMechanicBars(caster, !canControl, casterId, classMechIndex.get(casterId) || 0) || ''; } catch { /* engine not ready */ }
        if (ap || hpMana || mech) {
            resources = `<div class="ft-action-resources"><span data-ft-ab-resource="ap" style="display:contents">${ap}</span><span data-ft-ab-resource="vitals" style="display:contents">${hpMana}</span><span data-ft-ab-resource="mechanics" style="display:contents">${mech}</span></div>`;
        }
        // A stale open bit after leaving combat stays harmless: without an AP pill there is no launcher,
        // so do not render an orphaned control panel.
        if (ap && apFoldIsOpen) actionPointFold = renderActionPointFoldout(casterId, caster);
    }
    // The whole thing is a recessed "tray" FRAME (.ft-action-bars, column): the fly-up strip (top, grows
    // upward in the bottom-anchored full-screen dock), then the top row (status strip left + island center),
    // then the slot row (.ft-action-bars__main).
    // Settings tray floats ABOVE the bar as its OWN panel (sibling of .ft-action-bars), not a band inside the
    // recessed frame — keeps the bar reading as one solid unit and matches the fly-up detaching above the slots.
    const themeAttr = themeStyle(readTheme(caster));
    // BG3-style choice bar — floats above the bar when a clicked ability needs an extra choice (upcast
    // level / shapeshift form / damage type / …) that has no control in the dock. Sibling panel like the
    // settings tray; '' unless one is open for this caster.
    const choiceBar = canControl
        ? (renderAbilityTargetingBar(casterId, caster) || renderAoePlacementBar(casterId, caster) || renderAbilityChoiceBar(casterId, caster))
        : '';
    // BG3-style weapon fold-out — floats above the bar (same shell as the choice bar) when an
    // equipped-weapon slot was clicked in the dock; '' unless one is open for this caster.
    const weaponFold = canControl ? renderWeaponFoldout(casterId, caster) : '';
    const itemTargetFold = canControl ? renderItemTargetFoldout(casterId, caster) : '';
    // Small-screen TOP-ROW tabs: the meta row (status cluster + ability island + contextual actions + controls)
    // is too crowded on phone/tablet, so it collapses to a tab strip showing ONE group at a time (Abilities /
    // Spells / Status / Conditional). The settings gear is a standalone button at the strip's right. Renders for
    // BOTH the full-screen dock AND the in-sheet bar (the in-sheet bar overflows on mobile exactly the same way);
    // the strip itself is display:none on desktop, so the data attr is harmless above the ≤1024 breakpoint.
    // Default 'abilities'; fall back to it when the selected tab is unavailable (no spells / no status cluster /
    // no contextual action / stale value). [[owner 2026-06-27, generalized to in-sheet 2026-06-30]]
    const islandGroups: Set<string> = islandGroupsPresent(caster);
    const hasContext = contextActions !== '';
    const hasStatus = statusCluster !== '';
    const TOP_TAB_GROUPS = ['spells', 'curses', 'mentas'] as const;
    let activeTopTab: TopTab = topTab.get(casterId) || 'abilities';
    const VALID_TABS = ['abilities', 'spells', 'curses', 'mentas', 'status', 'conditional'];
    if (!VALID_TABS.includes(activeTopTab)) activeTopTab = 'abilities';
    // Selected a tab the caster no longer has (switched casters / lost a pool / left the encounter) → fall back.
    if ((activeTopTab === 'spells' || activeTopTab === 'curses' || activeTopTab === 'mentas')
        && !islandGroups.has(activeTopTab)) activeTopTab = 'abilities';
    if (activeTopTab === 'status' && !hasStatus) activeTopTab = 'abilities';
    if (activeTopTab === 'conditional' && !hasContext) activeTopTab = 'abilities';
    const topTabs = renderTopTabStrip(casterId, activeTopTab, islandGroups, hasStatus, hasContext, canConfigure, settingsOpen.get(casterId) === true, inDock, inventoryOpen);
    const topTabAttr = ` data-ft-toptab="${activeTopTab}"`;
    // WoW-style timed-activity countdown bar (gathering/reading/training): a fixed-width, centered cast bar
    // that floats ABOVE the action bars (normal-flow sibling like the choice/settings panels —
    // .ft-action-bars is overflow:hidden, so an absolute child would be clipped). STABLE shell only
    // (width:100% placeholder, empty time) so the identical-repaint skip below still fires every clock tick;
    // the live fill width + remaining text are applied post-mount by ftUpdateActivityBars() (also driven
    // standalone from campaign_update).
    const activityBar = buildActivityBarHtml(caster);
    // Out of AP on your own turn → the louder End Turn, nearest the bar (the portrait strip stays too).
    const endTurnPrompt = campaignDock ? renderEndTurnPrompt(caster, casterId) : '';
    // Painted turn state, so reconcileDockTurnControls can tell when a turn change left this dock stale.
    if (campaignDock) (container as HTMLElement & { _ftAbTurnKey?: string })._ftAbTurnKey = dockTurnControlsKey(caster);
    const html = `<div data-ft-ab-resource="ap-fold" style="display:contents">${actionPointFold}</div>` + choiceBar + weaponFold + itemTargetFold + settingsTray + skillsPopupHtml + activityBar
        + `<div data-ft-ab-resource="end-turn" style="display:contents">${endTurnPrompt}</div>`
        + `<div class="ft-action-bars${canControl ? '' : ' ft-action-bars--readonly'}${editable ? ' is-editable' : ''}${binding ? ' is-binding' : ''}" data-ft-bar-size="${barSize}"${automaticNpcBars ? ' data-ft-auto-npc="1"' : ''}${topTabAttr}${themeAttr ? ` style="${themeAttr}"` : ''}>`
        + `<div class="ft-ability-flyout"${flyoutGroup ? ` data-ft-island-group="${flyoutGroup}"` : ''}>${flyout}</div>`
        + topTabs
        + `<div class="ft-action-bars-top"><div class="ft-action-bars-top__left">${skillsLauncher}${statusCluster}</div>${island}<div class="ft-action-bars-top__right">${contextActions}${controls}</div></div>`
        + resources
        + `<div class="ft-action-bars__main">`
        + (campaignDock ? renderHandsCluster(caster, casterId, inventoryOpen, clusterLaunchers) : '')
        + `<div class="ft-action-bars__rows" style="${rowsStyle}">${cells}${divs}</div>`
        + `</div>`
        + `</div>`;
    // Duplicate announcements keep the authored tree; changed markup patches only its differing nodes.
    const memoContainer = container as HTMLElement & { _ftAbLastHtml?: string };
    if (memoContainer._ftAbLastHtml === html) return;
    memoContainer._ftAbLastHtml = html;
    // A resource fill that is STILL GLIDING when this (different-markup) commit replaces it would
    // otherwise snap to its target: capture its live rendered width first so the new element can
    // resume from exactly where the old one was (Codex checkpoint 3, 2026-08-28).
    // Caster-scoped: a dock that just switched actors must not inherit the previous actor's glide.
    const memoCaster = container as HTMLElement & { _ftAbLastCasterId?: string };
    const liveFills = memoCaster._ftAbLastCasterId === casterId ? captureLiveResourceFills(container) : {};
    memoCaster._ftAbLastCasterId = casterId;
    patchMarkup(container, html);
    // Resource patches can change a slot whose authored markup still compares equal.
    syncActionBarAttackSlots(caster, container);
    container.querySelectorAll<HTMLElement>('[data-ft-ab-resource]').forEach(host => rememberPatchMarkup(host, container));
    // ONE-COMMIT DIAGNOSTIC (gated by the ftDebugLog 'action-bar' channel — silent in production): the
    // proof-of-ownership record for the single-snapshot/single-paint contract. One line per ACTUAL DOM
    // commit (never per render attempt) with the caster, its wire version and the resource signature, so
    // a duplicate owner shows up as two commits for one version. tools/test/action-bar-single-paint-
    // transport-smoke.mjs asserts the same thing offline. [[2026-08-28 hardening]]
    if (typeof ftDebugLog === 'function') {
        try { ftDebugLog('action-bar', () => {
            const ec = caster.encounter_creatures && !Array.isArray(caster.encounter_creatures)
                ? caster.encounter_creatures as Record<string, unknown> : {};
            const s = (v: unknown): unknown => Array.isArray(v) ? v[0] : v;
            const encounterId = String(s(ec.encounter_id) ?? '');
            const heldEncounter = window.FateTide?.encounterCache instanceof Map
                ? window.FateTide.encounterCache.get(encounterId) as { _ws_version?: unknown } | undefined
                : undefined;
            return {
                commit: casterId,
                encounterId,
                // Two version domains: the caster row's own wire version and the ENCOUNTER envelope's
                // (the one a delta advances) — one commit per encounter version is the contract.
                casterWsVersion: (caster as { _ws_version?: unknown })._ws_version ?? null,
                encounterWsVersion: heldEncounter?._ws_version ?? null,
                inDock,
                ap: `${s(ec.aps) ?? ''}/${s(ec.aps_total) ?? ''}`,
                hp: `${s(ftComputedValue(caster, 'vitality', 'v_current')) ?? ''}/${s(ftComputedValue(caster, 'vitality', 'effective_v_max')) ?? ''}`,
                mana: `${s(ec.mana) ?? ''}`,
                bytes: html.length,
            };
        }); } catch { /* diagnostics must never break the paint */ }
    }
    window.ftMountRuneIcons?.(container);
    window.ftMountIcons?.(container);
    window.initializeTomSelect?.();
    ensureDividerDrag();
    ensureInventoryBridge();
    ensureCreatureTargetBridge();
    ensureActionBarHotkeys();
    ensureActionBarDelegation();
    // Decide the hand cluster's placement from measured geometry SYNCHRONOUSLY, in this same task, so
    // the browser never paints the inline default and then jumps (no flash).
    if (campaignDock) {
        placeHandsCluster();
        ensureHandsPlacementObservers(container.closest<HTMLElement>('[data-ft3d-actor-dock]'));
    }
    // Animate the resource fills from their previous widths → new (HP/Mana/class tween; AP pill pulses on
    // change). Compares against the per-CASTER cache (survives a dock rebuild); no-op on first mount / no dock.
    animateResourceState(container, casterId, liveFills);
    // Fill the activity bar's live width + remaining-time text post-mount (kept out of `html` so the
    // identical-repaint skip above still fires). No-op when this caster isn't doing a timed activity.
    ftUpdateActivityBars(casterId);
}

// ── resource-bar animation: tween HP/Mana/class fills + pulse the AP pill when values change on a WS push ──
// A configuration repaint creates fills already at the new width — a plain
// CSS transition can't animate them, and reading the "old" width off the just-replaced DOM is fragile. Instead we
// remember each pane's last values in a WeakMap (keyed by the persistent pane element, which ftRerenderPcSheet
// reuses across caster_update repaints). On render: read the new target widths, and for any that CHANGED, paint
// the fill at its OLD width first (transition off, synchronously — so the very first paint is the old value, no
// snap) then on the NEXT frame restore the transition and set the target so it GLIDES old→new (the rAF is what
// makes a freshly-inserted element actually transition — uses the existing rAF re-enable pattern). AP pips
// are discrete <img>s, so the pill pulses when its signature changes. Value-driven → cosmetic repaints don't animate.
type ResFillKey = 'hp' | 'temp' | 'mana' | 'mech';
interface ResSnapshot { fills: Partial<Record<ResFillKey, string>>; apSig: string | null; }
// Keyed by casterId (NOT the pane element): the dock pane can be torn down + rebuilt on a push, which would lose
// a per-element cache and make it never animate. Only the dock renders resources, so one caster → one fill set.
const resCache = new Map<string, ResSnapshot>();

function resFillKey(el: Element): ResFillKey | '' {
    const c = el.classList;
    if (c.contains('ft-action-res-fill--hp')) return 'hp';
    if (c.contains('ft-action-res-fill--temp')) return 'temp';
    if (c.contains('ft-action-res-fill--mana')) return 'mana';
    if (c.contains('ft-action-res-fill--mech')) return 'mech';
    return '';
}
/** Live rendered width (as a `NN%` of its track) of every resource fill that is MID-GLIDE right now —
 *  read off the DOM before a commit replaces the element. Empty when nothing is animating (or the
 *  Web Animations API is unavailable), so the normal cached-baseline path applies. */
interface LiveFill { width: string; remainingMs: number; }
const RES_GLIDE_MS = 750;
function captureLiveResourceFills(container: HTMLElement): Partial<Record<ResFillKey, LiveFill>> {
    const live: Partial<Record<ResFillKey, LiveFill>> = {};
    try {
        container.querySelectorAll('.ft-action-res-fill').forEach((node) => {
            const el = node as HTMLElement;
            const k = resFillKey(el);
            if (!k || typeof el.getAnimations !== 'function') return;
            const anim = el.getAnimations()[0];
            if (!anim) return;
            const track = el.parentElement;
            const trackWidth = track ? track.getBoundingClientRect().width : 0;
            if (!(trackWidth > 0)) return;
            const pct = Math.max(0, Math.min(100, el.getBoundingClientRect().width / trackWidth * 100));
            // Time left on the interrupted glide, so a same-target replacement finishes ON schedule
            // instead of restarting the full glide on every unrelated repaint.
            const elapsed = typeof anim.currentTime === 'number' ? anim.currentTime : 0;
            live[k] = { width: `${Math.round(pct * 100) / 100}%`, remainingMs: Math.max(120, RES_GLIDE_MS - elapsed) };
        });
    } catch { /* measurement is best-effort; fall back to the cached baseline */ }
    return live;
}
function animateResourceState(
    container: HTMLElement,
    casterId: string,
    liveFills: Partial<Record<ResFillKey, LiveFill>> = {},
): void {
    const curFills: Partial<Record<ResFillKey, string>> = {};
    const fillEls: Array<{ el: HTMLElement; k: ResFillKey; target: string }> = [];
    container.querySelectorAll('.ft-action-res-fill').forEach((node) => {
        const el = node as HTMLElement;
        const k = resFillKey(el);
        if (!k) return;
        const target = el.style.width;
        curFills[k] = target;
        fillEls.push({ el, k, target });
    });
    if (fillEls.length === 0) return;   // not the dock (no resource bars) → don't clobber the caster's cache
    const ap = container.querySelector('.ft-action-ap') as HTMLElement | null;
    const curApSig = ap ? ap.innerHTML : null;
    const prev = resCache.get(casterId);
    // First sighting of this caster → set the baseline, no animation (nothing to glide from).
    // NOTE: deliberately NOT gated on prefers-reduced-motion — these are small, explicitly-requested FUNCTIONAL
    // bar tweens (HP/Mana/AP), and the owner's dev box has reduced-motion on but wants them. [[owner 2026-06-26]]
    if (!prev) { resCache.set(casterId, { fills: curFills, apSig: curApSig }); return; }

    // Web Animations API: play OLD→NEW explicitly (no "before" computed style / reflow needed for the
    // innerHTML-rebuilt fill — it sits at its target width while the animation glides on top).
    const canAnimate = typeof Element.prototype.animate === 'function';
    if (canAnimate) {
        for (const { el, k, target } of fillEls) {
            // A fill caught mid-glide by this commit resumes from its LIVE width (even when its target is
            // unchanged — the replacement element would otherwise sit snapped at the target); an idle
            // fill glides from the cached baseline only when the target actually moved.
            const live = liveFills[k];
            const old = live?.width ?? prev.fills[k];
            if (old == null || old === '' || (live == null && old === target)) continue;
            // Same target mid-glide → finish in the remaining time; a NEW target gets the full glide.
            const duration = live && prev.fills[k] === target ? live.remainingMs : RES_GLIDE_MS;
            try {
                el.animate([{ width: old }, { width: target }],
                    { duration, easing: 'cubic-bezier(.22,.61,.36,1)' });
            } catch { /* WAAPI hiccup — leave it snapped */ }
        }
        if (ap && prev.apSig != null && curApSig !== prev.apSig) {        // AP changed → pulse the pill
            try {
                ap.animate(
                    [{ transform: 'scale(1)' }, { transform: 'scale(1.16)', offset: 0.35 }, { transform: 'scale(1)' }],
                    { duration: 720, easing: 'ease' });
            } catch { /* no-op */ }
        }
    }
    // IMMEDIATE COMMIT (2026-08-28): the baseline is now the state we just painted. This used to be a
    // 900 ms delayed commit because the dock historically repainted TWICE per change (the route listener in
    // my_campaign.php re-ran ftRerenderPcSheet after the websocket layer had already painted), and an
    // immediate commit made that 2nd render snap. That second owner is gone — one applied snapshot is one
    // DOM commit (proved by tools/test/action-bar-single-paint-transport-smoke.mjs; identical markup never
    // reaches here at all thanks to the _ftAbLastHtml skip) — so a rapid successive DISTINCT change now
    // glides from the immediately preceding authoritative state instead of a stale pre-window baseline.
    resCache.set(casterId, { fills: curFills, apSig: curApSig });
}

// ── Timed-activity progress bar (WoW-style cast bar above the action bars) ─────────────────────────────
// gathering / reading / training — a creature carries one of these status effects (target bucket) while
// doing the timed activity; the backend (layer_on_status_effects) stamps a label skill + campaign-second
// bounds onto the entry (activity_kind / activity_skill_id / activity_skill_name / activity_start_seconds /
// activity_end_seconds). Mirrors the CONCENTRATING_STATUS_ID discriminator pattern in ability-button.ts.
// The same fields drive the above-token bar on the map (xz-token-activity bundle).
const ACTIVITY_STATUS_IDS: Record<string, string> = {
    '3F171782-CC56-32DF-81B4-8C0ED89F47F4': 'Gathering',
    'ED30CCB0-1645-B97E-B9A6-867D8D0094BD': 'Reading',
    '426C7E0B-EF44-498C-10B2-B50A9E28E45C': 'Training',
};

/** The creature's active timed-activity status (gathering/reading/training, target bucket), or null. */
function getActivityStatus(caster: CachedCaster): Record<string, unknown> | null {
    const statuses = ftCreatureStatuses(caster, 'target');
    if (!Array.isArray(statuses)) return null;
    for (const st of statuses) {
        const id = String(ftItemsCol(st, 'item_id') || '').toUpperCase();
        if (ACTIVITY_STATUS_IDS[id]) return st as Record<string, unknown>;
    }
    return null;
}

/** Remaining seconds → largest-unit human string (h/m/s), e.g. "1h 12m", "12m 30s", "45s". */
function ftHumanizeActivitySeconds(total: number): string {
    let s = Math.max(0, Math.round(total));
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60); const sec = s - m * 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
}

/**
 * STABLE shell for the activity bar (returns '' when no activity). No live width/time in the markup —
 * those are applied post-mount by ftUpdateActivityBars so the identical-repaint skip keeps firing. Far left
 * is TWO icons — the ACTIVITY (Gathering/Reading/Training) then the specific SKILL (Herbalism/History…) —
 * with NO text label (the full name rides the tooltip). Then a track whose fill DRAINS as the clock advances,
 * with the remaining time centered over it.
 */
function buildActivityBarHtml(caster: CachedCaster): string {
    const st = getActivityStatus(caster);
    if (!st) return '';
    const statusId = String(ftItemsCol(st, 'item_id') || '');
    const kindLabel = ACTIVITY_STATUS_IDS[statusId.toUpperCase()] || 'Working';
    const skillId = String((st.activity_skill_id as string) || '');
    const hasSkill = skillId !== '' && checkIfValidUUID(skillId);
    let skillName = String((st.activity_skill_name as string) || '').trim();
    if (skillName === '' && hasSkill && typeof ftItemNameById === 'function') skillName = ftItemNameById(skillId) || '';
    // Far-left: the ACTIVITY icon (iconMap resolves the status UUID → Reading/Gathering/Training) THEN the
    // specific SKILL icon (History, Herbalism…). No text — the two icons read as "reading: history"; the full
    // name rides the tooltip. (owner 2026-06-30: drop the redundant skill-name text, lead with the activity.)
    const activityIcon = iconMap(statusId, 18, 18);
    const skillIcon = hasSkill ? iconMap(skillId, 18, 18) : '';
    const title = (hasSkill && skillName !== '') ? `${kindLabel}: ${skillName}` : kindLabel;
    // Per-activity fill color (CSS, keyed off the kind): gathering=green, reading=blue, training=amber.
    const kind = ACTIVITY_STATUS_IDS[statusId.toUpperCase()] ? kindLabel.toLowerCase() : '';
    return `<div class="ft-activity-bar" data-ft-activity="1" data-ft-activity-kind="${kind}" title="${escapeHtml(title)}">`
        + `<span class="ft-activity-icons">${activityIcon}${skillIcon}</span>`
        + `<span class="ft-activity-track"><span class="ft-activity-fill" style="width:0%"></span><span class="ft-activity-time"></span></span>`
        + `</div>`;
}

/** Current campaign clock as absolute seconds (computed.campaign.campaign_now_seconds on the active
 *  campaign blob), or NaN when unavailable. Same basis as the activity status bounds → just subtract. */
function ftCampaignNowSeconds(): number {
    const ft = window.FateTide as { campaign?: { id?: string }; campaignCache?: Map<string, unknown> } | undefined;
    const campId = ft?.campaign?.id;
    if (!campId) return NaN;
    const camp = ft?.campaignCache?.get(campId);
    if (!camp) return NaN;
    return Number(ftComputedValue(camp, 'campaign', 'campaign_now_seconds'));
}

/**
 * Reflow every mounted activity bar (optionally just one caster's) from the CURRENT campaign clock.
 * Server-authoritative + glide-on-clock-move: sets the persistent fill element's width (CSS transition
 * animates the change) and the remaining-time text. Driven post-render AND standalone from the
 * campaign_update WS handler (a pure clock tick that doesn't repaint the pane). Exposed on window.
 */
export function ftUpdateActivityBars(casterId?: string): void {
    if (typeof document === 'undefined') return;
    const sel = casterId
        ? `.ft-pc-sheet-pane[data-ft-pc-pane="action_bar"][data-ft-caster-id="${String(casterId).replace(/["\\]/g, '')}"]`
        : '.ft-pc-sheet-pane[data-ft-pc-pane="action_bar"]';
    const panes = document.querySelectorAll<HTMLElement>(sel);
    if (panes.length === 0) return;
    const now = ftCampaignNowSeconds();
    panes.forEach((pane) => {
        const bar = pane.querySelector<HTMLElement>('.ft-activity-bar');
        if (!bar) return;
        const fill = bar.querySelector<HTMLElement>('.ft-activity-fill');
        const timeEl = bar.querySelector<HTMLElement>('.ft-activity-time');
        if (!fill) return;
        const cid = pane.getAttribute('data-ft-caster-id') || '';
        const caster = cid ? casterFromCache(cid) : null;
        const st = caster ? getActivityStatus(caster) : null;
        const start = st ? Number(st.activity_start_seconds) : NaN;
        const end = st ? Number(st.activity_end_seconds) : NaN;
        if (!st || !isFinite(start) || !isFinite(end) || end <= start || !isFinite(now)) {
            // Indeterminate (missing bounds / no clock yet) — empty bar, no time (self-corrects next tick).
            fill.style.width = '0%';
            if (timeEl) timeEl.textContent = '';
            return;
        }
        const total = end - start;
        const remaining = Math.max(0, Math.min(total, end - now));
        // WoW-style: the fill GROWS left→right toward completion (elapsed %); the centered text still
        // counts the remaining time down. (owner 2026-06-30: fill to completion, not drain.)
        const pct = Math.max(0, Math.min(100, ((total - remaining) / total) * 100));
        fill.style.width = `${pct.toFixed(2)}%`;
        if (timeEl) timeEl.textContent = ftHumanizeActivitySeconds(remaining);
    });
}

/** Resource deltas retain the slots, open choices, icons and input focus. Layout/configuration still mounts through renderActionBar. */
export function patchActionBarResources(caster: CachedCaster, casterId: string, container: HTMLElement): void {
    if (!container.querySelector('.ft-action-bars')) { renderActionBar(caster, casterId, container); return; }
    const control = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const inDock = !!container.closest('[data-ft3d-actor-dock]');
    const open = control && actionPointFoldOpen.get(casterId) === true;
    const ap = inDock ? window.renderActionBarAp?.(casterId, caster, control, open) || '' : '';
    const parts: Record<string, string> = {
        ap,
        vitals: inDock ? window.renderActionBarHpMana?.(casterId, caster) || '' : '',
        mechanics: inDock ? classMechanicBars(caster, !control, casterId, classMechIndex.get(casterId) || 0) || '' : '',
        'ap-fold': ap && open ? renderActionPointFoldout(casterId, caster) : '',
        'end-turn': inDock ? renderEndTurnPrompt(caster, casterId) : '',
    };
    for (const [key, html] of Object.entries(parts)) {
        const host = container.querySelector<HTMLElement>(`[data-ft-ab-resource="${key}"]`);
        if (host) patchMarkup(host, html);
    }
    syncActionBarAttackSlots(caster, container);
    // The sheet/event hydrator owns one visible-button pass. Preserve the painted turn key so the
    // deferred encounter reconcile can still update the portrait's turn strip after an AP boundary.
    const memo = container as HTMLElement & { _ftAbLastHtml?: string };
    delete memo._ftAbLastHtml;
    animateResourceState(container, casterId);
}

/** Repaint just this caster's action-bar pane(s) from cache — for client-only pref changes / optimistic edits. */
function repaintActionBars(casterId: string): void {
    const caster = casterFromCache(casterId);
    if (!caster) return;
    const safe = String(casterId).replace(/["\\]/g, '');
    document.querySelectorAll<HTMLElement>(
        `.ft-pc-sheet-pane[data-ft-pc-pane="action_bar"][data-ft-caster-id="${safe}"]`,
    ).forEach((el) => renderActionBar(caster, casterId, el));
}

/** Open/close the AP control fold-up. Opening it claims the dock's single overlay lane and cancels only
 *  in-progress targeting/placement owned by this caster, matching the established fold-up behavior. */
function ftActionBarToggleActionPointFold(casterId: string, force?: boolean): void {
    if (!casterId) return;
    const open = force ?? actionPointFoldOpen.get(casterId) !== true;
    if (open) {
        const activeTargeting = abilityTargetingLifecycle.current();
        if (activeTargeting?.casterId === casterId) cancelAbilityTargeting(false);
        const aoeCasterId = String(window.aoeManager?.ft3dActive?.envelope?.intent?.casterId || '');
        if (aoeCasterId === casterId) cancelActiveAoePlacement();
        claimActionBarOverlayLane(casterId, 'action-points');
        actionPointFoldOpen.set(casterId, true);
    } else {
        actionPointFoldOpen.delete(casterId);
    }
    repaintActionBars(casterId);
}

/** Patch the open Skills tray and its owning bar's authored subtree together. A missing or empty popup
 *  uses the bar renderer to mount/remove it; type and Show all changes stay within the tray. */
function repaintSkillsPopup(casterId: string): void {
    const caster = casterFromCache(casterId);
    if (!caster) return;
    const safe = String(casterId).replace(/["\\]/g, '');
    const panes = document.querySelectorAll<HTMLElement>(
        `.ft-pc-sheet-pane[data-ft-pc-pane="action_bar"][data-ft-caster-id="${safe}"]`,
    );
    const tray = renderActionBarSkillsTray(caster, casterId, skillsPopupType.get(casterId) || DEFAULT_SKILL_TYPE, skillsPopupShowAll.get(casterId) === true);
    panes.forEach((pane) => {
        const popup = pane.querySelector<HTMLElement>('.ft-action-skills-popup');
        if (!popup || !tray) { renderActionBar(caster, casterId, pane); return; } // not mounted / emptied → full
        // Keep the bar's authored tree in step with this scoped edit before the next roll patches it.
        patchMarkup(popup, tray);
        window.ftMountRuneIcons?.(popup);
        window.ftMountIcons?.(popup);
    });
}

/** Click an UNEQUIPPED bound inventory item → equip it — client truth only (N6c 2026-10-01, R-089 / R-101: the legacy
 *  equip_or_swap_inventory_inc.php is DELETED). websocket/inventory-equip-truth.ts equipInventory() plans the equip on the row the page
 *  holds (a free hand, or the swap that stows the off-hand / two-handed weapon; worn gear / a donning start; AP incl. Fast Draw) and posts
 *  the envelope; a row in a bag / sheath is a move-then-equip chain; what the page cannot decide is refused with words, never posted.
 *  Instant decisions repaint locally; timed starts and refusals retain their saved-row refresh. */
export function ftActionBarEquipInventory(invId: string): void {
    void (async () => {
        const answer = await equipInventory(invId).catch((error: unknown) => ({ ok: false, status: 0, error: 'error', message: 'The equip could not be saved: ' + String(error) }));
        const warn = document.getElementById('div_sidelog_warning');
        if (warn) warn.innerHTML = '';
        if (!answer.ok) (window as unknown as { displayWarning?: (m: string) => void }).displayWarning?.(answer.message
            || (answer.status === 409 ? 'Your equipment changed elsewhere — try again.' : answer.status === 403 ? 'You cannot equip that.' : 'The equip could not be saved.'));
        const caster = (window as unknown as { ftGetActiveCasterId?: () => string }).ftGetActiveCasterId?.();
        if (caster && !('local' in answer && answer.local)) await (window as unknown as { refreshCasterSheet?: (id: string) => unknown }).refreshCasterSheet?.(caster);
    })();
}

/** Click a center-island category icon → expand its fly-up medallion strip (or collapse if it's already
 *  the open one). Open-state is module-level so it survives caster_update repaints. */
export function ftActionBarSelectAbilityCategory(casterId: string, key: string, level: number | null): void {
    const lvl = (level === null || level === undefined) ? null : Number(level);
    const cur = abilityIslandOpen.get(casterId);
    if (cur && cur.key === key && (cur.level ?? null) === lvl) {
        abilityIslandOpen.delete(casterId);
    } else {
        claimActionBarOverlayLane(casterId, 'ability-island');
        abilityIslandOpen.set(casterId, { key, level: lvl, sort: cur?.sort ?? 'alpha', skillSourceId: null });
    }
    repaintActionBars(casterId);
}

/** Second level of the NPC Skill Abilities island: choose one granting skill, then show only its runes. */
export function ftActionBarSelectSkillAbilitySource(casterId: string, sourceId: string): void {
    const cur = abilityIslandOpen.get(casterId);
    if (!cur || cur.key !== 'skill_abilities') return;
    const nextSourceId = String(sourceId ?? '');
    const selected = String(cur.skillSourceId ?? '').toUpperCase() === nextSourceId.toUpperCase()
        ? null
        : nextSourceId;
    abilityIslandOpen.set(casterId, { ...cur, skillSourceId: selected });
    repaintActionBars(casterId);
}

/** ESC from the campaign shell (owner 2026-08-27): close whatever the bar has floating — the ability
 *  island fly-up, the choice bar / weapon fold-out / item-target fold, the skills tray, the settings tray
 *  — and report whether anything WAS open, so the caller only falls through to "open Settings" when the
 *  bar had nothing to dismiss. Ability TARGETING is not handled here; the shell cancels it first. */
export function ftActionBarDismissOverlays(casterId: string): boolean {
    let closed = false;
    if (abilityIslandOpen.has(casterId)) { abilityIslandOpen.delete(casterId); closed = true; }
    if (actionPointFoldOpen.has(casterId)) { actionPointFoldOpen.delete(casterId); closed = true; }
    if (abChoice && abChoice.casterId === casterId) { ftActionBarChoiceCancel(casterId); return true; }
    if (abWeaponMenu && abWeaponMenu.casterId === casterId) { abWeaponMenu = null; closed = true; }
    if (abItemTargetMenu && abItemTargetMenu.casterId === casterId) { setItemTargetMenu(null); closed = true; }
    if (skillsPopupOpen.get(casterId) === true) { skillsPopupOpen.delete(casterId); closed = true; }
    if (settingsOpen.get(casterId) === true) { settingsOpen.delete(casterId); closed = true; }
    // Bind mode can be entered from Settings → Keys without the tray being open — dismiss it on its own.
    if (bindMode.get(casterId) === true) { bindMode.delete(casterId); closed = true; }
    if (bindListening && bindListening.casterId === casterId) { bindListening = null; closed = true; }
    if (closed) repaintActionBars(casterId);
    return closed;
}

/** Cycle which class mechanic the dock readout shows (▲/▼ carrots) when there are 2+. */
export function ftActionBarPivotMechanic(casterId: string, delta: number): void {
    const cur = classMechIndex.get(casterId) || 0;
    classMechIndex.set(casterId, cur + (Number(delta) || 0));
    repaintActionBars(casterId);
}

/** Switch the open fly-up strip's sort (alphabetical ↔ by requires-level) and repaint. */
export function ftActionBarIslandSort(casterId: string, sort: 'alpha' | 'level'): void {
    const cur = abilityIslandOpen.get(casterId);
    if (!cur) return;
    abilityIslandOpen.set(casterId, { ...cur, sort });
    repaintActionBars(casterId);
}

/** Toggle the (client-side, cookie-backed) lock and repaint. */
export function ftActionBarToggleLock(casterId: string): void {
    if (typeof setCookie === 'function') setCookie(LOCK_COOKIE, isLocked() ? '0' : '1', 365);
    repaintActionBars(casterId);
}

/** Gear → toggle the settings tray. Closing it also exits configure mode + cancels any pending bind. */
export function ftActionBarToggleSettings(casterId: string): void {
    const open = !(settingsOpen.get(casterId) === true);
    if (open) claimActionBarOverlayLane(casterId, 'settings');
    settingsOpen.set(casterId, open);
    if (!open) {
        bindMode.delete(casterId);
        if (bindListening?.casterId === casterId) bindListening = null;
    }
    repaintActionBars(casterId);
}

/** Skills launcher → toggle the floating Skills quick-tray. Opening it seeds the selected type to innate (the
 *  default) when this caster hasn't picked one yet. */
export function ftActionBarToggleSkills(casterId: string): void {
    const open = !(skillsPopupOpen.get(casterId) === true);
    if (open) claimActionBarOverlayLane(casterId, 'skills');
    skillsPopupOpen.set(casterId, open);
    if (open && !skillsPopupType.has(casterId)) skillsPopupType.set(casterId, DEFAULT_SKILL_TYPE);
    repaintActionBars(casterId);
}

/** Skills tray type button → fold out that skill type's chips. No-op for the already-open type. Updates only
 *  the tray DOM (not the whole bar) so switching type doesn't flash the rest of the action bar. */
export function ftActionBarSkillType(casterId: string, type: string): void {
    if ((skillsPopupType.get(casterId) || DEFAULT_SKILL_TYPE) === type) return;
    skillsPopupType.set(casterId, type);
    repaintSkillsPopup(casterId);
}

/** Skills tray "Show all" toggle → include/exclude untrained (tier-0) skills. Targeted tray re-render (the
 *  toggle + the chip set both live inside the popup), so it doesn't flash the rest of the bar. */
export function ftActionBarSkillsShowAll(casterId: string): void {
    skillsPopupShowAll.set(casterId, !(skillsPopupShowAll.get(casterId) === true));
    repaintSkillsPopup(casterId);
}

/** "Configure shortcuts" → toggle keybind bind-mode (overlays each filled slot; click a slot then press a key). */
export function ftActionBarToggleBindMode(casterId: string): void {
    const on = !(bindMode.get(casterId) === true);
    bindMode.set(casterId, on);
    if (!on && bindListening?.casterId === casterId) bindListening = null;
    repaintActionBars(casterId);
}

/** Click a slot's bind overlay → that slot starts listening; the next key (via the global keydown) binds it. */
export function ftActionBarBindSlot(casterId: string, barIdx: number, slotIdx: number): void {
    if (bindMode.get(casterId) !== true) return;
    bindListening = { casterId, barIdx, slotIdx };
    repaintActionBars(casterId);
}

/** Set the responsive dock-width preference for this PC. Width is character-global even though the existing
 *  action_bars schema stores `size` per row, so every row is updated together just like the theme. */
export function ftActionBarSetSize(casterId: string, size: string): void {
    if (!VALID_SIZES.includes(size)) return;
    const caster = casterFromCache(casterId);
    if (!caster) return;
    let bars = readBars(caster).map((bar) => ({ ...bar, slots: (bar.slots || []).map((slot) => ({ ...slot })) }));
    if (bars.length === 0) {
        bars = [{ bar_index: 0, size: 'm', slot_count: ROW_SLOTS, position: 'bottom', slots: [] }];
    }
    bars.forEach((bar) => { bar.size = size; });
    persist(casterId, bars);
}

/** Set the color theme (preset name or "#rrggbb") for this PC and persist. The theme is character-global,
 *  so we stamp it on EVERY bar row (read back via readTheme). Creates a default bar if the PC has none so
 *  there's a row to carry the value. */
export function ftActionBarSetTheme(casterId: string, theme: string): void {
    const caster = casterFromCache(casterId);
    if (!caster) return;
    let bars = readBars(caster).map((b) => ({ ...b, slots: (b.slots || []).map((s) => ({ ...s })) }));
    if (bars.length === 0) {
        bars = [{ bar_index: 0, size: 'm', slot_count: ROW_SLOTS, position: 'bottom', slots: [] }];
    }
    bars.forEach((b) => { b.theme = theme; });
    persist(casterId, bars);
}

/** Custom-color swatch → normalize the hex and store it as the theme (a "#rrggbb" string). No-op if invalid. */
export function ftActionBarSetCustomColor(casterId: string, hex: string): void {
    const ch = hexToRgbChannels(hex);
    if (!ch) return;
    ftActionBarSetTheme(casterId, '#' + String(hex).replace(/^#/, '').toLowerCase());
}

// ── write path: optimistic saved-row correction + applied relay ─────────────────────────────────────
function persist(casterId: string, bars: ABBar[]): void {
    const caster = casterFromCache(casterId);
    if (!caster) return;
    const itemId = pcItemId(caster);
    if (!itemId) return;
    const payload = bars.map((b, i) => ({
        bar_id: b.bar_id || crypto.randomUUID().toUpperCase(),
        bar_index: i,
        size: normSize(b.size),
        slot_count: normCount(b.slot_count),
        position: b.position || 'bottom',
        label: b.label,
        // theme is character-global (same on every bar); the server validates against the preset set / #hex.
        theme: b.theme,
        slots: (b.slots || []).map((s) => ({
            slot_id: s.slot_id || crypto.randomUUID().toUpperCase(),
            slot_index: Number(s.slot_index),
            ability_id: s.ability_id,
            source_id: s.source_id,
            slot_type: s.slot_type,
            // hot_key: undefined is dropped by JSON (→ server null → positional default); '' is the
            // explicit-unbound sentinel and IS sent (so freeing a default digit persists). See assignHotKey.
            hot_key: s.hot_key,
        })),
    }));
    const delta = actionBarSavedRows(itemId, readBars(caster), payload);
    const encounterId = String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_id') ?? '');
    const operationId = crypto.randomUUID();
    // Correct the held input/memo before painting; a later status compile must read this layout.
    // Banking the operation also makes its websocket echo a no-op on this page.
    window.ftCorrectSavedRows?.(casterId, delta.rows, { deletes: delta.deletes, operationId });
    caster.action_bars = payload;
    repaintActionBars(casterId);
    // Real nested JSON (NOT stringified — get_data's mysql_safe_ajax would entity-mangle a JSON string).
    queryData(
        `${BASE_URL}options/tools/player_characters/update/update_player_character_action_bars_inc.php`,
        '',
        { item_id: itemId, action_bars: payload, applied_frame: { kind: 'applied', operation_id: operationId,
            encounter_id: encounterId || itemId, actor_id: casterId, rev: 0, member_revs: {},
            rows: delta.rows, deletes: delta.deletes, computed: {}, new_entities: [] } },
        false,
    );
}

/** Preferences use owner-scoped replacements; bars/slots retain their native row deltas. */
function persistActionBarPreferences(casterId: string, preferences: { action_bar_dividers?: number[]; action_bar_settings?: { dance_playlist: string[] } }): void {
    const caster = casterFromCache(casterId);
    if (!caster) return;
    const itemId = pcItemId(caster);
    if (!itemId) return;
    const rows = Object.entries(preferences).map(([table, value]) => ({ table, row_id: itemId,
        row: { owner_id: itemId, [table === 'action_bar_dividers' ? 'positions' : 'settings']: value } }));
    const operationId = crypto.randomUUID();
    window.ftCorrectSavedRows?.(casterId, rows, { operationId });
    if (preferences.action_bar_dividers) caster.action_bar_dividers = preferences.action_bar_dividers;
    if (preferences.action_bar_settings) caster.action_bar_settings = { ...(caster.action_bar_settings ?? {}), ...preferences.action_bar_settings };
    repaintActionBars(casterId);
    queryData(`${BASE_URL}options/tools/player_characters/update/update_player_character_action_bars_inc.php`, '',
        { item_id: itemId, ...preferences, applied_frame: { kind: 'applied', operation_id: operationId,
            encounter_id: String(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_id') ?? '') || itemId,
            actor_id: casterId, rev: 0, member_revs: {}, rows, deletes: [], computed: {}, new_entities: [] } }, false);
}

export { persist as ftActionBarSave };

export function ftActionBarAddRow(casterId: string): void {
    const caster = casterFromCache(casterId);
    if (!caster) return;
    let bars = readBars(caster);
    if (bars.length === 0) {
        bars = [{ bar_index: 0, size: 'm', slot_count: ROW_SLOTS, position: 'bottom', slots: [] }];
    }
    if (bars.length >= MAX_BARS) return;
    const last = bars[bars.length - 1];
    bars = bars.concat([{
        bar_index: bars.length,
        size: normSize(last?.size),
        slot_count: normCount(last?.slot_count),
        position: 'bottom',
        slots: [],
    }]);
    persist(casterId, bars);
}

export function ftActionBarRemoveRow(casterId: string): void {
    const caster = casterFromCache(casterId);
    if (!caster) return;
    const bars = readBars(caster);
    if (bars.length <= MIN_BARS) return;
    persist(casterId, bars.slice(0, -1));
}

// ── dividers: owner-level positions, persisted independently of bars ─────────────────────────────────
function persistDividers(casterId: string, positions: number[]): void {
    const clean = Array.from(new Set(positions.filter((p) => p >= 1 && p <= ROW_SLOTS - 1))).sort((a, b) => a - b);
    persistActionBarPreferences(casterId, { action_bar_dividers: clean });
}

/** Add a divider at a sensible free boundary (prefer the middle, fan outward to the first free spot). */
export function ftActionBarAddDivider(casterId: string): void {
    const caster = casterFromCache(casterId);
    if (!caster) return;
    const dividers = currentDividers(caster);
    if (dividers.length >= ROW_SLOTS - 1) return;
    const mid = Math.round(ROW_SLOTS / 2);
    const order = [mid];
    for (let d = 1; d < ROW_SLOTS; d++) order.push(mid - d, mid + d);
    const pos = order.find((c) => c >= 1 && c <= ROW_SLOTS - 1 && !dividers.includes(c));
    if (pos === undefined) return;
    persistDividers(casterId, dividers.concat([pos]));
}

// ── divider drag: ONE row-spanning element. Move (snap to nearest button boundary) or drag-off
//    vertically to remove. Geometry uses the shared grid's row-0 slots (all rows are column-aligned). ──
interface DividerDrag { casterId: string; rows: HTMLElement; oldPos: number; ghost: HTMLElement; target: number; remove: boolean; pointerId: number; captured: boolean; }
let dividerDrag: DividerDrag | null = null;

function rowZeroSlots(rows: HTMLElement): HTMLElement[] {
    const slots = Array.from(rows.querySelectorAll<HTMLElement>('.ft-action-slot[data-ft-ab-bar="0"]'));
    slots.sort((a, b) => Number(a.getAttribute('data-ft-ab-slot')) - Number(b.getAttribute('data-ft-ab-slot')));
    return slots;
}
function nearestBoundary(rows: HTMLElement, clientX: number): number {
    const slots = rowZeroSlots(rows);
    let best = 1, bestD = Infinity;
    for (let n = 1; n <= ROW_SLOTS - 1 && n < slots.length; n++) {
        const left = slots[n - 1];
        const right = slots[n];
        if (!left || !right) continue;
        const a = left.getBoundingClientRect(), b = right.getBoundingClientRect();
        const mid = (a.right + b.left) / 2;
        const d = Math.abs(mid - clientX);
        if (d < bestD) { bestD = d; best = n; }
    }
    return best;
}
function boundaryClientX(rows: HTMLElement, n: number): number {
    const slots = rowZeroSlots(rows);
    if (n < 1 || n >= slots.length) return 0;
    const left = slots[n - 1];
    const right = slots[n];
    return left && right ? (left.getBoundingClientRect().right + right.getBoundingClientRect().left) / 2 : 0;
}

function onDividerPointerMove(e: PointerEvent): void {
    if (!dividerDrag) return;
    const { rows, ghost } = dividerDrag;
    const r = rows.getBoundingClientRect();
    const remove = e.clientY < r.top - 26 || e.clientY > r.bottom + 26;
    dividerDrag.remove = remove;
    const pos = nearestBoundary(rows, e.clientX);
    dividerDrag.target = pos;
    ghost.style.left = `${boundaryClientX(rows, pos) - r.left + rows.scrollLeft}px`;
    ghost.classList.toggle('is-remove', remove);
}

/** The live pointermove listener. The divider's REMOVE gesture is "drag off vertically" — in the FT3D
 *  dock that means straight up into the encounter iframe, which would otherwise swallow the release and
 *  strand the drag (see captureDragPointer). Capture on the first real move, not on pointerdown, so a
 *  plain press on the grip still delivers its compatibility `click` to the grip.
 *  ⚠ EVERY divider listener is scoped to the pointer that started the drag. Without that, a SECOND
 *  touch's move drove the ghost with the wrong coordinates and its release COMMITTED the divider
 *  move/removal. (codex-review) That scoping is also why these are plain listeners, not `{once:true}`:
 *  an unrelated pointer's terminal event would burn a once-listener on a call that just returns. */
function onDividerPointerMoveLive(e: PointerEvent): void {
    if (!dividerDrag || e.pointerId !== dividerDrag.pointerId) return;
    if (!dividerDrag.captured) {
        dividerDrag.captured = true;
        captureDragPointer(dividerDrag.pointerId);
    }
    onDividerPointerMove(e);
}

/** Drop the listeners + transient state of the live divider drag. Returns the state so the caller can
 *  decide whether to persist (pointerup) or not (pointercancel / blur). */
function teardownDividerDrag(): DividerDrag | null {
    document.removeEventListener('pointermove', onDividerPointerMoveLive);
    document.removeEventListener('pointerup', onDividerPointerUp);
    document.removeEventListener('pointercancel', onDividerPointerCancel);
    document.body.style.userSelect = '';
    const st = dividerDrag;
    dividerDrag = null;
    shieldEncounterFrame('divider', false);
    if (!st) return null;
    if (st.captured) releaseDragPointer(st.pointerId);
    st.ghost.remove();
    return st;
}

function onDividerPointerUp(e: PointerEvent): void {
    if (!dividerDrag || e.pointerId !== dividerDrag.pointerId) return;
    const st = teardownDividerDrag();
    if (!st) return;
    const caster = casterFromCache(st.casterId);
    if (!caster) return;
    let dividers = currentDividers(caster);
    if (st.remove) {
        persistDividers(st.casterId, dividers.filter((p) => p !== st.oldPos));
        return;
    }
    if (st.target === st.oldPos || dividers.includes(st.target)) return; // no-op or occupied
    dividers = dividers.filter((p) => p !== st.oldPos).concat([st.target]);
    persistDividers(st.casterId, dividers);
}

/** Cancel is not a drop: tear the divider drag down without persisting a move OR a remove. */
function onDividerPointerCancel(e: PointerEvent): void {
    if (!dividerDrag || e.pointerId !== dividerDrag.pointerId) return;
    teardownDividerDrag();
}

function onDividerPointerDown(e: PointerEvent): void {
    if (isLocked()) return;
    const grip = (e.target as HTMLElement)?.closest?.('.ft-action-divider.is-editable') as HTMLElement | null;
    if (!grip) return;
    const rows = grip.closest('.ft-action-bars__rows') as HTMLElement | null;
    const pane = grip.closest('.ft-pc-sheet-pane') as HTMLElement | null;
    const casterId = pane?.getAttribute('data-ft-caster-id') || '';
    if (!rows || !casterId) return;
    e.preventDefault();
    const oldPos = Number(grip.getAttribute('data-ft-divider-pos'));
    const ghost = document.createElement('div');
    ghost.className = 'ft-action-divider-ghost';
    rows.appendChild(ghost);
    dividerDrag = { casterId, rows, oldPos, ghost, target: oldPos, remove: false, pointerId: e.pointerId, captured: false };
    onDividerPointerMove(e);
    shieldEncounterFrame('divider', true); // drag-off-to-remove goes UP into the iframe — same swallow
    document.addEventListener('pointermove', onDividerPointerMoveLive);
    document.addEventListener('pointerup', onDividerPointerUp);
    document.addEventListener('pointercancel', onDividerPointerCancel);
    document.body.style.userSelect = 'none';
}

function ensureDividerDrag(): void {
    if (typeof document === 'undefined' || window._ftAbDividerDrag) return;
    window._ftAbDividerDrag = true;
    document.addEventListener('pointerdown', onDividerPointerDown);
}

// ── ability drag-drop: bind an ability/skill onto a slot, move/swap within the bars, or drag a bar
//    button off to unbind. Coexists with press-hold (info) + click (use); a drag begins past a small
//    threshold and the trailing click is swallowed. Disabled while locked. ───────────────────────────
const DRAG_THRESHOLD = 8;
const AB_PANE_SEL = '.ft-pc-sheet-pane[data-ft-pc-pane="action_bar"]';

interface AbilityDragState {
    srcEl: HTMLElement;
    abilityId: string; itemType: string; sourceId: string | null;
    originCasterId: string | null; originBar: number | null; originSlot: number | null;
    startX: number; startY: number; pointerId: number;
    dragging: boolean; ghost: HTMLElement | null; overSlot: HTMLElement | null;
}
let abilityDrag: AbilityDragState | null = null;
let abilityDragSwallowUntil = 0;

/** A deep-enough clone of the blob's bars so we can mutate slots before persisting. */
function editableBars(caster: CachedCaster): ABBar[] {
    let bars = readBars(caster);
    if (bars.length === 0) bars = [{ bar_index: 0, size: 'm', slot_count: ROW_SLOTS, position: 'bottom', slots: [] }];
    return bars.map((b) => ({ ...b, slots: (b.slots || []).map((s) => ({ ...s })) }));
}
function getSlotAt(bar: ABBar, idx: number): ABSlot | undefined {
    return (bar.slots || []).find((s) => Number(s.slot_index) === idx);
}
function setSlotAt(bar: ABBar, idx: number, ability: { abilityId: string; sourceId?: string | null; itemType?: string | null } | null): void {
    // Keybinds are POSITIONAL: a custom key stays with the SLOT, not the ability. Capture the outgoing slot's
    // custom key (non-empty hot_key) BEFORE we overwrite, so it carries onto a newly-dropped ability and
    // survives an ability being dragged OUT (as a "ghost" keyed slot). '' (explicit-unbound) / undefined
    // (positional default) are NOT custom keys — nothing to retain.
    const prev = (bar.slots || []).find((s) => Number(s.slot_index) === idx);
    const keepKey = (typeof prev?.hot_key === 'string' && prev.hot_key !== '') ? prev.hot_key : undefined;
    bar.slots = (bar.slots || []).filter((s) => Number(s.slot_index) !== idx);
    if (ability && ability.abilityId) {
        const slot: ABSlot = { slot_index: idx, ability_id: ability.abilityId };
        if (ability.sourceId) slot.source_id = ability.sourceId;
        if (ability.itemType) slot.slot_type = ability.itemType;
        if (keepKey !== undefined) slot.hot_key = keepKey; // new ability inherits the slot's key
        bar.slots.push(slot);
    } else if (keepKey !== undefined) {
        // Ability removed but a custom key was set → keep a ghost (blank slot, key retained). The server
        // persists this as a key-only row (ability_id NULL); a future drop here re-inherits the key.
        bar.slots.push({ slot_index: idx, ability_id: '', hot_key: keepKey });
    }
}

function onAbilityPointerDown(e: PointerEvent): void {
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return;
    if (isLocked()) return; // locked → no drag/bind (click + press-hold still work)
    let tgt = e.target as HTMLElement;
    // A saved-target thumbnail overlays the ability face and takes pointer events (for its hover tooltip), so
    // grabbing one would otherwise match nothing and kill the slot's drag. Retarget to the slot's own button
    // (the remove-× keeps its click and never drags).
    if (tgt?.closest?.('.ft-action-tgt-chip') && !tgt.closest('.ft-action-tgt-chip__x')) {
        const slotBtn = tgt.closest('.ft-action-slot')?.querySelector('.btn_ability_ranges[data-ability_id]') as HTMLElement | null;
        if (slotBtn) tgt = slotBtn;
    }
    // Abilities: a rune ability button (data-ability_id). Skills: any [data-ft-info-item-id] whose id is a
    // known skill (window.SKILL_CATALOG) — same useAbility(id) click, bound as slot_type 'skill'.
    let btn = tgt?.closest?.('.btn_ability_ranges[data-ability_id]') as HTMLElement | null;
    let abilityId = '';
    let itemType = '';
    let weaponSourceId: string | null = null; // 'throw_weapon' for a thrown-weapon drag source (else null)
    if (btn) {
        abilityId = btn.getAttribute('data-ability_id') || '';
        itemType = btn.getAttribute('data-item_type') || '';
    } else {
        const skillBtn = tgt?.closest?.('[data-ft-info-item-id]') as HTMLElement | null;
        const sid = skillBtn?.getAttribute('data-ft-info-item-id') || '';
        const skillCat = window.SKILL_CATALOG;
        if (skillBtn && sid && skillCat && (skillCat[sid] || skillCat[sid.toUpperCase()])) {
            btn = skillBtn;
            abilityId = sid;
            itemType = 'skill';
        }
    }
    // A "special" universal ability button (short rest / resolve / spot hidden / custom-* / …): these
    // render with data-ability_id but NOT .btn_ability_ranges, so recognise them by the registry and bind
    // as slot_type 'special'. A plain click still fires the ability (drag only starts past the threshold).
    if (!btn) {
        const specBtn = tgt?.closest?.('[data-ability_id]') as HTMLElement | null;
        const specId = specBtn?.getAttribute('data-ability_id') || '';
        if (specBtn && specialAbility(specId)) {
            btn = specBtn;
            abilityId = specId;
            itemType = 'special';
        }
    }
    // A "bar action" button (add AP / add reaction / clear triggers) — non-ability buttons marked with
    // data-ft-bar-action; bind as slot_type 'action' (rendered from BAR_ACTIONS at the slot).
    if (!btn) {
        const actEl = tgt?.closest?.('[data-ft-bar-action]') as HTMLElement | null;
        const actKey = actEl?.getAttribute('data-ft-bar-action') || '';
        if (actEl && barAction(actKey)) {
            btn = actEl;
            abilityId = actKey;
            itemType = 'action';
        }
    }
    // A bound INVENTORY slot button (the weapon-icon button renderInventorySlot emits): draggable within
    // the bar for move/unbind ONLY. Its bind data (ability_id/slot_type) is read from the slot at drop, so
    // no abilityId is needed here. Matches ONLY our bar slot buttons — the SOURCE inventory tiles are
    // .draggable/.inventory-item (owned by the dragManager) and are excluded by the guard below.
    let isInvSlotBtn = false;
    if (!btn) {
        const invBtn = tgt?.closest?.('.ft-action-inv-btn') as HTMLElement | null;
        if (invBtn && invBtn.closest('.ft-action-slot[data-ft-ab-bar]')) {
            btn = invBtn;
            isInvSlotBtn = true;
        }
    }
    // A MISSING slot (stale binding — a deleted/unprepared spell, a removed item, an unknown skill): it has
    // no button, only the "?" glyph, so none of the matchers above fire. Recognise the slot ITSELF as the
    // drag handle so the player can drag it off the bar to UNBIND (delete the dead binding), exactly like any
    // other bar button. No abilityId/bind data is needed — move/unbind reads (bar, slot) from the slot's data
    // attrs at drop. (owner 2026-06-27)
    let isMissingSlot = false;
    if (!btn) {
        const missEl = tgt?.closest?.('.ft-action-slot--missing[data-ft-ab-bar]') as HTMLElement | null;
        if (missEl) {
            btn = missEl;
            isMissingSlot = true;
        }
    }
    // An equipped-weapon drag SOURCE from the Weapons tab: a tile carrying data-ft-weapon-inv-id binds the
    // weapon onto a bar slot as slot_type 'inventory' (same shape as an inventory-tab drag). A THROW tile
    // adds data-ft-weapon-variant="throw" → bound via source_id 'throw_weapon' so the slot fires the throw,
    // not the normal hit (a throwable weapon can be dropped on the bar twice). A plain click still fires the
    // weapon's own attack/throw (drag only starts past the threshold). These tiles aren't .draggable/
    // .inventory-item, so the dragManager bail below leaves them to us.
    if (!btn) {
        const wEl = tgt?.closest?.('[data-ft-weapon-inv-id]') as HTMLElement | null;
        const wInv = wEl?.getAttribute('data-ft-weapon-inv-id') || '';
        if (wEl && wInv) {
            btn = wEl;
            abilityId = wInv;
            itemType = 'inventory';
            if (wEl.getAttribute('data-ft-weapon-variant') === 'throw') weaponSourceId = 'throw_weapon';
        }
    }
    if (!btn || (!abilityId && !isInvSlotBtn && !isMissingSlot)) return;
    // Inventory SOURCE tiles are owned by the dragManager (they're `.draggable`/`.inventory-item`). Don't
    // let the ability/skill drag preempt that — bail for any button inside a dragManager-owned tile so the
    // inventory bridge handles it. Action-bar slots (incl. our .ft-action-inv-btn) are NOT
    // .draggable/.inventory-item ancestors, so bound bar buttons still drag normally.
    if (btn.closest('.draggable, .inventory-item')) return;

    const slotEl = btn.closest('.ft-action-slot') as HTMLElement | null;
    const pane = btn.closest(AB_PANE_SEL) as HTMLElement | null;
    let originCasterId: string | null = null, originBar: number | null = null, originSlot: number | null = null;
    if (slotEl && pane && slotEl.hasAttribute('data-ft-ab-bar')) {
        // dragging an existing bar button → move within / unbind (bar+slot from the slot's data attrs)
        originCasterId = pane.getAttribute('data-ft-caster-id');
        originBar = Number(slotEl.getAttribute('data-ft-ab-bar'));
        originSlot = Number(slotEl.getAttribute('data-ft-ab-slot'));
        if (!Number.isFinite(originBar) || originBar < 0) return;
    } else {
        // dragging a SOURCE ability/skill button → bind; only if a bar is on the page.
        if (!document.querySelector(AB_PANE_SEL)) return;
    }

    abilityDrag = {
        srcEl: btn, abilityId, itemType,
        // data-source = the weapon/inventory the attack fires from (emitted by generateAbilityButton);
        // fall back to the nested-inventory id for consumable sources. A weapon THROW tile overrides it
        // with 'throw_weapon' so the bound slot fires the throw variant.
        sourceId: weaponSourceId ?? (btn.getAttribute('data-source') || btn.getAttribute('data-nested-inventory-id') || null),
        originCasterId, originBar, originSlot,
        startX: e.clientX, startY: e.clientY, pointerId: e.pointerId,
        dragging: false, ghost: null, overSlot: null,
    };
    shieldEncounterFrame('ability', true); // armed for the WHOLE press — see shieldEncounterFrame
}

/** ⛔ The FT3D encounter is a same-origin `<iframe>` and the action-bar dock FLOATS OVER it
 *  (my_campaign.php `.ft3d-campaign-actor-dock`, z-index 6). An iframe owns the pointer the moment the
 *  cursor crosses its box — "the scene ... swallows its own pointer events, so the parent never sees a
 *  click on the canvas" (my_campaign.php ft3dDismissCurtainOnIntent). So a drag OFF the bar INTO the 3D
 *  scene delivered no further pointermove and, fatally, no pointerup to the parent document: the unbind
 *  branch of onAbilityPointerUp never ran (the slot stayed bound) and the ghost was orphaned in <body>.
 *  An explicit pointer capture suspends hit-testing for that pointer id, so the whole stream stays in the
 *  parent regardless of what it flies over.
 *
 *  Capture on `document.body`, NOT on the dragged button: renderActionBar repaints the pane by innerHTML,
 *  so a mid-drag `caster_update` destroys st.srcEl — capture would be lost and the iframe would eat the
 *  release again. Body is never re-created.
 *
 *  Capture at DRAG START, never at pointerdown: while captured, the compatibility `click` is retargeted
 *  to the capture element, so capturing every press would fire click on <body> instead of the ability
 *  button and kill click-to-cast. Past the threshold we already swallow the trailing click. */
function captureDragPointer(pointerId: number): void {
    try { document.body.setPointerCapture(pointerId); } catch { /* pointer already released */ }
}

/** The capture above can only be taken once the 8px threshold makes it a drag — capturing on every press
 *  would retarget the compatibility `click` to <body> and kill click-to-cast. That leaves an ACTIVATION
 *  GAP: the dock sits flush against the encounter iframe, so an upward flick can cross into it within
 *  those 8px and the parent never gets the move that would have armed the capture. Shield the frame for
 *  the whole press instead — a body class the injected stylesheet turns into `pointer-events:none` on
 *  [data-ft3d-encounter-frame]. The player is holding a bar button down, so nothing in the scene wants
 *  those events anyway, and hit-testing simply falls through to the frame host div (still not a slot, so
 *  slotUnder() keeps returning null and the unbind branch keeps firing). (Codex consult, 2026-08-26) */
const DRAG_SHIELD_CLASS = 'ft-ab-drag-shield';
// OWNERSHIP, not a shared boolean: an ability drag and a divider drag can overlap on two pointers, and a
// plain toggle would let whichever ends first drop the shield out from under the other. (codex-review)
const dragShieldOwners = new Set<string>();
function shieldEncounterFrame(owner: 'ability' | 'divider', on: boolean): void {
    if (typeof document === 'undefined' || !document.body) return;
    if (on) dragShieldOwners.add(owner);
    else dragShieldOwners.delete(owner);
    document.body.classList.toggle(DRAG_SHIELD_CLASS, dragShieldOwners.size > 0);
}
function releaseDragPointer(pointerId: number): void {
    try {
        if (document.body.hasPointerCapture(pointerId)) document.body.releasePointerCapture(pointerId);
    } catch { /* already released with the pointerup */ }
}

function startAbilityDrag(e: PointerEvent): void {
    const st = abilityDrag;
    if (!st) return;
    st.dragging = true;
    captureDragPointer(st.pointerId);
    document.body.style.userSelect = 'none';
    const ghost = document.createElement('div');
    ghost.className = 'ft-action-drag-ghost';
    const face = st.srcEl.querySelector('.ft-rune-mount') || st.srcEl;
    ghost.appendChild(face.cloneNode(true) as HTMLElement);
    document.body.appendChild(ghost);
    st.ghost = ghost;
    moveAbilityGhost(e);
}

function moveAbilityGhost(e: PointerEvent): void {
    const st = abilityDrag;
    if (!st?.ghost) return;
    st.ghost.style.left = `${e.clientX}px`;
    st.ghost.style.top = `${e.clientY}px`;
}

function slotUnder(clientX: number, clientY: number): HTMLElement | null {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    return (el?.closest?.(`${AB_PANE_SEL} .ft-action-slot`) as HTMLElement | null) || null;
}

function onAbilityPointerMove(e: PointerEvent): void {
    const st = abilityDrag;
    if (!st || e.pointerId !== st.pointerId) return;
    if (!st.dragging) {
        const dx = e.clientX - st.startX, dy = e.clientY - st.startY;
        if (dx * dx + dy * dy < DRAG_THRESHOLD * DRAG_THRESHOLD) return;
        startAbilityDrag(e);
    }
    e.preventDefault();
    moveAbilityGhost(e);
    const slot = slotUnder(e.clientX, e.clientY);
    if (slot !== st.overSlot) {
        st.overSlot?.classList.remove('ft-action-slot--dragover');
        st.overSlot = slot;
        slot?.classList.add('ft-action-slot--dragover');
    }
}

function onAbilityPointerUp(e: PointerEvent): void {
    const st = abilityDrag;
    if (!st || e.pointerId !== st.pointerId) return;
    abilityDrag = null;
    releaseDragPointer(st.pointerId);
    shieldEncounterFrame('ability', false);
    document.body.style.userSelect = '';
    if (!st.dragging) return; // a click/hold, not a drag
    st.ghost?.remove();
    st.overSlot?.classList.remove('ft-action-slot--dragover');
    abilityDragSwallowUntil = Date.now() + 700; // eat the trailing click

    const dropSlot = slotUnder(e.clientX, e.clientY);

    if (dropSlot && dropSlot.hasAttribute('data-ft-ab-bar')) {
        const pane = dropSlot.closest(AB_PANE_SEL) as HTMLElement | null;
        const casterId = pane?.getAttribute('data-ft-caster-id') || st.originCasterId || '';
        if (!pane || !casterId) return;
        const caster = casterFromCache(casterId);
        if (!caster) return;
        const targetBar = Number(dropSlot.getAttribute('data-ft-ab-bar'));
        const targetSlot = Number(dropSlot.getAttribute('data-ft-ab-slot'));
        if (!Number.isFinite(targetBar) || targetBar < 0) return;
        const bars = editableBars(caster);
        if (!bars[targetBar]) return;

        if (st.originBar !== null && st.originSlot !== null) {
            // MOVE / SWAP
            if (st.originBar === targetBar && st.originSlot === targetSlot) return;
            const oBar = bars[st.originBar], tBar = bars[targetBar];
            if (!oBar) return;
            const moving = getSlotAt(oBar, st.originSlot);
            if (!moving) return;
            const occupying = getSlotAt(tBar, targetSlot);
            setSlotAt(oBar, st.originSlot, null);
            setSlotAt(tBar, targetSlot, { abilityId: moving.ability_id, sourceId: moving.source_id, itemType: moving.slot_type });
            if (occupying) setSlotAt(oBar, st.originSlot, { abilityId: occupying.ability_id, sourceId: occupying.source_id, itemType: occupying.slot_type });
        } else {
            // BIND from a source (overwrite)
            setSlotAt(bars[targetBar], targetSlot, { abilityId: st.abilityId, sourceId: st.sourceId, itemType: st.itemType });
        }
        persist(casterId, bars);
        return;
    }

    // Dropped off any slot while dragging a BAR button → UNBIND.
    if (st.originBar !== null && st.originSlot !== null && st.originCasterId) {
        const caster = casterFromCache(st.originCasterId);
        if (!caster) return;
        const bars = editableBars(caster);
        const originBar = bars[st.originBar];
        if (originBar) {
            setSlotAt(originBar, st.originSlot, null);
            persist(st.originCasterId, bars);
        }
    }
}

/** A CANCEL is not a drop. `pointercancel` fires when the browser steals the gesture (touch scroll/zoom,
 *  a system gesture, the pointer leaving the window on some platforms) — routing it into
 *  onAbilityPointerUp made the OFF-SLOT branch fire and silently UNBOUND the slot the player was only
 *  trying to scroll past. Tear the drag down without persisting anything. (Codex consult, 2026-08-26) */
function onAbilityPointerCancel(e: PointerEvent): void {
    const st = abilityDrag;
    if (!st || e.pointerId !== st.pointerId) return;
    abilityDrag = null;
    teardownAbilityDrag(st);
    // NO click-swallow window here: a spec-compliant `pointercancel` produces no compatibility click, so
    // arming the 700ms swallow would only eat an unrelated real click that lands just after. (codex-review)
}

/** Transient teardown shared by cancel and the window-blur failsafe — everything startAbilityDrag put in
 *  place, and nothing that persists. */
function teardownAbilityDrag(st: AbilityDragState): void {
    releaseDragPointer(st.pointerId);
    shieldEncounterFrame('ability', false);
    document.body.style.userSelect = '';
    st.ghost?.remove();
    st.overSlot?.classList.remove('ft-action-slot--dragover');
}

/** Belt-and-braces: the shield must NEVER outlive the press — a stuck `pointer-events:none` would leave
 *  the whole 3D scene dead to the mouse, a far worse bug than the one it fixes. Registered AFTER the
 *  handlers that clear the drag state, so by the time this runs a finished gesture has nulled it. */
function clearDragShieldIfIdle(): void {
    if (!abilityDrag) shieldEncounterFrame('ability', false);
    if (!dividerDrag) shieldEncounterFrame('divider', false);
}

/** LAST-RESORT failsafe. clearDragShieldIfIdle listens to the very terminal events whose absence causes
 *  a stuck shield, so it cannot cover a press that dies with no pointerup/pointercancel at all (an OS
 *  dialog, a tab switch, teardown mid-press). Window blur means the held gesture is gone either way —
 *  abort both drags without persisting, so the 3D scene can never be left dead to the mouse, which would
 *  be a far worse bug than the one the shield fixes. (codex-review) */
function abortDragsOnBlur(): void {
    if (abilityDrag) {
        const st = abilityDrag;
        abilityDrag = null;
        teardownAbilityDrag(st);
    }
    if (dividerDrag) teardownDividerDrag();
    dragShieldOwners.clear();
    document.body?.classList.remove(DRAG_SHIELD_CLASS);
}

function onAbilityDragClickBlock(e: MouseEvent): void {
    if (abilityDragSwallowUntil && Date.now() < abilityDragSwallowUntil) {
        abilityDragSwallowUntil = 0;
        e.stopImmediatePropagation();
        e.preventDefault();
    }
}

/** Bar-slot icons are natively draggable by default (inventory `<img class="token-img">`, PNG rune icons),
 *  so a NATIVE image-drag fires on press-drag and hijacks our pointer-drag — showing the "No-drop" cursor
 *  over slots and blocking move/unbind. Suppress native dragstart for anything inside a bar slot so the
 *  pointer-drag is the only drag. SOURCE inventory tiles live OUTSIDE `.ft-action-slot` (in the inventory
 *  tab), so the dragManager's own drag is left untouched. */
function onAbilitySlotDragStart(e: DragEvent): void {
    const t = e.target as HTMLElement | null;
    // Bar slots AND the Weapons-tab drag tiles (data-ft-weapon-inv-id): suppress the native image-drag so
    // our pointer-drag is the only drag (an <img> weapon face would otherwise hijack it).
    if (t && typeof t.closest === 'function' && (t.closest('.ft-action-slot') || t.closest('[data-ft-weapon-inv-id]'))) {
        e.preventDefault();
    }
}

function ensureAbilityDrag(): void {
    if (typeof document === 'undefined' || window._ftAbAbilityDrag) return;
    window._ftAbAbilityDrag = true;
    document.addEventListener('dragstart', onAbilitySlotDragStart, true);
    document.addEventListener('pointerdown', onAbilityPointerDown);
    document.addEventListener('pointermove', onAbilityPointerMove);
    document.addEventListener('pointerup', onAbilityPointerUp);
    document.addEventListener('pointercancel', onAbilityPointerCancel);
    document.addEventListener('pointerup', clearDragShieldIfIdle);
    document.addEventListener('pointercancel', clearDragShieldIfIdle);
    window.addEventListener('blur', abortDragsOnBlur);
    window.addEventListener('pagehide', abortDragsOnBlur);
    document.addEventListener('click', onAbilityDragClickBlock, true);
}

// ── inventory → bar bridge: the Inventory tab drags items via window.dragManager (custom drag of
//    `.draggable` tiles). We READ that drag (state.isDragging / draggedElement) — no edits to the shared
//    system — and on release over a bar slot bind the item. A weapon resolves to its attack ability (the
//    attack whose `source` == the weapon's inventory_id); non-weapons (potions/coatings) are a later pass. ──
let invDragOverSlot: HTMLElement | null = null;

function activeInventoryDrag(): string | null {
    const dm = window.dragManager;
    if (!dm?.state?.isDragging) return null;
    const el = dm.state.draggedElement as HTMLElement | null;
    if (!el || typeof el.getAttribute !== 'function' || typeof el.classList?.contains !== 'function') return null;
    // A creature token ALSO carries data-draggable_id (= encounter_creature_id), so the data-draggable_id
    // fallback below would mistake it for an inventory drop and (worse) persist a bogus inventory binding to
    // the slot before the creature-target bridge can run. Creature drags belong to that bridge — ignore them
    // here. (Codex Playwright, 2026-07-22: the drag-bridge collision that blocked Feature B.)
    if (el.classList.contains('encounter-creature') || el.classList.contains('gm-encounter-creature-card')) return null;
    const invId = el.getAttribute('data-inventory_id') || el.getAttribute('data-draggable_id') || '';
    return invId || null;
}

/** A dragged weapon (by inventory_id) → its attack ability (matched on the attack's `source`). */
function resolveInventoryBinding(caster: CachedCaster, invId: string): { abilityId: string; sourceId: string; itemType: string } | null {
    const abilities = caster?.computed?.creature?.abilities;
    if (!abilities) return null;
    const U = String(invId).toUpperCase();
    for (const type of ['attack_ability', 'opportunity_attack_ability']) {
        const arr = abilities[type];
        if (!Array.isArray(arr)) continue;
        for (const e of arr) {
            const src = e?.source ?? e?.items?.source;
            if (src && String(src).toUpperCase() === U) {
                const aid = e?.item_id || e?.items?.item_id;
                if (aid) return { abilityId: String(aid), sourceId: String(invId), itemType: type };
            }
        }
    }
    return null;
}

function onInvBridgeMove(e: PointerEvent): void {
    const slot = activeInventoryDrag() ? slotUnder(e.clientX, e.clientY) : null;
    if (slot === invDragOverSlot) return;
    invDragOverSlot?.classList.remove('ft-action-slot--dragover');
    invDragOverSlot = slot;
    slot?.classList.add('ft-action-slot--dragover');
}

function onInvBridgeUp(e: PointerEvent): void {
    if (!invDragOverSlot) return;
    const invId = activeInventoryDrag();
    const slotEl = slotUnder(e.clientX, e.clientY);
    invDragOverSlot.classList.remove('ft-action-slot--dragover');
    invDragOverSlot = null;
    if (isLocked() || !invId || !slotEl || !slotEl.hasAttribute('data-ft-ab-bar')) return;
    const pane = slotEl.closest(AB_PANE_SEL) as HTMLElement | null;
    const casterId = pane?.getAttribute('data-ft-caster-id') || '';
    if (!casterId) return;
    const caster = casterFromCache(casterId);
    if (!caster) return;
    const targetBar = Number(slotEl.getAttribute('data-ft-ab-bar'));
    const targetSlot = Number(slotEl.getAttribute('data-ft-ab-slot'));
    if (!Number.isFinite(targetBar) || targetBar < 0) return;
    const bars = editableBars(caster);
    if (!bars[targetBar]) return;
    // Bind the inventory ITEM (slot_type 'inventory', ability_id = inventory_id) and resolve at render:
    // equipped + has an attack → its firing attack button; otherwise the weapon's dimmed icon. Works for
    // both equipped and on-hand weapons, and tracks equip/unequip without re-binding.
    setSlotAt(bars[targetBar], targetSlot, { abilityId: invId, itemType: 'inventory' });
    persist(casterId, bars);
}

function ensureInventoryBridge(): void {
    if (typeof document === 'undefined' || window._ftAbInvBridge) return;
    window._ftAbInvBridge = true;
    document.addEventListener('pointermove', onInvBridgeMove);
    document.addEventListener('pointerup', onInvBridgeUp);
    document.addEventListener('pointercancel', () => {
        invDragOverSlot?.classList.remove('ft-action-slot--dragover');
        invDragOverSlot = null;
    });
}

// ══ Feature B: creature → ability target-slotting (full-screen dock) ═══════════════════════════════
// Drag a creature token onto a slotted ability → save it as a target; clicking the ability preloads the
// LIVE saved set into its targeting draft. Targets are ENCOUNTER-SCOPED UI state, stored in sessionStorage keyed by the
// active encounter (mirrors targeting-combat.ts's ft_aoe_last_anchor_v1) — NOT the DB (action_bar_slots is a
// rigid fixed-column table). The Janus cast-validation chain is authoritative on cast (team skip / size-type
// nullify / item-type veto); the drop-eligibility check below is advisory UX only.
const FT_ABILITY_TARGETS_KEY = 'ft_ability_creature_targets_v1';
const FT_MAX_SLOTTED_TARGETS = 6;

interface SlottedTarget { id: string; thumb: string; name: string; }
// { [encounterId]: { [pcItemId]: { [abilityId]: SlottedTarget[] } } }
type AbilityTargetsBlob = Record<string, Record<string, Record<string, SlottedTarget[]>>>;

function ftActiveEncounterId(): string { return String(window.ftGetActiveEncounterId?.() || '').trim(); }

function readAbilityTargetsBlob(): AbilityTargetsBlob {
    try {
        const raw = sessionStorage.getItem(FT_ABILITY_TARGETS_KEY);
        const o: unknown = raw ? JSON.parse(raw) : null;
        return (o && typeof o === 'object' && !Array.isArray(o)) ? (o as AbilityTargetsBlob) : {};
    } catch { return {}; }
}
/** False when the store refused the write (quota / private mode) — the drop path turns that into a warning
 *  instead of a chip that silently never appears (Codex review 2026-07-24). */
function writeAbilityTargetsBlob(blob: AbilityTargetsBlob): boolean {
    try {
        sessionStorage.setItem(FT_ABILITY_TARGETS_KEY, JSON.stringify(blob));
        return true;
    } catch { return false; }   // quota / private mode
}
function isPlainObject(v: unknown): v is Record<string, unknown> {
    return !!v && typeof v === 'object' && !Array.isArray(v);
}
// encounter_creature_ids are UUIDs → restrict to that shape so a corrupted store can never inject an
// unsafe value into a `[data-encounter_creature_id="…"]` selector (DOMException) or bypass the cap.
const FT_TARGET_UUID_RE = /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/;

/** Coerce any persisted value into a clean SlottedTarget[]: UUID-only ids, case-insensitive dedupe, cap. */
function normalizeTargets(list: unknown): SlottedTarget[] {
    if (!Array.isArray(list)) return [];
    const seen = new Set<string>();
    const out: SlottedTarget[] = [];
    for (const raw of list) {
        if (!isPlainObject(raw)) continue;
        const id = String(raw.id ?? '');
        if (!FT_TARGET_UUID_RE.test(id)) continue;
        const key = id.toUpperCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ id, thumb: String(raw.thumb ?? ''), name: String(raw.name ?? '') });
        if (out.length >= FT_MAX_SLOTTED_TARGETS) break;
    }
    return out;
}
function getSlottedTargets(pcId: string, abilityId: string): SlottedTarget[] {
    const blob = readAbilityTargetsBlob();
    // Most slots have no saved targets. Avoid a document-wide encounter-scope lookup
    // for every slot when this browser's store is empty.
    if (Object.keys(blob).length === 0) return [];
    const enc = ftActiveEncounterId();
    if (!enc || !pcId || !abilityId) return [];
    const encMap = blob[enc];
    if (!isPlainObject(encMap)) return [];
    const pcMap = encMap[pcId.toUpperCase()];
    if (!isPlainObject(pcMap)) return [];
    return normalizeTargets(pcMap[abilityId.toUpperCase()]);
}
/** False when nothing could be persisted (no encounter/pc/ability key, or the store refused the write). */
function setSlottedTargets(pcId: string, abilityId: string, targets: SlottedTarget[]): boolean {
    const enc = ftActiveEncounterId();
    if (!enc || !pcId || !abilityId) return false;
    const pc = pcId.toUpperCase(); const ab = abilityId.toUpperCase();
    const blob = readAbilityTargetsBlob();
    const encMap: Record<string, Record<string, SlottedTarget[]>> = isPlainObject(blob[enc]) ? blob[enc] : (blob[enc] = {});
    const pcMap: Record<string, SlottedTarget[]> = isPlainObject(encMap[pc]) ? encMap[pc] : (encMap[pc] = {});
    const clean = normalizeTargets(targets);
    if (clean.length) pcMap[ab] = clean; else delete pcMap[ab];
    return writeAbilityTargetsBlob(blob);
}
function addSlottedTarget(pcId: string, abilityId: string, t: SlottedTarget): boolean {
    if (!FT_TARGET_UUID_RE.test(t.id)) return false;
    const cur = getSlottedTargets(pcId, abilityId);
    if (cur.length >= FT_MAX_SLOTTED_TARGETS) return false;
    if (cur.some((x) => x.id.toUpperCase() === t.id.toUpperCase())) return false;
    return setSlottedTargets(pcId, abilityId, [...cur, t]);
}
/** Live subset — only targets whose creature token is on the map RIGHT NOW. NON-DESTRUCTIVE: never writes
 *  storage (a render before tokens mount / mid map-teardown must not delete a valid saved set). Stale ids
 *  are encounter-scoped and simply don't render or preload while their creature is absent. */
function liveSlottedTargets(pcId: string, abilityId: string): SlottedTarget[] {
    const targets = getSlottedTargets(pcId, abilityId);
    if (targets.length === 0) return [];
    const perceived = new Set((ft3dLivePerceivedEntityIds() ?? [])
        .map((id) => String(id).trim().toUpperCase())
        .filter(Boolean));
    return targets
        .filter((t) => perceived.has(t.id.toUpperCase())
            || document.querySelector(`.encounter-creature[data-encounter_creature_id="${t.id}"]`) !== null);
}

function ftActionBarRemoveCreatureTarget(pcId: string, abilityId: string, creatureId: string, repaintCasterId: string): void {
    if (!pcId || !abilityId || !creatureId) return;
    setSlottedTargets(pcId, abilityId, getSlottedTargets(pcId, abilityId)
        .filter((x) => x.id.toUpperCase() !== creatureId.toUpperCase()));
    if (repaintCasterId) repaintActionBars(repaintCasterId);
}

function creatureRowById(creatureId: string): CachedCaster | null {
    const rows = (window as unknown as { ft3dEncounterArrCache?: { arr?: { encounter_creatures_arr?: CachedCaster[] } } })
        .ft3dEncounterArrCache?.arr?.encounter_creatures_arr ?? [];
    return casterFromCache(creatureId) ?? rows.find(row =>
        String(row.encounter_creatures?.encounter_creature_id ?? '').toUpperCase() === creatureId.toUpperCase()) ?? null;
}

function creatureAvatarUrl(row: unknown): string {
    try {
        const picture = row && typeof selectCreaturePicture === 'function'
            ? selectCreaturePicture(row, { use_picture_thumbnail: true }) : null;
        return picture && typeof pictureField0 === 'function' ? String(pictureField0(picture, 'pic_url') || '') : '';
    } catch { return ''; }
}

/** The picture the LIVE map token shows — the fallback when the creature row resolves no avatar (no cached
 *  row, or a creature whose art reaches only the token). Token markup (creature-render/creature-thumbnail.ts):
 *  static picture → `img.ft3d-token-media`; animated token → a `<video>` carrying an `<img>` still; no media →
 *  an initials box. This is usually the full standee art, so its consumers letterbox it (object-fit:contain). */
function tokenImageSrc(el: Element | null | undefined): string {
    if (!el) return '';
    const img = (el.querySelector('img.ft3d-token-media') as HTMLImageElement | null)
        ?? (el.querySelector('video img') as HTMLImageElement | null)
        ?? (el.querySelector('img.img-thumbnail, img.token-img') as HTMLImageElement | null)
        // Last resort: any picture in the token that isn't overlay chrome (AP/reaction icons, bars).
        ?? (Array.from(el.querySelectorAll<HTMLImageElement>('img[src]'))
            .find((cand) => !cand.closest('.encounter-creature-overlay') && !cand.closest('.ap-dots')) ?? null);
    return (img?.getAttribute('src') || '').trim();
}

/** Avatar + name for a map token: the creature row's compact pick first, the token's own image only when the
 *  row resolves none. The name is the token's bs-tooltip title — Bootstrap MOVES `title` to
 *  `data-bs-original-title` the first time it initialises a tooltip, so read every spelling. */
function creatureFaceFromToken(el: Element | null | undefined): { thumb: string; name: string } {
    if (!el) return { thumb: '', name: '' };
    const row = creatureRowById(el.getAttribute('data-encounter_creature_id') ?? '');
    const name = (row && typeof getCreatureName === 'function' ? getCreatureName(row) : '')
        || el.getAttribute('data-bs-title')
        || el.getAttribute('data-bs-original-title')
        || el.getAttribute('title')
        || el.getAttribute('data-creature_name')
        || '';
    return { thumb: creatureAvatarUrl(row) || tokenImageSrc(el), name: name.trim() };
}
/** Same, by encounter_creature_id (UUID-guarded — the id reaches a selector). */
function creatureFaceById(creatureId: string): { thumb: string; name: string } {
    if (typeof document === 'undefined' || !FT_TARGET_UUID_RE.test(creatureId)) return { thumb: '', name: '' };
    const token = document.querySelector(`.encounter-creature[data-encounter_creature_id="${creatureId}"]`);
    const row = creatureRowById(creatureId);
    if (!row) return creatureFaceFromToken(token);
    return {
        thumb: creatureAvatarUrl(row) || tokenImageSrc(token),
        name: typeof getCreatureName === 'function' ? String(getCreatureName(row) || '') : '',
    };
}

/** Two-letter fallback face for a target with no picture (mirrors the token's own initials box). */
function targetInitials(name: string): string {
    const n = name.trim();
    if (!n) return '?';
    return n.charAt(0).toUpperCase() + (n.length > 1 ? n.charAt(1).toLowerCase() : '');
}

/** Thumbnail overlay for an ability slot that has saved creature targets (only the chips take pointer
 *  events — the overlay's own gaps stay click-through). Face + name are RE-RESOLVED from the live row on
 *  every render, so a mid-encounter picture/name change (and any pre-fix entry saved with an empty thumb)
 *  self-heals without re-dragging; the stored copy is the offline fallback. */
function renderSlotTargetsOverlay(caster: CachedCaster, abilityId: string): string {
    const pcId = pcItemId(caster);
    if (!pcId || !abilityId) return '';
    const targets = liveSlottedTargets(pcId, abilityId).slice(0, FT_MAX_SLOTTED_TARGETS);
    if (!targets.length) return '';
    const chips = targets.map((t) => {
        const live = creatureFaceById(t.id);
        const thumb = live.thumb || t.thumb;
        const name = live.name || t.name;
        const label = name || 'Saved target';
        // data-ft-info-item-id makes the THUMBNAIL a press-hold target (press-hold.ts) → hold it to open
        // that creature's card (openItemInfo renders a live encounter creature from the cached blob). It also
        // hands press-hold's click blocker the node, so a hold can NEVER fall through to the chip's
        // click-to-cast retarget below (Codex review 2026-07-24: a pointer-events:auto chip that no
        // press-hold selector matched would have cast the ability on release of an inspect hold). It sits on
        // the image, not the chip, so the remove-× (a sibling) keeps its own plain click.
        const infoAttr = `data-ft-info-item-id="${escapeHtml(t.id)}"`;
        const face = thumb
            ? `<img src="${escapeHtml(thumb)}" alt="${escapeHtml(label)}" class="ft-action-tgt-chip__img" style="object-fit:contain" ${infoAttr}>`
            : `<span class="ft-action-tgt-chip__img ft-action-tgt-chip__img--none" ${infoAttr}>`
                + `${escapeHtml(targetInitials(name))}</span>`;
        return `<span class="ft-action-tgt-chip" ${tip(label, 'top')}>${face}`
            + `<button type="button" class="ft-action-tgt-chip__x" data-ft-ab-action="creature-target-remove"`
            + ` aria-label="Remove ${escapeHtml(label)}"`
            + ` data-ft-pc-id="${escapeHtml(pcId)}" data-ft-ability-id="${escapeHtml(abilityId)}"`
            + ` data-ft-creature-id="${escapeHtml(t.id)}">&#10005;</button></span>`;
    }).join('');
    return `<div class="ft-action-tgt-overlay">${chips}</div>`;
}

// ── the creature → ability-slot bridge (reads window.dragManager; dock-only, no shared-state edits) ──
let creatureDragOverSlot: HTMLElement | null = null;

function activeCreatureDragId(): string | null {
    const dm = window.dragManager;
    if (!dm?.state?.isDragging) return null;
    const el = dm.state.draggedElement as HTMLElement | null;
    if (!el || typeof el.classList?.contains !== 'function' || !el.classList.contains('encounter-creature')) return null;
    return el.getAttribute('data-encounter_creature_id') || el.getAttribute('data-draggable_id') || null;
}
/** Verdict for a creature dragged over a DOCK bar slot. `reason` empty ⇒ the drop will save; non-empty ⇒
 *  the drop is refused and the string is the player-facing explanation (shown as a warning on release and
 *  as a RED slot outline while still dragging). null (from the checker) ⇒ not over a dock slot at all, so
 *  the drag belongs to someone else (the map, the bar's own move/unbind) — no highlight, no warning. */
interface CreatureDropCheck {
    slotEl: HTMLElement;
    abilityId: string;      // '' when the slot can't hold saved targets
    casterId: string;
    caster: CachedCaster | null;
    reason: string;
}

function slotAbilityName(caster: CachedCaster | null, abilityId: string): string {
    if (!caster || !abilityId) return 'this ability';
    return abilityEntryName(findAbility(caster, abilityId)) || 'this ability';
}

function dockSlotUnder(clientX: number, clientY: number): HTMLElement | null {
    const slot = slotUnder(clientX, clientY);
    return slot && slot.closest('.ft3d-actionbar-dock') ? slot : null;   // full-screen dock only
}

/** Advisory pre-check for a creature → ability-slot drop. Every refusal path returns a REASON (owner
 *  2026-07-24: a blocked drag must say WHY) — including the ones that used to fail silently: an empty /
 *  non-ability slot, an own-equipment picker, a read-only bar, a duplicate, and the per-ability cap.
 *  The Janus cast-validation chain stays authoritative at cast time; this is UX only and fails OPEN.
 *  Called on pointerup AND once per hovered-slot change during the drag (the ring colour), so keep it to
 *  DOM reads + one sessionStorage read + the shared gate — never anything that writes. */
function creatureDropCheck(slot: HTMLElement, creatureId: string): CreatureDropCheck {
    const pane = slot.closest(AB_PANE_SEL) as HTMLElement | null;
    const casterId = pane?.getAttribute('data-ft-caster-id') || '';
    const caster = casterId ? casterFromCache(casterId) : null;
    const out = (reason: string, abilityId = ''): CreatureDropCheck => ({ slotEl: slot, abilityId, casterId, caster, reason });

    // Real rune ability buttons only: special/action/skill/inventory slots (short rest, add-AP, a bound
    // weapon icon …) never render the target overlay and aren't creature-target abilities.
    const btn = slot.querySelector('.btn_ability_ranges[data-ability_id]') as HTMLElement | null;
    if (!btn) {
        if (!slot.classList.contains('ft-action-slot--filled')) {
            return out('That bar slot is empty — drop the creature onto a slotted ability to save it as a target.');
        }
        return out("That slot's action can't hold a saved creature target — only ability slots can.");
    }
    const abilityId = btn.getAttribute('data-ability_id') || '';
    if (!abilityId) return out("That slot's action can't hold a saved creature target — only ability slots can.");
    if (btn.hasAttribute('data-ft-item-target-types')) {
        return out(`${slotAbilityName(caster, abilityId)} targets your own equipment, not a creature.`, abilityId);
    }
    if (slot.closest('.ft-action-bars--readonly')) {
        return out('You can only save targets on action bars you control.', abilityId);
    }
    if (!caster) return out("Couldn't resolve that character's action bar — try reopening the sheet.", abilityId);
    if (!ftActiveEncounterId()) {
        return out('Saved targets are encounter-scoped — there is no active encounter to save one against.', abilityId);
    }
    const pcId = pcItemId(caster);
    if (!pcId) return out("Couldn't resolve that character's action bar — try reopening the sheet.", abilityId);

    const abName = slotAbilityName(caster, abilityId);
    // Only a real encounter_creature UUID can be stored (normalizeTargets / addSlottedTarget enforce it) —
    // catch a malformed id HERE so it warns instead of silently dropping through addSlottedTarget's guard.
    if (!FT_TARGET_UUID_RE.test(creatureId)) {
        return out("Couldn't identify that creature — nothing was saved. Try dragging its map token again.", abilityId);
    }
    const saved = getSlottedTargets(pcId, abilityId);
    if (saved.some((x) => x.id.toUpperCase() === creatureId.toUpperCase())) {
        const nm = creatureFaceById(creatureId).name || 'That creature';
        return out(`${nm} is already a saved target for ${abName}.`, abilityId);
    }
    if (saved.length >= FT_MAX_SLOTTED_TARGETS) {
        return out(`${abName} already holds the maximum of ${FT_MAX_SLOTTED_TARGETS} saved targets — remove one first.`, abilityId);
    }
    // Per-creature legality (team / size / creature type / sub-type / status-not). Reuse the targeting-combat
    // gate so the drop and the target-compat ring can't drift.
    const gate = window.FateTide?.abilityDropTargetFailure?.(abilityId, casterId, creatureId);
    if (gate) return out(String(gate), abilityId);
    return out('', abilityId);
}

function clearCreatureDragOver(): void {
    creatureDragOverSlot?.classList.remove('ft-action-slot--tgt-dragover', 'ft-action-slot--tgt-dragover-bad');
    creatureDragOverSlot = null;
}

function onCreatureBridgeMove(e: PointerEvent): void {
    const creatureId = activeCreatureDragId();
    const slot = creatureId ? dockSlotUnder(e.clientX, e.clientY) : null;
    if (slot === creatureDragOverSlot) return;             // cheap gate: only re-check when the slot changes
    clearCreatureDragOver();
    creatureDragOverSlot = slot;
    if (!slot || !creatureId) return;
    // Pre-drop signal: cyan = will save, red-dashed = refused (the reason is spoken on release).
    const bad = !!creatureDropCheck(slot, creatureId).reason;
    slot.classList.add(bad ? 'ft-action-slot--tgt-dragover-bad' : 'ft-action-slot--tgt-dragover');
}

function onCreatureBridgeUp(e: PointerEvent): void {
    clearCreatureDragOver();
    const creatureId = activeCreatureDragId();
    if (!creatureId) return;
    const dropSlot = dockSlotUnder(e.clientX, e.clientY);
    if (!dropSlot) return;                                 // released off the dock — not our drop
    const check = creatureDropCheck(dropSlot, creatureId);
    if (check.reason) {
        window.displayWarning?.(escapeHtml(check.reason));
        check.slotEl.classList.add('ft-action-slot--tgt-reject');
        setTimeout(() => check.slotEl?.classList.remove('ft-action-slot--tgt-reject'), 500);
        return;
    }
    const caster = check.caster;
    if (!caster) return;                                   // unreachable (checked above) — keeps TS honest
    const draggedEl = window.dragManager?.state?.draggedElement as HTMLElement | null;
    const face = creatureFaceFromToken(draggedEl);
    const thumb = face.thumb || creatureFaceById(creatureId).thumb;
    const name = face.name || creatureFaceById(creatureId).name;
    if (!addSlottedTarget(pcItemId(caster), check.abilityId, { id: creatureId, thumb, name })) {
        // Everything semantic was cleared by the check above, so a failure here is the STORE refusing us
        // (private mode / full quota). Say so rather than leaving a drop that visibly did nothing.
        window.displayWarning?.("Couldn't save that target — this browser is blocking session storage.");
        check.slotEl.classList.add('ft-action-slot--tgt-reject');
        setTimeout(() => check.slotEl?.classList.remove('ft-action-slot--tgt-reject'), 500);
        return;
    }
    repaintActionBars(check.casterId);
}
function ensureCreatureTargetBridge(): void {
    if (typeof document === 'undefined' || window._ftAbCreatureBridge) return;
    window._ftAbCreatureBridge = true;
    ensureCreatureTargetStyles();
    document.addEventListener('pointermove', onCreatureBridgeMove);
    document.addEventListener('pointerup', onCreatureBridgeUp);
    document.addEventListener('pointercancel', clearCreatureDragOver);
}
function ensureCreatureTargetStyles(): void {
    if (typeof document === 'undefined' || document.getElementById('ft-ab-creature-target-css')) return;
    const style = document.createElement('style');
    style.id = 'ft-ab-creature-target-css';
    style.textContent = `
.ft-action-slot{position:relative;}
.ft-action-slot--tgt-dragover{outline:2px solid #5bd6ff;outline-offset:-2px;border-radius:6px;}
/* Refused drop (wrong team / size / already saved / cap / non-ability slot): red DASHED ring while dragging,
   so the "you can't put it here" read arrives BEFORE the release warning. */
.ft-action-slot--tgt-dragover-bad{outline:2px dashed #ff5b5b;outline-offset:-2px;border-radius:6px;background:rgba(255,91,91,.12);}
.ft-action-slot--tgt-reject{animation:ftTgtReject .5s ease;}
@keyframes ftTgtReject{0%,100%{outline:2px solid transparent;}25%,75%{outline:2px solid #ff5b5b;outline-offset:-2px;}}
/* The overlay itself is click-THROUGH (its gaps must hit the ability button beneath); the CHIPS take pointer
   events so the delegated bs-tooltip mouseover in template.js can fire and name the target on hover. A chip
   click is retargeted to the slot's ability button (onAbilityChoiceClickIntercept / onAbilityPointerDown), so
   clicking a thumbnail still casts on the saved set and dragging one still moves the slot. */
.ft-action-tgt-overlay{position:absolute;left:2px;right:2px;bottom:2px;display:flex;flex-wrap:wrap;gap:2px;pointer-events:none;z-index:3;}
.ft-action-tgt-chip{position:relative;width:18px;height:18px;pointer-events:auto;cursor:pointer;}
.ft-action-tgt-chip__img{display:block;width:18px;height:18px;border-radius:50%;object-fit:cover;border:1px solid rgba(0,0,0,.7);box-shadow:0 0 0 1px rgba(255,255,255,.35);background:#222;}
.ft-action-tgt-chip__img--none{display:flex;align-items:center;justify-content:center;background:#556;color:#fff;font-size:8px;font-weight:700;line-height:1;letter-spacing:-.3px;}
.ft-action-tgt-chip__x{position:absolute;top:-5px;right:-5px;width:12px;height:12px;line-height:10px;font-size:9px;padding:0;border:none;border-radius:50%;background:#b22;color:#fff;cursor:pointer;opacity:.85;pointer-events:auto;transition:opacity .1s;}
.ft-action-tgt-chip__x:hover{opacity:1;}
`;
    document.head.appendChild(style);
}


// ── styling (injected once) ──────────────────────────────────────────────────────────────────────
function ensureActionBarStyles(): void {
    if (typeof document === 'undefined' || document.getElementById('ft-action-bars-css')) return;
    const style = document.createElement('style');
    style.id = 'ft-action-bars-css';
    style.textContent = `
/* Recessed tray FRAME around the whole bar (BG3 hotbar feel). Column: status strip (top edge) + slot row. */
/* Color SYSTEM: the frame/slot/accent palette is driven by --ftab-* custom properties so a per-PC theme
   (preset or custom #hex) can be applied as a single inline style on this element (see themeStyle()). The
   defaults below ARE the neutral "dark" theme (cool slate frame + soft steel accent). Presets override the
   frame tokens; a custom color only re-tints --ftab-rim-rgb / --ftab-accent-rgb (the glow), keeping the
   dark frame — minimalist by design. */
.ft-action-bars{
  --ftab-bg-1:#262931;--ftab-bg-2:#191b21;--ftab-bg-3:#0f1014;
  --ftab-border:#070809;
  --ftab-rim-rgb:140,150,168;        /* metal inner trim (rgba channels) */
  --ftab-accent-rgb:150,175,220;     /* hover glow / filled rim / controls (rgba channels) */
  --ftab-slot-top:rgba(42,46,54,.55);--ftab-slot-bot:rgba(8,9,12,.8);
  display:flex;flex-direction:column;border:1px solid var(--ftab-border);border-radius:12px;background:linear-gradient(180deg,var(--ftab-bg-1) 0%,var(--ftab-bg-2) 42%,var(--ftab-bg-3) 100%);box-shadow:inset 0 0 0 1px rgba(var(--ftab-rim-rgb),.3),inset 0 2px 3px rgba(0,0,0,.5),inset 0 -12px 26px rgba(0,0,0,.4),0 8px 24px rgba(0,0,0,.3),0 26px 60px rgba(0,0,0,.34);overflow:hidden;}
/* Slot row + controls (the former .ft-action-bars flex row). */
.ft-action-bars__main{display:flex;align-items:flex-start;gap:8px;padding:6px 8px 5px;}
/* Status-effect cluster ABOVE the bar (BG3-style): a horizontal strip of floating status icons led by the
   bordered manage button on the far left. Desktop caps the inline set and exposes the remainder through a
   +N button; the mobile Status tab restores every icon in its horizontally scrolling row. */
.ft-action-status{flex:1 1 auto;min-width:0;display:flex;flex-wrap:wrap;align-items:flex-end;align-content:flex-end;gap:8px;padding:0 10px;}
.ft-action-status__edit{flex:0 0 auto;width:30px;height:30px;min-width:30px;padding:0;border-radius:6px;display:inline-flex;align-items:center;justify-content:center;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.2);color:#fff;cursor:pointer;}
.ft-action-status__edit:hover{background:rgba(255,255,255,.13);}
.ft-action-status__icon{flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;padding:0;border:0;background:transparent;cursor:pointer;line-height:0;}
.ft-action-status__icon img,.ft-action-status__icon svg,.ft-action-status__icon .ft-rune-mount,.ft-action-status__icon .ft-icon,.ft-action-status__icon .ft-icon-map{width:22px;height:22px;display:block;}
.ft-action-status__icon--overflow{display:none;}
.ft-action-status__more{flex:0 0 auto;height:24px;min-width:30px;padding:0 6px;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;border:1px solid rgba(var(--ftab-rim-rgb),.45);background:rgba(255,255,255,.08);color:#fff;font-size:10px;font-weight:800;line-height:1;cursor:pointer;}
.ft-action-status__more:hover{border-color:rgba(var(--ftab-accent-rgb),.65);background:rgba(255,255,255,.14);}
/* Desktop header: statuses/skills left, the ability island center, contextual controls right. Equal flexible
   SIDE tracks keep the middle track centered over the whole bar even when the status side is much wider than
   the controls side. Statuses wrap upward inside the left track; they never push the ability island sideways.
   All three groups share the lower baseline, and any genuinely-needed extra height grows upward over the map. */
.ft-action-bars-top{position:relative;display:grid;grid-template-columns:minmax(0,1fr) auto minmax(0,1fr);grid-template-areas:"left island right";align-items:end;column-gap:10px;min-height:32px;padding:3px 9px;}
.ft-action-bars-top:empty{display:none;}
.ft-action-bars-top__left{grid-area:left;width:100%;min-width:0;display:flex;align-items:flex-end;}
/* Ability island — category icons (free/quick/…/curse/menta-L/spell-L), centered in the available middle
   column. Desktop wrapping is intentional and accounted for by the grid; mobile overrides this to one swipe row. */
.ft-action-island{grid-area:island;position:static;width:max-content;min-width:0;max-width:min(720px,60vw);display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:6px;z-index:2;}
.ft-island-cat{position:relative;display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;padding:0;border:0;border-radius:6px;background:transparent;cursor:pointer;line-height:0;}
.ft-island-cat:hover{background:rgba(255,255,255,.08);}
.ft-island-cat.is-active{background:rgba(255,255,255,.15);box-shadow:inset 0 0 0 1px rgba(255,255,255,.3);}
.ft-island-cat .ft-icon,.ft-island-cat .ft-icon-map,.ft-island-cat svg,.ft-island-cat img{width:22px;height:22px;display:block;}
.ft-island-lvl{position:absolute;bottom:-3px;right:-3px;font-size:9px;font-weight:700;line-height:1;background:rgba(0,0,0,.85);color:#fff;border:1px solid rgba(255,255,255,.25);border-radius:7px;padding:0 3px;}
/* Fly-up strip: the open category's usable medallions (reuses .ft-ability-iconstrip.ft-ai--m sizing). */
.ft-ability-flyout{display:flex;justify-content:center;}
.ft-ability-flyout:empty{display:none;}
.ft-ability-flyout__inner{width:100%;padding:6px 8px 4px;animation:ftIslandFlyUp .16s ease-out;}
.ft-ability-flyout__inner--skill.has-source-selection{animation:none;}
.ft-ability-flyout__head{display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:4px;}
.ft-ability-flyout__title{font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:rgba(255,255,255,.75);}
.ft-ability-flyout__sorts{display:inline-flex;gap:3px;}
.ft-island-sort{font-size:10px;font-weight:600;padding:1px 6px;border-radius:4px;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.04);color:rgba(255,255,255,.8);cursor:pointer;}
.ft-island-sort.is-active{background:rgba(255,255,255,.18);color:#fff;}
.ft-ability-flyout__strip{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:6px;}
.ft-skill-ability-runes{display:flex;flex-wrap:nowrap;align-items:center;justify-content:center;gap:6px;max-width:100%;overflow-x:auto;overflow-y:hidden;scrollbar-width:thin;}
.ft-skill-ability-sources{display:flex;flex-wrap:nowrap;align-items:center;justify-content:center;gap:5px;max-width:100%;overflow-x:auto;overflow-y:hidden;scrollbar-width:thin;}
.ft-skill-ability-sources.has-selection{margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,.1);}
.ft-skill-ability-source{flex:0 0 auto;position:relative;display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;padding:0;border:1px solid rgba(255,255,255,.14);border-radius:6px;background:rgba(255,255,255,.04);color:#fff;cursor:pointer;line-height:0;}
.ft-skill-ability-source:hover{background:rgba(255,255,255,.1);border-color:rgba(var(--ftab-accent-rgb),.45);}
.ft-skill-ability-source.is-active{background:linear-gradient(180deg,rgba(var(--ftab-accent-rgb),.34),rgba(0,0,0,.45));border-color:rgba(var(--ftab-accent-rgb),.65);box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.38);}
.ft-skill-ability-source img,.ft-skill-ability-source svg,.ft-skill-ability-source .ft-icon,.ft-skill-ability-source .ft-icon-map{width:22px;height:22px;display:block;}
/* Movement fly-up is denser than the medallion strips (speed selector + stance/mount/vehicle/haven
   buttons), so tighten + keep it to ONE clean row (owner 2026-06-29).
   - nowrap + overflow-x scroll = single row, scroll only if a PC ever exceeds the fly-up width.
   - flex:0 0 auto on the children is the key fix: WITHOUT it, nowrap lets flex-shrink squeeze each
     bordered group narrow, and the groups' own flex-wrap then breaks their buttons onto a 2nd internal
     row (the "looks like shit" sparse 2-row layout). Pin the children + force the groups single-row.
   - icons shrunk to 24px so the full set fits one row on desktop without scrolling. */
.ft-movement-strip{gap:3px;flex-wrap:nowrap;overflow-x:auto;overflow-y:hidden;justify-content:flex-start;scrollbar-width:thin;}
.ft-movement-strip::-webkit-scrollbar{height:6px;}
.ft-movement-strip > *{flex:0 0 auto;}
.ft-movement-strip .ft-move-speed-group,
.ft-movement-strip .ft-move-mount-group,
.ft-movement-strip .ft-move-vehicle-group{flex-wrap:nowrap;}
.ft-movement-strip .p-1{padding:.12rem !important;}
.ft-movement-strip .btn{padding:.2rem;}
.ft-movement-strip img,.ft-movement-strip svg{width:24px !important;height:24px !important;}
@keyframes ftIslandFlyUp{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
/* Full-screen-dock resource readout: slim HP + Mana bars (+ class-mechanic charge strips). */
.ft-action-resources{display:flex;flex-wrap:wrap;align-items:center;gap:8px 14px;padding:3px 10px 5px;}
.ft-action-res-bar{display:flex;align-items:center;gap:5px;flex:1 1 0;min-width:110px;cursor:pointer;}
/* AP pips + reaction — fixed-width BADGED pill leading the resources row (HP/Mana/class flex to fill the rest). */
.ft-action-ap{display:inline-flex;align-items:center;flex:0 0 auto;gap:3px;padding:2px 7px;border-radius:999px;background:rgba(0,0,0,.4);border:1px solid rgba(255,255,255,.14);box-shadow:inset 0 1px 2px rgba(0,0,0,.45);}
.ft-action-ap[data-ft-ap-interactive="1"]{padding:0;transition:border-color .12s ease,box-shadow .12s ease;}
.ft-action-ap__button{display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border:0;border-radius:999px;background:transparent;color:inherit;cursor:pointer;line-height:0;}
.ft-action-ap[data-ft-ap-interactive="1"]:hover,.ft-action-ap[data-ft-ap-interactive="1"]:focus-within{border-color:rgba(var(--ftab-accent-rgb),.62);box-shadow:inset 0 1px 2px rgba(0,0,0,.45),0 0 8px rgba(var(--ftab-accent-rgb),.34);}
.ft-action-ap__button:focus-visible{outline:2px solid rgba(var(--ftab-accent-rgb),.9);outline-offset:2px;}
.ft-action-ap-pip{display:block;width:14px;height:14px;}
.ft-action-res-ico{flex:0 0 auto;display:inline-flex;line-height:0;}
.ft-action-res-ico img,.ft-action-res-ico svg,.ft-action-res-ico .ft-icon,.ft-action-res-ico .ft-icon-map{width:16px;height:16px;display:block;}
.ft-action-res-track{position:relative;flex:1 1 auto;display:flex;height:15px;border-radius:8px;background:rgba(0,0,0,.5);border:1px solid rgba(255,255,255,.18);overflow:hidden;box-shadow:inset 0 1px 2px rgba(0,0,0,.55);}
.ft-action-res-fill{flex:0 0 auto;height:100%;transition:width .38s cubic-bezier(.22,.61,.36,1);}
/* AP pill pulses when a point is spent/restored (pip art is discrete, so we pulse the whole badge). */
.ft-action-ap--changed{animation:ftApPulse .42s ease;}
@keyframes ftApPulse{0%{transform:scale(1);}34%{transform:scale(1.14);box-shadow:inset 0 1px 2px rgba(0,0,0,.45),0 0 9px rgba(var(--ftab-accent-rgb),.5);}100%{transform:scale(1);}}
@media (prefers-reduced-motion:reduce){.ft-action-res-fill{transition:none;}.ft-action-ap--changed{animation:none;}}
.ft-action-res-fill--hp{background:linear-gradient(180deg,#ec5054,#9c0f14);}
.ft-action-res-fill--temp{background:linear-gradient(180deg,#ffd44a,#dca000);}
.ft-action-res-fill--mana{background:linear-gradient(180deg,#46baea,#1370ac);}
.ft-action-res-fill--mech{background:linear-gradient(180deg,#f2a93c,#b06a10);}
.ft-action-res-label{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.95);pointer-events:none;}
/* Timed-activity countdown bar (WoW-style cast bar) — gathering/reading/training: fixed-width, centered,
   floats ABOVE the bar in normal flow (sibling like the choice/settings panels). Activity + skill icons on
   the far left, then a track whose fill GROWS left→right toward completion as the campaign clock advances,
   with the remaining time centered over it. Width is set post-mount by ftUpdateActivityBars; glides each tick. */
.ft-activity-bar{display:flex;align-items:center;gap:8px;width:320px;max-width:100%;margin:0 auto 6px;padding:5px 11px;border:1px solid rgba(0,0,0,.65);border-radius:10px;background:linear-gradient(180deg,rgba(30,32,40,.97),rgba(15,16,21,.99));box-shadow:inset 0 1px 0 rgba(255,255,255,.05),0 4px 14px rgba(0,0,0,.55);animation:ftIslandFlyUp .14s ease-out;}
.ft-activity-icons{flex:0 0 auto;display:inline-flex;align-items:center;gap:4px;line-height:0;}
.ft-activity-icons img,.ft-activity-icons svg,.ft-activity-icons .ft-icon,.ft-activity-icons .ft-icon-map{width:18px;height:18px;display:block;}
.ft-activity-track{position:relative;flex:1 1 auto;display:flex;height:16px;border-radius:8px;background:rgba(0,0,0,.5);border:1px solid rgba(255,255,255,.18);overflow:hidden;box-shadow:inset 0 1px 2px rgba(0,0,0,.55);}
.ft-activity-fill{flex:0 0 auto;height:100%;width:0%;background:linear-gradient(180deg,#7ad17a,#2e8b2e);transition:width .4s cubic-bezier(.22,.61,.36,1);}
/* Per-activity fill color — tell activities apart at a glance. Gathering keeps the default green. */
.ft-activity-bar[data-ft-activity-kind="reading"] .ft-activity-fill{background:linear-gradient(180deg,#5bb8f0,#1f6fb0);}
.ft-activity-bar[data-ft-activity-kind="training"] .ft-activity-fill{background:linear-gradient(180deg,#f4c454,#c8861a);}
.ft-activity-time{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.95);pointer-events:none;}
@media (prefers-reduced-motion:reduce){.ft-activity-fill{transition:none;}}
/* Class-mechanic bars (same look as HP/Mana) + their tiny ± charge buttons; sit in a wrapping group. */
.ft-action-res-bar--mech{flex:1 1 0;min-width:130px;}
.ft-action-res-chg{flex:0 0 auto;width:18px;height:18px;padding:0;display:inline-flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.08);border-radius:4px;color:#fff;cursor:pointer;line-height:0;}
.ft-action-res-chg:hover{background:rgba(255,255,255,.16);}
.ft-action-res-chg img,.ft-action-res-chg svg,.ft-action-res-chg .ft-icon,.ft-action-res-chg .ft-icon-map{width:12px;height:12px;display:block;}
/* ▲/▼ pivot to switch class mechanic when a caster has 2+ (a compact vertical carrot stack). */
.ft-action-mech-pivot{display:inline-flex;flex-direction:column;gap:1px;flex:0 0 auto;}
.ft-action-mech-carrot{width:15px;height:9px;padding:0;display:inline-flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.08);color:#fff;cursor:pointer;font-size:7px;line-height:1;border-radius:3px;}
.ft-action-mech-carrot:hover{background:rgba(255,255,255,.18);}
/* The whole block is ONE grid: shared columns across rows → uniform slots + row-spanning dividers. */
.ft-action-bars__rows{flex:1 1 auto;min-width:0;position:relative;display:grid;gap:4px;overflow-x:auto;overflow-y:hidden;padding-bottom:2px;scrollbar-width:thin;scrollbar-color:rgba(255,255,255,.25) transparent;}
.ft-action-bars__rows::-webkit-scrollbar{height:6px;}
.ft-action-bars__rows::-webkit-scrollbar-thumb{background:rgba(255,255,255,.25);border-radius:3px;}
.ft-action-bars__rows::-webkit-scrollbar-track{background:transparent;}
/* Uniform slot box for EVERY state (square, same border) — the button inside is chromeless so filled
   and empty slots share one footprint (no offset). */
/* Beveled, recessed slot: warm metal trim + deep inset + a faint warm bottom edge — reads as a socket. */
.ft-action-slot{position:relative;aspect-ratio:1;min-width:44px;display:flex;align-items:center;justify-content:center;border-radius:7px;border:1px solid rgba(0,0,0,.7);background:linear-gradient(180deg,var(--ftab-slot-top),var(--ftab-slot-bot));box-shadow:inset 0 0 0 1px rgba(var(--ftab-rim-rgb),.2),inset 0 2px 5px rgba(0,0,0,.7),0 1px 0 rgba(var(--ftab-accent-rgb),.13);overflow:hidden;transition:box-shadow .12s ease,border-color .12s ease;}
.ft-action-slot--empty{border-style:dashed;border-color:rgba(140,108,60,.22);background:rgba(6,5,4,.5);box-shadow:inset 0 2px 6px rgba(0,0,0,.55);}
/* Filled slot reads slightly raised (gold inner rim); hover lights a warm rune-glow (skip the can't-use ones). */
.ft-action-slot--filled{box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.26),inset 0 1px 4px rgba(0,0,0,.5),0 1px 0 rgba(var(--ftab-accent-rgb),.12);}
.ft-action-slot--filled:not(.is-unusable):hover{border-color:rgba(var(--ftab-accent-rgb),.6);box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.5),inset 0 1px 3px rgba(0,0,0,.4),0 0 11px rgba(var(--ftab-accent-rgb),.4);}
/* A slot whose bound ability/item/skill no longer resolves (deleted spell, removed item…): a dimmed,
   desaturated socket with a centered "?" glyph (help-circle-outline) so it reads as "this is gone", not
   as an empty/unbound slot. Dashed warm-red rim hints "stale binding — clear or re-point me". */
.ft-action-slot--missing{opacity:.55;border-style:dashed;border-color:rgba(190,90,80,.4);}
.ft-action-slot--missing img,.ft-action-slot--missing .ft-icon,.ft-action-slot--missing .ft-icon-map{width:60%;height:60%;max-width:30px;max-height:30px;object-fit:contain;filter:grayscale(.7);opacity:.85;pointer-events:none;}
/* Editable: a missing slot is grabbable — drag it off the bar to unbind (delete) the dead binding. */
.ft-action-bars.is-editable .ft-action-slot--missing{cursor:grab;}
/* Item-granted ability whose SOURCE ITEM is no longer equipped: unlike --missing we KEEP the real
   color-coded rune so you still recognize the power (owner: "we color code them more" — don't flatten
   it to a grey ?). Desaturate + dim the icon and ring the slot in a dashed AMBER "unequipped" rim,
   distinct from the RED AP/mana "can't use" ring. The cast is hard-blocked server-side; a click just
   surfaces the reason. Hotkey/corner badges stay crisp (only the icon dims). (owner 2026-07-13) */
.ft-action-slot--unavailable{border-style:dashed;border-color:rgba(228,170,90,.6);box-shadow:inset 0 0 0 1px rgba(228,170,90,.32),inset 0 1px 4px rgba(0,0,0,.5);}
.ft-action-slot--unavailable .ft-rune-mount,.ft-action-slot--unavailable img,.ft-action-slot--unavailable .ft-icon,.ft-action-slot--unavailable .ft-icon-map{opacity:.5;filter:grayscale(.6);}
.ft-action-slot--unavailable .ft-action-key-badge{opacity:1;filter:none;}
.ft-action-slot--filled.ft-action-slot--unavailable:hover{border-color:rgba(228,170,90,.85);box-shadow:inset 0 0 0 1px rgba(228,170,90,.5),0 0 10px rgba(228,170,90,.3);}
/* Remaining charges use the top-right corner; AP/recharge discs stay on the bottom rim.
   Charged slots compact their hotkey at top-left; trigger toggles sit just below it. */
[data-ft-pc-pane="action_bar"] .btn_ability_ranges{position:relative;}
.ft-action-charge-pill{position:absolute;top:1px;right:1px;z-index:6;min-width:14px;padding:1px;border:1px solid #87909e;border-radius:7px;background:#161b24;color:#f8fafc;font:700 11px/1.1 ui-monospace,SFMono-Regular,Menlo,monospace;text-align:center;pointer-events:none;opacity:1!important;filter:none!important;}
.ft-action-charge-pill--empty{background:#a51d2d;border-color:#ff8c98;color:#fff;}
.ft-action-slot:has(.ft-action-charge-pill) .ft-action-key-badge{left:1px;transform:none;padding-inline:2px;}
.ft-trigger-ability-wrapper:has(.ft-action-charge-pill) > .ft-trigger-badge{left:1px;right:auto;top:16px;}
/* Buttons fill the slot, chromeless (the slot provides the box). */
.ft-action-slot .ft-trigger-ability-wrapper{width:100%;height:100%;}
.ft-action-slot .btn_ability_ranges{width:100%;height:100%;padding:2px;background:transparent;border:0;box-shadow:none;}
.ft-action-slot .ft-rune-mount{width:100%;height:100%;}
.ft-action-slot--skill .ft-action-skill-btn{width:100%;height:100%;padding:2px;background:transparent;border:0;display:flex;align-items:center;justify-content:center;}
.ft-action-slot--skill .ft-action-skill-btn img,.ft-action-slot--skill .ft-action-skill-btn svg,.ft-action-slot--skill .ft-action-skill-btn .ft-icon,.ft-action-slot--skill .ft-action-skill-btn .ft-icon-map{width:100%;height:100%;object-fit:contain;}
/* Special universal ability (short rest / resolve / spot hidden / custom-*): chromeless icon button. */
.ft-action-slot--special .ft-action-special-btn{width:100%;height:100%;padding:3px;background:transparent;border:0;display:flex;align-items:center;justify-content:center;}
.ft-action-slot--special .ft-action-special-btn img,.ft-action-slot--special .ft-action-special-btn svg,.ft-action-slot--special .ft-action-special-btn .ft-icon,.ft-action-slot--special .ft-action-special-btn .ft-icon-map{width:100%;height:100%;object-fit:contain;}
.ft-action-bars.is-editable .ft-action-slot--special .ft-action-special-btn{cursor:grab;}
/* Bound INVENTORY item: its thumbnail FILLS the slot (the tile's fixed 44px box is overridden to 100%
   so the art isn't a small icon floating in padding); dimmed when the item isn't equipped. */
.ft-action-slot--inv{padding:1px;}
.ft-action-slot--inv-unequipped{opacity:.45;}
/* "Can't use" abilities (AP-short / concentration-busy) IN THE BAR: keep the red border + glow at FULL
   strength (button opacity stays 1) but DIM the icon CONTENT — like unequipped inventory — so it clearly
   reads inactive. Previously the whole-button 0.6 was beaten by the action-bar styles, leaving border-only
   (owner 2026-06-26). Border/box-shadow live on the button, so dimming the children doesn't touch them. */
.ft-action-slot .ft-ability-btn-ap-short,
.ft-action-slot .ft-ability-btn-concentration-busy{opacity:1 !important;}
.ft-action-slot .ft-ability-btn-ap-short > *,
.ft-action-slot .ft-ability-btn-concentration-busy > *{opacity:.4;filter:grayscale(.35);}
/* Stance-gated: the button itself greys (style.css), so its content must not dim a second time under an AP ring. */
.ft-action-slot .ft-ability-btn-status-gated > *{opacity:1;filter:none;}
/* Reaction (triggered-ability) on/off toggle INSIDE a slot: the wrapper + button fill the slot so the rune
   sizes right, and the toggle badge is pulled just INSIDE the top-right corner — the default -5px/-5px badge
   was being clipped by the slot's overflow:hidden, so the reaction couldn't be armed/disarmed from the bar
   (owner 2026-06-26). z-index above the hotkey/equip badges so it's always the tappable thing on top-right. */
/* Generic slot-level "can't use" state for NON-ability buttons (special abilities like Short Rest in combat,
   etc.) — the .btn_ability_ranges ring can't target them, so ring the SLOT (red border) + dim its content. */
.ft-action-slot.is-unusable{border-color:var(--bs-danger,#dc3545)!important;box-shadow:0 0 8px 1px rgba(220,53,69,.7)!important;}
.ft-action-slot.is-unusable > *{opacity:.4;filter:grayscale(.35);}
.ft-action-slot .ft-trigger-ability-wrapper{display:flex;width:100%;height:100%;}
.ft-action-slot .ft-trigger-ability-wrapper > button{width:100%;height:100%;}
.ft-action-slot .ft-trigger-badge{top:1px;right:1px;z-index:7;}
/* Active-reaction "sparkle" must sweep the BORDER only. Behind a SOLID button it does (only the 2px that
   pokes out shows); but a slot's button is transparent, so the conic gradient bled across the whole face
   like a lighthouse. Mask the ::before to a 2px ring on the edge + draw it OVER the button (below the toggle
   badge), so the sweep travels the border regardless of button opacity (owner 2026-06-26). */
.ft-action-slot .ft-trigger-ability-wrapper.ft-trigger-active::before{
  inset:0;border-radius:8px;padding:2px;z-index:3;
  -webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);
  -webkit-mask-composite:xor;mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);mask-composite:exclude;
}
/* Bound-inventory button: chromeless, fills the slot; the weapon thumbnail inside is the face. */
/* isolation:isolate → the button is its OWN stacking context, so the thumbnail's map-token z-index
   (the global .inventory-item.no-environmental-cover[data-size] rule pins it at z-index:10150!important)
   is contained here and can't paint over the corner badges. The thumb is then pinned back to 0 (winning
   4-class selector beats that 3-class !important rule) so the badges (z-index 5) sit cleanly on top. */
.ft-action-slot--inv .ft-action-inv-btn{position:relative;isolation:isolate;width:100%;height:100%;padding:0;background:transparent;border:0;display:flex;align-items:center;justify-content:center;overflow:visible;}
.ft-action-bars .ft-action-slot--inv .ft-action-inv-btn > .inventory-item{width:100%!important;height:100%!important;z-index:0!important;}
.ft-action-slot--inv .ft-action-inv-btn .token-img{width:100%!important;height:100%!important;object-fit:contain;}
.ft-action-slot--inv .ft-action-inv-btn img,.ft-action-slot--inv .ft-action-inv-btn canvas,.ft-action-slot--inv .ft-action-inv-btn video{max-width:100%;max-height:100%;object-fit:contain;}
.ft-action-bars.is-editable .ft-action-slot--inv .ft-action-inv-btn{cursor:grab;}
/* Corner badges: attack type (top-left) + equipment slot (top-right) on an equipped item — ON TOP of the
   art, with a small dark backing so the glyph reads against any weapon picture. */
.ft-action-inv-corner{position:absolute;top:0;z-index:5;pointer-events:none;line-height:0;padding:1px;border-radius:3px;background:rgba(8,10,16,.72);box-shadow:0 0 0 1px rgba(0,0,0,.45);}
.ft-action-inv-corner--tl{left:0;}
.ft-action-inv-corner--tr{right:0;}
.ft-action-inv-corner svg,.ft-action-inv-corner img,.ft-action-inv-corner .ft-icon,.ft-action-inv-corner .ft-icon-map{width:15px;height:15px;display:block;}
/* Hotkey badge: top-CENTER (top-left = attack-type glyph, top-right = equip-slot glyph). Within the slot's
   overflow:hidden bounds so it never clips; faded on an empty slot so the binding still reads. */
.ft-action-key-badge{position:absolute;top:1px;left:50%;transform:translateX(-50%);z-index:6;font:700 9px/1 ui-monospace,SFMono-Regular,Menlo,monospace;color:#fff;background:rgba(8,10,16,.82);border:1px solid rgba(255,255,255,.28);border-top:0;border-radius:0 0 5px 5px;padding:1px 5px;pointer-events:none;letter-spacing:.02em;}
.ft-action-slot--empty .ft-action-key-badge{opacity:.4;}
/* Brief flash when a hotkey fires the slot (so the keypress is felt even off-screen-of-cursor). */
.ft-action-slot--keyfire{animation:ftKeyFire .16s ease-out;}
@keyframes ftKeyFire{0%{box-shadow:inset 0 0 0 2px rgba(255,255,255,.75);}100%{box-shadow:inset 0 1px 2px rgba(0,0,0,.45);}}
/* Menu-active: the ability whose choice menu is currently open glows a pulsing gold border, so the player can
   see which ability is being configured (owner UX 2026-06-27). !important to beat the per-state slot shadows. */
.ft-action-slot--menu-active{border-color:rgba(255,206,84,.95)!important;z-index:3;animation:ftMenuActiveGlow 1.15s ease-in-out infinite alternate;}
@keyframes ftMenuActiveGlow{
  from{box-shadow:0 0 0 1px rgba(255,206,84,.55),0 0 8px 1px rgba(255,184,40,.45);}
  to{box-shadow:0 0 0 2px rgba(255,214,110,.98),0 0 18px 5px rgba(255,184,40,.8);}
}
/* Gear (settings) button + its open state. */
.ft-action-bars__settings.is-open{background:rgba(255,255,255,.16);box-shadow:inset 0 0 0 1px rgba(255,255,255,.3);}
/* Settings tray: its OWN floating panel ABOVE the bar (not a band inside the frame) — same recessed look as
   the bar so it reads as a matching menu, with a gap separating the two. */
.ft-action-settings{display:flex;flex-direction:column;gap:5px;padding:6px 10px;margin-bottom:6px;border:1px solid rgba(0,0,0,.65);border-radius:10px;background:linear-gradient(180deg,rgba(30,32,40,.96),rgba(15,16,21,.98));box-shadow:inset 0 1px 0 rgba(255,255,255,.05),0 3px 10px rgba(0,0,0,.5);}
.ft-action-settings__row{display:flex;flex-wrap:wrap;align-items:center;gap:7px;}
.ft-ab-settings__section{display:inline-flex;flex-wrap:wrap;align-items:center;gap:4px;padding-right:9px;border-right:1px solid rgba(var(--ftab-rim-rgb),.28);}
.ft-ab-settings__section:last-child{padding-right:0;border-right:0;}
.ft-ab-settings__btn{font-size:11px;line-height:1.2;padding:2px 8px;}
/* Responsive dock-width preference. The outer dock interprets s/m/l for its own map context. */
.ft-ab-size{display:inline-flex;flex-wrap:wrap;align-items:center;gap:6px;}
.ft-ab-size__lbl{font-size:11px;font-weight:600;color:rgba(255,255,255,.7);margin-right:2px;}
.ft-ab-size__choices{display:inline-flex;align-items:center;gap:4px;}
.ft-ab-size__btn.is-active{border-color:rgba(var(--ftab-accent-rgb),.75)!important;background:rgba(var(--ftab-accent-rgb),.28)!important;color:#fff;box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.2);}
/* Theme picker row: label + preset swatch dots + a custom-color well. */
.ft-ab-theme{display:flex;flex-wrap:wrap;align-items:center;gap:6px;padding-top:4px;border-top:1px solid rgba(var(--ftab-rim-rgb),.18);}
.ft-ab-theme__lbl{font-size:11px;font-weight:600;color:rgba(255,255,255,.7);margin-right:2px;}
.ft-ab-theme-sw{width:18px;height:18px;padding:0;border-radius:50%;border:1px solid rgba(255,255,255,.3);background:var(--sw,#888);cursor:pointer;box-shadow:inset 0 0 0 1px rgba(0,0,0,.45);transition:transform .1s ease;}
.ft-ab-theme-sw:hover{transform:scale(1.12);}
.ft-ab-theme-sw.is-active{box-shadow:0 0 0 2px #fff,inset 0 0 0 1px rgba(0,0,0,.45);}
.ft-ab-theme-custom{position:relative;width:20px;height:20px;border-radius:50%;border:1px dashed rgba(255,255,255,.45);background:var(--sw,transparent);display:inline-flex;align-items:center;justify-content:center;cursor:pointer;overflow:hidden;font-size:10px;line-height:1;}
.ft-ab-theme-custom.is-active{border-style:solid;box-shadow:0 0 0 2px #fff;}
.ft-ab-theme-custom input{position:absolute;inset:0;width:100%;height:100%;opacity:0;cursor:pointer;border:0;padding:0;}
.ft-ab-dance{display:flex;flex-wrap:wrap;align-items:center;gap:6px;padding-top:4px;border-top:1px solid rgba(var(--ftab-rim-rgb),.18);}
.ft-ab-dance__list{display:contents;list-style:none;margin:0;padding:0;}
.ft-ab-dance__chip{display:inline-flex;align-items:center;gap:2px;padding:1px 2px 1px 6px;border-radius:10px;border:1px solid rgba(var(--ftab-rim-rgb),.45);background:rgba(0,0,0,.35);font-size:11px;color:#fff;}
.ft-ab-dance__n{font-size:9px;opacity:.6;margin-right:3px;}
.ft-ab-dance__btn{min-width:16px;height:16px;padding:0 3px;border:0;border-radius:8px;background:transparent;color:rgba(255,255,255,.75);font-size:12px;line-height:1;cursor:pointer;}
.ft-ab-dance__btn:hover:not(:disabled){background:rgba(255,255,255,.15);color:#fff;}
.ft-ab-dance__btn:disabled{opacity:.25;cursor:default;}
.ft-ab-dance__add{height:20px;max-width:130px;font-size:11px;padding:0 4px;border-radius:4px;border:1px solid rgba(255,255,255,.3);background:rgba(0,0,0,.45);color:#fff;}
.ft-ab-dance__hint{font-size:10px;color:rgba(255,255,255,.5);}
/* Skills launcher — bordered icon button at the LEFT of the meta row (matches the status cluster's manage
   button), and its floating popup (own panel above the bar, like the settings tray). */
.ft-action-skills-launch{flex:0 0 auto;width:30px;height:30px;min-width:30px;margin-right:8px;padding:0;border-radius:6px;display:inline-flex;align-items:center;justify-content:center;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.2);color:#fff;cursor:pointer;line-height:0;}
.ft-action-skills-launch:hover{background:rgba(255,255,255,.13);}
.ft-action-skills-launch.is-open{background:linear-gradient(180deg,rgba(var(--ftab-accent-rgb),.34),rgba(0,0,0,.5));border-color:rgba(var(--ftab-accent-rgb),.5);box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.4);}
.ft-action-skills-launch img,.ft-action-skills-launch svg,.ft-action-skills-launch .ft-icon,.ft-action-skills-launch .ft-icon-map{width:22px;height:22px;display:block;}
/* Skills quick-tray popup: two stacked rows (like the ability island) — the selected type's chips fold out on
   TOP, the skill-TYPE selector sits on the BOTTOM. Slim panel above the bar like the settings tray. */
.ft-action-skills-popup{display:flex;flex-direction:column;gap:6px;padding:7px 9px;margin-bottom:6px;border:1px solid rgba(0,0,0,.65);border-radius:10px;background:linear-gradient(180deg,rgba(30,32,40,.97),rgba(15,16,21,.99));box-shadow:inset 0 1px 0 rgba(255,255,255,.05),0 4px 14px rgba(0,0,0,.55);animation:ftIslandFlyUp .14s ease-out;}
/* Fold-out row of the selected type's chips. min-height keeps the panel stable when a type has few skills. */
.ft-action-skills-flyout{display:flex;flex-wrap:wrap;align-items:center;gap:6px;min-height:40px;padding-top:2px;}
.ft-action-skills-flyout:empty::before{content:"No skills of this type";font-size:11px;color:rgba(255,255,255,.4);font-style:italic;}
/* Skill-type selector row (bottom), divided from the fold-out above. */
.ft-action-skilltypes{display:flex;flex-wrap:wrap;align-items:center;gap:5px;padding-top:6px;border-top:1px solid rgba(255,255,255,.1);}
.ft-action-skilltype{flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;padding:0;border:1px solid rgba(255,255,255,.16);border-radius:6px;background:rgba(255,255,255,.05);color:rgba(255,255,255,.72);cursor:pointer;line-height:0;}
.ft-action-skilltype:hover{color:#fff;background:rgba(255,255,255,.1);}
.ft-action-skilltype.is-active{background:linear-gradient(180deg,rgba(var(--ftab-accent-rgb),.32),rgba(0,0,0,.4));color:#fff;border-color:rgba(var(--ftab-accent-rgb),.5);box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.4);}
.ft-action-skilltype img,.ft-action-skilltype svg,.ft-action-skilltype .ft-icon,.ft-action-skilltype .ft-icon-map{width:22px;height:22px;display:block;}
/* Divider between the type buttons and the "Show all" toggle at the end of the row. */
.ft-action-skilltypes__divider{flex:0 0 auto;align-self:stretch;width:1px;margin:1px 3px;background:rgba(255,255,255,.18);}
/* "Show all skills" toggle: active state tinted red-ish (it surfaces the disadvantage-rolled untrained skills). */
.ft-action-skilltype--all.is-active{background:linear-gradient(180deg,rgba(220,53,69,.34),rgba(0,0,0,.4));border-color:rgba(220,53,69,.6);box-shadow:inset 0 0 0 1px rgba(220,53,69,.45);}
/* A chip: chromeless icon button; the slot-like socket reads as draggable/clickable. position:relative anchors
   the hover badge cluster. overflow:visible so the badges can spill past the chip edge. */
.ft-action-skill-chip{position:relative;flex:0 0 auto;width:36px;height:36px;padding:0;display:inline-flex;align-items:center;justify-content:center;border-radius:7px;border:1px solid rgba(0,0,0,.6);background:linear-gradient(180deg,var(--ftab-slot-top),var(--ftab-slot-bot));box-shadow:inset 0 0 0 1px rgba(var(--ftab-rim-rgb),.22),inset 0 1px 3px rgba(0,0,0,.55);color:#fff;cursor:grab;line-height:0;touch-action:none;overflow:visible;}
.ft-action-skill-chip:hover{border-color:rgba(var(--ftab-accent-rgb),.6);box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.45),0 0 9px rgba(var(--ftab-accent-rgb),.35);}
/* Untrained (tier-0) skill chip — rolls at DISADVANTAGE: red socket + glow so it reads as "risky / not your
   trained skill". Its icon is slightly de-saturated to reinforce the un-mastered look. */
.ft-action-skill-chip--untrained{border-color:rgba(220,53,69,.6);background:linear-gradient(180deg,rgba(80,20,26,.85),rgba(40,8,12,.92));box-shadow:inset 0 0 0 1px rgba(220,53,69,.4),inset 0 1px 3px rgba(0,0,0,.55),0 0 7px rgba(220,53,69,.3);}
.ft-action-skill-chip--untrained:hover{border-color:rgba(239,80,92,.85);box-shadow:inset 0 0 0 1px rgba(239,80,92,.6),0 0 11px rgba(220,53,69,.5);}
.ft-action-skill-chip--untrained img,.ft-action-skill-chip--untrained svg,.ft-action-skill-chip--untrained .ft-icon-map{filter:grayscale(.35) brightness(.92);}
.ft-action-skill-chip--ro{cursor:default;}
.ft-action-skill-chip img,.ft-action-skill-chip svg,.ft-action-skill-chip .ft-icon,.ft-action-skill-chip .ft-icon-map{width:28px;height:28px;display:block;}
/* Hover-revealed badges (skill tier top-left, ADV/DIS top-center): hidden until the chip is hovered, then they
   "pop in" — a desktop affordance. pointer-events:none so they never block the click/drag/press-hold. */
.ft-action-skill-chip__hov{position:absolute;inset:0;z-index:4;opacity:0;transform:scale(.85);transition:opacity .1s ease,transform .1s ease;pointer-events:none;}
.ft-action-skill-chip:hover .ft-action-skill-chip__hov{opacity:1;transform:scale(1);}
.ft-action-skill-chip__tier{position:absolute;top:-6px;left:-6px;line-height:0;}
.ft-action-skill-chip__tier img,.ft-action-skill-chip__tier svg,.ft-action-skill-chip__tier .ft-icon,.ft-action-skill-chip__tier .ft-icon-map{width:18px;height:18px;display:block;}
.ft-action-skill-chip__advdis{position:absolute;top:-8px;left:50%;transform:translateX(-50%);line-height:0;}
.ft-action-skill-chip__advdis img,.ft-action-skill-chip__advdis svg,.ft-action-skill-chip__advdis .ft-icon,.ft-action-skill-chip__advdis .ft-icon-map{width:16px;height:16px;display:block;}
@media (pointer:coarse){.ft-action-skill-chip{width:42px;height:42px;}.ft-action-skill-chip img,.ft-action-skill-chip svg,.ft-action-skill-chip .ft-icon,.ft-action-skill-chip .ft-icon-map{width:32px;height:32px;}.ft-action-skilltype{width:38px;height:38px;}}
/* BG3-style ability CHOICE bar — its OWN floating panel above the bar (sibling, like the settings tray),
   for the extra choice an ability needs before firing (upcast level / shapeshift form / damage type / …). */
.ft-action-choicebar{display:flex;flex-direction:column;gap:7px;padding:8px 11px;margin-bottom:6px;border:1px solid rgba(0,0,0,.65);border-radius:10px;background:linear-gradient(180deg,rgba(30,32,40,.97),rgba(15,16,21,.99));box-shadow:inset 0 1px 0 rgba(255,255,255,.05),0 4px 14px rgba(0,0,0,.55);animation:ftIslandFlyUp .14s ease-out;}
/* AP pill fold-up: the exact Core Abilities action-economy row, compacted into one wrapping strip. */
.ft-action-apfold{position:relative;display:flex;align-items:center;min-height:68px;padding:8px 42px 8px 10px;}
.ft-action-apfold__buttons{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:6px;width:100%;}
.ft-action-apfold__btn{flex:0 0 auto;width:70px;height:54px;padding:4px;border-radius:5px;border:1px solid rgba(0,0,0,.6);display:inline-flex;align-items:center;justify-content:center;cursor:pointer;line-height:0;color:#fff;background:#939aa1;box-shadow:inset 0 1px 0 rgba(255,255,255,.12),0 1px 2px rgba(0,0,0,.35);}
.ft-action-apfold__btn--adjust{background:#df4f52;border-color:rgba(116,17,21,.75);}
.ft-action-apfold__btn:hover{filter:brightness(1.1);border-color:rgba(255,255,255,.6);box-shadow:inset 0 1px 0 rgba(255,255,255,.18),0 0 9px rgba(var(--ftab-accent-rgb),.35);}
.ft-action-apfold__btn:focus-visible{outline:2px solid rgba(var(--ftab-accent-rgb),.9);outline-offset:2px;}
.ft-action-apfold__btn img,.ft-action-apfold__btn svg,.ft-action-apfold__btn .ft-icon,.ft-action-apfold__btn .ft-icon-map{width:32px;height:32px;display:block;object-fit:contain;}
.ft-action-apfold__close{position:absolute;top:7px;right:7px;margin-left:0;}
.ft-action-choicebar__head{display:flex;align-items:center;gap:8px;}
.ft-action-choicebar__title{font-size:12px;font-weight:700;color:#fff;letter-spacing:.02em;}
.ft-action-choicebar__lbl{font-size:11px;font-weight:600;color:rgba(255,255,255,.6);text-transform:uppercase;letter-spacing:.04em;}
.ft-action-choicebar__body{display:flex;flex-wrap:wrap;align-items:center;gap:8px;}
.ft-action-choicebar__cast{font-size:12px;font-weight:700;line-height:1;padding:5px 16px;border-radius:7px;border:1px solid rgba(232,184,96,.55);color:#fff;cursor:pointer;background:linear-gradient(180deg,rgba(232,184,96,.32),rgba(0,0,0,.45));box-shadow:inset 0 0 0 1px rgba(255,255,255,.06);}
.ft-action-choicebar__cast:hover{border-color:rgba(232,184,96,.8);box-shadow:0 0 10px rgba(228,160,72,.4);}
.ft-action-choicebar__cast:disabled{opacity:.42;cursor:not-allowed;box-shadow:none;}
.ft-action-targetingbar{gap:4px;padding:7px 9px;border-color:rgba(76,214,255,.58);box-shadow:inset 0 1px 0 rgba(255,255,255,.05),0 0 18px rgba(38,183,224,.2),0 4px 14px rgba(0,0,0,.55);}
.ft-action-targetingbar .ft-action-choicebar__head{min-height:28px;gap:6px;}
.ft-action-targetingbar .ft-action-choicebar__cast{margin-left:auto;}
.ft-action-targetingbar__count{display:inline-flex;align-items:center;justify-content:center;min-width:28px;padding:2px 6px;border-radius:999px;border:1px solid rgba(255,255,255,.14);background:rgba(0,0,0,.28);font-size:10px;font-weight:800;color:rgba(255,255,255,.72);font-variant-numeric:tabular-nums;}
.ft-action-targetingbar__range,.ft-action-targetingbar__optionicon{display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;flex:0 0 auto;border-radius:6px;border:1px solid rgba(77,211,245,.2);background:rgba(77,211,245,.08);line-height:0;}
.ft-action-targetingbar__range img,.ft-action-targetingbar__range svg,.ft-action-targetingbar__range .ft-icon-map,.ft-action-targetingbar__optionicon img,.ft-action-targetingbar__optionicon svg,.ft-action-targetingbar__optionicon .ft-icon-map{width:18px;height:18px;display:block;}
.ft-action-targetingbar__body{display:block;min-width:0;padding-top:2px;border-top:1px solid rgba(255,255,255,.07);}
.ft-action-targetingbar__targetlist{display:flex;align-items:center;gap:6px;min-height:52px;overflow-x:auto;overflow-y:hidden;padding:4px 1px 2px;scrollbar-width:thin;scrollbar-color:rgba(115,209,237,.5) rgba(0,0,0,.22);}
.ft-projectile-assignment{display:inline-flex;align-items:center;gap:6px;flex:0 0 auto;padding:3px 6px;border:1px solid rgba(255,209,102,.3);border-radius:7px;color:#ffd166;font-size:12px;white-space:nowrap;}
.ft-projectile-assignment>b{min-width:22px;font-variant-numeric:tabular-nums;}
.ft-action-targetingbar__empty{display:flex;align-items:center;min-height:48px;padding:0 7px;font-size:10px;color:rgba(255,255,255,.42);font-style:italic;}
.ft-action-targetingbar__target{position:relative;display:block;flex:0 0 52px;width:52px;height:52px;padding:2px;overflow:hidden;border:1px solid rgba(86,208,240,.42);border-radius:7px;background:#111820;box-shadow:inset 0 0 0 1px rgba(255,255,255,.05);cursor:pointer;}
.ft-action-targetingbar__target:hover{border-color:rgba(239,80,92,.85);box-shadow:0 0 9px rgba(220,53,69,.42);}
.ft-action-targetingbar__portrait{display:flex;align-items:center;justify-content:center;width:100%;height:100%;object-fit:contain;border-radius:5px;background:#171a20;color:#182028;font-size:13px;font-weight:800;}
.ft-action-targetingbar__portrait--none{background:#8edff3;}
.ft-action-targetingbar__vbar{position:absolute;z-index:2;left:4px;right:4px;bottom:4px;height:7px;overflow:hidden;border:1px solid rgba(0,0,0,.82);border-radius:4px;background:rgba(0,0,0,.72);transition:opacity .12s ease;}
.ft-action-targetingbar__vbar i{display:block;height:100%;background:linear-gradient(90deg,#ca3547,#ee5965);box-shadow:inset 0 1px 0 rgba(255,255,255,.22);}
.ft-action-targetingbar__remove{position:absolute;z-index:3;inset:2px;display:flex;align-items:center;justify-content:center;border-radius:5px;background:rgba(116,15,25,.76);color:#fff;font-size:19px;font-weight:800;opacity:0;transition:opacity .12s ease;}
.ft-action-targetingbar__target:hover .ft-action-targetingbar__remove,.ft-action-targetingbar__target:focus-visible .ft-action-targetingbar__remove{opacity:1;}
.ft-action-targetingbar__target:hover .ft-action-targetingbar__vbar,.ft-action-targetingbar__target:focus-visible .ft-action-targetingbar__vbar{opacity:0;}
.ft-action-targetingbar__option{display:flex;align-items:center;gap:4px;margin-left:auto;}
.ft-action-targetingbar__inventory{display:inline-flex;align-items:center;gap:6px;min-height:28px;margin:0 4px;font-size:12px;white-space:nowrap;cursor:pointer;}
.ft-action-targetingbar__inventory input{width:14px;height:14px;margin:0;accent-color:#4dd3f5;}
.ft-action-targetingbar__inventory:has(input:disabled){opacity:.45;cursor:not-allowed;}
.ft-action-targetingbar__option + .ft-action-choicebar__cast{margin-left:2px;}
.ft-action-targetingbar__option .ft-ab-choice-step{width:23px;height:23px;font-size:14px;}
.ft-action-targetingbar__option .ft-ab-choice-step:disabled{opacity:.32;cursor:not-allowed;}
.ft-action-targetingbar__level{min-width:18px;text-align:center;font-size:13px;font-weight:800;color:#fff;font-variant-numeric:tabular-nums;}
.ft-action-targetingbar__option .ft-action-choicebar__upeffects{flex-basis:auto;justify-content:flex-end;margin-left:2px;}
.ft-action-aoebar__options{display:flex;flex-wrap:wrap;align-items:center;gap:8px;min-height:0;}
.ft-action-aoebar__upcast{flex-wrap:nowrap;white-space:nowrap;}
.ft-action-aoebar .ft-action-choicebar__head{flex-wrap:nowrap;white-space:nowrap;}
.ft-action-aoebar--projectiles .ft-action-choicebar__head{flex-wrap:wrap;white-space:normal;gap:6px;}
.ft-action-aoebar--projectiles .ft-action-choicebar__hint{flex-basis:100%;white-space:normal;}
.ft-action-aoebar--projectiles .ft-action-targetingbar__count{flex-shrink:0;white-space:nowrap;}
.ft-action-aoebar .ft-action-choicebar__upeffects{flex:0 1 auto;flex-basis:auto;flex-wrap:nowrap;min-width:0;}
.ft-action-aoebar__count{margin-left:auto;font-size:9px;font-weight:700;color:rgba(255,255,255,.52);white-space:nowrap;font-variant-numeric:tabular-nums;}
/* Cast-as-Ritual button: arcane-tinted, glyph + label; sits between Cast and the cancel ✕. */
.ft-action-choicebar__ritual{display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:700;line-height:1;padding:5px 12px;border-radius:7px;border:1px solid rgba(170,130,245,.5);color:#fff;cursor:pointer;background:linear-gradient(180deg,rgba(170,130,245,.28),rgba(0,0,0,.45));box-shadow:inset 0 0 0 1px rgba(255,255,255,.06);}
.ft-action-choicebar__ritual:hover{border-color:rgba(190,160,250,.8);box-shadow:0 0 10px rgba(170,130,245,.4);}
/* Armed (toggled ON) — amber, so it reads as "this WILL cast as a ritual". */
.ft-action-choicebar__ritual.is-armed{border-color:rgba(255,206,84,.85);background:linear-gradient(180deg,rgba(255,206,84,.42),rgba(0,0,0,.4));box-shadow:0 0 12px rgba(255,184,40,.55);}
.ft-action-choicebar__ritual.is-armed:hover{border-color:rgba(255,214,110,.95);}
.ft-action-choicebar__ritual img,.ft-action-choicebar__ritual svg,.ft-action-choicebar__ritual .ft-icon,.ft-action-choicebar__ritual .ft-icon-map{width:16px;height:16px;display:block;}
.ft-action-choicebar__hint{font-size:10px;color:rgba(255,255,255,.45);font-style:italic;}
/* Quick Ritual checkbox (ritual_spell_mana_discount): normal-time cast at a mana discount. */
.ft-action-choicebar__quickritual{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;color:#fff;cursor:pointer;margin:0;padding:5px 10px;border-radius:7px;border:1px solid rgba(170,130,245,.35);background:linear-gradient(180deg,rgba(170,130,245,.14),rgba(0,0,0,.35));}
.ft-action-choicebar__quickritual:hover{border-color:rgba(190,160,250,.65);}
.ft-action-choicebar__quickritual input{margin:0;cursor:pointer;}
.ft-action-choicebar__quickritual span{display:inline-flex;align-items:center;gap:4px;line-height:1;}
.ft-action-choicebar__quickritual img,.ft-action-choicebar__quickritual .ft-icon,.ft-action-choicebar__quickritual .ft-icon-map{width:16px;height:16px;display:block;}
.ft-action-choicebar__cancel{margin-left:auto;flex:0 0 auto;width:24px;height:24px;padding:0;border-radius:6px;display:inline-flex;align-items:center;justify-content:center;font-size:11px;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.06);color:rgba(255,255,255,.75);cursor:pointer;}
.ft-action-choicebar__cancel:hover{background:rgba(255,255,255,.14);color:#fff;}
.ft-ab-choice-select{min-width:150px;max-width:280px;font-size:12px;line-height:1.3;padding:4px 8px;border-radius:6px;border:1px solid rgba(255,255,255,.22);background:rgba(0,0,0,.45);color:#fff;}
.ft-ab-choice-select option{color:#000;}
.ft-action-choicebar .ft-ab-damage-select,.ft-action-choicebar .ts-wrapper.ft-ab-damage-select{width:56px!important;min-width:56px!important;max-width:56px!important;flex:0 0 56px;}
.ft-action-choicebar .ts-wrapper.ft-ab-damage-select .ts-control{width:56px!important;min-width:56px!important;max-width:56px!important;min-height:28px;background:rgba(0,0,0,.45);border-color:rgba(255,255,255,.22);}
.ft-action-choicebar .ts-wrapper.ft-ab-damage-select .ts-dropdown{z-index:10025;}
.ft-ab-choice-stepper{display:inline-flex;align-items:center;gap:4px;}
.ft-ab-choice-step{width:26px;height:26px;padding:0;border-radius:6px;display:inline-flex;align-items:center;justify-content:center;font-size:15px;font-weight:700;line-height:1;border:1px solid rgba(255,255,255,.22);background:rgba(255,255,255,.08);color:#fff;cursor:pointer;}
.ft-ab-choice-step:hover{background:rgba(255,255,255,.18);}
.ft-ab-choice-num{width:58px;text-align:center;font-size:13px;font-weight:700;padding:4px 4px;border-radius:6px;border:1px solid rgba(255,255,255,.22);background:rgba(0,0,0,.45);color:#fff;}
.ft-ab-choice-num::-webkit-outer-spin-button,.ft-ab-choice-num::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}
/* AOE upcast control in the fold-up: label + stepper (read-only value span) + "+N ft area" note. */
.ft-action-choicebar__upcast{display:inline-flex;align-items:center;gap:7px;flex-wrap:wrap;}
.ft-action-choicebar__upcast-val{min-width:26px;text-align:center;font-size:14px;font-weight:800;color:#fff;font-variant-numeric:tabular-nums;}
.ft-action-choicebar__upcast-dmg{display:inline-flex;align-items:center;gap:4px;white-space:nowrap;font-variant-numeric:tabular-nums;}
.ft-action-choicebar__upcast-dmg img,.ft-action-choicebar__upcast-dmg svg,.ft-action-choicebar__upcast-dmg .ft-icon,.ft-action-choicebar__upcast-dmg .ft-icon-map{width:16px;height:16px;display:block;flex:0 0 auto;}
.ft-action-choicebar__upcast-note{font-size:11px;font-weight:700;color:#ffce54;letter-spacing:.02em;}
/* "What does upcasting do?" badges — one per upcast effect (damage / +targets / +area / +duration …). Icon +
   per-level amount; full description in the tooltip. Wraps onto its own line under the stepper. */
.ft-action-choicebar__upeffects{display:flex;flex-wrap:wrap;align-items:center;gap:5px;flex-basis:100%;}
.ft-upeffect{display:inline-flex;align-items:center;gap:3px;font-size:10px;font-weight:700;line-height:1;padding:2px 6px;border-radius:5px;border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.06);color:rgba(255,255,255,.82);white-space:nowrap;font-variant-numeric:tabular-nums;cursor:help;}
.ft-upeffect img,.ft-upeffect svg,.ft-upeffect .ft-icon,.ft-upeffect .ft-icon-map{width:13px;height:13px;display:block;flex:0 0 auto;}
/* "Pick a form first" flash when Cast is pressed on a required-but-blank select. */
.ft-abchoice-needpick{border-color:#dc3545!important;box-shadow:0 0 0 2px rgba(220,53,69,.5)!important;animation:ftKeyFire .3s ease-in-out 2;}
/* WEAPON fold-out: groups centered in the panel; each group's badge straddles the top BORDER of a
   shrink-wrapped frame that hugs the medallion row (badge uses left:0;right:0 flex-center). */
/* Shrink-wrapped and centred over the bar (owner 2026-10-15 "condense"): a sparse kit no longer spans the whole dock;
   a big one grows to 100% and its groups wrap, then scroll. */
.ft-action-choicebar.ft-action-wfold{position:relative;gap:4px;overflow:visible;padding:7px 10px 8px;width:fit-content;min-width:min(100%,220px);max-width:100%;margin-left:auto;margin-right:auto;}
/* No toolbar (natural weapon, nested consumable, item-target pick): the group notch IS the top spacing. */
.ft-action-choicebar.ft-action-wfold:not(:has(> .ft-action-wfold__tools)){padding-top:0;}
/* Toolbar (owner 2026-10-15): mode | flight | carry segments in ONE row on the close button's line. */
.ft-action-wfold__tools{display:flex;flex-wrap:wrap;align-items:center;gap:6px 10px;min-height:24px;padding-right:30px;}
.ft-action-wfold__modes{display:inline-flex;border-radius:7px;border:1px solid rgba(255,255,255,.16);background:rgba(0,0,0,.28);overflow:hidden;}
.ft-action-wfold-tool{display:inline-flex;align-items:center;gap:5px;height:24px;padding:0 9px;border:0;border-left:1px solid rgba(255,255,255,.1);background:transparent;color:rgba(255,255,255,.7);font-size:11px;font-weight:700;line-height:1;white-space:nowrap;cursor:pointer;}
.ft-action-wfold-tool:first-child{border-left:0;}
.ft-action-wfold-tool:hover{background:rgba(255,255,255,.08);color:#fff;}
.ft-action-wfold-tool.is-on{background:rgba(var(--ftab-accent-rgb),.26);color:#fff;box-shadow:inset 0 -2px 0 rgba(var(--ftab-accent-rgb),.9);}
.ft-action-wfold-tool img,.ft-action-wfold-tool svg,.ft-action-wfold-tool .ft-icon,.ft-action-wfold-tool .ft-icon-map{width:16px;height:16px;display:block;flex:0 0 auto;}
.ft-action-wfold__inner{width:100%;display:flex;justify-content:center;overflow:visible;}
.ft-action-wfold__close{position:absolute;top:7px;right:7px;margin-left:0!important;z-index:4;}
/* Medallion groups: wrap, then Y-SCROLL past two rows (3 paint gutter + 2 x (16 notch + 94 frame) + 4 gap) instead
   of growing over the map; the scrollbar rides the panel's right edge. */
.ft-action-wfold__groups{width:100%;display:flex;flex-wrap:wrap;align-items:flex-start;justify-content:center;gap:4px 8px;padding-top:3px;max-height:min(230px,32vh);overflow-x:hidden;overflow-y:auto;overscroll-behavior:contain;scrollbar-width:thin;scrollbar-color:rgba(255,255,255,.28) transparent;}
/* Outer cell — its top padding is the badge notch (half the 32px badge), so the scroll box never clips a badge. */
.ft-action-wfold-group{padding-top:16px;}
/* Bordered frame shrink-wraps the medallion row; badge anchors to THIS box's width. */
.ft-action-wfold-group__frame{position:relative;display:inline-block;vertical-align:top;padding:16px 8px 8px;border-radius:10px;border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.04);box-shadow:inset 0 1px 0 rgba(255,255,255,.05);overflow:visible;}
/* Badge straddles the top border, centered on the frame (= medallion row width). */
.ft-action-wfold-group__badge{position:absolute;top:0;left:50%;transform:translate(-50%,-50%);z-index:2;display:flex;align-items:center;justify-content:center;padding:2px 7px;border-radius:7px;border:1px solid rgba(255,255,255,.2);background:linear-gradient(180deg,rgba(32,34,42,.98),rgba(14,15,20,.99));box-shadow:0 0 0 2px rgba(15,16,21,.95),0 2px 6px rgba(0,0,0,.45);line-height:0;white-space:nowrap;}
.ft-action-wfold-group__badge .ft-icon-map{display:block;line-height:0;}
.ft-action-wfold-group__badge img,.ft-action-wfold-group__badge svg,.ft-action-wfold-group__badge .ft-icon,.ft-action-wfold-group__badge .ft-icon-map{width:26px;height:26px;display:block;}
.ft-action-wfold-group__list{display:inline-flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:6px;vertical-align:top;}
/* Attack medallion: ~popout size (68px display, 96px L2 LOD) — rune fills the socket. */
.ft-action-wfold-btn{position:relative;flex:0 0 auto;width:68px;height:68px;padding:1px;display:inline-flex;align-items:center;justify-content:center;border-radius:9px;border:1px solid rgba(0,0,0,.6);background:linear-gradient(180deg,var(--ftab-slot-top),var(--ftab-slot-bot));box-shadow:inset 0 0 0 1px rgba(var(--ftab-rim-rgb),.22),inset 0 1px 3px rgba(0,0,0,.55);color:#fff;cursor:pointer;line-height:0;overflow:hidden;}
.ft-action-wfold-btn:hover{border-color:rgba(var(--ftab-accent-rgb),.7);box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.45),0 0 9px rgba(var(--ftab-accent-rgb),.4);}
.ft-action-wfold-btn .ft-rune-mount,.ft-action-wfold-btn .ft-rune-wfold{width:100%!important;height:100%!important;display:flex;align-items:center;justify-content:center;}
.ft-action-wfold-btn .ft-rune-mount svg,.ft-action-wfold-btn .ft-rune-wfold svg{width:100%;height:100%;display:block;}
.ft-action-wfold-btn img,.ft-action-wfold-btn svg,.ft-action-wfold-btn .ft-icon,.ft-action-wfold-btn .ft-icon-map{width:100%;height:100%;display:block;flex:0 0 auto;object-fit:contain;}
/* Charge-empty attack: dimmed + red ring (hint only — the server still enforces). */
.ft-action-wfold-btn.is-short{opacity:.45;filter:grayscale(.35);border-color:rgba(220,53,69,.65);}
/* Required status (a Monk stance) not held: fully greyed; the tooltip names the stance. */
.ft-action-wfold-btn.is-status-gated{opacity:.4;filter:grayscale(1);}
.ft-ab-settings__configure.is-active{background:#2e7d32;border-color:#43a047;color:#fff;}
.ft-ab-settings__hint{font-size:11px;color:rgba(255,255,255,.7);margin-left:4px;}
/* Configure (bind) mode: each filled slot gets a click-to-rebind overlay covering the ability button. */
.ft-action-bars.is-binding .ft-action-slot{outline:1px dashed rgba(255,255,255,.35);outline-offset:-2px;}
.ft-action-bind-overlay{position:absolute;inset:0;z-index:8;display:flex;align-items:center;justify-content:center;text-align:center;font:600 9px/1.1 ui-monospace,SFMono-Regular,Menlo,monospace;color:#fff;background:rgba(8,10,16,.66);border:0;border-radius:8px;cursor:pointer;padding:2px;}
.ft-action-bind-overlay:hover{background:rgba(20,40,70,.78);}
.ft-action-bind-overlay.is-listening{background:rgba(46,125,50,.82);box-shadow:inset 0 0 0 2px #66bb6a;animation:ftKeyFire .9s ease-in-out infinite alternate;}
.ft-action-bars.is-editable .ft-action-slot--filled .btn,.ft-action-bars.is-editable .ft-action-slot--filled .ft-action-skill-btn{cursor:grab;}
.ft-action-slot--dragover{outline:2px solid #7aa2ff;outline-offset:-2px;background:rgba(122,162,255,.12);}
/* Divider: ONE element spanning all rows (grid-row 1/-1). Solid black→red→black bar + glow, ||-grip
   centered over the whole height. */
.ft-action-divider{position:relative;align-self:stretch;justify-self:stretch;min-height:44px;}
.ft-action-divider::before{content:"";position:absolute;top:0;bottom:0;left:50%;width:11px;transform:translateX(-50%);border-radius:3px;background:linear-gradient(90deg,transparent 0%,rgba(43,0,0,.6) 18%,#7a0c0c 36%,#ff6a5a 50%,#7a0c0c 64%,rgba(43,0,0,.6) 82%,transparent 100%);box-shadow:0 0 7px rgba(239,68,68,.45);-webkit-mask-image:linear-gradient(180deg,transparent 0%,#000 14%,#000 86%,transparent 100%);mask-image:linear-gradient(180deg,transparent 0%,#000 14%,#000 86%,transparent 100%);}
.ft-action-divider__grip{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);display:flex;gap:2px;padding:3px 2px;border-radius:3px;background:rgba(20,8,10,.8);box-shadow:0 0 0 1px rgba(225,29,72,.4);}
.ft-action-divider__grip::before,.ft-action-divider__grip::after{content:"";display:block;width:2px;height:10px;background:#fb7185;border-radius:1px;}
.ft-action-divider.is-editable{cursor:grab;touch-action:none;}
.ft-action-divider.is-editable:active{cursor:grabbing;}
/* Divider drag ghost — full-height line at the snap boundary; greyed in the drag-off remove zone. */
.ft-action-divider-ghost{position:absolute;top:0;bottom:0;width:10px;transform:translateX(-50%);pointer-events:none;z-index:6;border-radius:2px;opacity:.6;background:linear-gradient(90deg,#000,#ff5050 50%,#000);}
.ft-action-divider-ghost.is-remove{opacity:.35;background:linear-gradient(90deg,#000,#888 50%,#000);}
/* Far-right contextual actions on the TOP row — flow AFTER the controls (which carry margin-left:auto),
   so the cluster ends at the bar's right edge with luck (first button, row-reverse) above the last slot. */
.ft-action-context{display:flex;flex-direction:row-reverse;flex-wrap:wrap;align-items:center;gap:5px;}
.ft-action-context-btn{flex:0 0 auto;width:34px;height:34px;min-width:34px;padding:0;border-radius:7px;display:inline-flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.06);color:#fff;cursor:pointer;line-height:0;}
.ft-action-context-btn:hover{background:rgba(255,255,255,.14);}
.ft-action-context-btn img,.ft-action-context-btn svg,.ft-action-context-btn .ft-icon,.ft-action-context-btn .ft-icon-map{width:22px;height:22px;display:block;}
/* The reused End-Concentration button keeps its own .btn skin — just size it to match the cluster. */
.ft-action-context .btn{width:34px;height:34px;min-width:34px;padding:0;display:inline-flex;align-items:center;justify-content:center;border-radius:7px;}
.ft-action-context .btn img{width:22px;height:22px;}
/* Top-right group on the border: contextual/luck cluster (left) + the bar controls (right). */
.ft-action-bars-top__right{grid-area:right;justify-self:end;display:flex;align-items:flex-end;gap:10px;}
/* Controls: variable bar-config buttons (+/−/add-divider) as one group, then the LOCK on the far right
   as its own group — the controls gap is the smidge of padding between the two. */
.ft-action-bars__controls{flex:0 0 auto;display:flex;flex-direction:row;align-items:center;gap:7px;}
  /* ── Context disc (owner 2026-08-28; moved 2026-09-15). Parked off the minimap's lower-right rim by
   positionTurnHaloMount — campaign sky/time + the calendar/encounter/lighting hover overlay, in both
   modes. In combat it adds the state mark and the turn owner's camera-focus badge. Turn CONTROL (End
   Turn, the movement rim) lives on the hands-cluster portrait (.ft-action-hands__portrait). */
.ft-turn-halo-mount{position:fixed;left:auto;top:auto;z-index:2;pointer-events:none;}
.ft-turn-halo-mount:empty{display:none;}
.ft-turn-halo{position:relative;width:88px;height:88px;flex:0 0 auto;display:grid;place-items:center;pointer-events:auto;filter:drop-shadow(0 4px 10px rgba(0,0,0,.6));outline:none;}
.ft-turn-halo__ring{position:absolute;inset:0;width:100%;height:100%;transform:rotate(-90deg);pointer-events:none;}
.ft-turn-halo__track{fill:rgba(6,10,16,.88);stroke:url(#ftHaloRimGrad);stroke-width:5;}
.ft-turn-halo__core{position:relative;z-index:1;width:62px;height:62px;border-radius:50%;display:grid;place-items:center;overflow:hidden;color:#fff;background:#0c1118;border:1px solid rgba(255,255,255,.22);padding:0;box-shadow:inset 0 0 14px rgba(0,0,0,.7);}
.ft-turn-halo--combat .ft-turn-halo__track{stroke:rgba(255,120,96,.55);}
.ft-turn-halo__sky{position:absolute;inset:0;width:100%;height:100%;}
.ft-turn-halo__skyfill{fill:#7fb7ff;}
.ft-turn-halo.is-day .ft-turn-halo__skyfill{fill:#6aa9f0;}
.ft-turn-halo.is-night .ft-turn-halo__skyfill{fill:#0b1430;}
.ft-turn-halo__ground{fill:#1f3120;}
.ft-turn-halo.is-night .ft-turn-halo__ground{fill:#0f1a14;}
.ft-turn-halo__sun{fill:#ffd45c;filter:drop-shadow(0 0 3px rgba(255,212,92,.9));}
.ft-turn-halo__moon circle{fill:#e8ecf5;}
.ft-turn-halo__moon .ft-turn-halo__moon-bite{fill:#0b1430;}
.ft-turn-halo__stars circle{fill:#fff;opacity:.85;}
.ft-turn-halo__core-label{position:relative;z-index:1;font-size:13px;font-weight:700;line-height:1.1;letter-spacing:.03em;text-align:center;text-shadow:0 1px 3px rgba(0,0,0,.9),0 0 6px rgba(0,0,0,.7);}
.ft-turn-halo__combat-info{position:absolute;z-index:4;bottom:-4px;left:-2px;display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;padding:4px;border-radius:50%;background:linear-gradient(180deg,#22343d,#0b1117);border:1px solid rgba(255,150,120,.7);box-shadow:0 2px 6px rgba(0,0,0,.72);color:#fff;line-height:0;}
.ft-turn-halo__combat-info img,.ft-turn-halo__combat-info svg{width:16px !important;height:16px !important;}
.ft-turn-halo__hourglass-art{width:100%;height:100%;overflow:visible;transition:filter .12s ease-out,transform .12s ease-out;filter:drop-shadow(0 2px 3px rgba(0,0,0,.8));}
.ft-turn-halo__hourglass-rim{fill:#b8c8cf;stroke:#e5f0f3;stroke-width:.8;}
.ft-turn-halo__hourglass-frame{fill:#607985;stroke:#b7ccd4;stroke-width:.7;}
.ft-turn-halo__hourglass-glass{fill:rgba(154,211,229,.16);stroke:#83adbc;stroke-width:.7;}
.ft-turn-halo__hourglass-sand,.ft-turn-halo__hourglass-stream{fill:#f5c75e;filter:drop-shadow(0 0 2px rgba(245,199,94,.65));}
.ft-turn-halo__clock{font-size:13px;font-variant-numeric:tabular-nums;display:block;}
.ft-turn-halo__ampm{font-size:9px;font-weight:600;margin-left:1px;opacity:.9;}
.ft-turn-halo__core-icon{display:inline-flex;justify-content:center;margin-top:1px;}
.ft-turn-halo__core-icon img,.ft-turn-halo__core-icon svg{width:18px !important;height:18px !important;filter:drop-shadow(0 1px 2px rgba(0,0,0,.9));}
/* Dock pane re-pointed at a new actor but not yet repainted (cold-cache wait): inert until the commit. */
.ft-pc-sheet-pane[data-ft-actor-switching]{pointer-events:none;}
/* Whose turn it is (when not yours): their face on the disc's corner, a camera-focus button. */
.ft-turn-halo__turn-badge{position:absolute;z-index:3;top:-2px;right:-4px;width:28px;height:28px;padding:0;border-radius:50%;overflow:hidden;border:2px solid #f5d76e;background:radial-gradient(circle at 45% 35%,#496965,#162729 72%);box-shadow:0 0 0 2px rgba(0,0,0,.6),0 0 10px rgba(245,215,110,.55);cursor:pointer;display:grid;place-items:center;animation:ftUnitTurnPulse 1.6s ease-in-out infinite;}
.ft-turn-halo__turn-badge img{position:absolute;inset:0;width:100% !important;height:100% !important;object-fit:contain;object-position:top center;}
.ft-turn-halo__turn-badge-initials{font:900 10px/1 ui-sans-serif,system-ui,sans-serif;color:#f7d99c;letter-spacing:.02em;}
.ft-turn-halo__turn-badge:hover{filter:brightness(1.15);}
.ft-turn-halo__turn-badge:focus-visible{outline:2px solid #8fe6ff;outline-offset:2px;}
/* Hover/focus overlay: calendar + encounter + lighting, replaces the minimap's campaign-hud card. The disc
   sits at the top-right, so the panel drops BELOW it and grows leftward from its right edge. */
.ft-turn-halo__overlay{position:absolute;left:auto;right:0;top:calc(100% + 8px);width:max-content;min-width:230px;max-width:min(320px,calc(100vw - 20px));padding:0;overflow:hidden;border-radius:.5rem;background:rgba(8,18,21,.97);border:1px solid rgba(91,205,202,.55);box-shadow:0 .6rem 1.6rem rgba(0,0,0,.55);color:#e9f2f2;font-size:12px;line-height:1.35;text-align:left;opacity:0;visibility:hidden;transform:translateY(-4px);transition:opacity .12s ease-out,transform .12s ease-out,visibility 0s linear .12s;pointer-events:none;z-index:9;}
.ft-turn-halo:hover .ft-turn-halo__overlay,.ft-turn-halo:focus-within .ft-turn-halo__overlay{opacity:1;visibility:visible;transform:none;transition-delay:0s;}
.ft-turn-halo-mount .ft-turn-halo__overlay{max-height:var(--halo-ov-max-h,none);overflow-y:auto;}
.ft-turn-halo-mount.is-overlay-up .ft-turn-halo__overlay{top:auto;bottom:calc(100% + 8px);}
.ft-turn-halo__ov-campaign{position:relative;padding:.34rem .7rem;font-size:11px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:#dffbfa;text-shadow:0 1px 2px rgba(0,0,0,.85);background:linear-gradient(100deg,rgba(91,205,202,.42),rgba(28,58,64,.55) 55%,rgba(91,205,202,.2));border-bottom:1px solid rgba(91,205,202,.4);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.ft-turn-halo__ov-place{position:relative;display:flex;align-items:flex-start;justify-content:space-between;gap:.5rem;margin:0;padding:.5rem .7rem .45rem;background-size:cover;background-position:center 60%;background:linear-gradient(135deg,rgba(91,205,202,.28),rgba(20,40,44,.2) 55%,rgba(91,205,202,.12));border-bottom:1px solid rgba(91,205,202,.35);}
.ft-turn-halo__ov-body{padding:.3rem .7rem .5rem;}
.ft-turn-halo__ov-group + .ft-turn-halo__ov-group{margin-top:.3rem;padding-top:.3rem;border-top:1px solid rgba(255,255,255,.12);}
/* Weather art over the sky (static). */
.ft-turn-halo__cloud{fill:#eef3f8;opacity:.95;}
.ft-turn-halo__cloud.is-dim{fill:#c9d3dd;}
.ft-turn-halo__cloud.is-dark{fill:#8d9aa8;}
.ft-turn-halo__cloud.is-ash{fill:#6b6560;}
.ft-turn-halo.is-night .ft-turn-halo__cloud{fill:#9aa6b4;}
.ft-turn-halo.is-night .ft-turn-halo__cloud.is-dim{fill:#7d8896;}
.ft-turn-halo.is-night .ft-turn-halo__cloud.is-dark{fill:#59636f;}
.ft-turn-halo__precip.is-rain line{stroke:#bfe3ff;stroke-width:1;stroke-linecap:round;opacity:.9;}
.ft-turn-halo__precip.is-snow circle{fill:#fff;opacity:.95;}
.ft-turn-halo__precip.is-hail circle{fill:#dfe9f2;stroke:#9fb4c4;stroke-width:.4;}
.ft-turn-halo__precip.is-dust circle{fill:#d9b56a;opacity:.8;}
.ft-turn-halo__precip.is-ash circle{fill:#9a948c;opacity:.85;}
.ft-turn-halo__fog rect{fill:#e6edf3;opacity:.45;}
.ft-turn-halo__fog.is-dust rect{fill:#c9a35a;opacity:.5;}
.ft-turn-halo__bolt{fill:#ffe45c;filter:drop-shadow(0 0 2px rgba(255,228,92,.9));}
/* Land by environment. */
.ft-turn-halo__ground.is-far{fill:#2e4a2c;}
.ft-turn-halo.is-night .ft-turn-halo__ground.is-far{fill:#16261a;}
.ft-turn-halo__ground.is-grass{fill:#3f7a2f;}
.ft-turn-halo.is-night .ft-turn-halo__ground.is-grass{fill:#1c3a1a;}
.ft-turn-halo__ground.is-sand{fill:#d9b26a;}
.ft-turn-halo__ground.is-sand.is-far{fill:#c39a55;}
.ft-turn-halo.is-night .ft-turn-halo__ground.is-sand{fill:#5d4b2e;}
.ft-turn-halo.is-night .ft-turn-halo__ground.is-sand.is-far{fill:#4a3b24;}
.ft-turn-halo__ground.is-snow{fill:#eef4fa;}
.ft-turn-halo__ground.is-snow.is-far{fill:#cfdbe7;}
.ft-turn-halo.is-night .ft-turn-halo__ground.is-snow{fill:#8fa0b4;}
.ft-turn-halo.is-night .ft-turn-halo__ground.is-snow.is-far{fill:#6b7b8e;}
.ft-turn-halo__ground.is-marsh{fill:#3d5a2a;}
.ft-turn-halo__ground.is-stone{fill:#4a4f56;}
.ft-turn-halo.is-night .ft-turn-halo__ground.is-stone{fill:#262a30;}
.ft-turn-halo__ground.is-planar{fill:#4a2d6e;}
.ft-turn-halo__trees path{fill:#183a1c;}
.ft-turn-halo.is-night .ft-turn-halo__trees path{fill:#0c1f10;}
.ft-turn-halo__mountain{fill:#5b6672;}
.ft-turn-halo.is-night .ft-turn-halo__mountain{fill:#2c333c;}
.ft-turn-halo__snowcap{fill:#f3f7fb;}
.ft-turn-halo__reeds path{stroke:#6f8a3a;stroke-width:1;fill:none;stroke-linecap:round;}
.ft-turn-halo__reeds.is-kelp path{stroke:#2f8a5a;stroke-width:1.4;}
.ft-turn-halo__water{fill:#2f6f9e;opacity:.9;}
.ft-turn-halo__water.is-deep{fill:#0f3a5c;opacity:1;}
.ft-turn-halo__buildings rect{fill:#2b3138;}
.ft-turn-halo.is-night .ft-turn-halo__buildings rect{fill:#161a1f;}
.ft-turn-halo__rock{fill:#2a2622;}
.ft-turn-halo__cavern{fill:#14110f;}
.ft-turn-halo__void{fill:#05070f;}
.ft-turn-halo__ov-place.has-art{background-position:center 72%;min-height:56px;}
.ft-turn-halo__ov-place.has-art::before{content:"";position:absolute;inset:0;background:linear-gradient(90deg,rgba(6,14,17,.72),rgba(6,14,17,.35) 55%,rgba(6,14,17,.15));}
.ft-turn-halo__ov-place-text{position:relative;display:flex;flex-direction:column;gap:1px;min-width:0;}
.ft-turn-halo__ov-place strong{font-size:13px;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.9);}
.ft-turn-halo__ov-env{position:relative;flex:0 0 auto;align-self:flex-start;font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;padding:2px 7px;border-radius:999px;background:rgba(8,18,21,.75);border:1px solid rgba(91,205,202,.55);color:#bfeeec;text-shadow:0 1px 2px rgba(0,0,0,.9);}
.ft-turn-halo__ov-env--icon{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;padding:0;border-radius:50%;}
.ft-turn-halo__ov-env--icon img,.ft-turn-halo__ov-env--icon svg{width:20px !important;height:20px !important;}
.ft-turn-halo__ov-place-text span{color:rgba(233,242,242,.85);text-shadow:0 1px 2px rgba(0,0,0,.9);}
.ft-turn-halo__ov-parent{color:rgba(233,242,242,.6);}
.ft-turn-halo__ov-sep{margin-right:.28em;color:rgba(91,205,202,.8);}
.ft-turn-halo__ov-rung{display:block;padding-left:calc(var(--rung,0) * .85em);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.ft-turn-halo__ov-row{display:flex;align-items:center;gap:.45rem;padding:.1rem 0;white-space:nowrap;}
.ft-turn-halo__ov-row > span:first-child{display:inline-flex;width:18px;justify-content:center;flex:0 0 auto;}
.ft-turn-halo__ov-row img,.ft-turn-halo__ov-row svg{width:16px !important;height:16px !important;}
.ft-turn-halo__ov-muted{color:rgba(233,242,242,.6);}
.ft-turn-halo__ov-season{margin-left:auto;display:inline-flex;}
/* ── Portrait turn frame (owner 2026-09-15). In combat the movement budget drains CLOCKWISE around the
   portrait from 12 o'clock (a conic sweep masked to a 3px border — no measuring, follows any portrait
   ratio); on your own turn an End Turn strip spans the bottom while the art above still opens the sheet. */
@property --ft-move-fill{syntax:'<number>';inherits:true;initial-value:0;}
.ft-action-hands__portrait{position:relative;display:flex;align-self:stretch;}
.ft-action-hands__portrait > .ft-action-bars__sheet{align-self:stretch;}
.ft-action-hands__move-rim{position:absolute;inset:-3px;z-index:3;border-radius:8px;padding:3px;pointer-events:none;background:conic-gradient(from 0deg,#55bfff calc(var(--ft-move-fill) * 360deg),rgba(8,14,20,.85) 0);-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask:linear-gradient(#000 0 0) content-box exclude,linear-gradient(#000 0 0);filter:drop-shadow(0 0 3px rgba(85,191,255,.65));transition:--ft-move-fill .25s ease-out;}
.ft-action-hands__portrait.is-overbanked .ft-action-hands__move-rim{background:conic-gradient(from 0deg,#ffd633 calc(var(--ft-move-fill) * 360deg),rgba(8,14,20,.85) 0);filter:drop-shadow(0 0 3px rgba(255,214,51,.6));}
.ft-action-hands__portrait.is-move-previewing .ft-action-hands__move-rim{transition:--ft-move-fill .08s linear;filter:drop-shadow(0 0 5px rgba(91,192,255,.95));}
.ft-action-hands__portrait.is-move-preview-committed .ft-action-hands__move-rim{filter:drop-shadow(0 0 6px rgba(143,230,255,1));}
.ft-action-hands__move-readout{display:none;position:absolute;z-index:4;left:0;right:0;top:0;bottom:0;flex-direction:column;align-items:center;justify-content:center;gap:1px;border-radius:6px;background:rgba(4,10,16,.55);line-height:1;font-variant-numeric:tabular-nums;color:#f4fbff;text-shadow:0 0 8px rgba(85,191,255,.85),0 2px 3px rgba(0,0,0,.95);pointer-events:none;}
.ft-action-hands__move-readout strong{font-size:24px;font-weight:800;}
.ft-action-hands__move-readout small{font-size:9px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:#bfe9ff;}
.ft-action-hands__portrait:is(.is-move-previewing,.is-move-preview-committed) .ft-action-hands__move-readout{display:flex;}
.ft-action-hands__end-turn{position:absolute;z-index:5;left:0;right:0;bottom:0;min-height:22px;margin:0;padding:3px 2px;border:0;border-top:1px solid rgba(160,215,230,.55);border-radius:0 0 6px 6px;background:linear-gradient(180deg,rgba(40,86,110,.96),rgba(12,30,40,.98));color:#f2f8ff;font:800 10px/1.05 ui-sans-serif,system-ui,sans-serif;letter-spacing:.08em;text-transform:uppercase;text-shadow:0 1px 2px rgba(0,0,0,.9);cursor:pointer;}
.ft-action-hands__end-turn:hover,.ft-action-hands__end-turn:focus-visible{background:linear-gradient(180deg,rgba(62,128,160,.98),rgba(18,48,62,.98));color:#fff;outline:none;box-shadow:inset 0 0 0 1px rgba(143,230,255,.8);}
@keyframes ftPortraitHurry{0%,100%{box-shadow:0 0 0 0 rgba(255,120,80,0);}50%{box-shadow:0 0 12px 2px rgba(255,120,80,.85);}}
.ft-action-hands__portrait.is-hurry .ft-action-hands__end-turn{background:linear-gradient(180deg,#c2552f,#6e2412);border-top-color:rgba(255,190,160,.8);animation:ftPortraitHurry 1.4s ease-in-out infinite;}
/* The louder End Turn once AP is gone: centred above the bar, nearest it among the floating panels. */
@keyframes ftEndTurnPromptPulse{0%,100%{box-shadow:0 0 0 1px rgba(255,170,120,.55),0 6px 18px rgba(0,0,0,.55),0 0 0 0 rgba(255,120,80,0);}50%{box-shadow:0 0 0 1px rgba(255,200,160,.9),0 6px 18px rgba(0,0,0,.55),0 0 22px 4px rgba(255,120,80,.6);}}
.ft-end-turn-prompt{display:flex;justify-content:center;margin:0 auto 8px;pointer-events:none;animation:ftIslandFlyUp .14s ease-out;}
.ft-end-turn-prompt__btn{pointer-events:auto;display:inline-flex;align-items:center;gap:10px;padding:6px 20px 6px 10px;border:1px solid rgba(255,190,150,.85);border-radius:999px;background:linear-gradient(180deg,#d4643a 0%,#8d2e17 100%);color:#fff;cursor:pointer;animation:ftEndTurnPromptPulse 1.6s ease-in-out infinite;}
.ft-end-turn-prompt__btn:hover,.ft-end-turn-prompt__btn:focus-visible{background:linear-gradient(180deg,#e8774a 0%,#a3381c 100%);outline:none;}
.ft-end-turn-prompt__glyph{display:block;width:30px;height:32px;line-height:0;}
.ft-end-turn-prompt__text{display:flex;flex-direction:column;align-items:flex-start;line-height:1.05;text-align:left;}
.ft-end-turn-prompt__text strong{font-size:17px;font-weight:900;letter-spacing:.08em;text-transform:uppercase;text-shadow:0 1px 3px rgba(0,0,0,.7);}
.ft-end-turn-prompt__text small{font-size:10px;font-weight:700;letter-spacing:.04em;color:#ffe3d6;}
@media (prefers-reduced-motion:reduce){.ft-end-turn-prompt__btn,.ft-action-hands__portrait.is-hurry .ft-action-hands__end-turn{animation:none;}}
/* BG3-style LEFT hand cluster (campaign dock only). Absolutely hung off the DOCK's left edge so the centred
   slot grid never moves; the dock is the containing block (.ft3d-campaign-actor-dock is position:absolute).
   Launchers (sheet/inventory) stack in a column, then the two hand slots. Hidden on tablet/phone where the
   top tab strip owns those launchers and there is no side room. */
.ft-action-hands{position:absolute;right:100%;bottom:6px;margin-right:6px;display:flex;align-items:flex-end;gap:6px;padding:5px 6px;border:1px solid var(--ftab-border);border-radius:10px;background:linear-gradient(180deg,var(--ftab-bg-1) 0%,var(--ftab-bg-2) 42%,var(--ftab-bg-3) 100%);box-shadow:inset 0 0 0 1px rgba(var(--ftab-rim-rgb),.3),inset 0 2px 3px rgba(0,0,0,.5),0 8px 24px rgba(0,0,0,.3);z-index:5;}
/* BG3 arrangement: the portrait tile (sheet launcher) on the LEFT stretches to the cluster's height; the
   RIGHT stack is the hand-slot row over the launcher row (same width, buttons 36px). The sheet tile
   lights up while the sheet window is open (data-ft-sheet-open on the dock, set by my_campaign.php
   setFt3dWindow — an attribute, not a class, so bar repaints keep it). */
.ft-action-hands{display:grid;grid-template-columns:auto auto;align-items:stretch;width:max-content;}
.ft-action-hands__stack{display:flex;flex-direction:column;gap:4px;}
.ft-action-hands__launchers{display:flex;flex-direction:row;gap:4px;}
.ft-action-hands .ft-action-bars__sheet{flex:0 0 auto;align-self:stretch;width:72px;min-width:72px;height:auto;min-height:0;padding:0;border-radius:6px !important;position:relative;overflow:visible;transition:none !important;}
.ft-action-hands .ft-action-bars__sheet--native-art{min-width:0;}
.ft-action-hands .ft-action-hands__launchers .ft-action-bars__btn,.ft-action-hands .ft-action-hands__launchers .ft-action-skills-launch,.ft-action-hands .ft-action-hands__launchers .ft-action-status__edit,.ft-action-hands__tile{flex:0 0 auto;width:32px;min-width:32px;height:32px;margin:0;padding:0;border-radius:6px !important;display:inline-flex;align-items:center;justify-content:center;border:1px solid rgba(var(--ftab-rim-rgb),.5) !important;background:linear-gradient(180deg,rgba(var(--ftab-rim-rgb),.16),rgba(0,0,0,.55)) !important;color:#e6ebf2 !important;}
.ft-action-hands__launchers{justify-content:flex-start;width:104px;}
.ft-action-hands__stealth{justify-content:flex-start;gap:4px;}
.ft-action-hands__tile{position:relative;cursor:pointer;line-height:0;}
.ft-action-hands__tile:disabled{opacity:.4;cursor:default;}
.ft-action-hands__tile.is-active{background:linear-gradient(180deg,rgba(var(--ftab-accent-rgb),.34),rgba(0,0,0,.5)) !important;box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.4) !important;}
.ft-action-hands__tile.is-active:disabled{opacity:.9;}
.ft-action-hands__tile.is-hidden{background:linear-gradient(180deg,rgba(63,120,128,.75),rgba(24,48,52,.9)) !important;border-color:rgba(111,226,219,.7) !important;box-shadow:inset 0 0 0 1px rgba(111,226,219,.35),0 0 8px rgba(63,120,128,.6) !important;}
.ft-action-hands .ft-action-hands__launchers .ft-action-skills-launch.is-open{background:linear-gradient(180deg,rgba(var(--ftab-accent-rgb),.34),rgba(0,0,0,.5)) !important;box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.4) !important;}
.ft-action-hands .ft-action-hands__launchers img,.ft-action-hands .ft-action-hands__launchers svg,.ft-action-hands .ft-action-hands__launchers .ft-icon-map{width:20px;height:20px;}
.ft-action-hands .ft-action-hands__launchers .ft-action-bars__inventory img,.ft-action-hands .ft-action-hands__launchers .ft-action-bars__inventory svg{width:18px;height:18px;}
.ft-action-bars__portrait-initials{position:absolute;inset:0;display:grid;place-items:center;border-radius:5px;background:radial-gradient(circle at 45% 35%,#496965,#162729 72%);color:#f7d99c;font:900 24px/1 ui-sans-serif,system-ui,sans-serif;letter-spacing:.02em;}
.ft-action-bars__portrait{position:absolute;inset:0;width:100%;height:100%;border-radius:5px;object-fit:cover;object-position:top center;display:block;}
[data-ft-sheet-open="1"] .ft-action-bars__sheet{border-color:rgba(var(--ftab-accent-rgb),.85) !important;box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.55),0 0 8px rgba(var(--ftab-accent-rgb),.4) !important;}
.ft-action-bars__portrait-num{position:absolute;right:3px;bottom:3px;min-width:16px;height:16px;padding:0 3px;border-radius:8px;background:rgba(8,10,16,.92);border:1px solid rgba(255,255,255,.4);color:#fff;font:700 9px/13px ui-monospace,SFMono-Regular,Menlo,monospace;text-align:center;pointer-events:none;}
.ft-action-hands__slots{display:flex;gap:4px;width:104px;}
.ft-action-hands .ft-action-slot--hand{flex:1 1 0;width:50px;min-width:0;}
.ft-action-hands__slots--natural-extra{justify-content:center;}
.ft-action-hands__slots--natural-extra .ft-action-slot--hand{flex:0 0 50px;width:50px;}
.ft-action-natural-btn{isolation:isolate;width:100%;height:100%;padding:0;background:transparent;border:0;display:flex;align-items:center;justify-content:center;overflow:hidden;position:relative;}
.ft-action-natural-btn .token-img{width:100%;height:100%;object-fit:contain;}
.ft-action-natural-btn > .ft-icon,.ft-action-natural-btn > .ft-icon-map,.ft-action-natural-btn > svg{width:82%;height:82%;}
.ft-action-slot--hand-empty{cursor:default;}
.ft-action-slot--hand-empty .ft-icon,.ft-action-slot--hand-empty .ft-icon-map,.ft-action-slot--hand-empty img,.ft-action-slot--hand-empty svg{opacity:.35;}
.ft-action-slot--hand-ghost{opacity:.3;cursor:default;}
/* Open hand: weapon art is mostly dark, so lift it with the gold glow instead of relying on the border alone. */
.ft-action-slot--hand.ft-action-slot--menu-active img,.ft-action-slot--hand.ft-action-slot--menu-active .token-img{filter:brightness(1.45) saturate(1.1);}
/* PLACEMENT (.is-inline) is decided by MEASURED GEOMETRY, not by a viewport breakpoint — see
   placeHandsCluster(). The old @media (max-width:1024px) fold assumed the only way to run out of
   room beside the dock was a narrow viewport. It isn't: the dock is centred and capped at
   --ft3d-actionbar-width, so the free space to its left is (stage - dock) / 2. Native-aspect artwork
   widened the cluster to ~220px and the WHOLE absolutely-hung cluster fell off the LEFT edge of the
   screen anywhere from 1025–1280px, and much wider still at bar size "l" (72.5rem) — the owner's
   2026-08-31 "still bugged" report. The cluster now folds inline whenever the measured gap can't
   hold it, at ANY viewport width or drawer/bar-size state.
   The markup ships WITH .is-inline: the fail-safe direction is "visible in the flow", so a placement
   pass that never runs degrades to a working bar rather than an invisible one. */
.ft-action-hands.is-inline{position:static;flex:0 0 auto;align-self:flex-start;margin:0 4px 0 0;padding:4px;gap:4px;box-shadow:none;}
/* UNIT TABS: the summons strip on top of the cluster. Spans both grid columns; width:0 + min-width:100%
   keeps a long strip from widening the cluster (its intrinsic contribution is 0) — extra tabs wrap. */
/* REAL TABS (owner 2026-09-01): the strip hangs OFF THE TOP EDGE of the card as attached file-folder
   tabs — it costs the card no interior height. The selected tab is unmistakable: full opacity, the
   card's own surface colour, an accent cap, and its bottom edge fused into the card border. */
.ft-action-hands{overflow:visible;}
.ft-action-hands .ft-action-units{position:absolute;left:-1px;bottom:100%;z-index:6;display:flex;align-items:flex-end;gap:2px;margin:0;padding:0 0 0 4px;width:max-content;max-width:calc(100% + 2px);flex-wrap:wrap;}
.ft-action-units__tab{position:relative;flex:0 0 auto;width:40px;height:30px;margin:0 0 -1px;padding:0;border:1px solid rgba(var(--ftab-rim-rgb),.4);border-bottom:0;border-radius:8px 8px 0 0;background:linear-gradient(180deg,rgba(8,14,18,.96),rgba(0,0,0,.9));color:#e6ebf2;overflow:hidden;cursor:pointer;opacity:.55;filter:saturate(.7);transition:opacity .12s,height .12s,filter .12s,border-color .12s;}
.ft-action-units__tab:hover{opacity:.9;filter:none;}
.ft-action-units__tab.is-active{opacity:1;filter:none;height:36px;width:46px;border-color:var(--ftab-border);background:linear-gradient(180deg,var(--ftab-bg-1) 0%,var(--ftab-bg-2) 60%,var(--ftab-bg-3) 100%);box-shadow:inset 0 3px 0 rgba(var(--ftab-accent-rgb),.95),inset 0 0 0 1px rgba(var(--ftab-rim-rgb),.35),0 -4px 12px rgba(var(--ftab-accent-rgb),.28);z-index:2;}
.ft-action-units__tab.is-active::after{content:"";position:absolute;left:0;right:0;bottom:0;height:2px;background:var(--ftab-bg-1);}
.ft-action-units__tab.is-home::before{content:"";position:absolute;left:3px;top:5px;width:6px;height:6px;border-radius:50%;background:rgba(var(--ftab-accent-rgb),.95);box-shadow:0 0 4px rgba(var(--ftab-accent-rgb),.9);z-index:2;}
/* .is-turn carries no styling (owner 2026-09-01: no glow/flicker on the tab whose unit owns the turn). */
/* Inline fallback (cluster folded into the bar's flow): keep the tabs in the flow above the cluster. */
.ft-action-hands.is-inline .ft-action-units{position:static;grid-column:1 / -1;width:0;min-width:100%;max-width:none;padding:0 0 2px;}
.ft-action-hands.is-inline .ft-action-units__tab{margin-bottom:0;}
@keyframes ftUnitTurnPulse{0%,100%{box-shadow:0 0 0 1px rgba(245,215,110,.55),0 0 6px rgba(245,215,110,.35);}50%{box-shadow:0 0 0 1px rgba(245,215,110,.95),0 0 14px rgba(245,215,110,.8);}}
.ft-action-units__tab.is-elsewhere,.ft-action-units__tab.is-dead{opacity:.35;cursor:default;filter:grayscale(.85);}
.ft-action-units__tab.is-elsewhere::after{content:"\\21E2";position:absolute;right:2px;bottom:0;font-size:12px;line-height:1;color:#dfe6ee;text-shadow:0 0 3px #000;z-index:2;}
.ft-action-units__tab.is-dead::after{content:"\\2717";position:absolute;right:2px;bottom:0;font-size:11px;line-height:1;color:#ff9a9a;text-shadow:0 0 3px #000;z-index:2;}
.ft-action-units__pic{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;object-position:top;}
.ft-action-units__initials{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;letter-spacing:.02em;color:#dfe6ee;}
.ft-action-units__num{position:absolute;right:1px;bottom:0;padding:0 3px;font-size:10px;line-height:13px;font-weight:700;border-radius:4px;background:rgba(0,0,0,.75);color:#fff;z-index:2;}
/* Tablet/phone SIZING stays viewport-driven — it is a touch-surface decision (owner 2026-08-27),
   independent of where the cluster is placed. */
@media (max-width:1024px){
  .ft-action-hands .ft-action-bars__sheet{width:44px;min-width:44px;}
  .ft-action-hands .ft-action-bars__sheet--native-art{min-width:0;}
  .ft-action-hands__slots,.ft-action-hands__launchers{width:80px;}
  .ft-action-hands .ft-action-slot--hand{width:38px;min-width:0;}
  .ft-action-hands .ft-action-hands__launchers .ft-action-bars__btn,.ft-action-hands .ft-action-hands__launchers .ft-action-skills-launch,.ft-action-hands .ft-action-hands__launchers .ft-action-status__edit,.ft-action-hands__tile{width:24px;min-width:24px;height:24px;}
  .ft-action-hands .ft-action-hands__launchers img,.ft-action-hands .ft-action-hands__launchers svg,.ft-action-hands .ft-action-hands__launchers .ft-icon-map{width:16px;height:16px;}
}
.ft-action-bars__varctl{display:flex;align-items:center;gap:5px;}
.ft-action-bars__lockctl{display:flex;align-items:center;}
.ft-action-bars__btn{width:30px;height:30px;min-width:30px;padding:0;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-weight:600;line-height:1;font-size:13px;}
.ft-action-bars__adddiv{font-size:11px;color:#fda4af;border-color:rgba(225,29,72,.4) !important;}
/* Controls take the theme accent (matches the frame): the Settings gear + the far-right contextual buttons.
   Glyph stays a light near-white for legibility across every theme; rim/glow follow --ftab-*. */
.ft-action-context-btn{border:1px solid rgba(var(--ftab-rim-rgb),.45) !important;background:linear-gradient(180deg,rgba(var(--ftab-rim-rgb),.18),rgba(0,0,0,.5)) !important;color:#e6ebf2 !important;}
.ft-action-context-btn:hover{border-color:rgba(var(--ftab-accent-rgb),.6) !important;box-shadow:0 0 8px rgba(var(--ftab-accent-rgb),.32) !important;}
.ft-action-bars__settings,.ft-action-bars__inventory{border:1px solid rgba(var(--ftab-rim-rgb),.5) !important;background:linear-gradient(180deg,rgba(var(--ftab-rim-rgb),.16),rgba(0,0,0,.55)) !important;color:#e6ebf2 !important;}
.ft-action-bars__settings:hover,.ft-action-bars__inventory:hover{border-color:rgba(var(--ftab-accent-rgb),.65) !important;box-shadow:0 0 8px rgba(var(--ftab-accent-rgb),.35) !important;}
.ft-action-bars__settings.is-open{background:linear-gradient(180deg,rgba(var(--ftab-accent-rgb),.34),rgba(0,0,0,.5)) !important;color:#fff !important;box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.4) !important;}
.ft-action-bars__inventory[aria-pressed="true"]{background:linear-gradient(180deg,rgba(var(--ftab-accent-rgb),.34),rgba(0,0,0,.5)) !important;color:#fff !important;box-shadow:inset 0 0 0 1px rgba(var(--ftab-accent-rgb),.4),0 0 8px rgba(var(--ftab-accent-rgb),.28) !important;}
/* Floating drag ghost (ability bind/move). */
/* Drag shield: while a bar button is held, the same-origin FT3D encounter iframe must not hit-test, or it
   swallows the pointer stream the moment the cursor crosses into the scene and the release never reaches
   the parent document (no unbind, orphaned ghost). See shieldEncounterFrame. */
body.ft-ab-drag-shield [data-ft3d-encounter-frame]{pointer-events:none;}
.ft-action-drag-ghost{position:fixed;z-index:30001;width:52px;height:52px;transform:translate(-50%,-50%);pointer-events:none;opacity:.85;filter:drop-shadow(0 2px 6px rgba(0,0,0,.6));}
.ft-action-drag-ghost .ft-rune-mount{width:100%;height:100%;}
/* ─── Mobile / tablet (owner 2026-06-27) ─────────────────────────────────────────────────────────────────
   The slot row already x-scrolls (overflow-x:auto, slots stay ≥44px = the iOS min touch target), so we don't
   shrink slots — we (a) grow the SURROUNDING chrome to finger size on any touch pointer, and (b) tighten outer
   padding + resource min-widths on narrow viewports so the bar fits without crowding. No layout reflow of the
   absolutely-centered island (kept stable to avoid surprises). */
@media (pointer:coarse){
  /* Option fold-up: finger-sized upcast stepper / cast / ritual / cancel. */
  .ft-ab-choice-step{width:34px;height:34px;font-size:18px;}
  .ft-ab-choice-num{width:64px;height:34px;font-size:15px;}
  .ft-action-choicebar__cast,.ft-action-choicebar__ritual{padding:9px 16px;font-size:13px;}
  .ft-action-choicebar__cancel{width:32px;height:32px;font-size:14px;}
  .ft-action-choicebar__upcast-val{font-size:16px;min-width:30px;}
  /* Bar chrome buttons up to a comfortable tap size. */
  .ft-island-cat{width:36px;height:36px;}
  .ft-island-cat .ft-icon,.ft-island-cat .ft-icon-map,.ft-island-cat svg,.ft-island-cat img{width:26px;height:26px;}
  .ft-action-status__edit{width:36px;height:36px;min-width:36px;}
  .ft-action-status__icon{width:30px;height:30px;}
  .ft-action-status__icon img,.ft-action-status__icon svg,.ft-action-status__icon .ft-rune-mount,.ft-action-status__icon .ft-icon,.ft-action-status__icon .ft-icon-map{width:28px;height:28px;}
  .ft-action-bars__btn{width:36px;height:36px;min-width:36px;}
  .ft-action-context-btn,.ft-action-context .btn{width:38px;height:38px;min-width:38px;}
  .ft-action-res-chg{width:24px;height:24px;}
  .ft-action-mech-carrot{width:22px;height:14px;}
}
/* ≤820px = phone + small-tablet (matches the dock's 99vw breakpoint in 30-main-body.css, so the inner chrome
   tightens in lockstep with the bar going full-width — 768 tablet portrait + landscape phones included). */
@media (max-width:820px){
  /* Reclaim width for the slot row; let chrome breathe less. */
  .ft-action-bars__main{gap:5px;padding:5px 5px 4px;}
  .ft-action-bars-top{padding:3px 5px;}
  .ft-action-status{padding:5px 6px 4px;gap:6px;}
  .ft-action-resources{gap:6px 8px;padding:3px 6px 5px;}
  .ft-action-res-bar{min-width:82px;}
  .ft-action-res-bar--mech{min-width:104px;}
  .ft-action-choicebar{padding:7px 9px;}
  .ft-action-choicebar__head{flex-wrap:wrap;}
  .ft-action-choicebar__hint{flex:1 1 100%;font-size:9px;}
  .ft-ab-choice-select{min-width:120px;}
  .ft-action-targetingbar .ft-action-choicebar__head{gap:5px;}
  .ft-action-targetingbar__option .ft-action-choicebar__upeffects{display:none;}
}
/* Short viewports (landscape phones ~390px tall — often >820px WIDE so they miss the block above): claw back
   vertical room by flattening the chrome rows so the map keeps as much height as possible. */
@media (max-height:540px){
  .ft-action-bars-top{padding-top:2px;padding-bottom:2px;min-height:0;}
  .ft-action-status{padding-top:2px;padding-bottom:2px;}
  .ft-action-resources{padding-top:2px;padding-bottom:3px;gap:4px 8px;}
  .ft-action-bars__main{padding-top:4px;padding-bottom:3px;}
}
/* ─── Small-screen TOP-ROW TABS (full-screen dock + in-sheet bar; owner 2026-06-27, in-sheet 2026-06-30) ───
   On phone/tablet the meta row (status cluster + ability island + controls + contextual actions) is too
   crowded for BOTH the dock and the in-sheet character-sheet bar, so it collapses to a tab strip that shows
   ONE group at a time. Desktop keeps everything visible (the strip is display:none and the group-hiding rules
   live inside the ≤1024 query). Slots + resources are
   NOT tabbed — they always render below. */
.ft-action-bars__toptabs{display:none;gap:4px;padding:4px 6px 0;}
.ft-action-toptab{flex:1 1 0;min-width:0;display:inline-flex;align-items:center;justify-content:center;gap:5px;font-size:11px;font-weight:700;letter-spacing:.02em;padding:6px 6px;border-radius:7px 7px 0 0;border:1px solid rgba(255,255,255,.14);border-bottom:0;background:rgba(255,255,255,.05);color:rgba(255,255,255,.66);cursor:pointer;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.ft-action-toptab img,.ft-action-toptab svg,.ft-action-toptab .ft-icon,.ft-action-toptab .ft-icon-map{width:18px;height:18px;display:block;flex:0 0 auto;}
.ft-action-toptab:hover{color:#fff;background:rgba(255,255,255,.1);}
.ft-action-toptab.is-active{background:linear-gradient(180deg,rgba(var(--ftab-accent-rgb),.32),rgba(0,0,0,.4));color:#fff;border-color:rgba(var(--ftab-accent-rgb),.5);box-shadow:inset 0 1px 0 rgba(255,255,255,.08);}
/* The CONDITIONAL ("Actions") tab needs no badge — it only EXISTS when a situational action is available, so it
   appearing in the strip is the signal. (The --cond class is kept as a hook in case we want an accent later.) */
/* Standalone settings GEAR — its own button at the right of the strip (not a tab; opens the floating tray). */
.ft-action-topgear{flex:0 0 auto;margin-left:2px;display:inline-flex;align-items:center;justify-content:center;padding:6px 9px;border-radius:7px 7px 0 0;border:1px solid rgba(255,255,255,.14);border-bottom:0;background:rgba(255,255,255,.05);color:rgba(255,255,255,.7);cursor:pointer;}
.ft-action-topgear img,.ft-action-topgear svg,.ft-action-topgear .ft-icon,.ft-action-topgear .ft-icon-map{width:18px;height:18px;display:block;}
.ft-action-topgear:hover{color:#fff;background:rgba(255,255,255,.1);}
.ft-action-topgear.is-open{background:linear-gradient(180deg,rgba(var(--ftab-accent-rgb),.34),rgba(0,0,0,.4));color:#fff;border-color:rgba(var(--ftab-accent-rgb),.5);}
@media (max-width:1024px){
  .ft-action-bars__toptabs{display:flex;}
  /* Restore the original single mobile content row beneath the tabs. display:contents puts Skills and
     Status back into that flex row, while the active tab decides which meta group is visible. */
  .ft-action-bars-top{display:flex;align-items:center;column-gap:0;row-gap:0;}
  .ft-action-bars-top__left{display:contents;}
  .ft-action-status__icon--overflow{display:inline-flex;}
  .ft-action-status__more{display:none;}
  /* Hide every meta group; the active tab re-shows exactly one. The bar controls (gear) stay hidden on mobile —
     the gear is a standalone button in the strip now. !important beats the per-group display rules. */
  .ft-action-bars[data-ft-toptab] .ft-action-status,
  .ft-action-bars[data-ft-toptab] .ft-action-island,
  .ft-action-bars[data-ft-toptab] .ft-ability-flyout,
  .ft-action-bars[data-ft-toptab] .ft-action-context,
  .ft-action-bars[data-ft-toptab] .ft-action-bars__controls{display:none !important;}
  /* Abilities / Spells / Curses / Mentas each show the island (de-absolute'd), filtered to their own group's
     category icons. ONE non-wrapping row that scrolls horizontally (wrapping grew the bar height = the "pop";
     scroll keeps a fixed height + reaches overflow). */
  .ft-action-bars[data-ft-toptab="abilities"] .ft-action-island,
  .ft-action-bars[data-ft-toptab="spells"]    .ft-action-island,
  .ft-action-bars[data-ft-toptab="curses"]    .ft-action-island,
  .ft-action-bars[data-ft-toptab="mentas"]    .ft-action-island{display:flex !important;position:static;transform:none;inset:auto;width:auto;flex:1 1 0;min-width:0;max-width:100%;flex-wrap:nowrap;overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;scrollbar-width:thin;}
  /* Within the shown island, show ONLY the active group's cats (hide every non-matching group). */
  .ft-action-bars[data-ft-toptab="abilities"] .ft-island-cat:not([data-ft-island-group="abilities"]),
  .ft-action-bars[data-ft-toptab="spells"]    .ft-island-cat:not([data-ft-island-group="spells"]),
  .ft-action-bars[data-ft-toptab="curses"]    .ft-island-cat:not([data-ft-island-group="curses"]),
  .ft-action-bars[data-ft-toptab="mentas"]    .ft-island-cat:not([data-ft-island-group="mentas"]){display:none !important;}
  /* Flyout shows only when the OPEN category belongs to the active tab's group. */
  .ft-action-bars[data-ft-toptab="abilities"] .ft-ability-flyout[data-ft-island-group="abilities"],
  .ft-action-bars[data-ft-toptab="spells"]    .ft-ability-flyout[data-ft-island-group="spells"],
  .ft-action-bars[data-ft-toptab="curses"]    .ft-ability-flyout[data-ft-island-group="curses"],
  .ft-action-bars[data-ft-toptab="mentas"]    .ft-ability-flyout[data-ft-island-group="mentas"]{display:flex !important;}
  .ft-action-bars[data-ft-toptab="status"]   .ft-action-status{display:flex !important;}
  /* Conditional tab → the situational actions as a left-aligned scrolling row like the others. The contextual
     cluster lives in __right (margin-left:auto, row-reverse); override it to fill + read left-to-right. The
     settings GEAR moved to the strip, so .ft-action-bars__controls stays hidden on mobile (never re-shown). */
  .ft-action-bars[data-ft-toptab="conditional"] .ft-action-bars-top__right{margin-left:0;flex:1 1 0;min-width:0;}
  .ft-action-bars[data-ft-toptab="conditional"] .ft-action-context{display:flex !important;flex:1 1 0;min-width:0;max-width:100%;flex-direction:row;flex-wrap:nowrap;justify-content:flex-start;overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;}
  /* Status cluster: same single scrolling row; the manage button stays pinned left while the icons scroll. */
  .ft-action-bars[data-ft-toptab="status"] .ft-action-status{flex:1 1 0;min-width:0;max-width:100%;flex-wrap:nowrap;overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;scrollbar-width:thin;}
  .ft-action-bars[data-ft-toptab="status"] .ft-action-status__edit{position:sticky;left:0;z-index:1;}
  /* Spell medallions in the fly-up also scroll horizontally rather than wrapping into a tall block. */
  .ft-ability-flyout__strip{flex-wrap:nowrap;overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;scrollbar-width:thin;justify-content:flex-start;}
  /* Hide the swipe-row scrollbars so they don't eat into the fixed 46px row (touch = swipe to scroll). */
  .ft-action-status,
  .ft-action-island,
  .ft-action-context{scrollbar-width:none;}
  .ft-action-status::-webkit-scrollbar,
  .ft-action-island::-webkit-scrollbar,
  .ft-action-context::-webkit-scrollbar{width:0;height:0;display:none;}
  /* FIXED row height so switching tabs never "pops" the bar up/down. The status cluster is the tallest group
     (30–36px manage button + 25px rune mounts + its OWN 9px vertical padding), which exceeded a min-height floor
     while the icon-only Abilities/Spells rows sat under it. A fixed height (not min-height) + border-box pins
     every tab to the same height; zeroing the status cluster's vertical padding keeps its content within it
     (centered, not clipped). */
  .ft-action-bars-top{height:46px;min-height:46px;box-sizing:border-box;align-items:center;overflow:hidden;}
  .ft-action-bars[data-ft-toptab="status"] .ft-action-status{padding-top:0;padding-bottom:0;}
  /* SPELLS + MENTAS tabs: the LEVEL number is the hard-to-read part, so show it BIG and drop the glyph (each is
     in its own tab now, so a "1" can't collide across pools). Curses have no level → they keep their icon. Hide
     everything in the button EXCEPT the level badge, robust to however the icon mounts (i/img/svg/canvas). */
  .ft-action-bars[data-ft-toptab="spells"] .ft-island-cat[data-ft-island-key="spell"] > *:not(.ft-island-lvl),
  .ft-action-bars[data-ft-toptab="mentas"] .ft-island-cat[data-ft-island-key="menta"] > *:not(.ft-island-lvl){display:none !important;}
  .ft-action-bars[data-ft-toptab="spells"] .ft-island-cat[data-ft-island-key="spell"],
  .ft-action-bars[data-ft-toptab="mentas"] .ft-island-cat[data-ft-island-key="menta"]{font-variant-numeric:tabular-nums;}
  .ft-action-bars[data-ft-toptab="spells"] .ft-island-cat[data-ft-island-key="spell"] .ft-island-lvl,
  .ft-action-bars[data-ft-toptab="mentas"] .ft-island-cat[data-ft-island-key="menta"] .ft-island-lvl{position:static;inset:auto;font-size:16px;line-height:1;font-weight:800;color:#fff;background:transparent;border:0;padding:0;box-shadow:none;}
}
@media (pointer:coarse){
  .ft-action-toptab{padding:9px 6px;font-size:12px;}
}
`;
    document.head.appendChild(style);
}

// An inspected sheet can be the player's first action, before any actor dock has rendered.
// Wait for the tail's press-hold setup, then register its blocker before the action intercept.
function ensureSheetAbilityClickHandlers(): void {
    window.setupSheetAbilityPressForInfo?.();
    ensureAbilityDrag();
    ensureAbilityChoiceBar();
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureSheetAbilityClickHandlers, { once: true });
} else {
    ensureSheetAbilityClickHandlers();
}
